(() => {
  'use strict';

  const initialized = new WeakSet();
  const instances = new WeakMap();
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqDark = window.matchMedia('(prefers-color-scheme: dark)');
  const USD_PRICE_BRACKETS = new Set([
    'usd_le_50',
    'usd_50_75',
    'usd_75_125',
    'usd_125_200',
    'usd_gt_200'
  ]);
  const V4_FOREGROUND_ATTEMPT_TIMEOUT_MS = 3500;
  const V4_BACKGROUND_ATTEMPT_TIMEOUT_MS = 5000;
  const V4_PROGRESSIVE_DEADLINE_MS = 12000;

  const PALETTES = {
    dark: {
      'archive-noir': { page: '#090c10', surface: '#11161d', card: '#11171d', text: '#f5f7fa', muted: '#96a1af', line: '#2a313a', accent: '#92d2fe', accent2: '#e468ff', image: '#ffffff' },
      'midnight-blue': { page: '#07111d', surface: '#0d1a28', card: '#101f2f', text: '#f4f8fc', muted: '#91a6ba', line: '#22384b', accent: '#77c8ff', accent2: '#9a8cff', image: '#ffffff' },
      'carbon-studio': { page: '#0b0b0c', surface: '#151516', card: '#19191b', text: '#f4f4f4', muted: '#99999f', line: '#303034', accent: '#d6ff54', accent2: '#8ad9ff', image: '#ffffff' },
      'electric-museum': { page: '#100b17', surface: '#191121', card: '#21162b', text: '#fbf7ff', muted: '#b1a0bd', line: '#3a2848', accent: '#73e4ff', accent2: '#ff62d2', image: '#ffffff' }
    },
    light: {
      'gallery-white': { page: '#f3f5f7', surface: '#ffffff', card: '#ffffff', text: '#12171d', muted: '#65707c', line: '#d7dde3', accent: '#1479b8', accent2: '#a93ebd', image: '#ffffff' },
      'paper-archive': { page: '#f3efe7', surface: '#fbf8f1', card: '#fffdf8', text: '#211f1a', muted: '#756f64', line: '#d9d0c2', accent: '#b94f2f', accent2: '#426e85', image: '#ffffff' },
      'ice-studio': { page: '#eef5f8', surface: '#f8fcfe', card: '#ffffff', text: '#10212b', muted: '#667b87', line: '#cbdce4', accent: '#087fa8', accent2: '#7659c7', image: '#ffffff' },
      'warm-ledger': { page: '#f5f0ea', surface: '#fffaf5', card: '#fffdf9', text: '#251c17', muted: '#7d6b60', line: '#dfd2c8', accent: '#a83f30', accent2: '#7a5a9b', image: '#ffffff' }
    }
  };

  const clampNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const clampRange = (value, fallback, minimum, maximum) => {
    return Math.min(maximum, Math.max(minimum, clampNumber(value, fallback)));
  };

  const colorToRgb = (value, fallback = '5 8 12') => {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    const shortHex = /^#([0-9a-f]{3})$/i.exec(trimmed);
    if (shortHex) {
      return shortHex[1].split('').map((digit) => parseInt(`${digit}${digit}`, 16)).join(' ');
    }
    const longHex = /^#([0-9a-f]{6})$/i.exec(trimmed);
    if (longHex) {
      const hex = longHex[1];
      return [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map((pair) => parseInt(pair, 16)).join(' ');
    }
    return fallback;
  };

  const setVar = (element, name, value) => {
    if (value !== undefined && value !== null && value !== '') {
      element.style.setProperty(name, String(value));
    }
  };

  class AdvancedCollections {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.s = this.config.settings || {};
      this.kind = this.config.kind || root.dataset.kind || 'collection';
      this.sectionId = this.config.sectionId || root.dataset.sectionId;
      this.engineMode = String(
        this.config.engineMode
        || root.dataset.searchEngineMode
        || 'legacy'
      ).trim().toLowerCase();
      if (!['v4_metafield', 'legacy', 'native_shopify'].includes(this.engineMode)) {
        this.engineMode = 'legacy';
      }
      this.v4RequestCount = 0;
      this.v4ExactResponseMs = null;
      this.v4RateLimitedUntil = 0;
      this.v4TransportActive = 0;
      this.v4TransportQueue = [];
      this.v4ProgressiveAbortController = null;
      this.v4ProgressiveDeadlineTimer = null;
      this.v4RecognizedAnalysis = null;
      this.v4NativeFallbackAuthorized = false;
      this.customerLayout = null;
      this.layoutBreakpoint = null;
      this.toneOverride = null;
      this.siteObserver = null;
      this.searchOrderPending = false;
      this.searchReadyBound = false;
      this.searchManifest = null;
      this.searchManifestBuild = null;
      this.searchManifestRecoveryAttempted = false;
      this.searchCurrentMappedPage = null;
      this.searchPageRenderPending = false;
      this.searchViewGeneration = 0;
      this.searchWarmGeneration = 0;
      this.searchNativeWarmGeneration = 0;
      this.searchNativePageCache = new Map();
      this.searchNativePagePromises = new Map();
      this.mappedSearchHtmlCache = new Map();
      this.searchNativeCacheKey = '';
      this.searchNativeAbortController = null;
      this.searchScopeAbortController = null;
      this.searchScopeLoad = null;
      this.searchScopeGeneration = 0;
      this.searchPageImageWarmers = new Set();
      this.handlePopState = null;
      this.filterDialogHistoryKey = 'vccAcFilterDialog';
      this.filterDialogHistoryOwner = `vcc-ac-filter:${this.sectionId || this.kind}`;
      this.filterDialogHistoryToken = '';
      this.filterDialogHistoryRoute = '';
      this.filterDialogHistoryBaseRoute = '';
      this.filterDialogHistoryNormalizedUrl = '';
      this.filterDialogObservedRoute = this.filterDialogRouteIdentity();
      this.filterDialogHistoryTraversalPending = false;
      this.filterDialogHistoryTraversalTimer = 0;
      this.filterDialogCloseTimer = 0;
      this.filterDialogFocusFrame = 0;
      this.filterDialogClosing = false;
      this.filterDialogOpener = null;
      this.filterDialogRestoreFocusOnClose = true;
      this.filterDialogNavigationPending = false;
      this.handleResponsiveChange = null;
      this.handleReducedMotionChange = null;
      this.handleDarkModeChange = null;
      this.handleWindowResize = null;
      this.handlePageShow = null;
      this.searchCuration = root.__vccSearchCuration || null;
      this.searchCurationAbortController = null;
      this.searchCurationGeneration = 0;
      this.searchCurationFinisherFrame = 0;
      this.searchCurationFinisherTimer = null;
      this.mappedSearchTransitionFrame = 0;
      this.mappedSearchTransitionTimer = null;
      this.mappedSearchTransitionGeneration = 0;
      this.destroyed = false;
      this.quickFilterTimer = null;
      this.toolbarSentinel = null;
      this.mobileDockObserver = null;
      this.mobileDockLatched = false;
      this.mobileDockScrollRoot = null;
      this.gridImageObserver = null;
      this.gridImageQueue = [];
      this.gridImageActive = 0;
      this.gridWarmScheduled = false;
      this.gridWarmStarted = false;
      this.atlasPreloads = new Map();
      this.atlasPreloadQueue = [];
      this.atlasPreloadActive = 0;
      this.atlasPreloadScheduled = false;
      this.atlasWarmStarted = false;
      this.atlasPreloadWaitTimer = null;
      this.atlasAuxTimer = null;
      this.atlasRenderId = 0;
      this.atlasGeometryFrame = 0;
      this.activeAtlasCard = null;

      this.setupSearchCuration();
      this.applyStaticConfig();
      this.hideDuplicatePageHeading();
      this.resolveTone();
      this.resolveLayout();
      this.bind();
      this.restoreFilterDialogHistory();
      this.syncWishlistStates();
      this.applySearchOrder();
      this.updateAtlasInspector(this.root.querySelector('.vcc-ac-card'));
      this.configureImageLoading();
      this.syncResultStatus();
      this.syncMobilePager();
      this.setupMobileToolbarDock();
      if (this.kind === 'search' && this.searchCurationIsFallbackLocked()) {
        window.setTimeout(() => this.prepareNativeSearchPages(), 0);
      }
      this.log('Initialized', this.config);
    }

    readConfig() {
      const node = this.root.querySelector('[data-vcc-ac-config]');
      if (!node) return { settings: {} };
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[VCC Advanced Collections] Invalid config JSON', error);
        return { settings: {} };
      }
    }

    log(...args) {
      if (this.config.debug) console.info('[VCC Advanced Collections]', ...args);
    }

    markSearchCuration(name, detail = {}) {
      // Optional Pathway hook: fixed phase/count only, never query or analysis.
      if (typeof window.__vccPathwaySearch === 'function') {
        try {
          if (name === 'search-analysis-start') window.__vccPathwaySearch(this.root, this.searchCurationGeneration, 'start', 'requested');
          else if (name === 'search-mapped-price-band-empty') window.__vccPathwaySearch(this.root, this.searchCurationGeneration, 'final', 'succeeded', 0);
          else if (name === 'search-recognized-v4-fail-closed') window.__vccPathwaySearch(this.root, this.searchCurationGeneration, 'final', 'failed');
          else if (name === 'search-fallback-reveal') this.observePathwaySearchSnapshot();
        } catch (_) {}
      }
      const state = this.searchCuration;
      const now = window.performance?.now?.() || Date.now();
      if (state) {
        state.marks = state.marks || {};
        state.marks[name] = now;
      }
      try {
        window.performance?.mark?.(`vcc-${name}`, { detail });
      } catch (_error) {
        try {
          window.performance?.mark?.(`vcc-${name}`);
        } catch (_markError) {}
      }
    }

    searchCurationIsFallbackLocked() {
      return this.root.dataset.searchCuration === 'fallback-locked'
        || this.searchCuration?.lockMode === 'fallback';
    }

    searchCurationIsPending() {
      const phase = this.root.dataset.searchCuration;
      return Boolean(
        this.searchCuration
        && !this.searchCuration.locked
        && (phase === 'pending' || phase === 'committing')
      );
    }

    searchCurationRemaining() {
      const state = this.searchCuration;
      if (!state || !Number.isFinite(Number(state.startedAt)) || !Number.isFinite(Number(state.deadlineMs))) {
        return Number.POSITIVE_INFINITY;
      }
      return Math.max(0, Number(state.deadlineMs) - ((window.performance?.now?.() || Date.now()) - Number(state.startedAt)));
    }

    grantSearchCurationRenderGrace(minimumMs = 900) {
      const state = this.searchCuration;
      if (!state || state.locked || this.searchCurationIsFallbackLocked()) return false;
      const graceMs = Math.max(180, Number(minimumMs) || 900);
      if (this.searchCurationRemaining() >= graceMs) return true;
      const now = window.performance?.now?.() || Date.now();
      const elapsedMs = Math.max(0, now - Number(state.startedAt || now));
      const currentDeadlineMs = Math.max(0, Number(state.deadlineMs) || 0);
      const configuredMaximumMs = Number(state.maxDeadlineMs);
      const maximumDeadlineMs = Number.isFinite(configuredMaximumMs)
        ? Math.max(currentDeadlineMs, configuredMaximumMs)
        : elapsedMs + graceMs;
      state.deadlineMs = Math.min(
        maximumDeadlineMs,
        Math.max(currentDeadlineMs, elapsedMs + graceMs)
      );
      const remainingMs = Math.max(0, state.deadlineMs - elapsedMs);
      window.clearTimeout(state.deadlineTimer);
      state.deadlineTimer = window.setTimeout(() => {
        if (state.locked) return;
        if (state.manifestRecoveryActive === true) {
          this.handleSearchManifestRecoveryDeadline('manifest-recovery-deadline');
          return;
        }
        try {
          if (typeof state.handleDeadline === 'function') state.handleDeadline('render-deadline');
          else if (typeof state.onDeadline === 'function') state.onDeadline('render-deadline');
          else this.fallbackSearchCuration('render-deadline');
        } catch (_error) {
          this.fallbackSearchCuration('render-deadline-error');
        }
      }, remainingMs);
      if (
        state.globalOverlay
        && window.VCCSearchTransition
        && typeof window.VCCSearchTransition.rebaseSafety === 'function'
      ) {
        window.VCCSearchTransition.rebaseSafety();
      }
      return remainingMs > 0;
    }

    mappedSearchFailureSourceKey(state, page = 1) {
      const scope = String(state?.scope || this.mappedSearchSourceScope(state?.source) || 'unknown');
      const normalizedPage = Math.max(1, Number(page) || 1);
      const sourceKey = String(
        this.searchSourceKey(state?.source)
        || state?.source?.collectionHandle
        || state?.source?.transportQuery
        || state?.source?.query
        || `source-${Number(state?.sourceIndex || 0)}`
      ).replace(/\s+/g, '-');
      return `${scope}:p${normalizedPage}:${sourceKey}`;
    }

    recordMappedSearchSourceFailure(manifest, state, page = 1, result = null) {
      if (!manifest || !state) return '';
      if (!(manifest.failedSourceKeys instanceof Set)) {
        manifest.failedSourceKeys = new Set();
      }
      const key = this.mappedSearchFailureSourceKey(state, page);
      if (key) manifest.failedSourceKeys.add(key);
      if (result?.transportUnproven === true) {
        if (!(manifest.transportUnprovenSourceKeys instanceof Set)) {
          manifest.transportUnprovenSourceKeys = new Set();
        }
        if (key) manifest.transportUnprovenSourceKeys.add(key);
        state.failureReason = String(result.failureReason || 'transport-unproven');
        state.transportUnproven = true;
      }
      return key;
    }

    mappedSearchFailureSourceKeys(manifest) {
      const recorded = manifest?.failedSourceKeys instanceof Set
        ? Array.from(manifest.failedSourceKeys)
        : [];
      if (recorded.length) return recorded;

      const outcome = String(manifest?.outcome || '');
      const targetScope = outcome === 'exact-incomplete'
        ? 'exact'
        : (outcome === 'core-page1-incomplete' ? 'core' : '');
      if (!targetScope) return [];
      return (manifest?.sources || [])
        .filter((state) => state?.scope === targetScope)
        .filter((state) => {
          return state.failed === true
            || Number(state.page || 0) < 1
            || (
              targetScope === 'exact'
              && state.hasMore === true
              && state.exhausted !== true
            );
        })
        .map((state) => {
          return this.mappedSearchFailureSourceKey(
            state,
            Math.max(1, Number(state.page || 0) + 1)
          );
        });
    }

    mappedSearchRecoveryDeadlineMs(manifest) {
      return manifest?.analysis?.rootCta?.collectionHandle === 'vintage-classics'
        ? 45000
        : 12000;
    }

    canReplayMappedSearchManifest(manifest) {
      if (
        this.searchManifestRecoveryAttempted
        || !manifest
        || manifest.sealed
        || manifest.cancelled
        || manifest.capped
        || this.destroyed
        || !this.root.isConnected
        || this.searchCurationIsFallbackLocked()
      ) return false;
      if (!['exact-incomplete', 'core-page1-incomplete'].includes(String(manifest.outcome || ''))) {
        return false;
      }
      const state = this.searchCuration;
      const startedAt = Number(state?.startedAt);
      if (!state || state.locked || !Number.isFinite(startedAt)) return false;
      const elapsedMs = Math.max(0, (window.performance?.now?.() || Date.now()) - startedAt);
      return elapsedMs < this.mappedSearchRecoveryDeadlineMs(manifest) - 180;
    }

    settleMappedExactTierFailure(manifest, exactStates) {
      manifest.relationshipSettled = false;
      if (manifest.entries.length && manifest.progressiveCommitted === true) {
        manifest.outcome = 'focused-degraded';
        manifest.degradedReason = manifest.exactTierFailure?.reason
          || 'exact-continuation-incomplete';
        manifest.continuationInterrupted = true;
        manifest.hasMore = true;
        manifest.focusedHasMore = true;
        manifest.sealed = true;
        manifest.focusedEntries = manifest.entries.slice();
        manifest.baseEntries = manifest.entries.slice();
        manifest.progressiveEntries = null;
        manifest.progressiveLoading = false;
        this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
        this.refreshMappedSearchTierDescriptors(manifest);
        return true;
      }

      const requiredSafeExactIncomplete = exactStates.some((state) => {
        return state?.source?.safeExact === true;
      });
      manifest.safeExactFallbackBlocked = requiredSafeExactIncomplete;
      manifest.explicitSafeEmpty = requiredSafeExactIncomplete;
      manifest.broadEntryCount = 0;
      manifest.broadSettledCount = 0;
      manifest.outcome = 'exact-incomplete';
      manifest.hasMore = false;
      manifest.focusedHasMore = false;
      manifest.sealed = false;
      return false;
    }

    armSearchManifestRecoveryDeadline(hardDeadlineMs = 12000) {
      const state = this.searchCuration;
      const startedAt = Number(state?.startedAt);
      if (!state || state.locked || !Number.isFinite(startedAt)) return false;
      const hardCapMs = Math.max(1000, Number(hardDeadlineMs) || 12000);
      const elapsedMs = Math.max(0, (window.performance?.now?.() || Date.now()) - startedAt);
      const remainingMs = Math.max(0, hardCapMs - elapsedMs);
      if (remainingMs <= 180) return false;

      state.maxDeadlineMs = hardCapMs;
      state.deadlineMs = hardCapMs;
      state.manifestRecoveryActive = true;
      this.root.dataset.searchManifestRecoveryActive = 'true';
      window.clearTimeout(state.deadlineTimer);
      state.deadlineTimer = window.setTimeout(() => {
        this.handleSearchManifestRecoveryDeadline('manifest-recovery-deadline');
      }, remainingMs);
      this.root.dataset.searchManifestRecoveryDeadlineMs = String(Math.round(hardCapMs));
      this.root.dataset.searchManifestRecoveryRemainingMs = String(Math.round(remainingMs));

      if (
        state.globalOverlay
        && window.VCCSearchTransition
        && typeof window.VCCSearchTransition.rebaseSafety === 'function'
      ) {
        window.VCCSearchTransition.rebaseSafety();
      }
      return true;
    }

    handleSearchManifestRecoveryDeadline(reason = 'manifest-recovery-deadline') {
      const state = this.searchCuration;
      if (!state || state.locked) return false;
      const manifest = this.searchManifest;
      state.manifestRecoveryActive = false;
      this.root.dataset.searchManifestRecoveryActive = 'false';
      this.root.dataset.searchManifestRecoveryFinalOutcome = 'deadline';
      const failedSourceKeys = this.mappedSearchFailureSourceKeys(manifest);
      if (failedSourceKeys.length) {
        this.root.dataset.searchManifestRecoveryFinalFailedSourceKeys = failedSourceKeys.join('|');
      }
      this.markSearchCuration('search-manifest-recovery-deadline', {
        attempt: 1,
        outcome: 'deadline',
        failedSourceKeys
      });
      return this.revealNativeSearchAfterManifestRecovery(reason, manifest);
    }

    revealNativeSearchAfterManifestRecovery(
      reason = 'native-fallback-after-recovery',
      manifest = this.searchManifest
    ) {
      if (this.v4RecognizedSearchMustFailClosed()) {
        return this.commitRecognizedV4FailClosed(reason, manifest);
      }
      const state = this.searchCuration;
      if (!state || state.locked) return false;
      const exhaustedOutcome = String(
        this.root.dataset.searchManifestRecoveryFinalOutcome
        || manifest?.outcome
        || 'unknown'
      );
      const terminalOutcome = 'native-fallback-after-recovery';
      const failedSourceKeys = this.mappedSearchFailureSourceKeys(manifest);

      this.normalizeMappedSearchFallbackUrl();
      if (this.redirectNativeSearchWithoutUsdBand()) return true;
      this.restoreNativeSearchPriceControls();
      if (manifest) {
        manifest.safeExactFallbackBlocked = true;
        manifest.explicitSafeEmpty = false;
      }
      state.manifestRecoveryActive = false;
      state.safeExactRequired = false;
      this.root.dataset.searchManifestRecoveryActive = 'false';
      this.cancelSearchCurationWork();
      this.searchOrderPending = false;
      state.onDeadline = null;
      this.renderMappedSearchRecoveryNotice('');
      delete this.root.dataset.searchCurationSafeEmpty;
      delete this.root.dataset.searchCurationNotReady;
      this.root.dataset.searchManifestRecoveryTerminalOutcome = terminalOutcome;
      this.root.dataset.searchCurationOutcome = terminalOutcome;
      this.root.dataset.searchManifestRecoveryExhaustedOutcome = exhaustedOutcome;
      if (failedSourceKeys.length) {
        this.root.dataset.searchManifestRecoveryFinalFailedSourceKeys = failedSourceKeys.join('|');
      }
      this.markSearchCuration('search-manifest-recovery-native-fallback', {
        attempt: 1,
        exhaustedOutcome,
        terminalOutcome,
        failedSourceKeys,
        reason
      });

      if (typeof state.revealNative !== 'function') {
        return this.fallbackSearchCuration(terminalOutcome);
      }
      this.root.addEventListener('vcc:search-curation-locked', () => {
        this.prepareNativeSearchPages();
      }, { once: true });
      const revealed = state.revealNative(terminalOutcome);
      if (revealed) {
        this.commitFilterDialogObservedRoute();
        this.markSearchCuration('search-fallback-reveal', {
          reason: terminalOutcome,
          exhaustedOutcome
        });
      }
      return revealed;
    }

    fallbackMappedSearchCuration(reason = 'fallback', manifest = this.searchManifest) {
      if (!this.searchManifestRecoveryAttempted) {
        return this.fallbackSearchCuration(reason);
      }
      if (
        !this.root.dataset.searchManifestRecoveryFinalOutcome
        || this.root.dataset.searchManifestRecoveryFinalOutcome === 'pending'
      ) {
        this.root.dataset.searchManifestRecoveryFinalOutcome = String(
          manifest?.outcome || reason || 'unrecoverable'
        );
      }
      return this.revealNativeSearchAfterManifestRecovery(reason, manifest);
    }

    async resetSearchCurationTransportForRecovery(generation) {
      const retiredCache = this.mappedSearchHtmlCache;
      const pendingRequests = Array.from(retiredCache.values())
        .filter((request) => request && typeof request.then === 'function');
      this.mappedSearchHtmlCache = new Map();
      this.searchTransportMode = '';
      this.searchCurationAbortController?.abort();
      this.searchCurationAbortController = null;

      if (pendingRequests.length) {
        let settleTimer = null;
        await Promise.race([
          Promise.allSettled(pendingRequests),
          new Promise((resolve) => {
            settleTimer = window.setTimeout(resolve, 80);
          })
        ]);
        window.clearTimeout(settleTimer);
      }
      await Promise.resolve();

      if (
        generation !== this.searchCurationGeneration
        || this.searchCurationIsFallbackLocked()
        || this.destroyed
        || !this.root.isConnected
      ) return false;
      this.searchCurationAbortController = new AbortController();
      return true;
    }

    async replayMappedSearchManifestOnce(
      initialManifest,
      engine,
      analysis,
      query,
      grid,
      generation
    ) {
      if (!this.canReplayMappedSearchManifest(initialManifest)) return initialManifest;
      if (!this.armSearchManifestRecoveryDeadline(
        this.mappedSearchRecoveryDeadlineMs(initialManifest)
      )) return initialManifest;

      const state = this.searchCuration;
      this.searchManifestRecoveryAttempted = true;
      const initialOutcome = String(initialManifest.outcome || 'unknown');
      const failedSourceKeys = this.mappedSearchFailureSourceKeys(initialManifest);
      this.root.dataset.searchManifestRecoveryAttempt = '1';
      this.root.dataset.searchManifestRecoveryInitialOutcome = initialOutcome;
      this.root.dataset.searchManifestRecoveryFinalOutcome = 'pending';
      this.root.dataset.searchManifestRecoveryFailedSourceKeys = failedSourceKeys.join('|');
      this.markSearchCuration('search-manifest-recovery-start', {
        attempt: 1,
        initialOutcome,
        failedSourceKeys
      });
      this.setSearchCurationProgress(
        Number(this.searchCuration?.progress || 0),
        'manifest-recovery-start'
      );

      grid.inert = true;
      grid.setAttribute('inert', '');
      grid.setAttribute('aria-hidden', 'true');
      grid.setAttribute('aria-busy', 'true');
      this.setSearchControlsPending(true);
      this.setMappedSearchLoading(grid, true);

      initialManifest.cancelled = true;
      const transportReady = await this.resetSearchCurationTransportForRecovery(generation);
      if (!transportReady) {
        state.manifestRecoveryActive = false;
        this.root.dataset.searchManifestRecoveryActive = 'false';
        if (this.root.dataset.searchManifestRecoveryFinalOutcome === 'pending') {
          this.root.dataset.searchManifestRecoveryFinalOutcome = 'superseded';
        }
        return initialManifest;
      }

      const recoveryManifest = this.createMappedSearchManifest(
        analysis,
        query,
        grid,
        Number(initialManifest.closeTarget || 0),
        engine
      );
      recoveryManifest.closeTarget = Math.max(0, Number(initialManifest.closeTarget) || 0);
      recoveryManifest.recoveryAttempt = 1;
      this.searchManifest = recoveryManifest;
      if (this.searchCuration) {
        this.searchCuration.safeExactRequired = recoveryManifest.sources.some((state) => {
          return state?.source?.exact && state.source.safeExact;
        });
      }

      try {
        const completedManifest = await this.buildMappedSearchManifest(
          recoveryManifest,
          engine,
          analysis
        );
        const finalOutcome = completedManifest.cancelled
          ? 'cancelled'
          : String(completedManifest.outcome || 'unknown');
        if (this.root.dataset.searchManifestRecoveryFinalOutcome === 'pending') {
          this.root.dataset.searchManifestRecoveryFinalOutcome = finalOutcome;
        }
        const finalFailedSourceKeys = this.mappedSearchFailureSourceKeys(completedManifest);
        if (finalFailedSourceKeys.length) {
          this.root.dataset.searchManifestRecoveryFinalFailedSourceKeys = finalFailedSourceKeys.join('|');
        }
        this.markSearchCuration('search-manifest-recovery-complete', {
          attempt: 1,
          initialOutcome,
          finalOutcome,
          failedSourceKeys,
          finalFailedSourceKeys
        });
        return completedManifest;
      } catch (error) {
        if (this.root.dataset.searchManifestRecoveryFinalOutcome === 'pending') {
          this.root.dataset.searchManifestRecoveryFinalOutcome = error?.name === 'AbortError'
            ? 'aborted'
            : 'error';
        }
        this.markSearchCuration('search-manifest-recovery-error', {
          attempt: 1,
          initialOutcome,
          errorName: String(error?.name || 'Error'),
          failedSourceKeys
        });
        throw error;
      }
    }

    setupSearchCuration() {
      const state = this.searchCuration;
      if (!state || state.locked || this.searchCurationIsFallbackLocked()) return;

      const grid = this.root.querySelector('[data-ac-grid]');
      if (grid) {
        grid.inert = true;
        grid.setAttribute('aria-hidden', 'true');
        grid.setAttribute('aria-busy', 'true');
      }
      this.setSearchControlsPending(true);

      if (typeof state.syncDom === 'function') state.syncDom(state.visible === true);
      state.onDeadline = (reason) => {
        if (state.manifestRecoveryActive === true || this.searchManifestRecoveryAttempted) {
          return this.handleSearchManifestRecoveryDeadline(reason || 'manifest-recovery-deadline');
        }
        return this.fallbackSearchCuration(reason || 'deadline');
      };
      state.revealSafeExact = (reason) => {
        try {
          this.normalizeMappedSearchFallbackUrl();
          if (state.manifestRecoveryActive === true || this.searchManifestRecoveryAttempted) {
            return this.handleSearchManifestRecoveryDeadline(reason || 'manifest-recovery-deadline');
          }
          if (this.commitSafeExactSearchFallback(reason || 'deadline')) return true;
        } catch (error) {
          this.log('Safe exact deadline handling was skipped', error);
        }
        if (this.searchManifest) this.searchManifest.safeExactFallbackBlocked = true;
        return this.fallbackMappedSearchCuration(reason || 'deadline', this.searchManifest);
      };
      this.searchCurationAbortController = new AbortController();
      this.setSearchCurationProgress(4, 'runtime-attached');
      if (this.searchCurationRemaining() <= 0) this.fallbackSearchCuration('deadline-before-runtime');
    }

    setSearchCurationProgress(value, reason = 'working', options = {}) {
      const state = this.searchCuration;
      if (!state) return false;
      if (typeof state.setProgress === 'function') {
        return state.setProgress(value, reason, options.terminal === true);
      }
      if (state.locked && state.lockMode !== 'curated' && options.terminal !== true) return false;

      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return false;
      const next = options.terminal === true
        ? 100
        : Math.max(Number(state.progress) || 0, Math.min(98, Math.max(0, parsed)));
      state.progress = next;
      state.progressReason = reason;
      const rounded = Math.round(next);
      if (options.terminal === true) this.root.dataset.searchCurationTerminal = 'true';
      this.root.dataset.searchCurationProgress = String(rounded);
      this.root.style.setProperty('--ac-loader-progress', String(next / 100));
      const loader = this.root.querySelector('[data-ac-search-loader]');
      loader?.setAttribute('aria-valuenow', String(rounded));
      loader?.setAttribute('aria-valuetext', `${rounded} percent`);
      const percent = this.root.querySelector('[data-ac-search-loader-percent]');
      if (percent) percent.textContent = `${rounded}%`;
      return true;
    }

    scheduleSearchCurationRelease(finalize) {
      const state = this.searchCuration;
      if (typeof state?.scheduleRelease === 'function') {
        state.scheduleRelease(finalize);
        return;
      }

      let finished = false;
      let frameOne = null;
      let frameTwo = null;
      let safetyTimer = null;
      const finish = () => {
        if (finished) return;
        finished = true;
        if (frameOne !== null) window.cancelAnimationFrame(frameOne);
        if (frameTwo !== null) window.cancelAnimationFrame(frameTwo);
        window.clearTimeout(safetyTimer);
        if (this.destroyed || !this.root.isConnected) return;
        finalize();
      };

      if (
        state?.visible !== true
        || document.visibilityState === 'hidden'
        || typeof window.requestAnimationFrame !== 'function'
      ) {
        finish();
        return;
      }

      frameOne = window.requestAnimationFrame(() => {
        frameTwo = window.requestAnimationFrame(finish);
      });
      safetyTimer = window.setTimeout(finish, 96);
    }

    scheduleSearchCurationFinisher(finisher) {
      if (typeof finisher !== 'function' || this.destroyed || !this.root.isConnected) return;
      const run = () => {
        this.searchCurationFinisherFrame = 0;
        this.searchCurationFinisherTimer = null;
        if (this.destroyed || !this.root.isConnected) return;
        try {
          finisher();
        } catch (error) {
          this.log('A non-critical mapped search finishing step was skipped', error);
        }
      };
      const queueRun = () => {
        if (this.destroyed || !this.root.isConnected) return;
        this.searchCurationFinisherTimer = window.setTimeout(run, 0);
      };

      if (document.visibilityState === 'hidden' || typeof window.requestAnimationFrame !== 'function') {
        queueRun();
        return;
      }
      this.searchCurationFinisherFrame = window.requestAnimationFrame(queueRun);
    }

    setSearchCurationPhase(phase) {
      const state = this.searchCuration;
      if (!state || state.locked || this.searchCurationIsFallbackLocked()) return;
      this.root.dataset.searchCurationPhase = phase;
      this.markSearchCuration(`search-${phase}`);
    }

    setSearchControlsPending(pending) {
      this.root.querySelectorAll(
        '[data-ac-toolbar], [data-ac-active-filters], [data-ac-mobile-dock], [data-ac-filter-dialog], [data-ac-filter-sidebar]'
      ).forEach((control) => {
        control.inert = pending;
        if (pending) {
          control.setAttribute('inert', '');
          control.setAttribute('aria-busy', 'true');
        } else {
          control.removeAttribute('inert');
          control.removeAttribute('aria-busy');
        }
      });
    }

    releaseSearchGrid() {
      const grid = this.root.querySelector('[data-ac-grid]');
      if (grid) {
        grid.inert = false;
        grid.removeAttribute('aria-hidden');
        grid.setAttribute('aria-busy', 'false');
      }
      this.setSearchControlsPending(false);
    }

    cancelSearchCurationWork() {
      this.searchCurationGeneration += 1;
      this.searchScopeGeneration += 1;
      if (this.searchManifest) this.searchManifest.cancelled = true;
      this.clearV4ProgressiveDeadline(this.searchManifest);
      if (this.v4ProgressiveAbortController) {
        this.v4ProgressiveAbortController.abort();
        this.v4ProgressiveAbortController = null;
      }
      if (this.searchCurationAbortController) {
        this.searchCurationAbortController.abort();
        this.searchCurationAbortController = null;
      }
      if (this.searchScopeAbortController) {
        this.searchScopeAbortController.abort();
        this.searchScopeAbortController = null;
      }
    }

    normalizeMappedSearchFallbackUrl() {
      if (this.kind !== 'search') return new URL(window.location.href);
      const url = new URL(window.location.href);
      const previous = url.toString();
      url.searchParams.delete('vcc_scope');
      url.searchParams.delete('vcc_mapped');
      url.searchParams.delete('page');
      if (url.toString() !== previous) {
        window.history.replaceState({ vccMappedSearch: false, page: 1 }, '', url);
      }
      return url;
    }

    normalizeMappedUsdPriceUrl(sourceUrl) {
      const url = sourceUrl instanceof URL
        ? sourceUrl
        : new URL(sourceUrl, window.location.href);
      const bandName = 'filter.p.m.custom.usd_price_bracket';
      const band = url.searchParams.getAll(bandName)
        .map((value) => String(value || '').trim())
        .find((value) => USD_PRICE_BRACKETS.has(value)) || '';
      url.searchParams.delete(bandName);
      if (band) {
        url.searchParams.set(bandName, band);
        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
      }
      return url;
    }

    restoreNativeSearchPriceControls() {
      if (this.kind !== 'search') return;
      this.root.querySelectorAll('[data-vcc-mapped-price-band-input]').forEach((input) => {
        input.remove();
      });
      this.root.querySelectorAll('[data-vcc-mapped-native-disabled]').forEach((input) => {
        input.disabled = input.dataset.vccMappedNativeDisabled === 'true';
        delete input.dataset.vccMappedNativeDisabled;
      });
      this.root.querySelectorAll('[data-vcc-mapped-native-hidden]').forEach((container) => {
        container.hidden = container.dataset.vccMappedNativeHidden === 'true';
        delete container.dataset.vccMappedNativeHidden;
      });
      this.root.querySelectorAll('.vcc-price-buckets[data-vcc-mapped-usd-price]').forEach((group) => {
        delete group.dataset.vccMappedUsdPrice;
      });
    }

    redirectNativeSearchWithoutUsdBand() {
      if (this.kind !== 'search' || this.engineMode !== 'v4_metafield') return false;
      const bandName = 'filter.p.m.custom.usd_price_bracket';
      const url = new URL(window.location.href);
      if (!url.searchParams.has(bandName)) return false;
      const nativeBandInput = this.root.querySelector(
        '[data-vcc-price-band-input]:not([data-vcc-mapped-price-band-input])'
      );
      if (nativeBandInput instanceof HTMLInputElement) return false;
      this.restoreNativeSearchPriceControls();
      url.searchParams.delete(bandName);
      url.searchParams.delete('filter.v.price.gte');
      url.searchParams.delete('filter.v.price.lte');
      url.searchParams.delete('vcc_scope');
      url.searchParams.delete('vcc_mapped');
      url.searchParams.delete('page');
      window.location.replace(url.toString());
      return true;
    }

    cancelMappedSearchScopeLoad() {
      const manifest = this.searchManifest;
      const hadPendingLoad = Boolean(
        this.searchScopeAbortController
        || this.searchScopeLoad
        || manifest?.broadLoading
      );
      if (!hadPendingLoad) return false;
      this.searchScopeGeneration += 1;
      this.searchScopeAbortController?.abort();
      this.searchScopeAbortController = null;
      this.searchScopeLoad = null;
      if (manifest) {
        manifest.broadLoading = false;
        this.root.dataset.searchScopeLoading = 'false';
        this.renderMappedSearchScopeControl(manifest);
      }
      return true;
    }

    abortableSearchCuration(promise) {
      const signal = this.searchCurationAbortController?.signal;
      if (!signal) return Promise.resolve(promise);
      if (signal.aborted) {
        const error = new Error('Search curation was cancelled.');
        error.name = 'AbortError';
        return Promise.reject(error);
      }
      return new Promise((resolve, reject) => {
        const onAbort = () => {
          const error = new Error('Search curation was cancelled.');
          error.name = 'AbortError';
          reject(error);
        };
        signal.addEventListener('abort', onAbort, { once: true });
        Promise.resolve(promise).then(
          (value) => {
            signal.removeEventListener('abort', onAbort);
            resolve(value);
          },
          (error) => {
            signal.removeEventListener('abort', onAbort);
            reject(error);
          }
        );
      });
    }

    buildSafeExactFallbackManifest(manifest, reason = 'fallback') {
      if (!manifest?.sources?.length) return null;
      if (manifest.safeExactFallbackBlocked === true) return null;
      const safeStates = manifest.sources
        .filter((state) => state?.source?.exact && state.source.safeExact);
      if (
        !safeStates.length
        || safeStates.some((state) => {
          return state.failed === true
            || Number(state.page || 0) < 1
            || state.hasMore === true
            || state.exhausted !== true;
        })
      ) return null;
      const safeSourceIndexes = new Set(safeStates
        .map((state) => Number(state.sourceIndex)));
      if (!safeSourceIndexes.size) return null;

      const candidates = manifest.exactEntries?.length
        ? manifest.exactEntries
        : (manifest.entries || []);
      const seen = new Set();
      const safeEntries = candidates
        .filter((entry) => safeSourceIndexes.has(Number(entry?.sourceIndex)))
        .filter((entry) => {
          const key = String(entry?.key || '');
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((entry, index) => Object.assign({}, entry, { rank: index }));
      const fallbackManifest = Object.assign({}, manifest, {
        entries: safeEntries.slice(),
        exactEntries: safeEntries.slice(),
        focusedEntries: safeEntries.slice(),
        broadEntries: [],
        baseEntries: safeEntries.slice(),
        baseCloseCount: safeEntries.length,
        closeCount: safeEntries.length,
        broadEntryCount: 0,
        broadSettledCount: 0,
        relationshipSettled: false,
        broadAvailable: false,
        broadLoaded: false,
        broadExpanded: false,
        broadHasMore: false,
        broadError: false,
        focusedHasMore: false,
        outcome: 'exact-only',
        hasMore: false,
        viewHasMore: false,
        sealed: true,
        capped: false,
        cancelled: false,
        degradedReason: reason,
        productKeys: new Set(safeEntries.map((entry) => entry.key).filter(Boolean)),
        sources: manifest.sources.map((state) => Object.assign({}, state))
      });
      this.deriveMappedSearchEntries(fallbackManifest, new URL(window.location.href));
      return fallbackManifest;
    }

    renderMappedSearchRecoveryNotice() {
      const existing = this.root.querySelector('[data-ac-search-recovery]');
      existing?.remove();
      return null;
    }
    commitSafeExactSearchFallback(reason = 'fallback') {
      this.normalizeMappedSearchFallbackUrl();
      const originalManifest = this.searchManifest;
      const fallbackManifest = this.buildSafeExactFallbackManifest(originalManifest, reason);
      if (!fallbackManifest) return false;
      if (!fallbackManifest.entries.length) {
        this.cancelSearchCurationWork();
        this.searchManifest = fallbackManifest;
        this.searchManifestBuild = null;
        this.searchOrderPending = false;
        return this.commitMappedUsdPriceBandEmpty(fallbackManifest, reason);
      }

      this.cancelSearchCurationWork();
      this.searchManifest = fallbackManifest;
      this.searchManifestBuild = null;
      this.searchOrderPending = false;
      this.grantSearchCurationRenderGrace();
      const grid = this.root.querySelector('[data-ac-grid]');
      if (!grid) return false;

      let preparedPage = null;
      let renderedPage = null;
      try {
        preparedPage = this.prepareMappedSearchPage(fallbackManifest, 1);
        if (!preparedPage) return false;
        renderedPage = this.applyPreparedMappedSearchPage(preparedPage, { animate: false });
        if (!renderedPage) {
          this.rollbackPreparedMappedSearchPage(preparedPage);
          return false;
        }

        if (!this.commitSearchCuration(fallbackManifest, renderedPage, { allowExpired: true })) {
          this.rollbackPreparedMappedSearchPage(preparedPage);
          return false;
        }
      } catch (error) {
        if (preparedPage) this.rollbackPreparedMappedSearchPage(preparedPage);
        this.log('Safe exact fallback could not be committed atomically', error);
        return false;
      }
      this.root.dataset.searchCurationDegradedReason = reason;
      this.root.dataset.searchMappedCloseCount = String(fallbackManifest.closeCount || 0);
      this.root.dataset.searchMappedResultCount = String(fallbackManifest.entries.length);
      this.setSearchCurationProgress(98, 'safe-exact-fallback-synchronized');
      this.revealCommittedSearchCuration(fallbackManifest, renderedPage, () => {
        if (preparedPage) this.finalizePreparedMappedSearchPage(preparedPage, { animate: false });
        try {
          this.renderMappedSearchNavigation(fallbackManifest, renderedPage);
          this.syncMappedFilterControls(new URL(window.location.href));
          this.updateMappedFacetCounts(fallbackManifest, new URL(window.location.href));
        } catch (error) {
          this.log('Safe exact fallback navigation sync was skipped', error);
        }
        try {
          this.setMappedSearchLoading(grid, false);
        } catch (error) {
          this.log('Safe exact fallback loading cleanup was skipped', error);
        }
      }, () => {
        this.warmMappedSearchPages(fallbackManifest, renderedPage);
      });
      this.markSearchCuration('search-safe-exact-fallback', {
        reason,
        resultCount: fallbackManifest.entries.length
      });
      return true;
    }

    commitMappedUsdPriceBandEmpty(manifest, reason = 'mapped-price-band-empty') {
      const currentUrl = new URL(window.location.href);
      const url = this.normalizeMappedUsdPriceUrl(new URL(currentUrl));
      const band = String(
        url.searchParams.get('filter.p.m.custom.usd_price_bracket') || ''
      ).trim();
      if (
        !manifest?.sealed
        || manifest.entries?.length
        || !manifest.baseEntries?.length
        || !USD_PRICE_BRACKETS.has(band)
      ) return false;
      const state = this.searchCuration;
      const grid = this.root.querySelector('[data-ac-grid]');
      if (!state || state.locked || !(grid instanceof HTMLElement)) return false;

      url.searchParams.delete('page');
      if (url.toString() !== currentUrl.toString()) {
        window.history.replaceState(window.history.state, '', url);
      }

      manifest.entries = [];
      manifest.closeCount = 0;
      manifest.viewHasMore = false;
      manifest.cancelled = false;
      this.searchManifest = manifest;
      if (!this.commitSearchCuration(manifest, 1, { allowExpired: true })) return false;

      const emptyOutcome = 'mapped-price-band-empty';
      this.root.dataset.searchManifestOutcome = emptyOutcome;
      this.root.dataset.searchMappedCloseCount = '0';
      this.root.dataset.searchMappedResultCount = '0';
      this.root.dataset.searchProgressiveLoading = 'false';
      this.root.dataset.searchProgressiveComplete = 'true';
      this.setSearchCurationProgress(100, emptyOutcome, { terminal: true });
      this.revealCommittedSearchCuration(manifest, 1, () => {
        grid.replaceChildren();
        this.searchCurrentMappedPage = 1;
        this.root.dataset.searchMappedEnhanced = 'true';
        const empty = this.ensureMappedEmptyState();
        if (empty) {
          const heading = empty.querySelector('h2');
          const copy = empty.querySelector('p');
          if (heading) heading.textContent = 'No products found';
          if (copy) copy.textContent = 'Try another price range.';
          empty.hidden = false;
        }
        this.setMappedSearchLoading(grid, false);
        this.renderMappedSearchNavigation(manifest, 1);
        this.syncMappedFilterControls(url);
        this.updateMappedFacetCounts(manifest, url);
      });
      this.markSearchCuration('search-mapped-price-band-empty', {
        reason,
        band,
        resultCount: 0
      });
      return true;
    }

    v4RecognizedSearchMustFailClosed() {
      return this.engineMode === 'v4_metafield'
        && Boolean(this.v4RecognizedAnalysis)
        && this.v4NativeFallbackAuthorized !== true;
    }

    commitRecognizedV4FailClosed(reason = 'recognized-v4-incomplete', manifest = this.searchManifest) {
      if (!this.v4RecognizedSearchMustFailClosed()) return false;
      const state = this.searchCuration;
      if (!state || state.locked) return false;
      const grid = this.root.querySelector('[data-ac-grid]');
      if (!(grid instanceof HTMLElement)) return false;

      const targetManifest = manifest || this.createMappedSearchManifest(
        this.v4RecognizedAnalysis,
        String(this.v4RecognizedAnalysis?.query || this.config.searchQuery || ''),
        grid,
        0,
        this.searchEngine()
      );
      const candidateEntries = [
        targetManifest.progressiveEntries,
        targetManifest.entries,
        targetManifest.exactEntries
      ].find((entries) => Array.isArray(entries) && entries.length) || [];
      const seenKeys = new Set();
      const verifiedEntries = candidateEntries.filter((entry) => {
        const key = String(entry?.key || '').trim();
        if (!key || seenKeys.has(key)) return false;
        seenKeys.add(key);
        const source = targetManifest.sources?.[Number(entry?.sourceIndex)]?.source;
        return source?.v4AttestationRequired === true
          && this.v4ProductSectionAttestation(entry?.product, source).passed === true;
      });

      // Transport failure is never proof that a recognized query has no
      // inventory. When every V4 tier is unproven and no attested card
      // survived, preserve the customer's raw query and use one exclusive
      // Native request. Never merge Native cards into a V4 result prefix.
      const unresolvedV4Source = (targetManifest.sources || []).some((sourceState) => {
        return sourceState?.source?.v4Metafield === true
          && (
            sourceState.transportUnproven === true
            || sourceState.failed === true
            || Number(sourceState.page || 0) < 1
          );
      });
      const transportUnproven = (
        targetManifest.transportUnprovenSourceKeys instanceof Set
        && targetManifest.transportUnprovenSourceKeys.size > 0
      ) || unresolvedV4Source;
      if (!verifiedEntries.length && transportUnproven) {
        this.v4NativeFallbackAuthorized = true;
        this.root.dataset.searchRecognizedV4Fallback = 'native-raw-query-transport-unproven';
        const currentUrl = new URL(window.location.href);
        const rawQuery = String(
          currentUrl.searchParams.get('vcc_raw')
          || this.v4RecognizedAnalysis?.rawQuery
          || this.v4RecognizedAnalysis?.fallbackQuery
          || this.config.searchQuery
          || ''
        ).trim();
        const currentQuery = String(currentUrl.searchParams.get('q') || '').trim();
        const neutralQuery = String(this.config.v4NeutralQuery || '-tag:__vcc_never__').trim();
        if (
          rawQuery
          && (
            currentQuery === neutralQuery
            || (
              currentUrl.searchParams.has('vcc_raw')
              && currentQuery !== rawQuery
            )
          )
        ) {
          return this.redirectRecognizedV4NativeFallback(reason);
        }
        return this.fallbackSearchCuration(String(reason || 'recognized-v4-incomplete') + '-native-grid');
      }

      this.v4ProgressiveAbortController?.abort();
      this.v4ProgressiveAbortController = null;
      this.clearV4ProgressiveDeadline(targetManifest);
      this.searchCurationAbortController?.abort();
      this.searchCurationAbortController = null;
      state.manifestRecoveryActive = false;
      state.safeExactRequired = false;
      this.root.dataset.searchManifestRecoveryActive = 'false';
      this.renderMappedSearchRecoveryNotice('');

      targetManifest.entries = verifiedEntries.slice();
      targetManifest.focusedEntries = verifiedEntries.slice();
      targetManifest.baseEntries = verifiedEntries.slice();
      targetManifest.productKeys = new Set(verifiedEntries.map((entry) => entry.key).filter(Boolean));
      targetManifest.progressiveEntries = null;
      targetManifest.progressiveLoading = false;
      targetManifest.progressiveCommitted = false;
      targetManifest.cancelled = false;
      targetManifest.hasMore = false;
      targetManifest.focusedHasMore = false;
      targetManifest.viewHasMore = false;
      targetManifest.relationshipSettled = false;
      targetManifest.recognizedV4FailClosed = true;
      targetManifest.recognizedV4FailureReason = String(reason || 'recognized-v4-incomplete');
      targetManifest.outcome = verifiedEntries.length
        ? 'recognized-v4-partial'
        : 'recognized-v4-empty';
      targetManifest.sealed = true;
      targetManifest.baseCloseCount = verifiedEntries.filter((entry) => {
        return entry?.resultSectionKey === 'exact-team';
      }).length;
      targetManifest.closeCount = targetManifest.baseCloseCount;
      this.searchManifest = targetManifest;
      this.deriveMappedSearchEntries(targetManifest, new URL(window.location.href));

      let preparedPage = null;
      let renderedPage = 1;
      if (targetManifest.entries.length) {
        preparedPage = this.prepareMappedSearchPage(targetManifest, 1);
        renderedPage = preparedPage
          ? this.applyPreparedMappedSearchPage(preparedPage, { animate: false })
          : null;
        if (!renderedPage) {
          if (preparedPage) this.rollbackPreparedMappedSearchPage(preparedPage);
          preparedPage = null;
          targetManifest.entries = [];
          targetManifest.focusedEntries = [];
          targetManifest.baseEntries = [];
          targetManifest.productKeys = new Set();
          targetManifest.baseCloseCount = 0;
          targetManifest.closeCount = 0;
          targetManifest.outcome = 'recognized-v4-empty';
          renderedPage = 1;
        }
      }

      if (!this.commitSearchCuration(targetManifest, renderedPage || 1, { allowExpired: true })) {
        if (preparedPage) this.rollbackPreparedMappedSearchPage(preparedPage);
        return false;
      }
      this.root.dataset.searchManifestOutcome = targetManifest.outcome;
      this.root.dataset.searchMappedCloseCount = String(targetManifest.closeCount || 0);
      this.root.dataset.searchMappedResultCount = String(targetManifest.entries.length);
      this.root.dataset.searchProgressiveLoading = 'false';
      this.root.dataset.searchProgressiveComplete = 'true';
      this.setSearchCurationProgress(100, targetManifest.outcome, { terminal: true });

      this.revealCommittedSearchCuration(targetManifest, renderedPage || 1, () => {
        if (preparedPage) {
          this.finalizePreparedMappedSearchPage(preparedPage, { animate: false });
        } else {
          grid.replaceChildren();
          this.searchCurrentMappedPage = 1;
          this.root.dataset.searchMappedEnhanced = 'true';
          const empty = this.ensureMappedEmptyState();
          if (empty) {
            const heading = empty.querySelector('h2');
            const copy = empty.querySelector('p');
            if (heading) heading.textContent = 'No matching products found';
            if (copy) copy.textContent = 'Browse the archive to keep exploring.';
            empty.hidden = false;
          }
        }
        this.setMappedSearchLoading(grid, false);
        this.renderMappedSearchNavigation(targetManifest, renderedPage || 1);
        this.syncMappedFilterControls(new URL(window.location.href));
        this.updateMappedFacetCounts(targetManifest, new URL(window.location.href));
      }, () => {
        if (targetManifest.entries.length) this.warmMappedSearchPages(targetManifest, 1);
      });
      this.markSearchCuration('search-recognized-v4-fail-closed', {
        reason,
        outcome: targetManifest.outcome,
        resultCount: targetManifest.entries.length
      });
      return true;
    }

    redirectRecognizedV4NativeFallback(reason = 'recognized-v4-incomplete') {
      if (this.kind !== 'search' || this.engineMode !== 'v4_metafield') return false;
      const url = new URL(window.location.href);
      const rawQuery = String(
        url.searchParams.get('vcc_raw')
        || this.v4RecognizedAnalysis?.rawQuery
        || this.v4RecognizedAnalysis?.fallbackQuery
        || this.config.searchQuery
        || url.searchParams.get('q')
        || ''
      ).trim();
      if (!rawQuery) return false;

      this.v4ProgressiveAbortController?.abort();
      this.v4ProgressiveAbortController = null;
      this.clearV4ProgressiveDeadline(this.searchManifest);
      this.searchCurationAbortController?.abort();
      this.searchCurationAbortController = null;
      this.restoreNativeSearchPriceControls();
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (
          key.startsWith('filter.p.m.vcc_search.')
          || key === 'vcc_raw'
          || key === 'vcc_scope'
          || key === 'vcc_mapped'
          || key === 'section_id'
          || key === 'sections'
          || key === 'page'
        ) url.searchParams.delete(key);
      });
      url.searchParams.set('q', rawQuery);
      url.searchParams.set('type', 'product');
      this.root.dataset.searchCurationOutcome = 'native-fallback-after-v4-failure';
      this.root.dataset.searchCurationReason = String(reason || 'recognized-v4-incomplete');
      window.VCCSearchTransition?.markSecondary?.('refine', rawQuery);
      window.location.replace(url.toString());
      return true;
    }

    fallbackSearchCuration(reason = 'fallback') {
      if (this.v4RecognizedSearchMustFailClosed()) {
        return this.commitRecognizedV4FailClosed(reason, this.searchManifest);
      }
      const state = this.searchCuration;
      if (!state || state.locked) return false;
      this.normalizeMappedSearchFallbackUrl();
      try {
        if (this.commitSafeExactSearchFallback(reason)) return true;
      } catch (error) {
        this.log('Safe exact fallback preparation was skipped', error);
      }

      if (this.redirectNativeSearchWithoutUsdBand()) return true;
      this.restoreNativeSearchPriceControls();

      if (this.searchManifest) {
        this.searchManifest.safeExactFallbackBlocked = true;
        this.searchManifest.explicitSafeEmpty = false;
      }
      state.manifestRecoveryActive = false;
      state.safeExactRequired = false;
      this.root.dataset.searchManifestRecoveryActive = 'false';
      this.cancelSearchCurationWork();
      this.searchOrderPending = false;
      state.onDeadline = null;
      this.renderMappedSearchRecoveryNotice('');
      delete this.root.dataset.searchCurationSafeEmpty;
      delete this.root.dataset.searchCurationNotReady;
      this.root.dataset.searchCurationOutcome = String(reason || 'native-fallback');

      if (typeof state.revealNative === 'function') {
        this.root.addEventListener('vcc:search-curation-locked', () => {
          this.prepareNativeSearchPages();
        }, { once: true });
        const revealed = state.revealNative(reason);
        if (revealed) {
          this.commitFilterDialogObservedRoute();
          this.markSearchCuration('search-fallback-reveal', { reason });
        }
        return revealed;
      }

      state.locked = true;
      state.lockMode = 'fallback';
      state.releasePending = true;
      state.reason = reason;
      state.onDeadline = null;
      window.clearTimeout(state.deadlineTimer);
      window.clearTimeout(state.visibleTimer);
      this.root.dataset.searchCuration = 'committing';
      this.root.dataset.searchCurationVisible = state.visible === true ? 'true' : 'false';
      this.root.dataset.searchCurationPhase = 'arrange';
      this.root.dataset.searchCurationReason = reason;
      this.root.dataset.searchMappedLoading = 'false';
      this.root.dataset.searchMappedPagePending = 'false';
      delete this.root.dataset.searchMappedCloseCount;
      delete this.root.dataset.searchMappedResultCount;
      this.setSearchCurationProgress(100, reason, { terminal: true });

      this.scheduleSearchCurationRelease(() => {
        state.visible = false;
        state.released = true;
        state.releasePending = false;
        this.root.dataset.searchCuration = 'fallback-locked';
        this.root.dataset.searchCurationVisible = 'false';
        this.root.dataset.searchCurationPhase = 'ready';
        this.releaseSearchGrid();
        const curation = this.root.querySelector('[data-ac-curation]');
        if (curation) curation.setAttribute('aria-hidden', 'true');
        const announcement = this.root.querySelector('[data-ac-curation-announcement]');
        if (announcement) announcement.textContent = 'Results ready.';
        this.root.querySelectorAll('[data-ac-search-progress]').forEach((progress) => {
          progress.hidden = true;
        });
        const pagination = this.root.querySelector('[data-ac-numbered-pagination]');
        if (pagination?.querySelector('.vcc-ac-pagination')) pagination.hidden = false;
        this.root.dispatchEvent(new CustomEvent('vcc:search-curation-locked', {
          bubbles: true,
          detail: { mode: 'fallback', reason }
        }));
        this.prepareNativeSearchPages();
        this.commitFilterDialogObservedRoute();
        this.markSearchCuration('search-fallback-reveal', { reason });
      });
      return true;
    }

    commitSearchCuration(manifest, page, options = {}) {
      const state = this.searchCuration;
      if (!state) return false;
      if (state.locked || this.searchCurationIsFallbackLocked()) return false;
      if (this.searchCurationRemaining() <= 0 && options.allowExpired !== true) return false;
      if (!manifest?.degradedReason) {
        const mappedUrl = new URL(window.location.href);
        mappedUrl.searchParams.set('vcc_mapped', '1');
        const currentState = window.history.state && typeof window.history.state === 'object'
          ? window.history.state
          : {};
        window.history.replaceState(
          Object.assign({}, currentState, { vccMappedSearch: true, page }),
          '',
          mappedUrl
        );
      }

      this.setSearchCurationPhase('arrange');
      const outcome = manifest?.outcome || 'full-curated';
      state.locked = true;
      state.lockMode = 'curated';
      state.reason = outcome;
      state.released = false;
      state.releasePending = false;
      state.onDeadline = null;
      state.manifestRecoveryActive = false;
      window.clearTimeout(state.deadlineTimer);
      window.clearTimeout(state.visibleTimer);
      this.searchCurationAbortController = null;
      this.root.dataset.searchManifestRecoveryActive = 'false';
      this.root.dataset.searchCuration = 'committing';
      this.root.dataset.searchCurationVisible = state.visible === true ? 'true' : 'false';
      this.root.dataset.searchCurationPhase = 'arrange';
      this.root.dataset.searchCurationReason = outcome;
      this.root.dataset.searchCurationOutcome = outcome;
      this.root.dataset.searchMappedLoading = 'false';
      this.root.dataset.searchMappedPagePending = 'false';
      this.markSearchCuration('search-grid-commit', {
        page,
        resultCount: manifest.entries.length,
        closeCount: manifest.closeCount || 0
      });
      this.commitFilterDialogObservedRoute();
      return true;
    }

    revealCommittedSearchCuration(manifest, page, finisher = null, backgroundFinisher = null) {
      const state = this.searchCuration;
      if (!state || state.lockMode !== 'curated' || state.released || state.releasePending) return false;

      const progressivePending = manifest?.progressiveLoading === true;
      if (typeof finisher === 'function') this.root.dataset.searchCurationFinisherPending = 'true';
      state.releasePending = true;
      this.setSearchCurationProgress(
        progressivePending ? 86 : 100,
        progressivePending ? 'related-results-loading' : 'results-locked',
        { terminal: !progressivePending }
      );
      this.scheduleSearchCurationRelease(() => {
        if (state.released && this.root.dataset.searchCuration === 'curated-locked') return;
        try {
          finisher?.();
        } catch (error) {
          this.log('Mapped search visible finalization was skipped', error);
        } finally {
          delete this.root.dataset.searchCurationFinisherPending;
        }
        try {
          this.applyMappedResultStatus(
            manifest.entries.length,
            page,
            manifest.pageSize,
            manifest.viewHasMore ?? manifest.hasMore
          );
        } catch (error) {
          this.log('Mapped search status sync was skipped', error);
        }
        state.visible = false;
        state.released = true;
        state.releasePending = false;
        this.root.dataset.searchCuration = 'curated-locked';
        this.root.dataset.searchCurationVisible = 'false';
        this.root.dataset.searchCurationPhase = progressivePending ? 'related' : 'ready';
        this.root.dataset.searchCurationReason = manifest?.outcome || 'full-curated';
        this.root.dataset.searchCurationOutcome = manifest?.outcome || 'full-curated';
        this.root.querySelectorAll('[data-ac-search-progress]').forEach((progress) => {
          progress.hidden = !progressivePending;
        });
        this.releaseSearchGrid();
        if (progressivePending) {
          this.root.querySelector('[data-ac-grid]')?.setAttribute('aria-busy', 'true');
        }
        const curation = this.root.querySelector('[data-ac-curation]');
        if (curation) curation.setAttribute('aria-hidden', 'true');
        const announcement = this.root.querySelector('[data-ac-curation-announcement]');
        if (announcement) {
          announcement.textContent = progressivePending
            ? 'Exact matches ready. Organizing related results.'
            : 'Results ready.';
        }
        this.markSearchCuration('search-grid-reveal', {
          page,
          resultCount: manifest.entries.length,
          closeCount: manifest.closeCount || 0
        });
        this.root.dataset.searchGridRevealAt = String(Math.round(window.performance?.now?.() || Date.now()));
        this.root.dispatchEvent(new CustomEvent('vcc:search-curation-locked', {
          bubbles: true,
          detail: {
            mode: 'curated',
            page,
            query: manifest.query,
            resultCount: manifest.entries.length,
            closeCount: manifest.closeCount || 0,
            outcome: manifest?.outcome || 'full-curated',
            duration: (window.performance?.now?.() || Date.now()) - Number(state.startedAt || 0)
          }
        }));
        this.scheduleSearchCurationFinisher(backgroundFinisher);
      });
      return true;
    }

    hideDuplicatePageHeading() {
      const heading = this.root.querySelector('.vcc-ac-heading h1');
      const headingText = heading?.textContent.trim();
      if (!headingText) return;
      const duplicate = Array.from(document.querySelectorAll('main h1')).find((candidate) => {
        return !this.root.contains(candidate) && candidate.textContent.trim() === headingText;
      });
      const duplicateSection = duplicate?.closest('.shopify-section');
      if (!duplicateSection) return;
      duplicateSection.hidden = true;
      duplicateSection.dataset.vccAcHeadingSuppressed = 'true';
      this.log('Suppressed the legacy page heading while Advanced Collections is active.');
    }

    applyStaticConfig() {
      const s = this.s;
      this.root.dataset.themeMode = this.config.themeMode || 'follow-site';
      this.root.dataset.metaStyle = s.ms || 'split';
      this.root.dataset.overlay = s.op || 'bottom-glass';
      this.root.dataset.splitWidth = s.sw || 'auto';
      this.root.dataset.hover = s.hv || 'lift-zoom';
      this.root.dataset.atlasInspector = s.ai || 'right';
      this.root.dataset.filterStyle = s.fs || 'drawer';
      this.root.dataset.filterOpen = 'false';
      this.root.dataset.stickyToolbar = String(Boolean(s.st));
      this.root.dataset.toolbarCompact = String(Boolean(s.tc));
      this.root.dataset.contentVisibility = String(Boolean(s.cv));
      this.root.dataset.reducedMotion = String(Boolean(s.rm && mqReduced.matches));
      this.root.dataset.overlayHover = String(Boolean(s.or && !mqMobile.matches));

      setVar(this.root, '--ac-radius', `${clampRange(s.rd, 8, 0, 8)}px`);
      setVar(this.root, '--ac-img-radius', `${clampRange(s.ir, 8, 0, 8)}px`);
      setVar(this.root, '--ac-ratio', s.ra || '1 / 1');
      setVar(this.root, '--ac-fit', s.ft || 'contain');
      setVar(this.root, '--ac-image-pad', `${clampNumber(s.ip, 0)}px`);
      setVar(this.root, '--ac-heading-font', s.htf || 'var(--font-heading--family)');
      setVar(this.root, '--ac-heading-weight', s.htw || '850');
      setVar(this.root, '--ac-heading-line', clampNumber(s.hln, 100) / 100);
      setVar(this.root, '--ac-heading-pad-top', `${clampNumber(s.hpt, 8)}px`);
      setVar(this.root, '--ac-heading-pad-bottom', `${clampNumber(s.hpb, 24)}px`);
      setVar(this.root, '--ac-title-font', s.tf || 'var(--font-body--family)');
      setVar(this.root, '--ac-title-weight', s.tw || '850');
      setVar(this.root, '--ac-title-line', clampNumber(s.th, 132) / 100);
      setVar(this.root, '--ac-title-space-top', `${clampNumber(s.tst, 10)}px`);
      setVar(this.root, '--ac-title-space-bottom', `${clampNumber(s.tsb, 0)}px`);
      setVar(this.root, '--ac-price-font', s.pf || 'var(--font-body--family)');
      setVar(this.root, '--ac-price-weight', s.pw || '900');
      setVar(this.root, '--ac-price-line', clampNumber(s.ph, 125) / 100);
      setVar(this.root, '--ac-price-space-top', `${clampNumber(s.pst, 7)}px`);
      setVar(this.root, '--ac-price-space-bottom', `${clampNumber(s.psb, 2)}px`);
      setVar(this.root, '--ac-overlay-height', `${clampNumber(s.oh, 38)}%`);
      setVar(this.root, '--ac-overlay-opacity', clampNumber(s.oo, 82) / 100);
      setVar(this.root, '--ac-overlay-blur', `${clampNumber(s.ob, 14)}px`);
      setVar(this.root, '--ac-overlay-rgb', colorToRgb(s.ok || '#05080c'));
      setVar(this.root, '--ac-overlay-start', `${clampRange(s.os, 10, 0, 85)}%`);
      setVar(this.root, '--ac-overlay-solid-height', `${clampRange(s.og, 26, 0, 70)}%`);
      setVar(this.root, '--ac-overlay-solid-opacity', clampRange(s.of, 94, 20, 100) / 100);
      setVar(this.root, '--ac-overlay-padding', `${clampRange(s.otp, 12, 6, 32)}px`);
      setVar(this.root, '--ac-overlay-text', s.ot || '#ffffff');
      setVar(this.root, '--ac-overlay-custom', s.oc || 'linear-gradient(180deg, rgba(10,14,19,0), rgba(10,14,19,.92))');
      setVar(this.root, '--ac-overlay-title', `${clampNumber(s.ots, 11)}px`);
      setVar(this.root, '--ac-overlay-meta', `${clampNumber(s.oms, 9)}px`);
      setVar(this.root, '--ac-overlay-lines', clampNumber(s.ol, 2));
      setVar(this.root, '--ac-split-opacity', clampNumber(s.so, 88) / 100);
      setVar(this.root, '--ac-split-blur', `${clampNumber(s.sb, 14)}px`);
      setVar(this.root, '--ac-hover-zoom', clampNumber(s.hz, 104) / 100);
      setVar(this.root, '--ac-atlas-gap', `${clampNumber(s.ag, 6)}px`);
      setVar(this.root, '--ac-atlas-inspector-width', `${clampRange(s.aiw, 520, 280, 760)}px`);

      const viewSwitch = this.root.querySelector('[data-ac-view-switch]');
      if (viewSwitch) viewSwitch.hidden = !this.config.viewSwitch;
      const themeToggle = this.root.querySelector('[data-ac-theme-toggle]');
      if (themeToggle) themeToggle.hidden = !this.config.themeToggle;

      this.applyResponsiveVariables();
      this.placeFilterForm();
    }

    applyResponsiveVariables() {
      const mobile = mqMobile.matches;
      const s = this.s;
      const density = s.dn || 'balanced';
      let gapDesktop = clampNumber(s.gap_d, 14);
      let gapMobile = clampNumber(s.gap_m, 9);
      let padDesktop = clampNumber(s.pad_d, 12);
      let padMobile = clampNumber(s.pad_m, 7);

      if (density === 'compact') {
        gapDesktop = 8;
        gapMobile = 5;
        padDesktop = 7;
        padMobile = 4;
      } else if (density === 'spacious') {
        gapDesktop = 22;
        gapMobile = 14;
        padDesktop = 18;
        padMobile = 10;
      }

      const width = this.root.getBoundingClientRect().width;
      const tablet = !mobile && width < 1050;
      const columns = mobile
        ? clampNumber(s.cols_m, 2)
        : tablet
          ? clampNumber(s.cols_t, 4)
          : clampNumber(s.cols_d, 5);
      const atlasColumns = mobile ? clampNumber(s.at_m, 5) : clampNumber(s.at_d, 8);

      setVar(this.root, '--ac-cols', columns);
      setVar(this.root, '--ac-atlas-cols', atlasColumns);
      setVar(this.root, '--ac-gap', `${mobile ? gapMobile : gapDesktop}px`);
      setVar(this.root, '--ac-pad', `${mobile ? padMobile : padDesktop}px`);
      setVar(this.root, '--ac-mobile-side-padding', `${clampRange(s.msp, 10, 0, 24)}px`);
      setVar(this.root, '--ac-filter-row-inset', `${clampRange(s.mfi, 8, 0, 24)}px`);
      setVar(this.root, '--ac-heading-size', `${mobile ? clampNumber(s.hts_m, 32) : clampNumber(s.hts_d, 52)}px`);
      setVar(this.root, '--ac-title-size', `${mobile ? clampNumber(s.ts_m, 10) : clampNumber(s.ts_d, 13)}px`);
      setVar(this.root, '--ac-title-lines', mobile ? clampNumber(s.tl_m, 2) : clampNumber(s.tl_d, 2));
      setVar(this.root, '--ac-meta-size', `${mobile ? clampNumber(s.mz_m, 8) : clampNumber(s.mz_d, 10)}px`);
      setVar(this.root, '--ac-price-size', `${mobile ? clampNumber(s.ps_m, 9) : clampNumber(s.ps_d, 11)}px`);
      if (mobile) {
        setVar(this.root, '--ac-overlay-height', `${clampRange(s.moh, s.oh || 48, 18, 100)}%`);
        setVar(this.root, '--ac-overlay-start', `${clampRange(s.mos, s.os || 12, 0, 85)}%`);
        setVar(this.root, '--ac-overlay-solid-height', `${clampRange(s.mog, s.og || 30, 0, 70)}%`);
        setVar(this.root, '--ac-overlay-padding', `${clampRange(s.mtp, s.otp || 10, 6, 28)}px`);
      } else {
        setVar(this.root, '--ac-overlay-height', `${clampRange(s.oh, 38, 18, 100)}%`);
        setVar(this.root, '--ac-overlay-start', `${clampRange(s.os, 10, 0, 85)}%`);
        setVar(this.root, '--ac-overlay-solid-height', `${clampRange(s.og, 26, 0, 70)}%`);
        setVar(this.root, '--ac-overlay-padding', `${clampRange(s.otp, 12, 6, 32)}px`);
      }
      this.root.dataset.overlayHover = String(Boolean(s.or && !mobile));
      this.syncMobilePresentation();
    }

    placeFilterForm() {
      const form = this.root.querySelector('[data-ac-filter-form="drawer"]');
      if (!form) return;
      const style = this.s.fs || 'drawer';
      const mobile = mqMobile.matches;
      const dialogMount = this.root.querySelector('[data-ac-filter-dialog-mount]');
      const sidebar = this.root.querySelector('[data-ac-filter-sidebar]');
      const topbar = this.root.querySelector('[data-ac-filter-topbar]');
      const atlasDesktop = this.root.dataset.layout === 'atlas' && !mobile;
      let target = dialogMount;

      if (atlasDesktop) target = sidebar;
      else if (!mobile && style === 'topbar') target = topbar;

      if (target && form.parentElement !== target) target.appendChild(form);
      this.root.dataset.atlasDesktop = String(atlasDesktop);
      this.root.dataset.filterOpen = String(atlasDesktop);
    }

    customPalette(tone) {
      const s = this.s;
      if (tone === 'light') {
        return { page: s.lbg, surface: s.lsf, card: s.lcd, text: s.ltx, muted: s.lmu, line: s.lln, accent: s.lac, accent2: s.la2, image: s.lib };
      }
      return { page: s.bg, surface: s.sf, card: s.cd, text: s.tx, muted: s.mu, line: s.ln, accent: s.ac, accent2: s.a2, image: s.ib };
    }

    applyTone(tone) {
      const normalized = tone === 'light' ? 'light' : 'dark';
      const preset = normalized === 'light' ? (this.s.lp || 'gallery-white') : (this.s.dp || 'archive-noir');
      const palette = preset === 'custom'
        ? this.customPalette(normalized)
        : PALETTES[normalized][preset] || Object.values(PALETTES[normalized])[0];

      this.root.dataset.tone = normalized;
      setVar(this.root, '--ac-page', palette.page);
      setVar(this.root, '--ac-surface', palette.surface);
      setVar(this.root, '--ac-card', palette.card);
      setVar(this.root, '--ac-text', palette.text);
      setVar(this.root, '--ac-muted', palette.muted);
      setVar(this.root, '--ac-line', palette.line);
      setVar(this.root, '--ac-accent', palette.accent);
      setVar(this.root, '--ac-accent-2', palette.accent2);
      setVar(this.root, '--ac-image', this.s.ws === false ? palette.image : '#ffffff');
      this.root.querySelector('[data-ac-theme-toggle]')?.setAttribute('aria-pressed', String(normalized === 'dark'));
      this.syncMobilePresentation();
    }

    syncMobilePresentation() {
      const mobile = mqMobile.matches;
      const darkMobile = mobile && this.root.dataset.tone === 'dark';
      this.root.dataset.darkMobileCard = darkMobile ? 'defined-scrim' : 'standard';

      const inspector = this.root.querySelector('[data-ac-atlas-inspector]');
      if (inspector) {
        inspector.hidden = mobile;
        inspector.setAttribute('aria-hidden', String(mobile));
      }
    }

    siteTone() {
      const html = document.documentElement;
      const body = document.body;
      const globalTone = String(html.dataset.vccTheme || '').toLowerCase();
      if (this.config.globalThemeEnabled === true && (globalTone === 'dark' || globalTone === 'light')) return globalTone;
      const candidates = [html.dataset.theme, body?.dataset?.theme, html.dataset.colorScheme, body?.dataset?.colorScheme]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const classes = `${html.className} ${body?.className || ''}`.toLowerCase();
      if (candidates.includes('light') || classes.includes('theme-light')) return 'light';
      if (candidates.includes('dark') || classes.includes('theme-dark')) return 'dark';
      return mqDark.matches ? 'dark' : 'light';
    }

    resolveTone() {
      const mode = this.config.themeMode || 'follow-site';
      const globalThemeEnabled = this.config.globalThemeEnabled === true;
      const independentMode = mode === 'customer' || mode === 'independent';

      if (globalThemeEnabled) {
        this.toneOverride = null;
        if (independentMode) {
          const stored = localStorage.getItem(`vcc-ac-tone-${this.kind}`);
          if (stored === 'dark' || stored === 'light') this.toneOverride = stored;
        }
      } else {
        const stored = localStorage.getItem(`vcc-ac-tone-${this.kind}`);
        if (stored === 'dark' || stored === 'light') this.toneOverride = stored;
      }

      let tone;
      if (this.toneOverride) tone = this.toneOverride;
      else if (mode === 'dark' || mode === 'light') tone = mode;
      else if (mode === 'system' || independentMode) tone = mqDark.matches ? 'dark' : 'light';
      else tone = this.siteTone();
      this.applyTone(tone);

      if (mode === 'follow-site' && !this.toneOverride && !this.siteObserver) {
        const attributeFilter = ['class', 'data-theme', 'data-color-scheme'];
        if (globalThemeEnabled) attributeFilter.push('data-vcc-theme');
        this.siteObserver = new MutationObserver(() => {
          const previousTone = this.root.dataset.tone;
          this.applyTone(this.siteTone());
          if (previousTone !== this.root.dataset.tone && !this.customerLayout) this.resolveLayout();
        });
        this.siteObserver.observe(document.documentElement, { attributes: true, attributeFilter });
        if (document.body) {
          this.siteObserver.observe(document.body, { attributes: true, attributeFilter });
        }
      }
    }

    breakpointName() {
      return mqMobile.matches ? 'mobile' : 'desktop';
    }

    storageKey() {
      return `vcc-ac-layout-${this.kind}-${this.breakpointName()}`;
    }

    allowedLayouts() {
      const values = [];
      if (this.config.allowGrid) values.push('archive-grid');
      if (this.config.allowBorderless) values.push('borderless');
      if (this.config.allowOverlay) values.push('image-overlay');
      if (this.config.allowAtlas) values.push('atlas');
      return values;
    }

    resolveLayout() {
      const allowed = this.allowedLayouts();
      const breakpoint = this.breakpointName();
      if (this.layoutBreakpoint && this.layoutBreakpoint !== breakpoint) this.customerLayout = null;
      this.layoutBreakpoint = breakpoint;
      let transientLayout = null;
      let requestedLayout = null;
      try {
        transientLayout = sessionStorage.getItem(`${this.storageKey()}-pending`);
      } catch (_error) {}
      try {
        requestedLayout = new URLSearchParams(window.location.search).get('vcc_view');
      } catch (_error) {}
      const stored = this.config.rememberView ? localStorage.getItem(this.storageKey()) : null;
      const tone = this.root.dataset.tone === 'dark' ? 'dark' : 'light';
      const defaultLayout = mqMobile.matches
        ? (tone === 'dark' ? (this.s.dml || this.s.ml) : this.s.ml)
        : (tone === 'dark' ? (this.s.ddl || this.s.dl) : this.s.dl);
      const layout = allowed.includes(this.customerLayout)
        ? this.customerLayout
        : allowed.includes(requestedLayout)
        ? requestedLayout
        : allowed.includes(transientLayout)
        ? transientLayout
        : allowed.includes(stored)
        ? stored
        : allowed.includes(defaultLayout)
          ? defaultLayout
          : allowed[0] || 'archive-grid';
      if (!this.customerLayout) this.customerLayout = allowed.includes(stored) ? stored : null;
      if (transientLayout) {
        try {
          sessionStorage.removeItem(`${this.storageKey()}-pending`);
        } catch (_error) {}
      }
      this.applyLayout(layout);
    }

    applyLayout(layout, remember = false) {
      if (!this.allowedLayouts().includes(layout)) return;
      this.root.dataset.layout = layout;
      if (remember) {
        this.customerLayout = layout;
        if (this.config.rememberView) localStorage.setItem(this.storageKey(), layout);
      }
      this.root.querySelectorAll('[data-ac-layout-choice]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.acLayoutChoice === layout));
      });
      this.placeFilterForm();
      if (layout === 'atlas') {
        this.preloadAtlasImages();
        this.updateAtlasInspector(this.root.querySelector('.vcc-ac-card'));
        this.syncAtlasInspectorHeight();
      } else {
        this.pauseAtlasPreloads();
        this.root.querySelector('[data-ac-atlas-inspector]')?.style.removeProperty('--ac-atlas-inspector-height');
      }
    }

    scrollAtlasToolbarIntoView() {
      if (mqMobile.matches) return;
      const target = this.root.querySelector('.vcc-ac-toolbar__main');
      if (!(target instanceof HTMLElement)) return;
      const behavior = this.root.dataset.reducedMotion === 'true' || mqReduced.matches ? 'auto' : 'smooth';
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          target.scrollIntoView({ behavior, block: 'start', inline: 'nearest' });
        });
      });
    }

    bind() {
      this.root.addEventListener('click', (event) => this.onClick(event));
      this.root.addEventListener('change', (event) => this.onChange(event));
      this.root.addEventListener('submit', (event) => this.onSubmit(event));
      this.root.addEventListener('toggle', (event) => this.onToggle(event), true);
      this.root.addEventListener('pointerover', (event) => {
        const card = event.target.closest('.vcc-ac-card');
        if (card && this.root.dataset.layout === 'atlas') this.updateAtlasInspector(card);
      });
      this.root.addEventListener('focusin', (event) => {
        const card = event.target.closest('.vcc-ac-card');
        if (card && this.root.dataset.layout === 'atlas') this.updateAtlasInspector(card);
      });
      this.root.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const filterDialog = this.root.querySelector('[data-ac-filter-dialog]');
        if (filterDialog?.open) {
          event.preventDefault();
          this.closeDialog(filterDialog);
          return;
        }
        this.root.querySelectorAll('[data-ac-mobile-actions].is-open').forEach((menu) => this.closeMobileActions(menu));
      });

      this.root.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeDialog(dialog);
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) this.closeDialog(dialog);
        });
      });

      this.handleWishlistSync = () => this.syncWishlistStates();
      document.addEventListener('vcc:wishlist:ready', this.handleWishlistSync);
      document.addEventListener('vcc:wishlist:updated', this.handleWishlistSync);

      this.handleResponsiveChange = () => this.onResponsiveChange();
      this.handleReducedMotionChange = () => {
        if (this.destroyed) return;
        this.root.dataset.reducedMotion = String(Boolean(this.s.rm && mqReduced.matches));
      };
      this.handleDarkModeChange = () => {
        if (this.destroyed) return;
        if (!this.toneOverride) this.resolveTone();
      };
      this.handleWindowResize = this.debounce(this.handleResponsiveChange, 100);
      mqMobile.addEventListener?.('change', this.handleResponsiveChange);
      mqReduced.addEventListener?.('change', this.handleReducedMotionChange);
      mqDark.addEventListener?.('change', this.handleDarkModeChange);
      window.addEventListener('resize', this.handleWindowResize, { passive: true });

      this.handlePopState = (event) => this.onPopState(event);
      window.addEventListener('popstate', this.handlePopState);
      this.handlePageShow = () => this.restoreFilterDialogHistory();
      window.addEventListener('pageshow', this.handlePageShow);
    }

    onResponsiveChange() {
      if (this.destroyed) return;
      this.applyResponsiveVariables();
      this.placeFilterForm();
      this.resolveLayout();
      this.syncFilterDialogBreakpoint();
      this.syncAtlasInspectorHeight();
      this.updateMobileDockState();
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fn(...args), wait);
      };
    }

    rememberTransientLayout() {
      const layout = this.root.dataset.layout;
      if (!this.allowedLayouts().includes(layout)) return;
      try {
        sessionStorage.setItem(`${this.storageKey()}-pending`, layout);
      } catch (_error) {}
    }

    syncLayoutParam(form) {
      if (!(form instanceof HTMLFormElement)) return;
      const layout = this.root.dataset.layout;
      let input = form.querySelector('input[name="vcc_view"]');
      if (layout === 'atlas' && this.allowedLayouts().includes(layout)) {
        if (!(input instanceof HTMLInputElement)) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'vcc_view';
          form.append(input);
        }
        input.value = layout;
      } else if (input) {
        input.remove();
      }
    }

    syncUrlLayoutParam(url) {
      if (!(url instanceof URL)) return;
      const layout = this.root.dataset.layout;
      if (layout === 'atlas' && this.allowedLayouts().includes(layout)) {
        url.searchParams.set('vcc_view', layout);
      } else {
        url.searchParams.delete('vcc_view');
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form instanceof HTMLFormElement && form.matches('[data-ac-filter-form]')) {
        const replaceFilterHistory = this.filterDialogOwnsCurrentHistory();
        const dialog = form.closest('[data-ac-filter-dialog]')
          || (replaceFilterHistory ? this.root.querySelector('[data-ac-filter-dialog]') : null);
        if (this.filterDialogInteractionBlocked(dialog, event)) return;
        if (this.searchCurationIsPending()) {
          event.preventDefault();
          return;
        }
        this.rememberTransientLayout();
        this.syncLayoutParam(form);
        if (
          this.kind === 'search'
          && this.searchManifest?.sealed
          && this.isCurrentMappedManifest(this.searchManifest)
          && this.mappedFilterFormSupported(form)
        ) {
          const url = this.mappedFilterFormUrl(form);
          if (url && replaceFilterHistory && this.filterDialogUrlMatchesCurrent(url, { mapped: true })) {
            event.preventDefault();
            window.VCCSearchTransition?.clearPendingSecondary?.();
            this.closeDialog(dialog);
            return;
          }
          if (url && this.applyMappedSearchUrlWithProgress(url, {
            history: replaceFilterHistory ? 'replace' : true,
            scroll: false
          })) {
            event.preventDefault();
            window.VCCSearchTransition?.clearPendingSecondary?.();
            this.root.querySelectorAll('.vcc-ac-filter-group--quick[open]').forEach((details) => {
              details.open = false;
            });
            if (dialog) {
              if (replaceFilterHistory) this.retireFilterDialogHistoryTracking();
              this.closeFilterDialogDirect(dialog);
            }
            return;
          }
        }
        if (dialog && replaceFilterHistory) {
          const url = this.nativeFilterFormUrl(form);
          if (url) {
            event.preventDefault();
            window.VCCSearchTransition?.clearPendingSecondary?.();
            if (this.filterDialogUrlMatchesCurrent(url)) {
              this.closeDialog(dialog);
              return;
            }
            this.retireFilterDialogHistoryForNavigation();
            this.closeFilterDialogDirect(dialog, { restoreFocus: false });
            this.navigateFilterDialogDestination(url);
          }
        }
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-ac-product-type-group]');
      if (input instanceof HTMLInputElement) {
        const group = input.dataset.acProductTypeGroup;
        this.root.querySelectorAll(`[data-ac-product-type-group="${group}"]`).forEach((pairedInput) => {
          pairedInput.checked = input.checked;
        });
      }

      const quickForm = event.target.closest('[data-ac-quick-filter-form]');
      if (quickForm instanceof HTMLFormElement) this.submitQuickFilterForm(quickForm);

      const atlasForm = event.target.closest('[data-ac-filter-form="drawer"]');
      if (
        atlasForm instanceof HTMLFormElement
        && this.root.dataset.atlasDesktop === 'true'
        && atlasForm.closest('[data-ac-filter-sidebar]')
      ) {
        this.submitQuickFilterForm(atlasForm);
      }
    }

    onToggle(event) {
      const details = event.target;
      if (
        !(details instanceof HTMLDetailsElement)
        || !details.open
        || !details.matches('.vcc-ac-filter-group--quick, [data-ac-sort-menu]')
      ) return;
      this.root.querySelectorAll('.vcc-ac-filter-group--quick[open], [data-ac-sort-menu][open]').forEach((sibling) => {
        if (sibling !== details) sibling.open = false;
      });
    }

    setProgressivePriceSortControlsPending(pending) {
      const disabled = pending === true;
      this.root.querySelectorAll(
        '[data-ac-sort-option="price-ascending"], [data-ac-sort-option="price-descending"]'
      ).forEach((option) => {
        if (disabled) {
          option.setAttribute('aria-disabled', 'true');
          option.dataset.acProgressiveDisabled = 'true';
        } else {
          option.removeAttribute('aria-disabled');
          delete option.dataset.acProgressiveDisabled;
        }
      });
    }

    applySort(value, menu) {
      const cleanValue = String(value || '').trim();
      if (!cleanValue) return;
      if (
        this.kind === 'search'
        && this.engineMode === 'v4_metafield'
        && this.searchManifest?.progressiveLoading === true
        && ['price-ascending', 'price-descending'].includes(cleanValue)
      ) {
        if (menu instanceof HTMLDetailsElement) menu.open = false;
        return;
      }
      if (this.searchCurationIsPending()) return;
      if (menu instanceof HTMLDetailsElement) menu.open = false;
      const currentValue = new URLSearchParams(window.location.search).get('sort_by') || '';
      if (currentValue === cleanValue) return;
      if (
        this.kind === 'search'
        && this.searchManifest?.sealed
        && this.isCurrentMappedManifest(this.searchManifest)
        && this.mappedSearchSortSupported(cleanValue)
      ) {
        const url = new URL(window.location.href);
        if (!cleanValue || cleanValue === 'relevance') url.searchParams.delete('sort_by');
        else url.searchParams.set('sort_by', cleanValue);
        url.searchParams.delete('page');
        this.syncUrlLayoutParam(url);
        if (this.applyMappedSearchUrlWithProgress(url, { history: true, scroll: false })) return;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', cleanValue);
      url.searchParams.delete('page');
      this.syncUrlLayoutParam(url);
      window.VCCSearchTransition?.markSecondary?.('refine', url.searchParams.get('vcc_raw') || url.searchParams.get('q') || '');
      window.location.assign(url.toString());
    }

    onClick(event) {
      if (
        this.searchCurationIsPending()
        && event.target.closest(
          '[data-ac-toolbar], [data-ac-active-filters], [data-ac-mobile-dock], [data-ac-filter-dialog], [data-ac-filter-sidebar]'
        )
      ) {
        event.preventDefault();
        return;
      }

      const scopeToggle = event.target.closest('[data-ac-search-scope-toggle]');
      if (scopeToggle) {
        event.preventDefault();
        const destination = String(
          scopeToggle.dataset.acSearchUniverseUrl || ''
        ).trim();
        if (destination) {
          window.location.assign(destination);
        }
        return;
      }

      if (!event.target.closest('[data-ac-sort-menu]')) {
        this.root.querySelectorAll('[data-ac-sort-menu][open]').forEach((menu) => {
          menu.open = false;
        });
      }

      const sortOption = event.target.closest('[data-ac-sort-option]');
      if (sortOption) {
        event.preventDefault();
        this.applySort(sortOption.dataset.acSortOption, sortOption.closest('[data-ac-sort-menu]'));
        return;
      }

      const filterLink = event.target.closest(
        '[data-ac-active-filters] a[href], [data-ac-filter-form] [data-ac-clear-filters][href]'
      );
      const replaceFilterHistory = Boolean(filterLink && this.filterDialogOwnsCurrentHistory());
      const filterDialog = filterLink
        ? filterLink.closest?.('[data-ac-filter-dialog]')
          || (replaceFilterHistory ? this.root.querySelector('[data-ac-filter-dialog]') : null)
        : null;
      if (filterLink && this.filterDialogInteractionBlocked(filterDialog, event)) return;
      const filterUrl = filterLink ? this.filterDialogNavigationUrl(filterLink.href) : null;
      if (
        filterUrl
        && this.kind === 'search'
        && this.searchManifest?.sealed
        && this.isCurrentMappedManifest(this.searchManifest)
      ) {
        try {
          if (
            replaceFilterHistory
            && this.mappedFilterUrlSupported(filterUrl)
            && this.filterDialogUrlMatchesCurrent(filterUrl, { mapped: true })
          ) {
            event.preventDefault();
            window.VCCSearchTransition?.clearPendingSecondary?.();
            this.closeDialog(filterDialog);
            return;
          }
          if (
            this.mappedFilterUrlSupported(filterUrl)
            && this.applyMappedSearchUrlWithProgress(filterUrl, {
              history: replaceFilterHistory ? 'replace' : true,
              scroll: false
            })
          ) {
            event.preventDefault();
            window.VCCSearchTransition?.clearPendingSecondary?.();
            if (filterDialog) {
              if (replaceFilterHistory) this.retireFilterDialogHistoryTracking();
              this.closeFilterDialogDirect(filterDialog);
            }
            if (event.detail === 0) {
              window.requestAnimationFrame(() => {
                const nextChip = this.root.querySelector('[data-ac-active-filters] a[href]');
                const filterTrigger = this.root.querySelector('[data-ac-open-filters]');
                (nextChip || filterTrigger)?.focus?.({ preventScroll: true });
              });
            }
            return;
          }
        } catch (_error) {}
      }
      if (filterUrl && replaceFilterHistory) {
        try {
          event.preventDefault();
          window.VCCSearchTransition?.clearPendingSecondary?.();
          if (this.filterDialogUrlMatchesCurrent(filterUrl)) {
            this.closeDialog(filterDialog);
            return;
          }
          this.retireFilterDialogHistoryForNavigation();
          this.closeFilterDialogDirect(filterDialog, { restoreFocus: false });
          this.navigateFilterDialogDestination(filterUrl);
          return;
        } catch (_error) {}
      }

      if (!event.target.closest('[data-ac-mobile-actions]')) {
        this.root.querySelectorAll('[data-ac-mobile-actions].is-open').forEach((menu) => this.closeMobileActions(menu));
      }

      const mobileActionToggle = event.target.closest('[data-ac-mobile-action-toggle]');
      if (mobileActionToggle) {
        const menu = mobileActionToggle.closest('[data-ac-mobile-actions]');
        if (menu) this.toggleMobileActions(menu);
        return;
      }

      const pricePreset = event.target.closest('[data-vcc-price-preset]');
      if (pricePreset) {
        const quickForm = pricePreset.closest('[data-ac-quick-filter-form]');
        if (quickForm instanceof HTMLFormElement) {
          window.setTimeout(() => this.submitQuickFilterForm(quickForm), 50);
        } else {
          const atlasForm = pricePreset.closest('[data-ac-filter-form="drawer"]');
          if (
            atlasForm instanceof HTMLFormElement
            && this.root.dataset.atlasDesktop === 'true'
            && atlasForm.closest('[data-ac-filter-sidebar]')
          ) {
            window.setTimeout(() => this.submitQuickFilterForm(atlasForm), 50);
          }
        }
        return;
      }

      const layoutButton = event.target.closest('[data-ac-layout-choice]');
      if (layoutButton) {
        const layout = layoutButton.dataset.acLayoutChoice;
        this.applyLayout(layout, true);
        if (layout === 'atlas') this.scrollAtlasToolbarIntoView();
        return;
      }

      const paginationLink = event.target.closest('.vcc-ac-pagination a[href]');
      if (paginationLink) {
        try {
          const paginationUrl = new URL(paginationLink.href, window.location.href);
          this.syncUrlLayoutParam(paginationUrl);
          paginationLink.href = paginationUrl.toString();
          this.rememberTransientLayout();
          if (
            this.kind === 'search'
            && this.searchManifest?.sealed
            && this.searchManifest.entries.length
            && this.isCurrentMappedManifest(this.searchManifest)
           ) {
              const requestedPage = Math.max(1, parseInt(paginationUrl.searchParams.get('page') || '1', 10) || 1);
              if (
                this.mappedSearchPageReady(this.searchManifest, requestedPage)
                && this.navigateMappedSearchPage(requestedPage, paginationUrl)
              ) {
                event.preventDefault();
                window.VCCSearchTransition?.clearPendingSecondary?.();
              }
           } else if (
             this.kind === 'search'
             && this.searchCurationIsFallbackLocked()
             && this.canNavigateNativeSearchPage(paginationUrl)
           ) {
             const requestedPage = Math.max(1, parseInt(paginationUrl.searchParams.get('page') || '1', 10) || 1);
             if (this.nativeSearchSnapshotReady(requestedPage, paginationUrl)) {
               event.preventDefault();
               window.VCCSearchTransition?.clearPendingSecondary?.();
               this.navigateNativeSearchPage(requestedPage, paginationUrl);
             }
           }
        } catch (_error) {}
        return;
      }

      if (event.target.closest('[data-ac-open-filters]')) {
        const style = this.s.fs || 'drawer';
        if (!mqMobile.matches && (style === 'sidebar' || style === 'topbar')) {
          this.root.dataset.filterOpen = String(this.root.dataset.filterOpen !== 'true');
        } else {
          this.openDialog(
            this.root.querySelector('[data-ac-filter-dialog]'),
            event.target.closest('[data-ac-open-filters]')
          );
        }
        return;
      }
      if (event.target.closest('[data-ac-close-filters]')) {
        this.closeDialog(this.root.querySelector('[data-ac-filter-dialog]'));
        return;
      }
      if (event.target.closest('[data-ac-close-quick]')) {
        this.closeDialog(this.root.querySelector('[data-ac-quick-dialog]'));
        return;
      }

      if (event.target.closest('[data-ac-theme-toggle]')) {
        const mode = this.config.themeMode || 'follow-site';
        const independentMode = mode === 'customer' || mode === 'independent';
        if (this.config.globalThemeEnabled === true && !independentMode) return;
        const next = this.root.dataset.tone === 'dark' ? 'light' : 'dark';
        this.toneOverride = next;
        localStorage.setItem(`vcc-ac-tone-${this.kind}`, next);
        this.applyTone(next);
        return;
      }

      const action = event.target.closest('[data-ac-action]');
      if (action && action.dataset.acAction !== 'view') {
        const actionResult = this.handleAction(action);
        const mobileActions = action.closest('[data-ac-mobile-actions]');
        if (mobileActions) {
          if (action.dataset.acAction === 'quick-add' && actionResult && typeof actionResult.finally === 'function') {
            actionResult.finally(() => this.closeMobileActions(mobileActions));
          } else {
            this.closeMobileActions(mobileActions);
          }
        }
      }
    }

    closeMobileActions(menu) {
      menu.classList.remove('is-open');
      menu.querySelector('[data-ac-mobile-action-toggle]')?.setAttribute('aria-expanded', 'false');
      menu.querySelector('.vcc-ac-mobile-actions__menu')?.setAttribute('aria-hidden', 'true');
    }

    toggleMobileActions(menu) {
      const shouldOpen = !menu.classList.contains('is-open');
      this.root.querySelectorAll('[data-ac-mobile-actions].is-open').forEach((openMenu) => {
        if (openMenu !== menu) this.closeMobileActions(openMenu);
      });
      menu.classList.toggle('is-open', shouldOpen);
      menu.querySelector('[data-ac-mobile-action-toggle]')?.setAttribute('aria-expanded', String(shouldOpen));
      menu.querySelector('.vcc-ac-mobile-actions__menu')?.setAttribute('aria-hidden', String(!shouldOpen));
    }

    submitQuickFilterForm(form) {
      window.clearTimeout(this.quickFilterTimer);
      this.quickFilterTimer = window.setTimeout(() => {
        this.rememberTransientLayout();
        this.syncLayoutParam(form);
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      }, 120);
    }

    syncResultStatus() {
      const desktopStatus = this.root.querySelector('.vcc-ac-status--desktop');
      const mobileStatus = this.root.querySelector('.vcc-ac-status--mobile');
      const desktopPage = desktopStatus?.querySelector('[data-ac-status-page]');
      const desktopRange = desktopStatus?.querySelector('[data-ac-status-range]');
      const mobileResults = mobileStatus?.querySelector('[data-ac-status-results]');
      const mobilePage = mobileStatus?.querySelector('[data-ac-status-page]');
      const pageLabel = desktopPage?.textContent || '';
      const rangeLabel = desktopRange?.textContent || '';
      const currentLink = this.root.querySelector('[data-ac-numbered-pagination] [aria-current="page"]');
      const requestedPage = new URLSearchParams(window.location.search).get('page');
      const currentPage = Math.max(1, parseInt(currentLink?.textContent || requestedPage || '1', 10) || 1);
      const totalPages = Math.max(1, parseInt(pageLabel.match(/\bof\s+(\d+)/i)?.[1] || '1', 10) || 1);
      const resultLabel = rangeLabel.match(/\bof\s+([\d,]+\+?)\s+items/i)?.[1];

      if (desktopPage) desktopPage.textContent = `Page ${currentPage} of ${totalPages}`;
      if (mobilePage) mobilePage.textContent = `Page ${currentPage} of ${totalPages}`;

      if (!desktopRange || !resultLabel) return;
      const resultCount = parseInt(resultLabel.replace(/[^\d]/g, ''), 10);
      if (!Number.isFinite(resultCount)) return;
      const pageSize = Math.max(1, clampNumber(this.s.pg, 48));
      const showingFirst = resultCount === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
      const showingLast = resultCount === 0 ? 0 : Math.min(currentPage * pageSize, resultCount);
      desktopRange.textContent = `Showing ${showingFirst}-${showingLast} of ${resultLabel} items`;
      if (mobileResults) mobileResults.textContent = `Results - ${resultLabel} items`;
    }

    syncMobilePager() {
      const targets = this.root.querySelectorAll('[data-ac-mobile-pager], [data-ac-mobile-dock-pager]');
      if (!targets.length) return;
      const wrapper = this.root.querySelector('[data-ac-numbered-pagination]');
      const source = wrapper?.hidden ? null : wrapper?.querySelector('.vcc-ac-pagination');
      if (!source) {
        targets.forEach((target) => {
          target.hidden = true;
          target.replaceChildren();
        });
        return;
      }
      targets.forEach((target) => {
        const clone = source.cloneNode(true);
        clone.classList.add('vcc-ac-pagination--compact');
        target.replaceChildren(clone);
        target.hidden = false;
      });
      window.requestAnimationFrame(() => this.ensureCurrentMobilePageVisible());
    }

    ensureCurrentMobilePageVisible() {
      this.root.querySelectorAll('[data-ac-mobile-dock-pager] .vcc-ac-pagination__pages').forEach((pages) => {
        const current = pages.querySelector('[aria-current="page"]');
        if (!(current instanceof HTMLElement)) return;
        const targetLeft = current.offsetLeft - ((pages.clientWidth - current.offsetWidth) / 2);
        pages.scrollLeft = Math.max(0, targetLeft);
      });
    }

    setupMobileToolbarDock() {
      const toolbar = this.root.querySelector('[data-ac-toolbar]');
      const dock = this.root.querySelector('[data-ac-mobile-dock]');
      if (!toolbar || !dock || this.mobileDockSync) return;
      this.toolbarSentinel = toolbar;
      this.mobileDockSync = () => {
        if (this.mobileDockFrame) return;
        this.mobileDockFrame = window.requestAnimationFrame(() => {
          this.mobileDockFrame = null;
          this.updateMobileDockState();
        });
      };

      const pageWrapper = document.querySelector('.page-wrapper');
      this.mobileDockScrollRoot = pageWrapper;
      window.addEventListener('scroll', this.mobileDockSync, { passive: true });
      pageWrapper?.addEventListener('scroll', this.mobileDockSync, { passive: true });
      this.updateMobileDockState();
    }

    getScrollTop() {
      const pageWrapper = document.querySelector('.page-wrapper');
      const wrapperTop = pageWrapper instanceof HTMLElement ? pageWrapper.scrollTop || 0 : 0;
      return Math.max(wrapperTop, window.scrollY || document.documentElement.scrollTop || 0);
    }

    updateMobileDockState(forceDock) {
      const dock = this.root.querySelector('[data-ac-mobile-dock]');
      if (!this.toolbarSentinel || !dock) {
        this.mobileDockLatched = false;
        this.root.dataset.mobileToolbarDocked = 'false';
        return;
      }
      if (!mqMobile.matches) {
        const wasDocked = this.mobileDockLatched || this.root.dataset.mobileToolbarDocked === 'true';
        if (!wasDocked && dock.getAttribute('aria-hidden') === 'true') return;
        this.mobileDockLatched = false;
        if (wasDocked) {
          this.root.querySelectorAll('.vcc-ac-filter-group--quick[open]').forEach((details) => {
            details.open = false;
          });
        }
        this.root.dataset.mobileToolbarDocked = 'false';
        dock.setAttribute('aria-hidden', 'true');
        return;
      }
      let shouldDock;
      if (typeof forceDock === 'boolean') {
        shouldDock = forceDock;
      } else {
        const scrollTop = this.getScrollTop();
        const alreadyDocked = this.mobileDockLatched || this.root.dataset.mobileToolbarDocked === 'true';
        shouldDock = alreadyDocked ? scrollTop > 2 : scrollTop > 160;
      }
      const stateChanged = this.root.dataset.mobileToolbarDocked !== String(shouldDock);
      this.mobileDockLatched = shouldDock;
      if (!stateChanged) return;
      this.root.querySelectorAll('.vcc-ac-filter-group--quick[open]').forEach((details) => {
        details.open = false;
      });
      this.root.dataset.mobileToolbarDocked = String(shouldDock);
      dock.setAttribute('aria-hidden', String(!shouldDock));
      if (shouldDock) window.requestAnimationFrame(() => this.ensureCurrentMobilePageVisible());
    }

    filterDialogHistoryMarker(state = window.history.state) {
      if (!state || typeof state !== 'object') return null;
      const marker = state[this.filterDialogHistoryKey];
      if (
        !marker
        || typeof marker !== 'object'
        || typeof marker.owner !== 'string'
        || typeof marker.token !== 'string'
        || typeof marker.route !== 'string'
        || !marker.token
        || !marker.route
      ) return null;
      return marker;
    }

    filterDialogHistoryIsOwned(marker) {
      return Boolean(marker && marker.owner === this.filterDialogHistoryOwner);
    }

    filterDialogOwnsCurrentHistory() {
      const marker = this.filterDialogHistoryMarker();
      return Boolean(
        this.filterDialogHistoryIsOwned(marker)
        && (!this.filterDialogHistoryToken || marker.token === this.filterDialogHistoryToken)
      );
    }

    filterDialogHistoryStateWithoutMarker(state = window.history.state) {
      const nextState = state && typeof state === 'object' ? { ...state } : {};
      delete nextState[this.filterDialogHistoryKey];
      return nextState;
    }

    filterDialogUsesHistory(dialog) {
      if (!dialog?.matches?.('[data-ac-filter-dialog]')) return false;
      if (mqMobile.matches) return true;
      const style = this.s.fs || 'drawer';
      const atlasDesktop = this.root.dataset.atlasDesktop === 'true' || this.root.dataset.layout === 'atlas';
      return !atlasDesktop && style !== 'sidebar' && style !== 'topbar';
    }

    beginFilterDialogHistory() {
      const currentMarker = this.filterDialogHistoryMarker();
      if (this.filterDialogHistoryIsOwned(currentMarker)) {
        this.filterDialogHistoryToken = currentMarker.token;
        this.filterDialogHistoryRoute = currentMarker.route;
        this.filterDialogHistoryBaseRoute = currentMarker.baseRoute || currentMarker.route;
        this.filterDialogHistoryNormalizedUrl = currentMarker.normalizedUrl || '';
        this.filterDialogObservedRoute = this.filterDialogRouteIdentity();
        return true;
      }
      const token = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      const route = this.filterDialogRouteIdentity();
      const currentState = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
      try {
        window.history.pushState({
          ...currentState,
          [this.filterDialogHistoryKey]: {
            owner: this.filterDialogHistoryOwner,
            token,
            route,
            baseRoute: route
          }
        }, '', window.location.href);
        this.filterDialogHistoryToken = token;
        this.filterDialogHistoryRoute = route;
        this.filterDialogHistoryBaseRoute = route;
        this.filterDialogHistoryNormalizedUrl = '';
        this.filterDialogObservedRoute = route;
        return true;
      } catch (_error) {
        this.filterDialogHistoryToken = '';
        this.filterDialogHistoryRoute = '';
        this.filterDialogHistoryBaseRoute = '';
        this.filterDialogHistoryNormalizedUrl = '';
        return false;
      }
    }

    normalizeMappedSearchHistoryEntry(sourceUrl, page) {
      const url = sourceUrl instanceof URL
        ? new URL(sourceUrl)
        : new URL(sourceUrl, window.location.href);
      const normalizedRoute = this.filterDialogRouteIdentity(url);
      const marker = this.filterDialogHistoryMarker();
      const nextState = {
        ...this.filterDialogHistoryStateWithoutMarker(),
        vccMappedSearch: true,
        page
      };
      if (this.filterDialogHistoryIsOwned(marker)) {
        const baseRoute = marker.baseRoute || marker.route;
        nextState[this.filterDialogHistoryKey] = {
          ...marker,
          route: normalizedRoute,
          baseRoute,
          normalizedUrl: url.toString()
        };
        this.filterDialogHistoryToken = marker.token;
        this.filterDialogHistoryRoute = normalizedRoute;
        this.filterDialogHistoryBaseRoute = baseRoute;
        this.filterDialogHistoryNormalizedUrl = url.toString();
      }
      window.history.replaceState(nextState, '', url);
      this.commitFilterDialogObservedRoute(url);
      return url;
    }

    normalizeFilterDialogBaseHistory() {
      if (
        !this.filterDialogHistoryToken
        || !this.filterDialogHistoryBaseRoute
        || !this.filterDialogHistoryNormalizedUrl
        || this.filterDialogRouteIdentity() !== this.filterDialogHistoryBaseRoute
      ) return false;
      try {
        const normalizedUrl = new URL(this.filterDialogHistoryNormalizedUrl, window.location.href);
        const normalizedRoute = this.filterDialogRouteIdentity(normalizedUrl);
        if (normalizedRoute !== this.filterDialogHistoryRoute) return false;
        const page = Math.max(1, parseInt(normalizedUrl.searchParams.get('page') || '1', 10) || 1);
        window.history.replaceState({
          ...this.filterDialogHistoryStateWithoutMarker(),
          vccMappedSearch: true,
          page
        }, '', normalizedUrl);
        this.filterDialogObservedRoute = normalizedRoute;
        return true;
      } catch (_error) {
        return false;
      }
    }

    filterDialogRouteIdentity(sourceUrl = window.location.href) {
      try {
        const url = sourceUrl instanceof URL
          ? new URL(sourceUrl)
          : new URL(sourceUrl, window.location.href);
        return JSON.stringify({
          origin: url.origin,
          pathname: url.pathname.replace(/\/$/, '') || '/',
          search: Array.from(url.searchParams.entries()).sort(([leftName, leftValue], [rightName, rightValue]) => {
            const nameOrder = leftName.localeCompare(rightName);
            return nameOrder || leftValue.localeCompare(rightValue);
          }),
          hash: url.hash
        });
      } catch (_error) {
        return String(sourceUrl || '');
      }
    }

    commitFilterDialogObservedRoute(sourceUrl = window.location.href) {
      const route = this.filterDialogRouteIdentity(sourceUrl);
      this.filterDialogObservedRoute = route;
      return route;
    }

    filterDialogUrlMatchesCurrent(sourceUrl, options = {}) {
      try {
        const candidate = sourceUrl instanceof URL
          ? new URL(sourceUrl)
          : new URL(sourceUrl, window.location.href);
        if (options.mapped === true) {
          candidate.searchParams.set('vcc_mapped', '1');
          const rawQuery = new URLSearchParams(window.location.search).get('vcc_raw');
          if (rawQuery && !candidate.searchParams.has('vcc_raw')) {
            candidate.searchParams.set('vcc_raw', rawQuery);
          }
          this.syncUrlLayoutParam(candidate);
        }
        return this.filterDialogRouteIdentity(candidate) === this.filterDialogRouteIdentity();
      } catch (_error) {
        return false;
      }
    }

    nativeFilterFormUrl(form) {
      try {
        const url = new URL(form.action || window.location.href, window.location.href);
        const params = new URLSearchParams();
        new FormData(form).forEach((value, name) => {
          if (typeof value === 'string') params.append(name, value);
        });
        url.search = params.toString();
        return this.canonicalizeFilterDialogNavigationUrl(url);
      } catch (_error) {
        return null;
      }
    }

    canonicalizeFilterDialogNavigationUrl(sourceUrl) {
      const url = sourceUrl instanceof URL
        ? new URL(sourceUrl)
        : new URL(sourceUrl, window.location.href);
      if (this.kind === 'search') {
        const current = new URL(window.location.href);
        const rawQuery = current.searchParams.get('vcc_raw');
        if (rawQuery && !url.searchParams.has('vcc_raw')) url.searchParams.set('vcc_raw', rawQuery);
      }
      this.syncUrlLayoutParam(url);
      return url;
    }

    filterDialogNavigationUrl(sourceUrl) {
      try {
        return this.canonicalizeFilterDialogNavigationUrl(sourceUrl);
      } catch (_error) {
        return null;
      }
    }

    navigateFilterDialogDestination(sourceUrl) {
      const url = this.filterDialogNavigationUrl(sourceUrl);
      if (!url || this.filterDialogNavigationPending) return false;
      this.filterDialogNavigationPending = true;
      const navigate = () => {
        if (this.kind === 'search') {
          const query = url.searchParams.get('vcc_raw') || url.searchParams.get('q') || '';
          window.VCCSearchTransition?.markSecondary?.('refine', query);
        }
        try {
          window.location.replace(url.toString());
        } catch (error) {
          this.filterDialogNavigationPending = false;
          throw error;
        }
      };
      if (this.kind === 'search' && typeof window.queueMicrotask === 'function') {
        window.queueMicrotask(navigate);
      } else if (this.kind === 'search') {
        Promise.resolve().then(navigate);
      } else {
        navigate();
      }
      return true;
    }

    filterDialogInteractionBlocked(_dialog, event = null) {
      if (
        !(
          this.filterDialogHistoryTraversalPending
          || this.filterDialogClosing
          || this.filterDialogNavigationPending
        )
      ) return false;
      event?.preventDefault?.();
      return true;
    }

    retireFilterDialogHistoryTracking() {
      window.clearTimeout(this.filterDialogHistoryTraversalTimer);
      this.filterDialogHistoryTraversalTimer = 0;
      this.filterDialogHistoryTraversalPending = false;
      this.filterDialogHistoryToken = '';
      this.filterDialogHistoryRoute = '';
      this.filterDialogHistoryBaseRoute = '';
      this.filterDialogHistoryNormalizedUrl = '';
    }

    retireFilterDialogHistoryForNavigation() {
      if (this.filterDialogOwnsCurrentHistory()) {
        try {
          window.history.replaceState(
            this.filterDialogHistoryStateWithoutMarker(),
            '',
            window.location.href
          );
        } catch (_error) {}
      }
      this.retireFilterDialogHistoryTracking();
    }

    retireFilterDialogHistoryLevel(dialog) {
      if (!this.filterDialogOwnsCurrentHistory()) {
        this.retireFilterDialogHistoryTracking();
        this.closeFilterDialogDirect(dialog, { restoreFocus: false, immediate: true });
        return false;
      }
      this.cancelFilterDialogClose(dialog);
      try {
        window.history.replaceState(
          this.filterDialogHistoryStateWithoutMarker(),
          '',
          window.location.href
        );
      } catch (_error) {
        this.requestFilterDialogClose(dialog, { restoreFocus: false });
        return false;
      }
      this.closeFilterDialogDirect(dialog, { restoreFocus: false, immediate: true });
      this.filterDialogRestoreFocusOnClose = false;
      this.filterDialogHistoryTraversalPending = true;
      window.clearTimeout(this.filterDialogHistoryTraversalTimer);
      try {
        window.history.back();
      } catch (_error) {
        this.retireFilterDialogHistoryTracking();
        return false;
      }
      this.filterDialogHistoryTraversalTimer = window.setTimeout(() => {
        if (!this.filterDialogHistoryTraversalPending) return;
        this.onPopState({ state: window.history.state });
      }, 500);
      return true;
    }

    filterDialogFocusTargetIsVisible(target) {
      if (
        !(target instanceof HTMLElement)
        || !target.isConnected
        || target.hasAttribute('disabled')
        || target.closest?.('[hidden], [inert], [aria-hidden="true"]')
      ) return false;
      const style = typeof window.getComputedStyle === 'function'
        ? window.getComputedStyle(target)
        : null;
      if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
      if (typeof target.getClientRects === 'function' && target.getClientRects().length === 0) return false;
      return true;
    }

    filterDialogFocusTarget() {
      if (this.filterDialogFocusTargetIsVisible(this.filterDialogOpener)) return this.filterDialogOpener;
      const triggers = Array.from(this.root.querySelectorAll('[data-ac-open-filters]'));
      return triggers.find((trigger) => this.filterDialogFocusTargetIsVisible(trigger)) || null;
    }

    cancelFilterDialogFocus() {
      if (this.filterDialogFocusFrame) window.cancelAnimationFrame(this.filterDialogFocusFrame);
      this.filterDialogFocusFrame = 0;
    }

    cancelFilterDialogClose(dialog) {
      this.cancelFilterDialogFocus();
      window.clearTimeout(this.filterDialogCloseTimer);
      this.filterDialogCloseTimer = 0;
      this.filterDialogClosing = false;
      dialog?.classList.remove('is-closing');
    }

    openFilterDialogDirect(dialog, opener = null) {
      if (!dialog) return;
      this.cancelFilterDialogClose(dialog);
      if (opener instanceof HTMLElement) this.filterDialogOpener = opener;
      else if (!this.filterDialogOpener?.isConnected) this.filterDialogOpener = this.filterDialogFocusTarget();
      if (!dialog.open) dialog.showModal();
      window.requestAnimationFrame(() => {
        if (dialog.open && !this.filterDialogClosing) dialog.classList.add('is-open');
      });
    }

    openFilterDialog(dialog, opener = null) {
      if (!dialog || (dialog.open && !this.filterDialogClosing)) return;
      if (!this.filterDialogUsesHistory(dialog) || !this.beginFilterDialogHistory()) return;
      this.openFilterDialogDirect(dialog, opener);
    }

    restoreFilterDialogHistory() {
      if (this.destroyed) return false;
      const dialog = this.root.querySelector('[data-ac-filter-dialog]');
      const marker = this.filterDialogHistoryMarker();
      const currentRoute = this.filterDialogRouteIdentity();
      this.filterDialogObservedRoute = currentRoute;
      if (!this.filterDialogHistoryIsOwned(marker)) {
        if (dialog?.open) {
          this.retireFilterDialogHistoryTracking();
          this.closeFilterDialogDirect(dialog, { restoreFocus: false, immediate: true });
        }
        return false;
      }
      this.filterDialogHistoryToken = marker.token;
      this.filterDialogHistoryRoute = marker.route;
      this.filterDialogHistoryBaseRoute = marker.baseRoute || marker.route;
      this.filterDialogHistoryNormalizedUrl = marker.normalizedUrl || '';
      if (marker.route !== currentRoute) {
        this.retireFilterDialogHistoryForNavigation();
        this.closeFilterDialogDirect(dialog, { restoreFocus: false, immediate: true });
        return false;
      }
      if (!this.filterDialogUsesHistory(dialog)) {
        this.retireFilterDialogHistoryLevel(dialog);
        return true;
      }
      this.openFilterDialogDirect(dialog);
      return true;
    }

    closeFilterDialogDirect(dialog, options = {}) {
      if (!dialog) return;
      this.cancelFilterDialogFocus();
      window.clearTimeout(this.filterDialogCloseTimer);
      this.filterDialogCloseTimer = 0;
      this.filterDialogClosing = true;
      dialog.classList.remove('is-open');
      dialog.classList.add('is-closing');
      const restoreFocus = options.restoreFocus !== false;
      const focusTarget = restoreFocus ? this.filterDialogFocusTarget() : null;
      const finish = () => {
        if (!this.filterDialogClosing) return;
        this.filterDialogCloseTimer = 0;
        this.filterDialogClosing = false;
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
        this.filterDialogOpener = null;
        if (focusTarget instanceof HTMLElement) {
          this.filterDialogFocusFrame = window.requestAnimationFrame(() => {
            this.filterDialogFocusFrame = 0;
            if (this.destroyed) return;
            const nextFocusTarget = this.filterDialogFocusTargetIsVisible(focusTarget)
              ? focusTarget
              : this.filterDialogFocusTarget();
            if (!dialog.open && nextFocusTarget) nextFocusTarget.focus({ preventScroll: true });
          });
        }
      };
      if (options.immediate === true || mqReduced.matches || !dialog.open) {
        finish();
        return;
      }
      this.filterDialogCloseTimer = window.setTimeout(finish, 180);
    }

    requestFilterDialogClose(dialog, options = {}) {
      if (!dialog || (!dialog.open && !this.filterDialogHistoryToken)) return;
      if (this.filterDialogHistoryTraversalPending || this.filterDialogClosing) return;
      if (!this.filterDialogOwnsCurrentHistory()) {
        this.retireFilterDialogHistoryTracking();
        this.closeFilterDialogDirect(dialog, { restoreFocus: options.restoreFocus !== false });
        return;
      }
      this.filterDialogRestoreFocusOnClose = options.restoreFocus !== false;
      this.filterDialogHistoryTraversalPending = true;
      window.clearTimeout(this.filterDialogHistoryTraversalTimer);
      try {
        window.history.back();
      } catch (_error) {
        this.filterDialogHistoryTraversalPending = false;
        return;
      }
      this.filterDialogHistoryTraversalTimer = window.setTimeout(() => {
        if (!this.filterDialogHistoryTraversalPending) return;
        if (this.filterDialogOwnsCurrentHistory()) {
          this.filterDialogHistoryTraversalPending = false;
          this.filterDialogHistoryTraversalTimer = 0;
          return;
        }
        this.onPopState({ state: window.history.state });
      }, 500);
    }

    onFilterDialogPopState(event) {
      const incomingMarker = this.filterDialogHistoryMarker(event?.state);
      const dialog = this.root.querySelector('[data-ac-filter-dialog]');
      if (!incomingMarker) this.normalizeFilterDialogBaseHistory();
      const currentRoute = this.filterDialogRouteIdentity();
      const routeChanged = Boolean(
        this.filterDialogObservedRoute
        && this.filterDialogObservedRoute !== currentRoute
      );
      if (this.filterDialogHistoryIsOwned(incomingMarker)) {
        window.clearTimeout(this.filterDialogHistoryTraversalTimer);
        this.filterDialogHistoryTraversalTimer = 0;
        this.filterDialogHistoryTraversalPending = false;
        this.filterDialogHistoryToken = incomingMarker.token;
        this.filterDialogHistoryRoute = incomingMarker.route;
        this.filterDialogHistoryBaseRoute = incomingMarker.baseRoute || incomingMarker.route;
        this.filterDialogHistoryNormalizedUrl = incomingMarker.normalizedUrl || '';
        if (incomingMarker.route !== currentRoute) {
          this.retireFilterDialogHistoryForNavigation();
          this.closeFilterDialogDirect(dialog, { restoreFocus: false, immediate: true });
          return routeChanged ? 'route' : 'consume';
        }
        if (!this.filterDialogUsesHistory(dialog)) {
          this.retireFilterDialogHistoryLevel(dialog);
          return routeChanged ? 'route' : 'consume';
        }
        if (routeChanged) return 'reopen';
        this.openFilterDialogDirect(dialog);
        return 'consume';
      }
      if (!this.filterDialogHistoryToken && !this.filterDialogHistoryTraversalPending) return 'route';
      const expectedBaseRoute = Boolean(
        this.filterDialogHistoryRoute
        && this.filterDialogHistoryRoute === currentRoute
        && this.filterDialogObservedRoute === currentRoute
      );
      const restoreFocus = expectedBaseRoute && this.filterDialogRestoreFocusOnClose;
      this.retireFilterDialogHistoryTracking();
      this.filterDialogRestoreFocusOnClose = true;
      this.closeFilterDialogDirect(dialog, { restoreFocus });
      return expectedBaseRoute ? 'consume' : 'route';
    }

    onPopState(event) {
      if (this.destroyed) return 'destroyed';
      const action = this.onFilterDialogPopState(event);
      const currentRoute = this.filterDialogRouteIdentity();
      const routeChanged = Boolean(
        this.filterDialogObservedRoute
        && this.filterDialogObservedRoute !== currentRoute
      );
      if (action === 'consume' || (action === 'route' && !routeChanged)) {
        this.filterDialogObservedRoute = currentRoute;
        return 'consume';
      }
      if (this.kind === 'search' && routeChanged) this.onSearchPopState();
      this.filterDialogObservedRoute = this.filterDialogRouteIdentity();
      if (action === 'reopen') {
        const dialog = this.root.querySelector('[data-ac-filter-dialog]');
        if (this.filterDialogUsesHistory(dialog)) {
          this.openFilterDialogDirect(dialog);
        } else {
          this.retireFilterDialogHistoryLevel(dialog);
        }
      }
      return action;
    }

    syncFilterDialogBreakpoint() {
      const dialog = this.root.querySelector('[data-ac-filter-dialog]');
      if (dialog?.open && !this.filterDialogUsesHistory(dialog)) this.requestFilterDialogClose(dialog);
    }

    destroyFilterDialogHistory() {
      window.clearTimeout(this.filterDialogHistoryTraversalTimer);
      window.clearTimeout(this.filterDialogCloseTimer);
      this.cancelFilterDialogFocus();
      this.filterDialogHistoryTraversalTimer = 0;
      this.filterDialogCloseTimer = 0;
      if (this.filterDialogOwnsCurrentHistory()) {
        try {
          window.history.replaceState(
            this.filterDialogHistoryStateWithoutMarker(),
            '',
            window.location.href
          );
          window.history.back();
        } catch (_error) {}
      }
      this.filterDialogHistoryTraversalPending = false;
      this.filterDialogHistoryToken = '';
      this.filterDialogHistoryRoute = '';
      this.filterDialogHistoryBaseRoute = '';
      this.filterDialogHistoryNormalizedUrl = '';
      this.filterDialogClosing = false;
      this.filterDialogOpener = null;
      this.filterDialogRestoreFocusOnClose = true;
      this.filterDialogNavigationPending = false;
      const dialog = this.root.querySelector('[data-ac-filter-dialog]');
      dialog?.classList.remove('is-open', 'is-closing');
      if (dialog?.open) dialog.close();
    }

    openDialog(dialog, opener = null) {
      if (dialog?.matches?.('[data-ac-filter-dialog]')) {
        this.openFilterDialog(dialog, opener);
        return;
      }
      if (!dialog || dialog.open) return;
      dialog.showModal();
      window.requestAnimationFrame(() => dialog.classList.add('is-open'));
    }

    closeDialog(dialog) {
      if (dialog?.matches?.('[data-ac-filter-dialog]')) {
        this.requestFilterDialogClose(dialog);
        return;
      }
      if (!dialog?.open) return;
      dialog.classList.remove('is-open');
      dialog.classList.add('is-closing');
      const finish = () => {
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
      };
      if (mqReduced.matches) {
        finish();
        return;
      }
      window.setTimeout(finish, 180);
    }

    dispatchIntegration(name, detail, source) {
      if (!name) return false;
      const customEvent = new CustomEvent(name, {
        bubbles: true,
        cancelable: true,
        detail: { ...detail, source }
      });
      source.dispatchEvent(customEvent);
      return customEvent.defaultPrevented;
    }

    triggerNativeAction(card, action, source) {
      const selector = action === 'wishlist'
        ? '.vcc-ac-native-actions .vcc-qa__choice--wishlist'
        : '.vcc-ac-native-actions .vcc-qa__choice--cart';
      const button = card.querySelector(selector);
      if (!(button instanceof HTMLButtonElement) || button.disabled) return false;
      const menu = button.closest('[data-vcc-product-quick-actions]');
      const runner = window.VCCProductQuickActions?.run;
      if (menu && typeof runner === 'function') {
        return runner(menu, action === 'wishlist' ? 'wishlist' : 'cart', source || button);
      }
      button.click();
      return true;
    }

    handleAction(button) {
      const card = button.closest('.vcc-ac-card');
      if (!card) return;
      const action = button.dataset.acAction;
      const detail = { handle: card.dataset.productHandle, url: card.dataset.productUrl, card };

      if (action === 'quick-view') {
        const handled = this.dispatchIntegration(this.config.quickViewEvent, detail, button);
        if (!handled) this.openQuickDialog(card);
      } else if (action === 'wishlist') {
        const nativeAction = this.triggerNativeAction(card, 'wishlist', button);
        if (!nativeAction) {
          const store = window.VCCWishlist;
          if (store) store.toggle(store.itemFromElement(card));
          else this.dispatchIntegration(this.config.wishlistEvent, detail, button);
        }
        return nativeAction;
      } else if (action === 'quick-add') {
        const nativeAction = this.triggerNativeAction(card, 'quick-add', button);
        if (!nativeAction) {
          const handled = this.dispatchIntegration(this.config.quickAddEvent, detail, button);
          if (!handled) window.location.assign(card.dataset.productUrl);
        }
        return nativeAction;
      } else if (action === 'compare') {
        this.dispatchIntegration('vcc:compare', detail, button);
        button.textContent = 'Added';
      }
    }

    getWishlist() {
      if (window.VCCWishlist) return window.VCCWishlist.getIds();
      try {
        const parsed = JSON.parse(localStorage.getItem('vccWishlist') || '[]');
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch (_error) {
        return [];
      }
    }

    syncWishlistStates() {
      const wishlist = new Set(this.getWishlist());
      this.root.querySelectorAll('.vcc-ac-card').forEach((card) => {
        const saved = wishlist.has(String(card.dataset.productId || ''));
        if (saved && window.VCCWishlist) {
          window.VCCWishlist.enrich(window.VCCWishlist.itemFromElement(card), { silent: true });
        }
        card.querySelectorAll('[data-ac-action="wishlist"]').forEach((button) => {
          button.setAttribute('aria-pressed', String(saved));
          button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${card.dataset.title || 'product'} ${saved ? 'from' : 'to'} wishlist`);
          button.classList.toggle('is-saved', saved);
          const resultLabel = button.querySelector('.vcc-ac-hover-action__label > span:last-child');
          if (resultLabel) resultLabel.textContent = saved ? 'Removed' : 'Saved';
        });
      });
    }

    openQuickDialog(card) {
      const dialog = this.root.querySelector('[data-ac-quick-dialog]');
      if (!dialog) return;
      const image = dialog.querySelector('[data-ac-quick-image]');
      image.src = card.dataset.image || '';
      image.alt = card.dataset.title || '';
      dialog.querySelector('[data-ac-quick-title]').textContent = card.dataset.title || '';
      dialog.querySelector('[data-ac-quick-price]').textContent = card.dataset.price || '';
      dialog.querySelector('[data-ac-quick-meta]').textContent = [card.dataset.year, card.dataset.size].filter(Boolean).join(' / ');
      const link = dialog.querySelector('[data-ac-quick-link]');
      link.href = card.dataset.productUrl || '#';
      this.openDialog(dialog);
    }

    readAtlasPayload(card) {
      if (!card) return null;
      if (card.vccAtlasPayload) return card.vccAtlasPayload;
      const node = card.querySelector('[data-ac-atlas-payload]');
      if (!node) return null;
      try {
        card.vccAtlasPayload = JSON.parse(node.textContent);
        return card.vccAtlasPayload;
      } catch (error) {
        this.log('Invalid Atlas product payload', error);
        return null;
      }
    }

    setAtlasRecord(inspector, key, value) {
      const cell = inspector.querySelector(`[data-ac-record="${key}"]`);
      if (!cell) return false;
      const cleanValue = String(value || '').trim();
      cell.hidden = !cleanValue;
      const target = cell.querySelector('[data-ac-record-value]');
      if (target) target.textContent = cleanValue;
      return Boolean(cleanValue);
    }

    atlasPriorityRank(priority) {
      if (priority === 'high') return 3;
      if (priority === 'auto') return 2;
      return 1;
    }

    enqueueAtlasImage(source, priority = 'low', front = false) {
      const url = String(source?.src || source || '').trim();
      if (!url) return null;

      let task = this.atlasPreloads.get(url);
      if (task?.status === 'error') {
        this.atlasPreloads.delete(url);
        task = null;
      }
      if (task) {
        if (this.atlasPriorityRank(priority) > this.atlasPriorityRank(task.priority)) {
          task.priority = priority;
          if (task.image) task.image.fetchPriority = priority;
        }
        if (front && task.status === 'queued') {
          const index = this.atlasPreloadQueue.indexOf(task);
          if (index >= 0) this.atlasPreloadQueue.splice(index, 1);
          this.atlasPreloadQueue.unshift(task);
        }
        return task;
      }

      let resolveTask;
      task = {
        url,
        priority,
        status: 'queued',
        image: null,
        promise: new Promise((resolve) => { resolveTask = resolve; }),
        resolve: resolveTask,
      };
      this.atlasPreloads.set(url, task);
      if (front) this.atlasPreloadQueue.unshift(task);
      else this.atlasPreloadQueue.push(task);
      return task;
    }

    startAtlasPreload(task) {
      if (!task || task.status !== 'queued') return false;
      task.status = 'loading';
      this.atlasPreloadActive += 1;

      const preload = new Image();
      task.image = preload;
      preload.decoding = 'async';
      preload.fetchPriority = task.priority;

      let settled = false;
      let timeout = 0;
      const finish = (status) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        task.status = status;
        this.atlasPreloadActive = Math.max(0, this.atlasPreloadActive - 1);
        task.resolve(task);
        this.drainAtlasPreloadQueue();
      };

      preload.addEventListener('load', async () => {
        try {
          await preload.decode?.();
        } catch (error) {
          this.log('Atlas image decode used the loaded response', error);
        }
        finish('loaded');
      }, { once: true });
      preload.addEventListener('error', () => finish('error'), { once: true });
      timeout = window.setTimeout(() => finish(preload.naturalWidth > 0 ? 'loaded' : 'error'), 20000);
      preload.src = task.url;
      return true;
    }

    drainAtlasPreloadQueue(force = false) {
      if (!force && !this.atlasWarmStarted) return;
      const limit = this.imageWarmConcurrency('atlas');
      while (this.atlasPreloadActive < limit && this.atlasPreloadQueue.length) {
        const task = this.atlasPreloadQueue.shift();
        this.startAtlasPreload(task);
      }
    }

    scheduleAtlasPreloadQueue() {
      if (this.atlasPreloadScheduled) {
        if (this.atlasWarmStarted) this.drainAtlasPreloadQueue();
        return;
      }
      this.atlasPreloadScheduled = true;

      const begin = () => {
        if (mqMobile.matches || this.root.dataset.layout !== 'atlas') {
          this.atlasPreloadScheduled = false;
          return;
        }
        this.atlasPreloadWaitTimer = null;
        window.requestAnimationFrame(() => {
          this.atlasPreloadScheduled = false;
          if (mqMobile.matches || this.root.dataset.layout !== 'atlas') return;
          this.atlasWarmStarted = true;
          this.drainAtlasPreloadQueue();
        });
      };

      if (document.readyState === 'complete') begin();
      else window.addEventListener('load', begin, { once: true });
    }

    prioritizeAtlasCard(card) {
      const payload = this.readAtlasPayload(card);
      const images = Array.isArray(payload?.images) ? payload.images : [];
      images.slice().reverse().forEach((source) => this.enqueueAtlasImage(source, 'high', true));
      this.drainAtlasPreloadQueue(true);
      return images;
    }

    pauseAtlasPreloads() {
      window.clearTimeout(this.atlasPreloadWaitTimer);
      this.atlasPreloadWaitTimer = null;
      this.atlasPreloadQueue.forEach((task) => {
        if (task.status === 'queued') this.atlasPreloads.delete(task.url);
      });
      this.atlasPreloadQueue = [];
      this.atlasPreloadScheduled = false;
      this.atlasWarmStarted = false;
    }

    setAtlasMediaImage(frame, image, source, alt = '', priority = 'auto') {
      if (!(image instanceof HTMLImageElement)) return;
      const url = String(source?.src || source || '').trim();
      image.onload = null;
      image.onerror = null;
      image.dataset.acInspectorSource = url;
      image.alt = url ? alt : '';
      frame?.classList.toggle('is-empty', !url);
      frame?.classList.remove('is-missing');
      frame?.classList.remove('is-loading');

      if (!url) {
        image.hidden = true;
        image.removeAttribute('src');
        return;
      }

      const sourceChanged = image.getAttribute('src') !== url;
      const alreadyReady = !sourceChanged && image.complete && image.naturalWidth > 0;
      image.hidden = false;
      image.loading = 'eager';
      frame?.classList.toggle('is-loading', !alreadyReady);
      image.fetchPriority = priority;
      const syncState = (failed = false) => {
        if (image.dataset.acInspectorSource !== url || image.getAttribute('src') !== url) return;
        const loaded = !failed && image.complete && image.naturalWidth > 0;
        image.hidden = !loaded;
        frame?.classList.remove('is-loading');
        frame?.classList.toggle('is-missing', !loaded);
      };
      image.onload = async () => {
        try {
          await image.decode?.();
        } catch (error) {
          this.log('Atlas inspector used the loaded image without a completed decode', error);
        }
        syncState(false);
      };
      image.onerror = () => syncState(true);
      if (sourceChanged) image.src = url;
      if (image.complete) window.queueMicrotask(syncState);
    }

    renderAtlasAuxiliary(inspector, images) {
      const container = inspector.querySelector('[data-ac-inspector-aux]');
      if (!container) return;
      window.clearTimeout(this.atlasAuxTimer);
      const showAux = inspector.dataset.showAux !== 'false';
      const auxiliary = showAux && Array.isArray(images) ? images.slice(1, 4) : [];
      const slots = Array.from(container.querySelectorAll('[data-ac-inspector-aux-slot]'));
      this.atlasRenderId += 1;

      slots.forEach((slot, index) => {
        const image = slot.querySelector('[data-ac-inspector-aux-image]');
        const source = auxiliary[index];
        slot.hidden = !showAux;
        if (source) this.enqueueAtlasImage(source, 'high', true);
        this.setAtlasMediaImage(slot, image, source, source?.alt || '', 'high');
      });

      container.hidden = !showAux;
      container.style.setProperty('--ac-atlas-aux-count', '3');
      if (!showAux || !auxiliary.length) return;
      this.drainAtlasPreloadQueue(true);
    }

    updateAtlasInspector(card) {
      if (!card || mqMobile.matches || this.root.dataset.layout !== 'atlas') return;
      const inspector = this.root.querySelector('[data-ac-atlas-inspector]');
      if (!inspector || inspector.hidden) return;
      if (this.activeAtlasCard === card && inspector.dataset.productId === card.dataset.productId) return;

      const payload = this.readAtlasPayload(card);
      if (!payload) return;
      this.activeAtlasCard?.classList.remove('is-atlas-active');
      this.activeAtlasCard = card;
      card.classList.add('is-atlas-active');
      inspector.dataset.productId = card.dataset.productId || '';

      const images = this.prioritizeAtlasCard(card);
      const feature = images[0];
      const image = inspector.querySelector('[data-ac-inspector-image]');
      if (image) {
        const frame = image.closest('.vcc-ac-atlas-ledger__feature');
        if (feature?.src) {
          const featureTask = this.enqueueAtlasImage(feature, 'high', true);
          const tileImage = card.querySelector('.vcc-ac-image img');
          const cachedGridSource = tileImage?.currentSrc || tileImage?.src || '';
          const initialSource = featureTask?.status === 'loaded' ? feature.src : cachedGridSource || feature.src;
          this.setAtlasMediaImage(frame, image, initialSource, feature.alt || payload.title || '', 'high');
          if (featureTask?.status !== 'loaded') {
            const productId = inspector.dataset.productId;
            featureTask?.promise.then((task) => {
              if (task.status !== 'loaded' || inspector.dataset.productId !== productId) return;
              this.setAtlasMediaImage(frame, image, feature, feature.alt || payload.title || '', 'high');
            });
          }
        } else {
          this.setAtlasMediaImage(frame, image, null);
        }
      }

      const totalImages = Math.max(1, Number(payload.imageCount) || images.length || 1);
      const visibleImages = Math.min(4, totalImages);
      const sku = String(payload.sku || '').trim();
      const fallback = inspector.dataset.stockFallback || 'Record';
      inspector.querySelector('[data-ac-inspector-sku]').textContent = sku || `${fallback}-${payload.id || card.dataset.productId || ''}`;
      inspector.querySelector('[data-ac-inspector-count]').textContent = String(totalImages);
      inspector.querySelector('[data-ac-inspector-counter]').textContent = `01 / ${String(visibleImages).padStart(2, '0')}`;
      inspector.querySelector('[data-ac-inspector-title]').textContent = payload.title || '';

      const meta = inspector.querySelector('[data-ac-inspector-meta-list]');
      if (meta) {
        meta.replaceChildren();
        (Array.isArray(payload.meta) ? payload.meta : []).forEach((entry) => {
          const value = String(entry?.value || '').trim();
          if (!value) return;
          const node = document.createElement('span');
          node.textContent = value;
          meta.append(node);
        });
        meta.hidden = meta.childElementCount === 0;
      }

      const description = inspector.querySelector('[data-ac-inspector-description]');
      const descriptionText = String(payload.description || '').trim();
      if (description) {
        description.textContent = descriptionText;
        description.hidden = !descriptionText;
      }

      const termsWrap = inspector.querySelector('[data-ac-inspector-terms-wrap]');
      const terms = Array.isArray(payload.terms) ? payload.terms.filter(Boolean) : [];
      if (termsWrap) {
        const termsText = termsWrap.querySelector('[data-ac-inspector-terms]');
        if (termsText) termsText.textContent = terms.join('  ×  ');
        termsWrap.hidden = terms.length === 0;
      }

      const visibleRecords = [
        this.setAtlasRecord(inspector, 'condition', payload.condition),
        this.setAtlasRecord(inspector, 'price', payload.price),
        this.setAtlasRecord(
          inspector,
          'availability',
          payload.showAvailability === false
            ? ''
            : payload.available
              ? inspector.dataset.availableText
              : inspector.dataset.soldOutText
        ),
      ].filter(Boolean).length;
      const records = inspector.querySelector('[data-ac-inspector-records]');
      if (records) {
        records.hidden = visibleRecords === 0;
        records.style.setProperty('--ac-atlas-record-count', String(Math.max(1, visibleRecords)));
      }

      this.renderAtlasAuxiliary(inspector, images);
      this.syncAtlasInspectorHeight();
    }

    preloadAtlasImages() {
      if (mqMobile.matches || this.root.dataset.layout !== 'atlas') return;
      const cards = Array.from(this.root.querySelectorAll('.vcc-ac-card'));
      if (!cards.length) return;

      const viewportCards = [];
      const remainingCards = [];
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const visible = rect.bottom > -200 && rect.top < window.innerHeight + 400;
        (visible ? viewportCards : remainingCards).push(card);
      });
      const orderedCards = [...viewportCards, ...remainingCards];

      const pendingGridImages = orderedCards
        .map((card) => card.querySelector('.vcc-ac-image img'))
        .filter((image) => image && image.dataset.acImageState !== 'loading' && image.dataset.acImageState !== 'ready');
      const promotedImages = new Set(pendingGridImages);
      this.gridImageQueue = [
        ...pendingGridImages,
        ...this.gridImageQueue.filter((image) => image.isConnected && !promotedImages.has(image)),
      ];
      pendingGridImages.forEach((image, index) => {
        image.dataset.acImageState = 'queued';
        image.dataset.acImagePriority = index < 12 ? 'high' : 'auto';
      });
      this.gridWarmStarted = true;
      this.drainGridImageQueue();

      const activeCard = this.activeAtlasCard?.isConnected ? this.activeAtlasCard : orderedCards[0];
      if (activeCard) this.prioritizeAtlasCard(activeCard);

      orderedCards.forEach((card) => {
        for (let imageIndex = 1; imageIndex < 4; imageIndex += 1) {
          const source = this.readAtlasPayload(card)?.images?.[imageIndex];
          if (source) this.enqueueAtlasImage(source, 'low');
        }
      });

      this.scheduleAtlasPreloadQueue();
    }

    syncAtlasInspectorHeight() {
      const inspector = this.root.querySelector('[data-ac-atlas-inspector]');
      if (!inspector) return;
      if (mqMobile.matches || this.root.dataset.layout !== 'atlas' || this.root.dataset.atlasInspector !== 'right') {
        window.cancelAnimationFrame(this.atlasGeometryFrame);
        this.root.style.removeProperty('--ac-atlas-runtime-width');
        inspector.style.removeProperty('--ac-atlas-available-height');
        inspector.removeAttribute('data-compact');
        inspector.removeAttribute('data-tight');
        return;
      }

      window.cancelAnimationFrame(this.atlasGeometryFrame);
      this.atlasGeometryFrame = window.requestAnimationFrame(() => {
        const style = getComputedStyle(inspector);
        const stickyTop = parseFloat(style.top) || 0;
        const availableHeight = Math.max(520, window.innerHeight - stickyTop - 12);
        const configuredMax = clampRange(this.s.aiw, 560, 280, 800);
        const horizontalMax = Math.max(280, Math.min(configuredMax, Math.floor(window.innerWidth * 0.46)));

        inspector.style.setProperty('--ac-atlas-available-height', `${Math.floor(availableHeight)}px`);
        inspector.dataset.compact = String(availableHeight < 820);
        inspector.dataset.tight = String(availableHeight < 700);

        const sizingStyle = getComputedStyle(inspector);
        const mastheadReserve = parseFloat(sizingStyle.getPropertyValue('--ac-atlas-masthead-reserve')) || 36;
        const copyReserve = parseFloat(sizingStyle.getPropertyValue('--ac-atlas-copy-reserve')) || 164;
        const recordsReserve = parseFloat(sizingStyle.getPropertyValue('--ac-atlas-records-reserve')) || 62;
        const nonMediaHeight = mastheadReserve + copyReserve + recordsReserve + 2;
        const showAuxiliary = inspector.dataset.showAux !== 'false';
        const auxiliary = inspector.querySelector('[data-ac-inspector-aux]');
        const auxiliaryGap = showAuxiliary && auxiliary ? parseFloat(getComputedStyle(auxiliary).columnGap) || 0 : 0;
        const mediaFactor = showAuxiliary ? 4 / 3 : 1;
        const gapCorrection = showAuxiliary ? (auxiliaryGap * 2) / 3 : 0;
        const heightLimitedWidth = Math.floor((availableHeight - nonMediaHeight + gapCorrection) / mediaFactor);
        const width = Math.max(280, Math.min(horizontalMax, heightLimitedWidth));
        this.root.style.setProperty('--ac-atlas-runtime-width', `${Math.round(width)}px`);
        inspector.dataset.overflow = String(inspector.scrollHeight > availableHeight + 2);
      });
    }

    searchProductKey(product) {
      return String(product?.id || product?.handle || product?.url || '').trim();
    }

    searchSourceKey(source) {
      const ignoreRequestedAttributes = source?.ignoreRequestedAttributes === true
        || source?.ignoreRequestedModifiers === true;
      return [
        source?.collectionHandle || source?.transportQuery || source?.query || '',
        Array.isArray(source?.allOf)
          ? source.allOf.join('|')
          : (Array.isArray(source?.v4Filters) ? source.v4Filters.join('|') : ''),
        ignoreRequestedAttributes || source?.ignoreRequestedTypes
          ? 'without-requested-type'
          : 'with-requested-type',
        ignoreRequestedAttributes || source?.ignoreRequestedSizes
          ? 'without-requested-size'
          : 'with-requested-size',
        ignoreRequestedAttributes || source?.ignoreRequestedColors
          ? 'without-requested-color'
          : 'with-requested-color',
        source?.sourcePlanKey || '',
        source?.domainRootFallback === true ? 'domain-root-fallback' : '',
        source?.scope || source?.tier || ''
      ].join('::').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    }

    searchPageSize() {
      return Math.max(1, clampNumber(this.s.pg, 48));
    }

    mappedSearchSourceScope(source) {
      if (source?.exact) return 'exact';
      if (source?.scope === 'core' || source?.scope === 'broad') return source.scope;
      if (source?.tier === 'broad') return 'broad';
      if (source?.tier === 'relationship' || source?.relationshipGroupId) {
        const kind = String(source?.relationshipKind || '').toLowerCase();
        return ['region', 'category', 'country'].includes(kind) ? 'broad' : 'core';
      }
      return 'generic';
    }

    mappedSearchDomainRootFallback(source) {
      if (
        source?.domainRootFallback === true
        || source?.kind === 'domain-root-fallback'
      ) return true;
      const handle = String(source?.collectionHandle || '').trim().toLowerCase();
      return this.mappedSearchSourceScope(source) === 'broad'
        && ['team-wear', 'brands', 'vintage-classics'].includes(handle);
    }

    currentSearchPage() {
      const requested = new URLSearchParams(window.location.search).get('page');
      return Math.max(1, parseInt(requested || '1', 10) || 1);
    }

    relationshipCompositeConcept(source, primaryType = '') {
      const query = String(source?.query || '').trim();
      if (!query) return '';
      const normalizedQuery = this.normalizeSearchPhrase(query);
      const candidates = (Array.isArray(source?.matchTerms) ? source.matchTerms : [])
        .map((term) => String(term || '').trim())
        .filter(Boolean)
        .filter((term) => {
          const normalizedTerm = this.normalizeSearchPhrase(term);
          return normalizedTerm && (
            normalizedQuery === normalizedTerm
            || normalizedQuery.startsWith(`${normalizedTerm} `)
          );
        })
        .sort((left, right) => {
          return this.normalizeSearchPhrase(right).length - this.normalizeSearchPhrase(left).length;
        });
      if (candidates.length) return candidates[0];

      const normalizedType = this.normalizeSearchPhrase(primaryType);
      if (normalizedType && normalizedQuery.endsWith(` ${normalizedType}`)) {
        const queryWords = query.split(/\s+/);
        const typeWords = String(primaryType || '').trim().split(/\s+/).filter(Boolean);
        if (typeWords.length && queryWords.length > typeWords.length) {
          return queryWords.slice(0, -typeWords.length).join(' ').trim();
        }
      }
      return query;
    }

    mappedSearchSources(analysis, query) {
      const seen = new Set();
      const sources = [];
      const add = (source, fallback) => {
        const next = Object.assign({}, fallback || {}, source || {});
        next.query = String(next.query || '').trim();
        if (!next.query) return;
        const key = this.searchSourceKey(next);
        if (!key || seen.has(key)) return;
        seen.add(key);
        sources.push(next);
      };

      const exactSources = (analysis?.exactSources || [])
        .slice()
        .sort((left, right) => Number(left?.tierOrder || 0) - Number(right?.tierOrder || 0));
      const reviewedSharedUnion = analysis?.ambiguous === true
        && exactSources.filter((source) => source?.safeExact === true).length > 1;
      const relationshipSources = (analysis?.broadSources || [])
        .filter((source) => this.mappedSearchSourceScope(source) === 'core')
        .sort((left, right) => {
          return Number(left?.tierOrder || 40) - Number(right?.tierOrder || 40)
            || Number(right?.weight || 0) - Number(left?.weight || 0);
        });
      const reviewedBroadSources = (analysis?.broadSources || [])
        .filter((source) => this.mappedSearchSourceScope(source) === 'broad')
        .sort((left, right) => {
          const leftOrder = left?.relationshipKind === 'region'
            ? 35
            : Number(left?.tierOrder || 40);
          const rightOrder = right?.relationshipKind === 'region'
            ? 35
            : Number(right?.tierOrder || 40);
          return leftOrder - rightOrder
            || Number(right?.weight || 0) - Number(left?.weight || 0);
        });

      const rawQuery = String(query || analysis?.rawQuery || '').trim();
      const matchedAlias = String(analysis?.matchedAlias || '').trim();
      const subjectLeague = String(analysis?.subject?.league || '').trim();
      const canonicalTeamQuery = String(
        exactSources[0]?.transportQuery
        || exactSources[0]?.query
        || analysis?.subject?.label
        || ''
      ).trim();
      const rawNormalized = this.normalizeSearchPhrase(rawQuery);
      const aliasNormalized = this.normalizeSearchPhrase(matchedAlias);
      const canonicalNormalized = this.normalizeSearchPhrase(canonicalTeamQuery);
      const canonicalDisplayNames = [
        analysis?.subject?.label,
        analysis?.subject?.name,
        analysis?.entity?.label,
        analysis?.entity?.name
      ].map((value) => this.normalizeSearchPhrase(value)).filter(Boolean);
      if (
        this.engineMode !== 'v4_metafield'
        && subjectLeague
        && analysis?.entity
        && analysis?.ambiguous !== true
        && analysis?.resolvedBareProfessionalCollision !== true
        && rawNormalized
        && aliasNormalized
        && rawNormalized.includes(aliasNormalized)
        && aliasNormalized !== canonicalNormalized
        && !canonicalDisplayNames.includes(aliasNormalized)
      ) {
        add({
          query: rawQuery,
          transportQuery: matchedAlias,
          label: 'Reviewed input alias',
          weight: 190,
          kind: 'exact-alias',
          exact: true,
          safeExact: true,
          reviewedAliasIntent: true,
          trustedTransport: false,
          matchTerms: [matchedAlias],
          tier: 'exact',
          scope: 'exact',
          tierOrder: -1,
          maxSourcePages: Math.max(
            1,
            Number(exactSources[0]?.maxSourcePages || 1)
          )
        });
      }
      (reviewedSharedUnion ? exactSources : exactSources.slice(0, 6))
        .forEach((source) => add(source));
      relationshipSources.forEach((source) => add(source));
      reviewedBroadSources.forEach((source) => add(source));
      if (!sources.length) {
        add({ query, label: 'Search', weight: 90, kind: 'exact', exact: true, tier: 'exact' });
      }
      if (reviewedSharedUnion || sources.length <= 30) return sources;

      const terminalFallbacks = sources.filter((source) => {
        return this.mappedSearchDomainRootFallback(source);
      }).slice(-3);
      if (!terminalFallbacks.length) return sources.slice(0, 30);

      const retained = sources
        .filter((source) => !this.mappedSearchDomainRootFallback(source))
        .slice(0, Math.max(0, 30 - terminalFallbacks.length));
      return retained.concat(terminalFallbacks);
    }

    mappedSearchManifestKey(query) {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (
          key === 'page'
          || key === 'section_id'
          || key === 'sections'
          || key === 'vcc_view'
          || key === 'vcc_scope'
          || key === 'vcc_mapped'
          || key === 'sort_by'
          || key === 'options'
          || key.startsWith('options[')
          || key.startsWith('filter.')
        ) {
          url.searchParams.delete(key);
        }
      });
      const params = Array.from(url.searchParams.entries())
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => {
          return leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue);
        })
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
      return [
        url.pathname,
        params,
        this.normalizeSearchPhrase(query),
        this.searchPageSize(),
        document.documentElement.lang || '',
        window.Shopify?.country || '',
        window.Shopify?.currency?.active || '',
        'stable-v2'
      ].join('::');
    }

    isCurrentMappedManifest(manifest) {
      if (!manifest) return false;
      const params = new URLSearchParams(window.location.search);
      const query = params.get('vcc_raw') || params.get('q') || this.config.searchQuery;
      return manifest.key === this.mappedSearchManifestKey(query);
    }

    canonicalizeMappedSearchTransportOptions(url) {
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key === 'options' || key.startsWith('options[')) {
          url.searchParams.delete(key);
        }
      });
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'hide');
      url.searchParams.set('options[fields]', 'title,product_type,variants.title,vendor,tag');
      return url;
    }

    mappedSearchTransportUrl(query, page, options = {}) {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (
          key.startsWith('filter.')
          || key === 'sort_by'
          || key === 'vcc_raw'
          || key === 'vcc_view'
          || key === 'vcc_scope'
          || key === 'vcc_overlap'
        ) {
          url.searchParams.delete(key);
        }
      });
      this.canonicalizeMappedSearchTransportOptions(url);
      url.searchParams.set('q', this.mappedSearchTransportQuery(query));
      url.searchParams.set('type', 'product');
      url.searchParams.set(
        'section_id',
        options.overlap === true
          ? 'vcc-search-results-overlap'
          : 'vcc-search-results'
      );
      url.searchParams.set('page', String(page || 1));
      return url.pathname + url.search;
    }

    mappedSearchRenderedUrl(query, page) {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (
          key.startsWith('filter.')
          || key === 'sort_by'
          || key === 'vcc_raw'
          || key === 'vcc_view'
          || key === 'vcc_scope'
          || key === 'vcc_overlap'
        ) {
          url.searchParams.delete(key);
        }
      });
      this.canonicalizeMappedSearchTransportOptions(url);
      url.searchParams.set('q', this.mappedSearchTransportQuery(query));
      url.searchParams.set('type', 'product');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.searchParams.set('page', String(page || 1));
      return url.pathname + url.search;
    }

    mappedSearchTransportCandidates(query, page, options = {}) {
      if (options.overlap === true) {
        return [this.mappedSearchTransportUrl(query, page, options)];
      }
      if (this.searchTransportMode === 'rendered') return [this.mappedSearchRenderedUrl(query, page)];
      return [this.mappedSearchTransportUrl(query, page, options), this.mappedSearchRenderedUrl(query, page)];
    }

    v4SearchSourceAllOf(source, options = {}) {
      const declaredClauses = Array.isArray(source?.clauses)
        ? source.clauses
        : (Array.isArray(source?.transportClauses) ? source.transportClauses : []);
      if (declaredClauses.length > 1) {
        throw new Error('V4 transport rejected multiple Boolean clauses in one source');
      }
      const declaredClause = declaredClauses[0];
      const explicit = Array.isArray(source?.allOf)
        ? source.allOf
        : (Array.isArray(declaredClause?.allOf) ? declaredClause.allOf : null);
      const legacyFilters = Array.isArray(source?.v4Filters) ? source.v4Filters : [];
      const legacyMode = source?.requiredIndexMode === 'any' ? 'any' : 'all';
      if (legacyMode === 'any' && new Set(legacyFilters.map(String)).size > 1) {
        throw new Error('V4 transport rejected an ANY source with multiple alternatives under AND filter logic');
      }
      const values = Array.from(new Set(
        (explicit || legacyFilters)
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      ));
      if (legacyMode === 'any' && values.length > 1) {
        throw new Error('V4 transport rejected an ANY source with multiple alternatives under AND filter logic');
      }
      if (source?.authoritativeTeamSource === true && !explicit) {
        throw new Error('Authoritative V4 transport requires one explicit allOf clause');
      }
      if (!values.length && options.allowEmpty !== true) {
        throw new Error('V4 transport rejected an empty allOf clause');
      }
      if (explicit && legacyFilters.length) {
        const explicitKey = values.slice().sort().join('|');
        const legacyKey = Array.from(new Set(legacyFilters
          .map((value) => String(value || '').trim())
          .filter(Boolean))).sort().join('|');
        if (explicitKey !== legacyKey) {
          throw new Error('V4 transport rejected mismatched allOf and v4Filters metadata');
        }
      }
      return values;
    }

    v4SearchTransportUrl(source, page, options = {}) {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (
          key.startsWith('filter.')
          || key === 'sort_by'
          || key === 'vcc_raw'
          || key === 'vcc_view'
          || key === 'vcc_scope'
          || key === 'vcc_overlap'
          || key === 'vcc_mapped'
        ) {
          url.searchParams.delete(key);
        }
      });
      this.canonicalizeMappedSearchTransportOptions(url);
      url.searchParams.set(
        'q',
        String(this.config.v4NeutralQuery || '-tag:__vcc_never__')
      );
      url.searchParams.set('type', 'product');
      url.searchParams.set(
        'section_id',
        options.overlap === true
          ? 'vcc-search-results-overlap'
          : 'vcc-search-results'
      );
      url.searchParams.set('page', String(page || 1));
      const parameter = String(
        this.config.v4FilterParameter || 'filter.p.m.vcc_search.index'
      );
      const allOf = this.v4SearchSourceAllOf(source);
      allOf.forEach((value) => url.searchParams.append(parameter, value));
      return url.pathname + url.search;
    }

    v4SearchTransportCandidates(source, page, options = {}) {
      return [this.v4SearchTransportUrl(source, page, options)];
    }

    mappedCollectionTransportCandidates(handle, page) {
      const normalizedHandle = String(handle || '')
        .trim()
        .replace(/^\/+|\/+$/g, '');
      if (!normalizedHandle || !/^[a-z0-9][a-z0-9-]*$/i.test(normalizedHandle)) return [];
      const url = new URL(`/collections/${normalizedHandle}`, window.location.origin);
      url.searchParams.set('page', String(page || 1));
      return [url.pathname + url.search];
    }

    productFromSearchHit(hit, source) {
      return {
        id: hit.dataset.id,
        handle: hit.dataset.handle,
        url: hit.dataset.url,
        title: hit.dataset.title,
        vendor: hit.dataset.vendor,
        type: hit.dataset.type || hit.dataset.productType,
        product_type: hit.dataset.productType || hit.dataset.type,
        tags: hit.dataset.tags,
        size: hit.dataset.size,
        sizes: hit.dataset.sizes || hit.dataset.size,
        colors: hit.dataset.colors,
        year: hit.dataset.year,
        priceRaw: hit.dataset.priceRaw,
        usdPriceBracket: hit.dataset.usdPriceBracket,
        available: hit.dataset.available === 'true',
        sourceKind: source.kind,
        sourceLabel: source.label
      };
    }

    productFromSearchCard(card, source) {
      return {
        id: card.dataset.productId,
        handle: card.dataset.productHandle,
        url: card.dataset.productUrl,
        title: card.dataset.title,
        vendor: card.dataset.vendor,
        type: card.dataset.productType,
        product_type: card.dataset.productType,
        tags: card.dataset.tags,
        size: card.dataset.size,
        sizes: card.dataset.sizes || card.dataset.size,
        colors: card.dataset.colors,
        year: card.dataset.year,
        priceRaw: card.dataset.priceRaw,
        usdPriceBracket: card.dataset.usdPriceBracket,
        available: card.dataset.available !== 'false',
        sourceKind: source.kind,
        sourceLabel: source.label
      };
    }

    enrichSearchProductFromCard(product, card) {
      if (!(card instanceof HTMLElement)) return product;
      product.title = card.dataset.title || product.title;
      product.vendor = card.dataset.vendor || product.vendor;
      product.type = card.dataset.productType || product.type;
      product.product_type = card.dataset.productType || product.product_type;
      product.tags = card.dataset.tags || product.tags;
      product.size = card.dataset.size || product.size;
      product.sizes = card.dataset.sizes || product.sizes || product.size;
      product.colors = card.dataset.colors || product.colors;
      product.year = card.dataset.year || product.year;
      product.priceRaw = card.dataset.priceRaw || product.priceRaw;
      product.usdPriceBracket = card.dataset.usdPriceBracket || product.usdPriceBracket;
      product.available = card.dataset.available !== 'false';
      return product;
    }

    searchProductHaystack(product) {
      return [
        product?.title,
        product?.vendor,
        product?.product_type || product?.type,
        product?.size,
        product?.sizes,
        product?.colors,
        product?.year,
        product?.handle,
        product?.url,
        product?.tags
      ].filter(Boolean).join(' ');
    }

    productMatchesAttribute(product, attribute, engine) {
      const terms = [
        attribute?.label,
        attribute?.name,
        attribute?.key,
        ...(attribute?.terms || [])
      ].filter(Boolean);
      if (!terms.length) return true;
      const haystack = this.searchProductHaystack(product);
      return terms.some((term) => engine.phraseInText
        ? engine.phraseInText(haystack, term)
        : String(haystack).toLowerCase().includes(String(term).toLowerCase()));
    }

    normalizeSearchPhrase(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/['\u2018\u2019\u201b]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    mappedSearchTransportQuery(query) {
      return String(query || '').replace(/['\u2018\u2019\u201b]/g, '');
    }

    mappedSearchExactTransportQuery(source, analysis) {
      const explicitTransport = String(source?.transportQuery || '').trim();
      const query = explicitTransport || String(source?.query || '').trim();
      const primaryType = analysis?.types?.[0]?.label
        || analysis?.types?.[0]?.name
        || '';
      const reviewedRelationship = Boolean(
        source?.relationshipMemberId
        || (
          source?.relationshipGroupId
          && ['lineage', 'market', 'region'].includes(
            String(source?.relationshipKind || '').trim().toLowerCase()
          )
        )
      );
      if (
        !query
        || (source?.exact !== true && !reviewedRelationship)
        || !String(analysis?.subject?.league || '').trim()
        || /["()]|\b(?:AND|OR|NOT)\b|(?:^|\s)-\S|(?:^|\s)[a-z_]+:/i.test(query)
      ) {
        return query;
      }
      const concept = this.relationshipCompositeConcept(source, primaryType);
      if (!concept) return query;
      const escaped = String(concept).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const hasTypeSuffix = Boolean(
        primaryType
        && this.normalizeSearchPhrase(query).endsWith(
          ` ${this.normalizeSearchPhrase(primaryType)}`
        )
      );
      return `"${escaped}"${hasTypeSuffix ? ` ${primaryType}` : ''}`;
    }

    mappedAttributeTerms(analysis) {
      const terms = [];
      [
        ...(analysis?.types || []),
        ...(analysis?.colors || []),
        ...(analysis?.sizes || [])
      ].forEach((attribute) => {
        terms.push(
          attribute?.label,
          attribute?.name,
          attribute?.key,
          ...(attribute?.terms || [])
        );
      });
      return Array.from(new Set(terms.map((term) => this.normalizeSearchPhrase(term)).filter(Boolean)))
        .sort((left, right) => right.length - left.length);
    }

    mappedAdvancedTransportMatchTerms(source, analysis) {
      const query = String(source?.transportQuery || '').trim();
      if (
        !query
        || !String(analysis?.subject?.league || '').trim()
        || !/["()]|\b(?:AND|OR|NOT)\b|(?:^|\s)-\S|(?:^|\s)[a-z_]+:/i.test(query)
      ) return [];
      return Array.from(new Set(query
        .split(/\s+\bOR\b\s+/i)
        .map((branch) => branch
          .replace(/(?:^|\s)-(?:[a-z_]+:)?(?:"[^"]*"|\S+)/gi, ' ')
          .replace(/\bNOT\b[\s\S]*$/i, ' ')
          .replace(/\bAND\b/gi, ' ')
          .replace(/\b(?:title|tag|vendor|product_type):/gi, ' ')
          .replace(/[()"]/g, ' '))
        .map((branch) => this.normalizeSearchPhrase(branch))
        .filter(Boolean)));
    }

    mappedSourceMatchTerms(source, analysis) {
      const explicit = Array.isArray(source?.matchTerms) ? source.matchTerms : [];
      const advancedTerms = this.mappedAdvancedTransportMatchTerms(source, analysis);
      if (explicit.length || advancedTerms.length) {
        return Array.from(new Set(explicit
          .concat(advancedTerms)
          .map((term) => this.normalizeSearchPhrase(term))
          .filter(Boolean)));
      }

      let concept = ` ${this.normalizeSearchPhrase(source?.query)} `;
      this.mappedAttributeTerms(analysis).forEach((term) => {
        concept = concept.replace(new RegExp(` ${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} `, 'g'), ' ');
      });
      concept = concept.replace(/\s+/g, ' ').trim();
      concept = concept.replace(/^(?:and|or)\s+|\s+(?:and|or)$/g, '').trim();
      return concept ? [concept] : [];
    }

    relationshipCompositeMatch(product, source, engine) {
      const members = Array.isArray(source?.relationshipMembers) ? source.relationshipMembers : [];
      if (!members.length) return null;
      const haystack = this.searchProductHaystack(product);
      for (let index = 0; index < members.length; index += 1) {
        const member = members[index];
        const terms = Array.isArray(member?.matchTerms) ? member.matchTerms : [];
        const matched = terms.some((term) => engine.phraseInText
          ? engine.phraseInText(haystack, term)
          : this.normalizeSearchPhrase(haystack).includes(this.normalizeSearchPhrase(term)));
        if (matched) return { index, source: Object.assign({}, source, member) };
      }
      return { index: members.length, source };
    }

    v4ProductIndexTokens(product) {
      const values = Array.isArray(product?.v4IndexValues)
        ? product.v4IndexValues
        : [product?.v4IndexValues];
      return Array.from(new Set(values.flatMap((value) => {
        return String(value || '').split(';');
      }).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)));
    }

    v4RelationshipProofs(source) {
      const allowedTargets = new Set(
        (Array.isArray(source?.allowedTeamTargets) ? source.allowedTeamTargets : [])
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean)
      );
      return (Array.isArray(source?.relationshipProofs) ? source.relationshipProofs : [])
        .map((proof) => ({
          team: String(proof?.team || proof?.target || '').trim().toLowerCase(),
          qualifier: String(
            proof?.qualifier || proof?.sharedQualifier || proof?.sharedLocationQualifier || ''
          ).trim().toLowerCase(),
          active: proof?.active !== false,
          queryable: proof?.queryable !== false
            && String(proof?.qualifierStatus || proof?.qualifier_status || 'queryable') !== 'metadata-only'
        }))
        .filter((proof) => {
          return proof.active
            && proof.queryable
            && /^team:[a-z0-9][a-z0-9-]*$/.test(proof.team)
            && /^(?:market|region):[a-z0-9][a-z0-9-]*$/.test(proof.qualifier)
            && (!allowedTargets.size || allowedTargets.has(proof.team));
        });
    }

    v4RelationshipAttestation(valueSet, source) {
      const proofs = this.v4RelationshipProofs(source);
      const allowedTargets = Array.from(new Set(
        (Array.isArray(source?.allowedTeamTargets)
          ? source.allowedTeamTargets
          : proofs.map((proof) => proof.team)
        ).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
      ));
      const allowedSet = new Set(allowedTargets);
      const declaredOrder = Array.from(new Set(
        (Array.isArray(source?.relationshipTeamOrder)
          ? source.relationshipTeamOrder
          : allowedTargets
        ).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
      ));
      allowedTargets.forEach((target) => {
        if (!declaredOrder.includes(target)) declaredOrder.push(target);
      });
      const proofByTeam = new Map();
      proofs.forEach((proof) => {
        if (!proofByTeam.has(proof.team)) proofByTeam.set(proof.team, []);
        proofByTeam.get(proof.team).push(proof);
      });
      for (let rank = 0; rank < declaredOrder.length; rank += 1) {
        const team = declaredOrder[rank];
        if ((allowedSet.size && !allowedSet.has(team)) || !valueSet.has(team)) continue;
        const proof = (proofByTeam.get(team) || []).find((candidate) => {
          return valueSet.has(candidate.qualifier);
        });
        if (!proof) continue;
        return {
          passed: true,
          team,
          qualifier: proof.qualifier,
          rank
        };
      }
      return {
        passed: false,
        team: '',
        qualifier: '',
        rank: Number.MAX_SAFE_INTEGER
      };
    }

    v4ProductSectionAttestation(product, source) {
      const values = this.v4ProductIndexTokens(product);
      const valueSet = new Set(values);
      const excluded = Array.from(new Set(
        ['system:exclude-search'].concat(source?.v4ExcludeFilters || [])
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean)
      ));
      if (excluded.some((token) => valueSet.has(token))) {
        return { passed: false, token: '', rank: Number.MAX_SAFE_INTEGER, values };
      }
      const higherTierTokens = Array.from(new Set(
        (Array.isArray(source?.v4HigherTierTokens) ? source.v4HigherTierTokens : [])
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean)
      ));
      if (higherTierTokens.some((token) => valueSet.has(token))) {
        return { passed: false, token: '', rank: Number.MAX_SAFE_INTEGER, values };
      }

      const required = this.v4SearchSourceAllOf(source)
        .map((value) => String(value || '').trim().toLowerCase());
      if (!required.length) {
        return { passed: false, token: '', rank: Number.MAX_SAFE_INTEGER, values };
      }
      const passed = required.every((token) => valueSet.has(token));
      if (!passed) {
        return { passed: false, token: '', rank: Number.MAX_SAFE_INTEGER, values };
      }
      if (
        source?.authoritativeTeamSource === true
        && String(source?.resultSectionKey || '') === 'market-region'
      ) {
        const relationship = this.v4RelationshipAttestation(valueSet, source);
        if (!relationship.passed) {
          return { passed: false, token: '', rank: Number.MAX_SAFE_INTEGER, values };
        }
        return {
          passed: true,
          token: relationship.team,
          rank: relationship.rank,
          values,
          relationshipTeamTarget: relationship.team,
          relationshipQualifier: relationship.qualifier
        };
      }
      const tokenOrder = Array.from(new Set(
        (Array.isArray(source?.requiredIndexTokenOrder)
          ? source.requiredIndexTokenOrder
          : required
        ).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
      ));
      const rank = tokenOrder.findIndex((token) => valueSet.has(token));
      const token = tokenOrder[Math.max(0, rank)]
        || required.find((candidate) => valueSet.has(candidate))
        || '';
      return {
        passed: true,
        token,
        rank: rank >= 0 ? rank : tokenOrder.length,
        values
      };
    }

    productMatchesMappedAttributes(product, analysis, source, engine) {
      if (source?.v4Metafield === true && source?.v4AttestationRequired === true) {
        const proof = this.v4ProductSectionAttestation(product, source);
        if (!proof.passed) return false;
        product.v4AttestationToken = proof.token;
        product.v4AttestationRank = proof.rank;
        if (proof.relationshipTeamTarget) {
          product.v4RelationshipTeamTarget = proof.relationshipTeamTarget;
          product.v4RelationshipQualifier = proof.relationshipQualifier;
        }
        // Authoritative sources have already proven every compiled allOf token
        // against the product index. Do not reapply the legacy title/haystack
        // matcher to that attested candidate set.
        if (
          source?.authoritativeTeamSource === true
          || source?.authoritativeClassicsSource === true
        ) return true;
        if (
          source?.ignoreRequestedAttributes === true
          || source?.ignoreRequestedModifiers === true
        ) return true;
      }
      if (
        source?.v4Metafield === true
        && source?.trustedTransport === true
        && source?.transportDefinesCandidateSet === true
      ) {
        return true;
      }
      const classicsTitleRescue = source?.classicsTitleRescue === true;
      if (classicsTitleRescue) {
        const indexValues = new Set(this.v4ProductIndexTokens(product));
        const requiredIndexRoot = String(source?.requiredIndexRoot || '').trim().toLowerCase();
        const allowCrossRootTitleRescue = source?.allowCrossRootTitleRescue === true;
        const requiredIndexIdentity = String(source?.requiredIndexIdentity || '').trim().toLowerCase();
        const requiredIndexIdentityPrefix = String(
          source?.requiredIndexIdentityPrefix || ''
        ).trim().toLowerCase();
        const hasIndexedRoot = Array.from(indexValues).some((value) => {
          return String(value || '').startsWith('root:');
        });
        const hasCompetingIndexedIdentity = Boolean(
          requiredIndexIdentity
          && requiredIndexIdentityPrefix
          && Array.from(indexValues).some((value) => {
            return String(value || '').startsWith(requiredIndexIdentityPrefix);
          })
          && !indexValues.has(requiredIndexIdentity)
        );
        const normalizedTags = this.normalizeSearchPhrase(product?.tags);
        if (
          (
            requiredIndexRoot
            && !indexValues.has(requiredIndexRoot)
            && !allowCrossRootTitleRescue
          )
          || (allowCrossRootTitleRescue && !hasIndexedRoot)
          || hasCompetingIndexedIdentity
          ||
          indexValues.has('system:exclude-search')
          || (` ${normalizedTags} `).includes(' system exclude search ')
        ) return false;
      }
      const haystack = classicsTitleRescue
        ? String(product?.title || '')
        : this.searchProductHaystack(product);
      const sourceTerms = this.mappedSourceMatchTerms(source, analysis);
      const matchesSourceTerm = (term) => engine.phraseInText
        ? engine.phraseInText(haystack, term)
        : this.normalizeSearchPhrase(haystack).includes(term);
      const sourceTermsMatch = source?.matchMode === 'all'
        ? sourceTerms.every(matchesSourceTerm)
        : sourceTerms.some(matchesSourceTerm);
      const trustedCollectionMembership = source?.trustedTransport === true
        && /^[a-z0-9][a-z0-9-]*$/i.test(
          String(source?.collectionHandle || '').trim()
        );
      const trustedQualifiedTransport = source?.trustedTransport === true
        && source?.transportDefinesCandidateSet === true;
      const reviewedProductHandles = new Set(
        (Array.isArray(source?.reviewedProductHandles)
          ? source.reviewedProductHandles
          : []
        ).map((handle) => String(handle || '').trim().toLowerCase()).filter(Boolean)
      );
      const reviewedProductHandleMatch = reviewedProductHandles.has(
        String(product?.handle || '').trim().toLowerCase()
      );
      if (reviewedProductHandles.size && !reviewedProductHandleMatch) {
        return false;
      }
      if (
        !trustedCollectionMembership
        && !trustedQualifiedTransport
        && sourceTerms.length
        && !sourceTermsMatch
        && !reviewedProductHandleMatch
      ) {
        return false;
      }
      const ignoreRequestedAttributes = source?.ignoreRequestedAttributes === true
        || source?.ignoreRequestedModifiers === true;
      const requiredGroups = {
        types: (ignoreRequestedAttributes || source?.ignoreRequestedTypes) ? [] : Array.from(new Set((analysis?.types || []).map((attribute) => {
          return this.canonicalMappedType(attribute?.label || attribute?.name || attribute?.key);
        }).filter(Boolean))),
        sizes: (ignoreRequestedAttributes || source?.ignoreRequestedSizes) ? [] : Array.from(new Set((analysis?.sizes || []).map((attribute) => {
          return this.canonicalMappedSize(attribute?.label || attribute?.name || attribute?.key);
        }).filter(Boolean))),
        colors: (ignoreRequestedAttributes || source?.ignoreRequestedColors) ? [] : Array.from(new Set((analysis?.colors || []).map((attribute) => {
          return this.canonicalMappedColor(attribute?.label || attribute?.name || attribute?.key);
        }).filter(Boolean)))
      };
      if (
        (analysis?.decades || []).length
        && !(analysis.decades || []).some((attribute) => {
          return this.productMatchesAttribute(product, attribute, engine);
        })
      ) return false;
      return this.productMatchesActiveMappedFilters(product, requiredGroups, engine);
    }

    mappedFilterInputLabel(input) {
      const label = input.closest('label');
      if (!(label instanceof HTMLElement)) return String(input.value || '').trim();
      const text = Array.from(label.children).find((child) => {
        return child instanceof HTMLElement
          && child.tagName === 'SPAN'
          && !child.classList.contains('vcc-ac-filter-swatch');
      });
      return String(text?.textContent || input.value || '').trim();
    }

    canonicalMappedSize(value) {
      const normalized = this.normalizeSearchPhrase(value).replace(/\bsize\b/g, '').trim();
      if (!normalized) return '';
      if (/adjustable|one size|osfa/.test(normalized)) return 'osfa';
      if (/^(?:3xl|xxxl|xxx large)$/.test(normalized)) return 'xxxl';
      if (/^(?:2xl|xxl|xx large|double xl|double extra large)$/.test(normalized)) return 'xxl';
      if (/^(?:xl|x large|extra large)$/.test(normalized)) return 'xl';
      if (/^(?:xs|x small|extra small)$/.test(normalized)) return 'xs';
      if (/^(?:s|small)$/.test(normalized)) return 's';
      if (/^(?:m|medium)$/.test(normalized)) return 'm';
      if (/^(?:l|large)$/.test(normalized)) return 'l';
      return normalized;
    }

    canonicalMappedType(value) {
      const normalized = this.normalizeSearchPhrase(value);
      if (!normalized) return '';
      if (/sweatshirt|hoodie|crew neck|crewneck/.test(normalized)) return 'sweatshirt';
      if (/sleeveless|tank top|tanktop/.test(normalized)) return 'sleeveless';
      if (/jersey/.test(normalized)) return 'jersey';
      if (/t shirt|tshirt|(?:^|\s)tee(?:s|\s|$)/.test(normalized)) return 't-shirt';
      if (/jacket|coat|windbreaker|anorak/.test(normalized)) return 'jacket';
      if (/hat|cap|snapback|strapback/.test(normalized)) return 'hats';
      if (/track ?suit/.test(normalized)) return 'tracksuit';
      if (/short/.test(normalized)) return 'shorts';
      if (/pant|trouser/.test(normalized)) return 'pants';
      if (/skirt/.test(normalized)) return 'skirt';
      if (/dress/.test(normalized)) return 'dress';
      return normalized;
    }

    canonicalMappedColor(value) {
      const normalized = this.normalizeSearchPhrase(value).replace(/\bcolour\b/g, 'color');
      if (/multi ?colou?r/.test(normalized)) return 'multicolor';
      return normalized.replace(/\bgray\b/g, 'grey');
    }

    mappedCanonicalFilterValues(input) {
      if (!(input instanceof HTMLInputElement)) return [];
      const kind = String(input.dataset.acFilterKind || '').toLowerCase();
      if (kind === 'type' && input.dataset.acProductTypeGroup === 'jersey-sleeveless') {
        return ['jersey', 'sleeveless'];
      }
      const label = input.dataset.acFilterLabel || input.value;
      const value = kind === 'type'
        ? this.canonicalMappedType(input.value || label)
        : (kind === 'size'
          ? this.canonicalMappedSize(label)
          : (kind === 'color' ? this.canonicalMappedColor(label) : ''));
      return value ? [value] : [];
    }

    mappedFilterSelectionKey(input) {
      if (!(input instanceof HTMLInputElement)) return '';
      const group = String(input.dataset.acProductTypeGroup || '').trim();
      return group
        ? `${input.name}::group::${group}`
        : `${input.name}::${input.value}`;
    }

    mappedCheckedControlCount(scope) {
      const root = scope?.querySelectorAll ? scope : this.root;
      const keys = new Set();
      root.querySelectorAll('input[type="checkbox"][name][data-ac-filter-kind]:checked').forEach((input) => {
        const key = this.mappedFilterSelectionKey(input);
        if (key) keys.add(key);
      });
      return keys.size;
    }

    activeMappedFilterGroups(sourceUrl = window.location.href) {
      const groups = { types: new Set(), sizes: new Set(), colors: new Set(), usdPriceBrackets: new Set() };
      const url = this.normalizeMappedUsdPriceUrl(
        sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href)
      );
      const params = url.searchParams;
      const usdPriceBracket = String(params.get('filter.p.m.custom.usd_price_bracket') || '').trim();
      if (USD_PRICE_BRACKETS.has(usdPriceBracket)) {
        groups.usdPriceBrackets.add(usdPriceBracket);
      }
      const priceMin = groups.usdPriceBrackets.size
        ? Number.NaN
        : Number.parseFloat(params.get('filter.v.price.gte') || '');
      const priceMax = groups.usdPriceBrackets.size
        ? Number.NaN
        : Number.parseFloat(params.get('filter.v.price.lte') || '');
      this.root.querySelectorAll('.vcc-ac-filter-form input[type="checkbox"][name][data-ac-filter-kind]').forEach((input) => {
        if (!params.getAll(input.name).includes(input.value)) return;
        const kind = String(input.dataset.acFilterKind || '').toLowerCase();
        if (kind === 'type') {
          this.mappedCanonicalFilterValues(input).forEach((type) => groups.types.add(type));
          return;
        }
        if (kind === 'size') {
          this.mappedCanonicalFilterValues(input).forEach((size) => groups.sizes.add(size));
          return;
        }
        if (kind === 'color') {
          this.mappedCanonicalFilterValues(input).forEach((color) => groups.colors.add(color));
        }
      });
      return {
        types: Array.from(groups.types),
        sizes: Array.from(groups.sizes),
        colors: Array.from(groups.colors),
        usdPriceBrackets: Array.from(groups.usdPriceBrackets),
        priceMin: Number.isFinite(priceMin) ? priceMin : null,
        priceMax: Number.isFinite(priceMax) ? priceMax : null
      };
    }

    productMatchesActiveMappedFilters(product, activeFilters, engine) {
      if (!activeFilters) return true;
      if (activeFilters.types.length) {
        const productType = this.canonicalMappedType(product?.product_type || product?.type);
        if (!productType || !activeFilters.types.includes(productType)) return false;
      }
      if (activeFilters.sizes.length) {
        const productSizes = String(product?.sizes || product?.size || '')
          .split('||')
          .map((size) => this.canonicalMappedSize(size))
          .filter(Boolean);
        if (!productSizes.some((size) => activeFilters.sizes.includes(size))) return false;
      }
      if (activeFilters.usdPriceBrackets?.length) {
        const productBracket = String(product?.usdPriceBracket || '').trim();
        if (!activeFilters.usdPriceBrackets.includes(productBracket)) return false;
      }
      if (activeFilters.colors.length) {
        const explicitColors = String(product?.colors || '')
          .split('||')
          .map((color) => this.canonicalMappedColor(color))
          .filter(Boolean);
        const normalizedTags = this.canonicalMappedColor(String(product?.tags || ''));
        const colorMatch = activeFilters.colors.some((color) => {
          if (explicitColors.length) return explicitColors.includes(color);
          if (color === 'multicolor') {
            return /multi ?color/.test(normalizedTags);
          }
          return engine.phraseInText
            ? engine.phraseInText(normalizedTags, color)
            : this.normalizeSearchPhrase(normalizedTags).includes(color);
        });
        if (!colorMatch) return false;
      }
      const hasPriceMinValue = activeFilters.priceMin !== null
        && activeFilters.priceMin !== undefined
        && activeFilters.priceMin !== '';
      const hasPriceMaxValue = activeFilters.priceMax !== null
        && activeFilters.priceMax !== undefined
        && activeFilters.priceMax !== '';
      const priceMin = hasPriceMinValue ? Number(activeFilters.priceMin) : NaN;
      const priceMax = hasPriceMaxValue ? Number(activeFilters.priceMax) : NaN;
      const hasPriceMin = hasPriceMinValue && Number.isFinite(priceMin);
      const hasPriceMax = hasPriceMaxValue && Number.isFinite(priceMax);
      if (hasPriceMin || hasPriceMax) {
        const rawPrice = Number(product?.priceRaw);
        const shopifyRate = Number.parseFloat(window.Shopify?.currency?.rate || '');
        const fallbackRate = Number.parseFloat(this.root.dataset.priceBaseUnit || '1');
        const baseUnit = Number.isFinite(shopifyRate) && shopifyRate > 0
          ? shopifyRate
          : (Number.isFinite(fallbackRate) && fallbackRate > 0 ? fallbackRate : 1);
        if (!Number.isFinite(rawPrice) || rawPrice < 0) return false;
        if (hasPriceMin && rawPrice < priceMin * baseUnit * 100) return false;
        if (hasPriceMax && rawPrice > priceMax * baseUnit * 100) return false;
      }
      return true;
    }

    mappedSearchSortSupported(value) {
      return ['', 'relevance', 'price-ascending', 'price-descending'].includes(
        String(value || '').trim().toLowerCase()
      );
    }

    mappedFilterControl(name, value) {
      return Array.from(this.root.querySelectorAll(
        '.vcc-ac-filter-form input[name][data-ac-filter-kind]'
      )).find((input) => input.name === name && String(input.value) === String(value)) || null;
    }

    mappedFilterUrlSupported(sourceUrl) {
      const url = this.normalizeMappedUsdPriceUrl(
        sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href)
      );
      for (const [name, value] of url.searchParams.entries()) {
        if (!name.startsWith('filter.') || value === '') continue;
        if (name === 'filter.v.price.gte' || name === 'filter.v.price.lte') continue;
        if (name === 'filter.p.m.custom.usd_price_bracket') {
          if (USD_PRICE_BRACKETS.has(value)) continue;
          return false;
        }
        const control = this.mappedFilterControl(name, value);
        if (!control || !['type', 'size', 'color'].includes(control.dataset.acFilterKind || '')) {
          return false;
        }
      }
      return this.mappedSearchSortSupported(url.searchParams.get('sort_by') || '');
    }

    mappedFilterFormSupported(form) {
      const url = this.mappedFilterFormUrl(form);
      return Boolean(url && this.mappedFilterUrlSupported(url));
    }

    mappedFilterFormUrl(form) {
      if (!(form instanceof HTMLFormElement)) return null;
      const url = new URL(form.action || window.location.pathname, window.location.href);
      url.search = '';
      const data = new FormData(form);
      data.forEach((value, name) => {
        const cleanValue = String(value || '').trim();
        if (!cleanValue || name === 'page') return;
        url.searchParams.append(name, cleanValue);
      });
      if (!url.searchParams.has('sort_by')) {
        const currentSort = String(
          new URLSearchParams(window.location.search).get('sort_by') || ''
        ).trim().toLowerCase();
        if (currentSort && currentSort !== 'relevance') {
          url.searchParams.set('sort_by', currentSort);
        }
      }
      url.searchParams.delete('page');
      this.syncUrlLayoutParam(url);
      return url;
    }

    mappedSearchViewKey(sourceUrl) {
      const url = sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href);
      const values = Array.from(url.searchParams.entries())
        .filter(([name, value]) => name.startsWith('filter.') && value !== '')
        .sort(([leftName, leftValue], [rightName, rightValue]) => {
          return leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue);
        })
        .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`);
      const sort = String(url.searchParams.get('sort_by') || 'relevance').toLowerCase();
      return `${values.join('&')}::${sort}::focused`;
    }

    mappedSearchResultSection(entry, manifest) {
      const sourceState = manifest?.sources?.[Number(entry?.sourceIndex)];
      const source = sourceState?.source || {};
      const key = String(entry?.resultSectionKey || source.resultSectionKey || '').trim();
      if (!key) return null;
      return {
        key,
        label: String(
          entry?.resultSectionLabel
          || source.resultSectionLabel
          || entry?.tierLabel
          || 'Related Results'
        ).trim(),
        kind: String(entry?.resultSectionKind || source.resultSectionKind || key).trim(),
        order: Math.max(
          0,
          Number(entry?.resultSectionOrder ?? source.resultSectionOrder ?? entry?.tierOrder ?? 0) || 0
        )
      };
    }

    groupMappedSearchResultSections(entries, manifest, transformGroup = null) {
      const groups = new Map();
      const orderedGroups = [];
      const unsectioned = [];
      (entries || []).forEach((entry, index) => {
        const section = this.mappedSearchResultSection(entry, manifest);
        if (!section) {
          unsectioned.push({ entry, index });
          return;
        }
        let group = groups.get(section.key);
        if (!group) {
          group = { section, firstIndex: index, entries: [] };
          groups.set(section.key, group);
          orderedGroups.push(group);
        }
        group.entries.push(entry);
      });
      if (!orderedGroups.length) return (entries || []).slice();
      const sectioned = orderedGroups
        .sort((left, right) => {
          return left.section.order - right.section.order
            || left.firstIndex - right.firstIndex;
        })
        .flatMap((group) => {
          return typeof transformGroup === 'function'
            ? transformGroup(group.entries.slice(), group.section)
            : group.entries;
        });
      // Mixed sectioned/unsectioned manifests are not expected for recognized
      // team routes, but retaining unsectioned entries prevents silent loss.
      return sectioned.concat(
        unsectioned.sort((left, right) => left.index - right.index).map(({ entry }) => entry)
      );
    }

    deriveMappedSearchEntries(manifest, sourceUrl) {
      if (!manifest) return [];
      const url = this.normalizeMappedUsdPriceUrl(
        sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href)
      );
      const engine = this.searchEngine() || {};
      const baseEntries = manifest.baseEntries || manifest.entries || [];
      const activeFilters = this.activeMappedFilterGroups(url);
      let entries = baseEntries.filter((entry) => {
        return this.productMatchesActiveMappedFilters(entry.product, activeFilters, engine);
      });
      const sort = String(url.searchParams.get('sort_by') || 'relevance').toLowerCase();
      if (sort === 'price-ascending' || sort === 'price-descending') {
        const direction = sort === 'price-ascending' ? 1 : -1;
        const comparePrice = (left, right) => {
          const leftPrice = Number(left?.product?.priceRaw);
          const rightPrice = Number(right?.product?.priceRaw);
          const safeLeft = Number.isFinite(leftPrice) ? leftPrice : Number.MAX_SAFE_INTEGER;
          const safeRight = Number.isFinite(rightPrice) ? rightPrice : Number.MAX_SAFE_INTEGER;
          return direction * (safeLeft - safeRight)
            || Number(left.rank || 0) - Number(right.rank || 0);
        };
        if (this.engineMode === 'v4_metafield') {
          const sectioned = entries.some((entry) => {
            return Boolean(this.mappedSearchResultSection(entry, manifest));
          });
          if (sectioned) {
            entries = this.groupMappedSearchResultSections(
              entries,
              manifest,
              (groupEntries) => groupEntries.sort(comparePrice)
            );
          } else {
            const groups = new Map();
            const orderedGroups = [];
            entries.forEach((entry, index) => {
              const sourceState = manifest.sources?.[Number(entry?.sourceIndex)];
              const metadata = this.mappedSearchSourceTierMetadata(sourceState);
              const key = String(entry?.tierKey || metadata.key || `v4:entry-${index}`);
              let group = groups.get(key);
              if (!group) {
                group = {
                  key,
                  order: Number(entry?.tierOrder ?? metadata.order ?? Number.MAX_SAFE_INTEGER),
                  firstIndex: index,
                  entries: []
                };
                groups.set(key, group);
                orderedGroups.push(group);
              }
              group.entries.push(entry);
            });
            entries = orderedGroups
              .sort((left, right) => {
                return left.order - right.order || left.firstIndex - right.firstIndex;
              })
              .flatMap((group) => group.entries.sort(comparePrice));
          }
        } else {
          const focusedEntries = manifest.focusedEntries || [];
          const exactBoundary = Math.max(0, Number(manifest.baseCloseCount) || 0);
          const exactKeys = new Set(
            focusedEntries.slice(0, exactBoundary).map((entry) => entry.key).filter(Boolean)
          );
          const focusedKeys = new Set(focusedEntries.map((entry) => entry.key).filter(Boolean));
          const exactLayer = [];
          const localLayer = [];
          const broadLayer = [];
          entries.forEach((entry) => {
            if (exactKeys.has(entry.key)) exactLayer.push(entry);
            else if (focusedKeys.has(entry.key)) localLayer.push(entry);
            else broadLayer.push(entry);
          });
          exactLayer.sort(comparePrice);
          localLayer.sort(comparePrice);
          broadLayer.sort(comparePrice);
          entries = exactLayer.concat(localLayer, broadLayer);
        }
      } else if (this.engineMode === 'v4_metafield') {
        entries = this.groupMappedSearchResultSections(
          entries,
          manifest,
          (groupEntries, section) => {
            if (section?.key === 'exact-team') return groupEntries;
            return groupEntries.sort((left, right) => {
              const leftRank = Number(left?.product?.v4AttestationRank);
              const rightRank = Number(right?.product?.v4AttestationRank);
              const safeLeftRank = Number.isFinite(leftRank)
                ? leftRank
                : Number.MAX_SAFE_INTEGER;
              const safeRightRank = Number.isFinite(rightRank)
                ? rightRank
                : Number.MAX_SAFE_INTEGER;
              return safeLeftRank - safeRightRank
                || Number(right?.score || 0) - Number(left?.score || 0)
                || Number(left?.rank || 0) - Number(right?.rank || 0);
            });
          }
        );
      }

      manifest.entries = entries;
      manifest.viewKey = this.mappedSearchViewKey(url);
      const hasActiveFilters = activeFilters.types.length
        || activeFilters.sizes.length
        || activeFilters.colors.length
        || activeFilters.usdPriceBrackets.length
        || activeFilters.priceMin !== null
        || activeFilters.priceMax !== null;
      manifest.viewHasMore = Boolean(manifest.hasMore && !hasActiveFilters);
      const exactBoundary = Math.max(0, Number(manifest.baseCloseCount) || 0);
      manifest.closeCount = entries.filter((entry) => Number(entry.rank) < exactBoundary).length;
      this.refreshMappedSearchTierDescriptors(manifest);
      return entries;
    }

    arrangeMappedBrandQualifiedEntries(manifest) {
      if (
        !manifest?.entries?.length
        || !(manifest.sources || []).some((state) => {
          return state?.source?.kind === 'brand-qualifier-tail';
        })
      ) return;
      const sourceOrder = (entry) => {
        const state = manifest.sources?.[Number(entry?.sourceIndex)];
        return Number(state?.source?.tierOrder ?? state?.sourceIndex ?? Number.MAX_SAFE_INTEGER);
      };
      manifest.entries = manifest.entries
        .map((entry, index) => ({ entry, index }))
        .sort((left, right) => {
          return sourceOrder(left.entry) - sourceOrder(right.entry)
            || Number(left.entry?.sourceIndex || 0) - Number(right.entry?.sourceIndex || 0)
            || Number(left.entry?.rank ?? left.index) - Number(right.entry?.rank ?? right.index)
            || left.index - right.index;
        })
        .map(({ entry }, index) => Object.assign(entry, { rank: index }));
    }

    removeMappedFilterValue(url, name, value) {
      const values = url.searchParams.getAll(name);
      let removed = false;
      url.searchParams.delete(name);
      values.forEach((candidate) => {
        if (!removed && candidate === value) {
          removed = true;
          return;
        }
        url.searchParams.append(name, candidate);
      });
    }

    mappedFilterLabel(name, value) {
      const control = this.mappedFilterControl(name, value);
      const group = control?.closest('.vcc-ac-filter-group');
      const groupLabel = String(group?.querySelector('summary > span')?.textContent || 'Filter').trim();
      const valueLabel = String(control?.dataset?.acFilterLabel || value || '').trim();
      return `${groupLabel}: ${valueLabel}`;
    }

    renderMappedActiveFilters(sourceUrl) {
      const mount = this.root.querySelector('[data-ac-active-filters]');
      if (!mount) return;
      const url = sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href);
      const filterPairs = Array.from(url.searchParams.entries())
        .filter(([name, value]) => name.startsWith('filter.') && value !== '');
      const priceBandValue = String(url.searchParams.get('filter.p.m.custom.usd_price_bracket') || '');
      const priceBandLabels = {
        usd_le_50: '$50 & under',
        usd_50_75: '$51 to $75',
        usd_75_125: '$76 to $125',
        usd_125_200: '$126 to $200',
        usd_gt_200: '$201 & up'
      };
      const priceActive = filterPairs.some(([name]) => {
        return name === 'filter.v.price.gte'
          || name === 'filter.v.price.lte'
          || name === 'filter.p.m.custom.usd_price_bracket';
      });
      const entries = filterPairs.filter(([name]) => {
        return name !== 'filter.v.price.gte'
          && name !== 'filter.v.price.lte'
          && name !== 'filter.p.m.custom.usd_price_bracket';
      });
      const chipGroups = new Map();
      entries.forEach(([name, value]) => {
        const control = this.mappedFilterControl(name, value);
        const key = control ? this.mappedFilterSelectionKey(control) : `${name}::${value}`;
        if (!chipGroups.has(key)) {
          const visibleGroupControl = control?.dataset.acProductTypeGroup
            ? Array.from(this.root.querySelectorAll(
                `input[data-ac-product-type-group="${control.dataset.acProductTypeGroup}"]`
              )).find((candidate) => !candidate.hidden)
            : null;
          chipGroups.set(key, {
            label: visibleGroupControl
              ? this.mappedFilterLabel(visibleGroupControl.name, visibleGroupControl.value)
              : this.mappedFilterLabel(name, value),
            values: []
          });
        }
        chipGroups.get(key).values.push([name, value]);
      });

      mount.replaceChildren();
      if (!chipGroups.size && !priceActive) {
        mount.hidden = true;
        return;
      }

      const heading = document.createElement('strong');
      heading.textContent = 'Active filters';
      mount.append(heading);
      chipGroups.forEach((chip) => {
        const removeUrl = new URL(url);
        chip.values.forEach(([name, value]) => {
          this.removeMappedFilterValue(removeUrl, name, value);
        });
        removeUrl.searchParams.delete('page');
        const link = document.createElement('a');
        link.href = removeUrl.toString();
        link.dataset.acFilterRemove = '';
        link.append(document.createTextNode(`${chip.label} `));
        const close = document.createElement('span');
        close.setAttribute('aria-hidden', 'true');
        close.textContent = 'x';
        link.append(close);
        mount.append(link);
      });
      if (priceActive) {
        const removeUrl = new URL(url);
        removeUrl.searchParams.delete('filter.v.price.gte');
        removeUrl.searchParams.delete('filter.v.price.lte');
        removeUrl.searchParams.delete('filter.p.m.custom.usd_price_bracket');
        removeUrl.searchParams.delete('page');
        const link = document.createElement('a');
        link.href = removeUrl.toString();
        link.dataset.acFilterRemove = '';
        const priceLabel = priceBandLabels[priceBandValue]
          ? `Price: ${priceBandLabels[priceBandValue]} `
          : 'Price ';
        link.append(document.createTextNode(priceLabel));
        const close = document.createElement('span');
        close.setAttribute('aria-hidden', 'true');
        close.textContent = 'x';
        link.append(close);
        mount.append(link);
      }

      const clearUrl = new URL(url);
      Array.from(clearUrl.searchParams.keys()).forEach((name) => {
        if (name.startsWith('filter.')) clearUrl.searchParams.delete(name);
      });
      clearUrl.searchParams.delete('page');
      const clear = document.createElement('a');
      clear.className = 'vcc-ac-active__clear';
      clear.dataset.acClearFilters = '';
      clear.href = clearUrl.toString();
      clear.textContent = 'Clear all';
      mount.append(clear);
      mount.hidden = false;
    }

    syncMappedUsdPriceBracketControls(sourceUrl) {
      const url = this.normalizeMappedUsdPriceUrl(
        sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href)
      );
      const mappedReady = this.kind === 'search'
        && this.engineMode === 'v4_metafield'
        && this.searchManifest?.sealed
        && this.isCurrentMappedManifest(this.searchManifest)
        && !this.searchCurationIsFallbackLocked();
      const bandName = 'filter.p.m.custom.usd_price_bracket';
      const band = String(url.searchParams.get(bandName) || '').trim();
      const hasNumericPrice = url.searchParams.has('filter.v.price.gte')
        || url.searchParams.has('filter.v.price.lte');

      if (mappedReady) {
        const current = new URL(window.location.href);
        const normalizedCurrent = this.normalizeMappedUsdPriceUrl(new URL(current));
        if (normalizedCurrent.toString() !== current.toString()) {
          window.history.replaceState(window.history.state, '', normalizedCurrent);
        }
      }

      if (!mappedReady || (hasNumericPrice && !band)) {
        this.restoreNativeSearchPriceControls();
        return url;
      }

      this.root.querySelectorAll('.vcc-ac-filter-form').forEach((form) => {
        const group = form.querySelector('.vcc-price-buckets');
        if (!(group instanceof HTMLElement)) return;
        let bandInput = group.querySelector('[data-vcc-price-band-input]');
        if (!(bandInput instanceof HTMLInputElement)) {
          bandInput = document.createElement('input');
          bandInput.type = 'hidden';
          bandInput.name = bandName;
          bandInput.dataset.vccPriceBandInput = '';
          bandInput.dataset.vccMappedPriceBandInput = '';
          bandInput.dataset.acFilterKind = 'price';
          group.prepend(bandInput);
        }
        bandInput.value = band;
        group.dataset.vccMappedUsdPrice = 'true';

        form.querySelectorAll('[data-vcc-price-min-input], [data-vcc-price-max-input]').forEach((input) => {
          if (!Object.prototype.hasOwnProperty.call(input.dataset, 'vccMappedNativeDisabled')) {
            input.dataset.vccMappedNativeDisabled = String(input.disabled);
          }
          input.disabled = true;
        });
        form.querySelectorAll('.vcc-ac-price-filter').forEach((container) => {
          if (!Object.prototype.hasOwnProperty.call(container.dataset, 'vccMappedNativeHidden')) {
            container.dataset.vccMappedNativeHidden = String(container.hidden);
          }
          container.hidden = true;
        });
      });

      return url;
    }

    syncMappedFilterControls(sourceUrl) {
      const url = this.syncMappedUsdPriceBracketControls(sourceUrl);
      const params = url.searchParams;
      this.root.querySelectorAll('.vcc-ac-filter-form input[name]').forEach((input) => {
        if (!input.name.startsWith('filter.')) return;
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = params.getAll(input.name).includes(input.value);
          return;
        }
        input.value = params.get(input.name) || '';
      });
      const pairedGroups = new Set(Array.from(this.root.querySelectorAll(
        '[data-ac-product-type-group]'
      )).map((input) => input.dataset.acProductTypeGroup).filter(Boolean));
      pairedGroups.forEach((group) => {
        const inputs = Array.from(this.root.querySelectorAll(
          `[data-ac-product-type-group="${group}"]`
        ));
        const checked = inputs.some((input) => input.checked);
        inputs.forEach((input) => { input.checked = checked; });
      });

      this.root.querySelectorAll('.vcc-ac-filter-group').forEach((group) => {
        const summary = group.querySelector('summary');
        if (!summary) return;
        const checked = this.mappedCheckedControlCount(group);
        const hasPrice = Array.from(group.querySelectorAll('[data-vcc-price-min-input], [data-vcc-price-max-input], [data-vcc-price-band-input]'))
          .some((input) => String(input.value || '').trim() !== '');
        const count = checked || (hasPrice ? 1 : 0);
        let badge = summary.querySelector('small');
        if (!count) {
          badge?.remove();
          return;
        }
        if (!badge) {
          badge = document.createElement('small');
          summary.append(badge);
        }
        badge.textContent = String(count);
      });

      const activePairs = Array.from(params.entries()).filter(([name, value]) => {
        return name.startsWith('filter.') && value !== '';
      });
      const activeSelectionKeys = new Set();
      activePairs.filter(([name]) => {
        return name !== 'filter.v.price.gte'
          && name !== 'filter.v.price.lte'
          && name !== 'filter.p.m.custom.usd_price_bracket';
      }).forEach(([name, value]) => {
        const control = this.mappedFilterControl(name, value);
        activeSelectionKeys.add(control ? this.mappedFilterSelectionKey(control) : `${name}::${value}`);
      });
      const nonPriceCount = activeSelectionKeys.size;
      const priceCount = activePairs.some(([name]) => {
        return name === 'filter.v.price.gte'
          || name === 'filter.v.price.lte'
          || name === 'filter.p.m.custom.usd_price_bracket';
      }) ? 1 : 0;
      const activeCount = nonPriceCount + priceCount;
      this.root.querySelectorAll('[data-ac-filter-label]').forEach((filterLabel) => {
        filterLabel.textContent = activeCount ? `Filter / ${activeCount}` : 'Filter';
      });

      const sort = String(params.get('sort_by') || 'relevance').toLowerCase();
      this.root.querySelectorAll('.vcc-ac-filter-form input[name="sort_by"]').forEach((input) => {
        input.value = sort === 'relevance' ? '' : sort;
        input.disabled = sort === 'relevance';
      });
      this.root.querySelectorAll('[data-ac-sort-option]').forEach((option) => {
        option.setAttribute('aria-selected', String(option.dataset.acSortOption === sort));
      });
      const selectedSort = this.root.querySelector(`[data-ac-sort-option="${sort}"]`);
      const sortLabel = this.root.querySelector('[data-ac-sort-label]');
      if (sortLabel && selectedSort) {
        const selectedLabel = selectedSort.textContent.trim();
        sortLabel.textContent = selectedLabel;
        sortLabel.closest('summary')?.setAttribute('aria-label', `Sort results: ${selectedLabel}`);
      }

      const priceMin = params.get('filter.v.price.gte') || '';
      const priceMax = params.get('filter.v.price.lte') || '';
      const priceBand = params.get('filter.p.m.custom.usd_price_bracket') || '';
      this.root.querySelectorAll('[data-vcc-price-preset]').forEach((button) => {
        const usesBand = Boolean(
          button.closest('.vcc-price-buckets')?.querySelector('[data-vcc-price-band-input]')
        );
        const selected = usesBand
          ? String(button.dataset.vccPriceBand || '') === priceBand
          : String(button.dataset.vccPriceMin || '') === priceMin
            && String(button.dataset.vccPriceMax || '') === priceMax;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      this.renderMappedActiveFilters(url);
    }

    updateMappedFacetCounts(manifest, sourceUrl) {
      if (!manifest?.baseEntries?.length) return;
      const url = sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href);
      const engine = this.searchEngine() || {};
      const active = this.activeMappedFilterGroups(url);
      const counts = new Map();
      this.root.querySelectorAll(
        '.vcc-ac-filter-form input[type="checkbox"][name][data-ac-filter-kind]'
      ).forEach((input) => {
        const kind = input.dataset.acFilterKind;
        if (!['type', 'size', 'color'].includes(kind)) return;
        const key = this.mappedFilterSelectionKey(input);
        if (!counts.has(key)) {
          const candidate = {
            types: active.types.slice(),
            sizes: active.sizes.slice(),
            colors: active.colors.slice(),
            usdPriceBrackets: active.usdPriceBrackets.slice(),
            priceMin: active.priceMin,
            priceMax: active.priceMax
          };
          const groupKey = kind === 'type' ? 'types' : (kind === 'size' ? 'sizes' : 'colors');
          candidate[groupKey] = this.mappedCanonicalFilterValues(input);
          const count = manifest.baseEntries.reduce((total, entry) => {
            return total + (this.productMatchesActiveMappedFilters(entry.product, candidate, engine) ? 1 : 0);
          }, 0);
          counts.set(key, count);
        }
        const count = counts.get(key) || 0;
        const label = input.closest('label');
        const countNode = label?.querySelector('small');
        if (countNode) countNode.textContent = String(count);
        const disabled = !input.hidden && count === 0 && !input.checked;
        input.disabled = disabled;
        label?.classList.toggle('is-disabled', disabled);
      });
    }

    ensureMappedEmptyState() {
      let empty = this.root.querySelector('.vcc-ac-empty');
      if (empty) return empty;
      empty = document.createElement('div');
      empty.className = 'vcc-ac-empty';
      const heading = document.createElement('h2');
      heading.textContent = 'No products found';
      const copy = document.createElement('p');
      copy.textContent = 'Try removing one or more filters.';
      empty.append(heading, copy);
      const shell = this.root.querySelector('[data-ac-grid-shell]');
      shell?.after(empty);
      return empty;
    }

    observePathwaySearchFinal(manifest) {
      if (typeof window.__vccPathwaySearch !== 'function') return;
      if (!manifest?.sealed || manifest.progressiveLoading || manifest.broadLoading) return;
      try {
        const count = Array.isArray(manifest?.entries) ? manifest.entries.length : null;
        const known = Number.isInteger(count) && count >= 0 && count <= 100000 && !(count === 0 && manifest?.hasMore);
        window.__vccPathwaySearch(this.root, this.searchCurationGeneration, 'final', known ? 'succeeded' : 'unmeasured', known ? count : undefined);
      } catch (_) {}
    }

    observePathwaySearchSnapshot() {
      if (this.destroyed || this.kind !== 'search' || typeof window.__vccPathwaySearch !== 'function') return;
      const manifest = this.searchManifest;
      if (manifest?.sealed && this.isCurrentMappedManifest(manifest)) this.observePathwaySearchFinal(manifest);
      else if (this.searchCurationIsFallbackLocked()) {
        // Shopify's performed search/count, supplied by the current Liquid
        // render, is a terminal native result. No query or rendered-card guess.
        const native = this.config.pathwayNativeSearch;
        const known = native?.performed === true && Number.isInteger(native.count) && native.count >= 0 && native.count <= 100000;
        try { window.__vccPathwaySearch(this.root, this.searchCurationGeneration, 'final', known ? 'succeeded' : 'unmeasured', known ? native.count : undefined); } catch (_) {}
      }
    }

    announceMappedSearchView(manifest) {
      this.observePathwaySearchFinal(manifest);
      const copy = this.root.querySelector('[data-vcc-search-masthead-copy]');
      if (copy) copy.dataset.broadCount = String(manifest.entries.length);
      this.root.dataset.searchMappedCloseCount = String(manifest.closeCount || 0);
      this.root.dataset.searchMappedResultCount = String(manifest.entries.length);
      try {
        this.root.dispatchEvent(new CustomEvent('vcc:search-manifest-ready', {
          detail: {
            query: manifest.query,
            analysis: manifest.analysis,
            closeCount: manifest.closeCount || 0,
            resultCount: manifest.entries.length,
            hasMore: manifest.viewHasMore,
            engineMode: this.engineMode,
            requestCount: this.engineMode === 'v4_metafield'
              ? this.v4RequestCount
              : undefined,
            exactResponseMs: this.engineMode === 'v4_metafield'
              ? this.v4ExactResponseMs
              : undefined
          }
        }));
      } catch (error) {
        this.log('Mapped search view announcement was skipped', error);
      }
    }

    applyMappedSearchUrl(sourceUrl, options = {}) {
      const manifest = this.searchManifest;
      if (!manifest?.sealed || !this.isCurrentMappedManifest(manifest)) return false;
      const progressivePending = manifest.progressiveLoading === true;
      if (manifest.broadLoading && options.scopeCommit !== true) {
        this.cancelMappedSearchScopeLoad();
      }
      const url = this.normalizeMappedUsdPriceUrl(
        sourceUrl instanceof URL ? new URL(sourceUrl) : new URL(sourceUrl, window.location.href)
      );
      url.searchParams.set('vcc_mapped', '1');
      const currentRawQuery = new URLSearchParams(window.location.search).get('vcc_raw');
      if (currentRawQuery && !url.searchParams.has('vcc_raw')) {
        url.searchParams.set('vcc_raw', currentRawQuery);
      }
      if (!this.mappedFilterUrlSupported(url)) return false;
      const previousEntries = manifest.entries;
      const previousCloseCount = manifest.closeCount;
      const previousViewKey = manifest.viewKey;
      const previousViewHasMore = manifest.viewHasMore;
      const nextViewKey = this.mappedSearchViewKey(url);
      const viewChanged = options.forceView === true || nextViewKey !== manifest.viewKey;
      if (viewChanged) this.deriveMappedSearchEntries(manifest, url);
      const requestedPage = Math.max(1, parseInt(url.searchParams.get('page') || '1', 10) || 1);
      const totalPages = Math.max(1, Math.ceil(manifest.entries.length / manifest.pageSize));
      const page = Math.min(requestedPage, totalPages);
      const pageWasNormalized = page !== requestedPage;
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      this.syncUrlLayoutParam(url);

      let prepared = null;
      if (manifest.entries.length) {
        prepared = this.prepareMappedSearchPage(manifest, page);
        if (!prepared) {
          manifest.entries = previousEntries;
          manifest.closeCount = previousCloseCount;
          manifest.viewKey = previousViewKey;
          manifest.viewHasMore = previousViewHasMore;
          return false;
        }
      }
      this.hideMappedSearchRootCta();

      if (options.history === 'replace') {
        window.history.replaceState({
          ...this.filterDialogHistoryStateWithoutMarker(),
          vccMappedSearch: true,
          page
        }, '', url);
      } else if (options.history === false && pageWasNormalized) {
        this.normalizeMappedSearchHistoryEntry(url, page);
      } else if (options.history !== false) {
        window.history.pushState({ vccMappedSearch: true, page }, '', url);
      }

      if (viewChanged) {
        this.syncMappedFilterControls(url);
        this.updateMappedFacetCounts(manifest, url);
      }
      if (!manifest.entries.length) {
        const grid = this.root.querySelector('[data-ac-grid]');
        grid?.replaceChildren();
        this.searchCurrentMappedPage = 1;
        this.root.dataset.searchMappedEnhanced = 'true';
        const empty = this.ensureMappedEmptyState();
        if (empty) empty.hidden = false;
      } else {
        this.applyPreparedMappedSearchPage(prepared, { animate: false });
        this.finalizePreparedMappedSearchPage(prepared, { animate: false });
      }
      this.renderMappedSearchNavigation(manifest, page);
      if (viewChanged && !progressivePending) this.announceMappedSearchView(manifest);
      this.warmMappedSearchPages(manifest, page);
      if (options.scroll === true) this.scrollMappedResultsIntoView();
      const announcement = this.root.querySelector('[data-ac-curation-announcement]');
      if (announcement) {
        announcement.textContent = progressivePending
          ? 'Results updated. Organizing related results.'
          : `${manifest.entries.length} results ready.`;
      }
      this.commitFilterDialogObservedRoute(url);
      return true;
    }

    applyMappedSearchUrlWithProgress(sourceUrl, options = {}) {
      const grid = this.root.querySelector('[data-ac-grid]');
      if (!(grid instanceof HTMLElement)) {
        return this.applyMappedSearchUrl(sourceUrl, options);
      }
      const generation = ++this.mappedSearchTransitionGeneration;
      window.clearTimeout(this.mappedSearchTransitionTimer);
      this.mappedSearchTransitionTimer = null;
      if (this.mappedSearchTransitionFrame) {
        window.cancelAnimationFrame(this.mappedSearchTransitionFrame);
        this.mappedSearchTransitionFrame = 0;
      }
      const startedAt = performance.now();
      this.setMappedSearchLoading(grid, true);
      const applied = this.applyMappedSearchUrl(sourceUrl, options);
      this.mappedSearchTransitionFrame = window.requestAnimationFrame(() => {
        this.mappedSearchTransitionFrame = window.requestAnimationFrame(() => {
          this.mappedSearchTransitionFrame = 0;
          const remaining = Math.max(0, 220 - (performance.now() - startedAt));
          this.mappedSearchTransitionTimer = window.setTimeout(() => {
            if (generation !== this.mappedSearchTransitionGeneration) return;
            this.mappedSearchTransitionTimer = null;
            this.setMappedSearchLoading(grid, false);
            this.renderMappedSearchScopeControl(this.searchManifest);
          }, remaining);
        });
      });
      return applied;
    }

    setMappedSearchLoading(grid, loading) {
      if (!grid) return;
      if (loading && this.searchCurationIsFallbackLocked()) return;
      const progressiveActive = Boolean(
        !loading
        && this.engineMode === 'v4_metafield'
        && !this.searchCurationIsFallbackLocked()
        && this.searchManifest?.progressiveCommitted === true
        && this.searchManifest?.progressiveLoading === true
        && this.searchManifest?.cancelled !== true
        && this.isCurrentMappedManifest(this.searchManifest)
        && this.root.dataset.searchProgressiveLoading === 'true'
      );
      const visibleLoading = loading || progressiveActive;
      if (visibleLoading) this.hideMappedSearchRootCta();
      this.root.dataset.searchMappedLoading = visibleLoading ? 'true' : 'false';
      grid.setAttribute('aria-busy', visibleLoading ? 'true' : 'false');
      this.root.querySelectorAll('[data-ac-search-progress]').forEach((progress) => {
        progress.hidden = !visibleLoading;
      });

      if (visibleLoading && this.currentSearchPage() > 1 && this.root.dataset.searchMappedEnhanced !== 'true') {
        this.root.dataset.searchMappedPagePending = 'true';
      } else if (!visibleLoading) {
        this.root.dataset.searchMappedPagePending = 'false';
      }

      const empty = this.root.querySelector('.vcc-ac-empty');
      if (empty) empty.hidden = visibleLoading || grid.children.length > 0;
      this.syncMobilePager();
    }

    cardHtmlFromSearchTemplate(template) {
      if (!(template instanceof HTMLTemplateElement)) return '';
      return String(template.innerHTML || '').trim();
    }

    cardFromSearchHtml(html) {
      if (!html) return null;
      const template = document.createElement('template');
      template.innerHTML = String(html).trim();
      return template.content.querySelector('.vcc-ac-card');
    }

    materializeMappedCard(entry, options = {}) {
      const card = entry?.card instanceof HTMLElement ? entry.card : this.cardFromSearchHtml(entry?.cardHtml);
      if (!card) return null;
      if (options.decorate !== false) {
        card.dataset.vccMappedSource = entry.sourceKind || '';
        card.dataset.vccSearchScore = String(entry.score || 0);
      }
      entry.card = card;
      return card;
    }

    mappedSearchPageReady(manifest, page) {
      if (!manifest?.sealed || !Array.isArray(manifest.entries)) return false;
      const requestedPage = Math.max(1, Number(page) || 1);
      const start = (requestedPage - 1) * manifest.pageSize;
      const entries = manifest.entries.slice(start, start + manifest.pageSize);
      return entries.length > 0 && entries.every((entry) => {
        return entry.card instanceof HTMLElement || Boolean(entry.cardHtml);
      });
    }

    warmSearchPageImages(cardsOrEntries) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      const limit = mqMobile.matches ? 4 : 6;
      cardsOrEntries.slice(0, limit).forEach((item) => {
        const card = item instanceof HTMLElement ? item : item?.card;
        const source = card?.querySelector?.('.vcc-ac-image img');
        if (!(source instanceof HTMLImageElement)) return;
        const sourceUrl = source.currentSrc || source.src;
        if (!sourceUrl) return;
        const warmer = new Image();
        const release = () => this.searchPageImageWarmers.delete(warmer);
        warmer.decoding = 'async';
        warmer.fetchPriority = 'low';
        warmer.addEventListener('load', release, { once: true });
        warmer.addEventListener('error', release, { once: true });
        this.searchPageImageWarmers.add(warmer);
        if (source.sizes) warmer.sizes = source.sizes;
        if (source.srcset) warmer.srcset = source.srcset;
        warmer.src = sourceUrl;
      });
    }

    warmMappedSearchPages(manifest, currentPage = 1) {
      const generation = ++this.searchWarmGeneration;
      if (!manifest?.sealed || !Array.isArray(manifest.entries) || !manifest.entries.length) return;
      const totalPages = Math.max(1, Math.ceil(manifest.entries.length / manifest.pageSize));
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const constrained = Boolean(connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || ''));
      const warmThroughPage = constrained ? 2 : 4;
      const pages = [];
      for (let page = 2; page <= Math.min(warmThroughPage, totalPages); page += 1) {
        if (page !== currentPage) pages.push(page);
      }
      if (!pages.length) return;

      const priorityPage = pages[0];
      const priorityStart = (priorityPage - 1) * manifest.pageSize;
      const priorityEntries = manifest.entries.slice(priorityStart, priorityStart + manifest.pageSize);
      const priorityQueue = priorityEntries.filter((entry) => !(entry.card instanceof HTMLElement));
      const queue = pages.slice(1).flatMap((page) => {
        const start = (page - 1) * manifest.pageSize;
        return manifest.entries.slice(start, start + manifest.pageSize);
      }).filter((entry) => !(entry.card instanceof HTMLElement));
      if (!priorityQueue.length && !queue.length) {
        this.warmSearchPageImages(priorityEntries);
        return;
      }

      const valid = () => {
        return generation === this.searchWarmGeneration
          && !this.destroyed
          && this.root.isConnected
          && this.isCurrentMappedManifest(manifest);
      };
      const backgroundWork = (deadline) => {
        if (
          !valid()
        ) return;
        let count = 0;
        const startedAt = window.performance?.now?.() || Date.now();
        const hardCap = mqMobile.matches ? 4 : 6;
        while (queue.length) {
          if (
            count > 0
            && deadline
            && typeof deadline.timeRemaining === 'function'
            && deadline.timeRemaining() < 3
          ) break;
          if (count >= hardCap) break;
          if ((window.performance?.now?.() || Date.now()) - startedAt >= 6) break;
          this.materializeMappedCard(queue.shift(), { decorate: false });
          count += 1;
        }
        if (!queue.length) return;
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(backgroundWork, { timeout: 800 });
        } else {
          window.setTimeout(() => backgroundWork(null), 32);
        }
      };

      const priorityWork = () => {
        if (!valid()) return;
        const startedAt = window.performance?.now?.() || Date.now();
        let count = 0;
        while (priorityQueue.length && count < (mqMobile.matches ? 4 : 6)) {
          if ((window.performance?.now?.() || Date.now()) - startedAt >= 6) break;
          this.materializeMappedCard(priorityQueue.shift(), { decorate: false });
          count += 1;
        }
        if (priorityQueue.length) {
          window.setTimeout(priorityWork, 24);
          return;
        }
        this.warmSearchPageImages(priorityEntries);
        if (!queue.length) return;
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(backgroundWork, { timeout: 800 });
        } else {
          window.setTimeout(() => backgroundWork(null), 32);
        }
      };
      window.setTimeout(priorityWork, 24);
    }

    mappedSearchOverlapTransportPage(page) {
      const primaryPage = Math.max(2, Number(page) || 2);
      const primaryBoundaryIndex = ((primaryPage - 1) * 50) - 1;
      return Math.floor(Math.max(0, primaryBoundaryIndex) / 17) + 1;
    }

    v4SearchIndexValues(script) {
      let parsed = null;
      try {
        parsed = JSON.parse(script?.textContent || 'null');
      } catch (_error) {}
      const rawValues = Array.isArray(parsed)
        ? parsed
        : (typeof parsed === 'string' ? [parsed] : []);
      return Array.from(new Set(rawValues.flatMap((value) => {
        return String(value || '').split(';');
      }).map((value) => String(value || '').trim()).filter(Boolean)));
    }

    v4SearchIndexMap(doc) {
      return new Map(
        Array.from(doc?.querySelectorAll?.('script[data-vcc-search-index]') || [])
          .map((script) => {
            return [
              String(script.getAttribute('data-vcc-search-index') || ''),
              this.v4SearchIndexValues(script)
            ];
          })
      );
    }

    v4AuthoritativeTeamRelationshipEmptyProof(
      source,
      requestedFilters,
      hitCount,
      filterAvailable
    ) {
      if (
        source?.v4Metafield !== true
        || source?.authoritativeTeamSource !== true
        || String(source?.resultSectionKey || '') !== 'market-region'
        || Number(hitCount) !== 0
        || filterAvailable !== true
      ) return false;

      const role = String(source?.clauseRole || '').trim();
      if (!['market-peer', 'region-peer'].includes(role)) return false;

      const proof = source?.relationshipClauseProof;
      if (
        !proof
        || proof.active !== true
        || proof.queryable !== true
        || proof.routeDeclared !== true
      ) return false;

      const relationshipTier = String(
        proof.relationshipTier || proof.relationship_tier || ''
      ).trim().toLowerCase();
      const qualifierTier = String(
        proof.qualifierTier || proof.qualifier_tier || ''
      ).trim().toLowerCase();
      const team = String(proof.team || '').trim().toLowerCase();
      const qualifier = String(proof.qualifier || '').trim().toLowerCase();
      if (
        relationshipTier !== role.replace(/-peer$/, '')
        || qualifierTier !== relationshipTier
        || !/^team:[a-z0-9][a-z0-9-]*$/.test(team)
        || !/^(?:market|region):[a-z0-9][a-z0-9-]*$/.test(qualifier)
      ) return false;

      const requested = Array.from(new Set(
        (Array.isArray(requestedFilters) ? requestedFilters : [])
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean)
      )).sort();
      const declared = Array.from(new Set(
        (Array.isArray(proof.allOf) ? proof.allOf : [])
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean)
      )).sort();
      return requested.length === 2
        && requested.length === declared.length
        && requested.every((value, index) => value === declared[index])
        && requested.includes(team)
        && requested.includes(qualifier);
    }

    v4FilterResponseAttestation(input, source) {
      const doc = typeof input === 'string'
        ? new DOMParser().parseFromString(input, 'text/html')
        : input;
      const sentinel = doc?.querySelector?.('[data-vcc-index-filter-available]');
      const filterRequestEchoed = sentinel?.getAttribute('data-vcc-index-filter-requested') === 'true';
      const filterAvailable = sentinel?.getAttribute('data-vcc-index-filter-available') === 'true';
      const shopifyActive = sentinel?.getAttribute('data-vcc-index-filter-active') === 'true';
      const requestedFilters = this.v4SearchSourceAllOf(source);
      // Shopify's rendered request.params marker is not reliable for storefront
      // Section responses, even when the actual filter object reports active.
      // The compiled source is the client-side request declaration; positive
      // responses still have to prove every card token below, while empty
      // responses still require Shopify's actual-active filter proof.
      const filterRequested = requestedFilters.length > 0;
      const hits = Array.from(
        doc?.querySelectorAll?.('.vcc-search-hit[data-available="true"]') || []
      );
      const indexes = new Map(
        Array.from(doc?.querySelectorAll?.('script[data-vcc-search-index]') || [])
          .map((script) => {
            const id = String(script.getAttribute('data-vcc-search-index') || '');
            let parsed = null;
            try {
              parsed = JSON.parse(script.textContent || 'null');
            } catch (_error) {}
            const values = new Set(
              (Array.isArray(parsed)
                ? parsed
                : (typeof parsed === 'string' ? parsed.split(';') : [])
              ).map((value) => String(value || '').trim()).filter(Boolean)
            );
            return [id, values];
          })
      );
      const requiredTokens = requestedFilters
        .map((value) => String(value || '').trim().toLowerCase());
      const excludedTokens = Array.from(new Set(
        ['system:exclude-search'].concat(source?.v4ExcludeFilters || [])
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean)
      ));
      let attestedHitCount = 0;
      let rejectedHitCount = 0;
      let requestedFilterHitCount = 0;
      hits.forEach((hit) => {
        const values = indexes.get(String(hit.getAttribute('data-id') || ''));
        const normalizedValues = values instanceof Set
          ? new Set(Array.from(values).map((value) => String(value || '').trim().toLowerCase()))
          : new Set();
        if (requestedFilters.every((token) => normalizedValues.has(token.toLowerCase()))) {
          requestedFilterHitCount += 1;
        }
        const excluded = excludedTokens.some((token) => normalizedValues.has(token));
        const attested = !excluded
          && requiredTokens.length > 0
          && requiredTokens.every((token) => normalizedValues.has(token));
        if (attested) attestedHitCount += 1;
        else rejectedHitCount += 1;
      });
      // A positive response can prove itself only when every returned card
      // carries every requested AND token. A successful empty response is also
      // safe for one exact route-declared Team relationship member: it adds no
      // products, while every non-empty sibling still requires card-level
      // attestation. Exact/History, undeclared clauses, unavailable filters,
      // and transport failures remain fail-closed.
      const cardLevelProof = hits.length > 0
        && requestedFilterHitCount === hits.length;
      const routeDeclaredRelationshipEmpty = this.v4AuthoritativeTeamRelationshipEmptyProof(
        source,
        requestedFilters,
        hits.length,
        filterAvailable
      );
      const verifiedEmpty = hits.length === 0
        && filterRequested
        && filterAvailable
        && (shopifyActive || routeDeclaredRelationshipEmpty);
      const filterApplied = filterAvailable
        && requestedFilters.length > 0
        && (cardLevelProof || verifiedEmpty);
      const transportUnproven = requestedFilters.length > 0 && !filterApplied;
      return {
        filterRequested,
        filterRequestEchoed,
        filterAvailable,
        filterApplied,
        shopifyActive,
        verifiedEmpty,
        transportUnproven,
        routeDeclaredRelationshipEmpty,
        attestation: verifiedEmpty
          ? (routeDeclaredRelationshipEmpty && !shopifyActive
            ? 'verified-empty-authoritative-team-relationship'
            : 'verified-empty')
          : cardLevelProof
            ? 'card-level-product-index'
            : filterRequested && hits.length === 0
              ? 'ambiguous-empty'
              : 'failed',
        hitCount: hits.length,
        requestedFilterHitCount,
        attestedHitCount,
        rejectedHitCount
      };
    }

    v4SessionFilterCacheRead(url) {
      if (this.engineMode !== 'v4_metafield' || !url) return '';
      try {
        const key = 'vcc-search-v4-filter-responses-4-11-3-usd-price-bracket-2';
        const cache = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (!cache || cache.version !== 2 || !Array.isArray(cache.entries)) return '';
        const now = Date.now();
        const entry = cache.entries.find((candidate) => {
          return candidate?.url === url
            && typeof candidate?.html === 'string'
            && now - Number(candidate?.createdAt || 0) <= 300000;
        });
        if (!entry) return '';
        this.root.dataset.v4SessionCacheHit = 'true';
        return entry.html;
      } catch (_error) {
        return '';
      }
    }

    v4SessionFilterCacheWrite(url, html, source) {
      const attestation = this.v4FilterResponseAttestation(html, source);
      if (
        this.engineMode !== 'v4_metafield'
        || !url
        || typeof html !== 'string'
        || !html
        || html.length > 800000
        || !attestation.filterApplied
        || !html.includes('class="vcc-search-hit"')
        || !html.includes('data-vcc-search-card=')
      ) return;
      try {
        const key = 'vcc-search-v4-filter-responses-4-11-3-usd-price-bracket-2';
        const now = Date.now();
        const stored = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        const retained = (stored?.version === 2 && Array.isArray(stored.entries)
          ? stored.entries
          : []
        ).filter((entry) => {
          return entry?.url !== url
            && typeof entry?.html === 'string'
            && now - Number(entry?.createdAt || 0) <= 300000;
        });
        retained.unshift({ url, html, createdAt: now });
        let totalCharacters = 0;
        const entries = retained.filter((entry) => {
          totalCharacters += entry.html.length;
          return totalCharacters <= 2200000;
        }).slice(0, 8);
        window.sessionStorage.setItem(key, JSON.stringify({ version: 2, entries }));
      } catch (_error) {}
    }

    v4SessionFilterCacheDelete(url) {
      if (!url) return;
      try {
        const key = 'vcc-search-v4-filter-responses-4-11-3-usd-price-bracket-2';
        const stored = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (stored?.version !== 2 || !Array.isArray(stored.entries)) return;
        const entries = stored.entries.filter((entry) => entry?.url !== url);
        if (entries.length === stored.entries.length) return;
        if (entries.length) {
          window.sessionStorage.setItem(key, JSON.stringify({ version: 2, entries }));
        } else {
          window.sessionStorage.removeItem(key);
        }
      } catch (_error) {}
    }

    queueV4TransportHtml(url, options = {}) {
      return new Promise((resolve) => {
        this.v4TransportQueue.push({
          url,
          signal: options.signal || this.searchCurationAbortController?.signal,
          generation: Number.isFinite(Number(options.generation))
            ? Number(options.generation)
            : this.searchCurationGeneration,
          deadlineReserveMs: Math.max(0, Number(options.deadlineReserveMs) || 0),
          background: options.background === true,
          attemptTimeoutMs: Math.max(
            1000,
            Math.min(
              8000,
              Number(options.attemptTimeoutMs)
                || (options.background === true
                  ? V4_BACKGROUND_ATTEMPT_TIMEOUT_MS
                  : V4_FOREGROUND_ATTEMPT_TIMEOUT_MS)
            )
          ),
          resolve
        });
        this.drainV4TransportQueue();
      });
    }

    drainV4TransportQueue() {
      while (this.v4TransportActive < 2 && this.v4TransportQueue.length) {
        const job = this.v4TransportQueue.shift();
        this.v4TransportActive += 1;
        this.runV4TransportJob(job)
          .then((html) => job.resolve(html), () => job.resolve(''))
          .finally(() => {
            this.v4TransportActive = Math.max(0, this.v4TransportActive - 1);
            this.drainV4TransportQueue();
          });
      }
    }

    async runV4TransportJob(job) {
      const invalid = () => {
        return job.signal?.aborted
          || job.generation !== this.searchCurationGeneration
          || this.searchCurationIsFallbackLocked()
          || this.destroyed
          || !this.root.isConnected;
      };
      if (invalid()) return '';

      const rateLimitWaitMs = Math.max(0, this.v4RateLimitedUntil - Date.now());
      if (
        rateLimitWaitMs
        && !await this.waitForMappedSearchRetry(rateLimitWaitMs, {
           signal: job.signal,
           generation: job.generation,
           deadlineReserveMs: job.deadlineReserveMs,
           ignoreDeadline: job.background
         })
      ) return '';
      if (invalid()) return '';

      this.v4RequestCount += 1;
      this.root.dataset.v4SearchRequests = String(this.v4RequestCount);
      const attemptController = new AbortController();
      let timedOut = false;
      const forwardAbort = () => attemptController.abort();
      job.signal?.addEventListener?.('abort', forwardAbort, { once: true });
      const attemptTimer = window.setTimeout(() => {
        timedOut = true;
        attemptController.abort();
      }, job.attemptTimeoutMs);
      try {
        const response = await fetch(job.url, {
          credentials: 'same-origin',
          signal: attemptController.signal
        });
        if (response.status === 429) {
          const retryAfter = String(response.headers.get('Retry-After') || '').trim();
          const retryAfterSeconds = Number(retryAfter);
          const retryAfterMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
            ? Math.max(500, Math.min(5000, retryAfterSeconds * 1000))
            : 4500;
          this.v4RateLimitedUntil = Math.max(
            this.v4RateLimitedUntil,
            Date.now() + retryAfterMs
          );
          this.root.dataset.v4RateLimited = 'true';
          this.root.dataset.v4RateLimitBackoffMs = String(Math.round(retryAfterMs));
          return '';
        }
        if (!response.ok) return '';
        const html = await response.text();
        if (this.root.dataset.v4RateLimited === 'true') {
          this.root.dataset.v4RateLimitRecovered = 'true';
        }
        return html;
      } catch (error) {
        if (timedOut) {
          const timeoutCount = Math.max(
            0,
            Number(this.root.dataset.v4TransportTimeoutCount || 0)
          ) + 1;
          this.root.dataset.v4TransportTimeout = 'true';
          this.root.dataset.v4TransportTimeoutCount = String(timeoutCount);
          this.root.dataset.v4TransportTimeoutMs = String(job.attemptTimeoutMs);
        } else if (!job.signal?.aborted) {
          this.log('V4 filter request ended without a usable response', error);
        }
        return '';
      } finally {
        window.clearTimeout(attemptTimer);
        job.signal?.removeEventListener?.('abort', forwardAbort);
      }
    }

    async fetchMappedSearchHtml(source, page, options = {}) {
      const v4Transport = source?.v4Metafield === true;
      const overlap = options.overlap === true && !source.collectionHandle && !v4Transport;
      const transportPage = overlap
        ? this.mappedSearchOverlapTransportPage(page)
        : page;
      const transportCache = this.mappedSearchHtmlCache;
      const collectionUrls = overlap || v4Transport
        ? []
        : this.mappedCollectionTransportCandidates(
          source.collectionHandle,
          transportPage
        );
      const urls = v4Transport
        ? this.v4SearchTransportCandidates(source, transportPage, { overlap: false })
        : collectionUrls.length
        ? collectionUrls
        : this.mappedSearchTransportCandidates(
          source.transportQuery || source.query,
          transportPage,
          { overlap }
        );
      for (const url of urls) {
        let request = transportCache.get(url);
        if (!request) {
          const sessionHtml = v4Transport ? this.v4SessionFilterCacheRead(url) : '';
          if (sessionHtml) {
            request = Promise.resolve(sessionHtml);
          } else if (v4Transport) {
            request = this.queueV4TransportHtml(url, {
              signal: options.signal || this.searchCurationAbortController?.signal,
              generation: this.searchCurationGeneration,
              deadlineReserveMs: options.deadlineReserveMs,
              background: options.background === true,
              attemptTimeoutMs: options.attemptTimeoutMs
            }).then((html) => {
              if (html && this.v4FilterResponseAttestation(html, source).filterApplied) {
                this.v4SessionFilterCacheWrite(url, html, source);
              }
              return html;
            });
          } else {
            request = fetch(url, {
              credentials: 'same-origin',
              signal: options.signal || this.searchCurationAbortController?.signal
            }).then(async (response) => {
              if (!response.ok) return '';
              return response.text();
            });
          }
          transportCache.set(url, request);
        }
        let html = '';
        try {
          html = await request;
        } catch (_error) {
          if (transportCache.get(url) === request) transportCache.delete(url);
          continue;
        }
        if (!html) {
          if (transportCache.get(url) === request) transportCache.delete(url);
          continue;
        }
        if (
          !source.collectionHandle
          && url.indexOf('section_id=vcc-search-results') === -1
          && this.mappedSearchHtmlCache === transportCache
        ) this.searchTransportMode = 'rendered';
        return html;
      }
      return '';
    }

    mappedSearchUsesRequiredRetry(source, page, options = {}, analysis = null) {
      if (options.requiredRetry === false) return false;
      if (options.requiredRetry === true) return true;
      return source?.exact === true
        || (
          Math.max(1, Number(page) || 1) === 1
          && this.mappedSearchSourceScope(source) === 'core'
        )
        || (
          source?.v4Metafield === true
          && this.mappedSearchSourceScope(source) === 'core'
          && analysis?.rootCta?.collectionHandle === 'vintage-classics'
        );
    }

    async waitForMappedSearchRetry(delayMs, options = {}) {
      const waitMs = Math.max(0, Number(delayMs) || 0);
      if (!waitMs) return true;
      const signal = options.signal || this.searchCurationAbortController?.signal;
      const generation = Number.isFinite(Number(options.generation))
        ? Number(options.generation)
        : this.searchCurationGeneration;
      const deadlineReserveMs = Math.max(0, Number(options.deadlineReserveMs) || 0);
      const invalid = () => {
        return signal?.aborted
          || generation !== this.searchCurationGeneration
          || this.searchCurationIsFallbackLocked()
          || this.destroyed
          || !this.root.isConnected;
      };
      if (invalid()) return false;
      const remainingMs = this.searchCurationRemaining();
      if (
        options.ignoreDeadline !== true
        &&
        Number.isFinite(remainingMs)
        && remainingMs <= waitMs + deadlineReserveMs + 80
      ) return false;

      return new Promise((resolve) => {
        let timer = null;
        let settled = false;
        const finish = (ready) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          signal?.removeEventListener?.('abort', onAbort);
          resolve(Boolean(ready) && !invalid());
        };
        const onAbort = () => finish(false);
        signal?.addEventListener?.('abort', onAbort, { once: true });
        timer = window.setTimeout(() => finish(true), waitMs);
      });
    }

    async fetchMappedSearchPageWithRetry(source, page, engine, analysis, options = {}) {
      const classicsCoreRetry = options.requiredRetry !== false
        && source?.v4Metafield === true
        && this.mappedSearchSourceScope(source) === 'core'
        && analysis?.rootCta?.collectionHandle === 'vintage-classics';
      const requiredRetry = this.mappedSearchUsesRequiredRetry(source, page, options, analysis);
      const attempts = Math.max(
        1,
        Number(options.attempts || (requiredRetry ? 3 : 2))
      );
      const retryDelays = Array.isArray(options.retryDelays)
        ? options.retryDelays.map((value) => Math.max(0, Number(value) || 0))
        : (
          source?.v4Metafield === true
            ? (requiredRetry ? [650, 1800] : [900])
            : (requiredRetry ? [250, 750] : [])
        );
      const generation = this.searchCurationGeneration;
      const signal = options.signal || this.searchCurationAbortController?.signal;
      const requestOptions = Object.assign({}, options, { signal });
      if (classicsCoreRetry) {
        requestOptions.attemptTimeoutMs = Math.max(
          7000,
          Number(requestOptions.attemptTimeoutMs) || 0
        );
      }
      const transportFailure = () => ({
        products: [],
        hasMore: false,
        failed: true,
        transportUnproven: source?.v4Metafield === true,
        failureReason: source?.v4Metafield === true
          ? 'transport-unproven'
          : 'transport-failed'
      });
      let result = transportFailure();
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        result = await this.fetchMappedSearchPage(
          source,
          page,
          engine,
          analysis,
          requestOptions
        ).catch(() => transportFailure());
        if (!result.failed) return result;
        if (
          signal?.aborted
          || generation !== this.searchCurationGeneration
          || this.searchCurationIsFallbackLocked()
          || this.destroyed
          || !this.root.isConnected
        ) break;
        if (attempt + 1 >= attempts) break;
        const retryDelay = retryDelays[Math.min(attempt, retryDelays.length - 1)] || 0;
        if (
          retryDelay
          && !await this.waitForMappedSearchRetry(retryDelay, {
            signal,
            generation,
            deadlineReserveMs: options.deadlineReserveMs,
            ignoreDeadline: options.background === true
          })
        ) break;
      }
      return result;
    }

    async fetchMappedSearchPage(source, page, engine, analysis, options = {}) {
      const products = [];
      const v4Transport = source?.v4Metafield === true;
      const transportSource = Object.assign({}, source, {
        query: this.mappedSearchExactTransportQuery(source, analysis)
      });
      const overlapRequest = !v4Transport && !source.collectionHandle && Number(page) > 1
        ? this.fetchMappedSearchHtml(
          transportSource,
          page,
          Object.assign({}, options, { overlap: true })
        ).catch(() => '')
        : Promise.resolve('');
      const [html, overlapHtml] = await Promise.all([
        this.fetchMappedSearchHtml(transportSource, page, options),
        overlapRequest
      ]);
      const invalidateV4TransportCache = () => {
        if (!v4Transport) return;
        this.v4SearchTransportCandidates(transportSource, page, { overlap: false })
          .forEach((url) => {
            this.mappedSearchHtmlCache.delete(url);
            this.v4SessionFilterCacheDelete(url);
          });
      };
      if (!html) {
        invalidateV4TransportCache();
        return {
          products,
          hasMore: false,
          failed: true,
          transportUnproven: v4Transport,
          failureReason: v4Transport ? 'transport-empty' : 'transport-failed'
        };
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (v4Transport) {
        const attestation = this.v4FilterResponseAttestation(doc, source);
        const filterAvailable = attestation.filterAvailable;
        const filterApplied = attestation.filterApplied;
        this.root.dataset.v4FilterRequested = String(attestation.filterRequested);
        this.root.dataset.v4FilterAvailable = String(filterAvailable);
        this.root.dataset.v4FilterApplied = String(filterApplied);
        this.root.dataset.v4FilterShopifyActive = String(attestation.shopifyActive);
        this.root.dataset.v4FilterVerifiedEmpty = String(attestation.verifiedEmpty);
        this.root.dataset.v4FilterRouteDeclaredEmpty = String(
          attestation.routeDeclaredRelationshipEmpty
        );
        this.root.dataset.v4FilterTransportUnproven = String(attestation.transportUnproven);
        this.root.dataset.v4FilterAttestation = attestation.attestation;
        if (!filterAvailable || !filterApplied) {
          invalidateV4TransportCache();
          return {
            products,
            hasMore: false,
            failed: true,
            v4FilterUnavailable: !filterAvailable,
            v4FilterNotApplied: filterAvailable && !filterApplied,
            transportUnproven: attestation.transportUnproven,
            failureReason: attestation.attestation || 'filter-unproven'
          };
        }
        if (source?.exact === true && this.v4ExactResponseMs === null) {
          const now = window.performance?.now?.() || Date.now();
          const startedAt = Number(this.searchCuration?.startedAt || now);
          this.v4ExactResponseMs = Math.max(0, Math.round(now - startedAt));
          this.root.dataset.v4ExactResponseMs = String(this.v4ExactResponseMs);
        }
      }
      const primaryHasMore = doc.querySelector(
        '[data-has-next="true"], .vcc-ac-pagination a[rel="next"]'
      ) !== null;
      const configuredMaxPages = Math.max(0, Number(
        options.maxPages || source.maxSourcePages || 0
      ));
      const trailingOverlapHtml = !v4Transport
        && !source.collectionHandle
        && primaryHasMore
        && (!configuredMaxPages || Number(page) < configuredMaxPages)
        ? await this.fetchMappedSearchHtml(
          transportSource,
          Number(page) + 1,
          Object.assign({}, options, { overlap: true })
        ).catch(() => '')
        : '';
      const documents = [doc];
      if (overlapHtml) {
        documents.push(new DOMParser().parseFromString(overlapHtml, 'text/html'));
      }
      if (trailingOverlapHtml) {
        documents.push(new DOMParser().parseFromString(trailingOverlapHtml, 'text/html'));
      }

      documents.forEach((resultDocument) => {
        const hits = Array.from(
          resultDocument.querySelectorAll('.vcc-search-hit[data-available="true"]')
        );
        const v4Indexes = (v4Transport || source?.classicsTitleRescue === true)
          ? this.v4SearchIndexMap(resultDocument)
          : new Map();
        const templates = new Map(
          Array.from(resultDocument.querySelectorAll('template[data-vcc-search-card]'))
            .map((template) => {
              return [String(template.dataset.vccSearchCard || ''), template];
            })
        );

        hits.forEach((hit) => {
          const product = this.productFromSearchHit(hit, source);
          product.v4IndexValues = (
            v4Indexes.get(String(product.id || '')) || []
          ).slice();
          const key = this.searchProductKey(product);
          const template = templates.get(String(product.id || ''));
          const templateCard = template?.content?.querySelector('.vcc-ac-card');
          this.enrichSearchProductFromCard(product, templateCard);
          const cardHtml = this.cardHtmlFromSearchTemplate(template);
          if (!key || !cardHtml) return;
          if (!this.productMatchesMappedAttributes(product, analysis, source, engine)) return;
          const relationshipMatch = this.relationshipCompositeMatch(product, source, engine);
          const score = engine.productScore(product, analysis, relationshipMatch?.source || source);
          products.push({
            key,
            product,
            cardHtml,
            score,
            relationshipOrder: relationshipMatch?.index,
            sourceKind: source.kind || ''
          });
        });

        if (!hits.length) {
          Array.from(resultDocument.querySelectorAll('.vcc-ac-card[data-product-id]')).forEach((card) => {
            const product = this.productFromSearchCard(card, source);
            product.v4IndexValues = (
              v4Indexes.get(String(product.id || '')) || []
            ).slice();
            const key = this.searchProductKey(product);
            if (!key || product.available === false) return;
            if (!this.productMatchesMappedAttributes(product, analysis, source, engine)) return;
            const relationshipMatch = this.relationshipCompositeMatch(product, source, engine);
            const score = engine.productScore(product, analysis, relationshipMatch?.source || source);
            products.push({
              key,
              product,
              cardHtml: card.outerHTML,
              score,
              relationshipOrder: relationshipMatch?.index,
              sourceKind: source.kind || ''
            });
          });
        }
      });

      const paginationPages = Array.from(doc.querySelectorAll(
        '[data-ac-numbered-pagination] a[href], .vcc-ac-pagination a[href]'
      )).map((link) => {
        try {
          return Math.max(
            1,
            Number(new URL(link.getAttribute('href'), window.location.origin).searchParams.get('page')) || 1
          );
        } catch (_error) {
          return 1;
        }
      });
      const declaredLastPages = Array.from(doc.querySelectorAll('[data-last-page]'))
        .map((node) => Math.max(0, Number(node.dataset.lastPage || 0)))
        .filter(Boolean);
      const discoveredLastPage = Math.max(
        Number(page) || 1,
        ...paginationPages,
        ...declaredLastPages
      );
      return {
        products,
        hasMore: primaryHasMore,
        totalPages: discoveredLastPage,
        totalPagesKnown: declaredLastPages.length > 0
          || discoveredLastPage > (Number(page) || 1) + 1,
        failed: false
      };
    }

    mappedSearchLimits(pageSize) {
      return {
        maxItems: Math.max(pageSize * 40, 2400),
        maxSourcePages: 24,
        batchSize: 12
      };
    }

    mappedSearchStateMaxPages(state, fallbackMax = 8) {
      const configured = Math.max(0, Number(state?.source?.maxSourcePages || 0));
      if (configured > 0) return Math.max(1, Math.min(fallbackMax, configured));
      const declaredCount = Math.max(
        0,
        Number(state?.source?.relationshipProductCount || 0)
      );
      if (declaredCount > 0) {
        const sourcePageSize = state?.source?.collectionHandle ? 96 : 50;
        return Math.max(
          1,
          Math.min(fallbackMax, Math.ceil(declaredCount / sourcePageSize))
        );
      }
      return fallbackMax;
    }

    mappedSearchSourceTierMetadata(state) {
      const source = state?.source || {};
      const scope = String(
        state?.scope || this.mappedSearchSourceScope(source) || 'core'
      );
      const rawOrder = Number(source.tierOrder);
      const order = scope === 'exact'
        ? 0
        : (Number.isFinite(rawOrder) ? rawOrder : 40);
      const groupId = scope === 'exact'
        ? 'exact'
        : String(
          source.relationshipGroupId
          || source.relationshipKind
          || source.kind
          || `tier-${order}`
        ).trim();
      const key = scope === 'exact'
        ? 'v4:exact'
        : `v4:${scope}:tier-${order}`;
      return {
        key,
        order,
        label: String(
          source.label || (scope === 'exact' ? 'Exact matches' : 'Related matches')
        ).trim(),
        kind: String(
          source.relationshipKind || source.kind || (scope === 'exact' ? 'exact' : 'relationship')
        ).trim(),
        groupId,
        scope
      };
    }

    mappedSearchTierGroups(states) {
      const groups = [];
      const groupByKey = new Map();
      (states || [])
        .slice()
        .sort((left, right) => {
          const leftMeta = this.mappedSearchSourceTierMetadata(left);
          const rightMeta = this.mappedSearchSourceTierMetadata(right);
          return leftMeta.order - rightMeta.order
            || Number(left?.sourceIndex || 0) - Number(right?.sourceIndex || 0);
        })
        .forEach((state) => {
          const metadata = this.mappedSearchSourceTierMetadata(state);
          let group = groupByKey.get(metadata.key);
          if (!group) {
            group = Object.assign({}, metadata, { states: [] });
            groupByKey.set(metadata.key, group);
            groups.push(group);
          }
          group.states.push(state);
        });
      return groups;
    }

    strictV4ExactTierStatus(states, fallbackMaxPages = 24) {
      const requiredStates = Array.isArray(states) ? states : [];
      const incompleteStates = requiredStates.filter((state) => {
        return Number(state?.page || 0) < 1
          || state.failed === true
          || state.exhausted !== true
          || state.hasMore === true;
      });
      const cappedStates = incompleteStates.filter((state) => {
        return state.hasMore === true
          && Number(state.page || 0) >= Math.max(
            1,
            Number(state.maxPages || fallbackMaxPages)
          );
      });
      const failedStates = incompleteStates.filter((state) => state.failed === true);
      return {
        settled: requiredStates.length > 0 && incompleteStates.length === 0,
        reason: cappedStates.length
          ? 'source-page-cap'
          : (failedStates.length ? 'source-failure' : 'exact-tier-incomplete'),
        sourceIndexes: incompleteStates.map((state) => Number(state.sourceIndex)),
        cappedSourceIndexes: cappedStates.map((state) => Number(state.sourceIndex))
      };
    }

    v4MappedSearchBalancesExactTier(analysis) {
      const kind = String(
        analysis?.primary?.kind
        || analysis?.entity?.kind
        || analysis?.entity?.type
        || analysis?.entityType
        || ''
      ).trim().toLowerCase();
      return kind === 'league' || kind === 'sport';
    }

    v4MappedSearchProductTeamKey(product, source = null) {
      const attestedRelationshipTarget = String(
        product?.v4RelationshipTeamTarget || ''
      ).trim().toLowerCase();
      if (attestedRelationshipTarget) return attestedRelationshipTarget;
      const values = Array.isArray(product?.v4IndexValues)
        ? product.v4IndexValues
        : [];
      const teams = new Set(values
        .map((value) => String(value || '').trim().toLowerCase())
        .filter((value) => /^team:[a-z0-9][a-z0-9-]*$/.test(value)));
      const declaredOrder = (Array.isArray(source?.relationshipTeamOrder)
        ? source.relationshipTeamOrder
        : []
      ).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
      const declared = declaredOrder.find((team) => teams.has(team));
      if (declared) return declared;
      if (
        source?.authoritativeTeamSource === true
        && String(source?.resultSectionKey || '') === 'market-region'
      ) return '';
      // Non-authoritative V4 plans retain their established deterministic key.
      return Array.from(teams).sort((left, right) => left.localeCompare(right))[0] || '';
    }

    rebalanceMappedSearchTierEntries(manifest, tier) {
      const order = Number(tier?.order || 0);
      const scope = String(tier?.scope || '').trim().toLowerCase();
      const leagueOrSportIntent = this.v4MappedSearchBalancesExactTier(manifest?.analysis);
      const teamIntent = Boolean(
        String(manifest?.analysis?.subject?.league || '').trim()
        || manifest?.analysis?.team
        || ['team', 'franchise'].includes(
          String(
            manifest?.analysis?.entity?.kind
            || manifest?.analysis?.entity?.type
            || manifest?.analysis?.entityType
            || ''
          ).trim().toLowerCase()
        )
      );
      const exactTier = scope === 'exact';
      const clusterTeamRelationshipMembers = teamIntent
        && !leagueOrSportIntent
        && !exactTier;
      if (
        this.engineMode !== 'v4_metafield'
        || !manifest
        || !tier
        || (exactTier ? !leagueOrSportIntent : (!teamIntent && !leagueOrSportIntent))
      ) return false;
      const targetEntries = Array.isArray(manifest.progressiveEntries)
        ? manifest.progressiveEntries
        : manifest.entries;
      const targetIndexes = [];
      const buckets = new Map();
      const bucketFirstIndexes = new Map();
      const unattested = [];
      targetEntries.forEach((entry, index) => {
        if (entry?.tierKey !== tier.key) return;
        targetIndexes.push(index);
        const memberKey = String(
          entry?.tierMemberKey || this.v4MappedSearchProductTeamKey(entry?.product, tier?.states?.[0]?.source)
        ).trim();
        entry.tierMemberKey = memberKey;
        if (!memberKey) {
          unattested.push(entry);
          return;
        }
        if (!buckets.has(memberKey)) buckets.set(memberKey, []);
        if (!bucketFirstIndexes.has(memberKey)) bucketFirstIndexes.set(memberKey, index);
        buckets.get(memberKey).push(entry);
      });
      if (targetIndexes.length < 2 || buckets.size < 2) return false;

      const stableEntryOrder = (left, right) => {
        return Number(right?.score || 0) - Number(left?.score || 0)
          || String(left?.key || '').localeCompare(String(right?.key || ''))
          || Number(left?.rank || 0) - Number(right?.rank || 0);
      };
      const plannedMemberOrder = new Map();
      (tier.states || []).forEach((state, stateIndex) => {
        const teamFilter = Array.isArray(state?.source?.v4Filters)
          ? state.source.v4Filters.find((filter) => {
            return /^team:[a-z0-9][a-z0-9-]*$/.test(String(filter || '').trim().toLowerCase());
          })
          : '';
        const key = String(teamFilter || '').trim().toLowerCase();
        if (key && !plannedMemberOrder.has(key)) plannedMemberOrder.set(key, stateIndex);
      });
      const orderedBuckets = Array.from(buckets.entries())
        .sort(([left, leftEntries], [right, rightEntries]) => {
          if (clusterTeamRelationshipMembers) {
            const leftPlanned = plannedMemberOrder.has(left)
              ? plannedMemberOrder.get(left)
              : Number.POSITIVE_INFINITY;
            const rightPlanned = plannedMemberOrder.has(right)
              ? plannedMemberOrder.get(right)
              : Number.POSITIVE_INFINITY;
            return leftPlanned - rightPlanned
              || rightEntries.length - leftEntries.length
              || Number(bucketFirstIndexes.get(left) || 0) - Number(bucketFirstIndexes.get(right) || 0)
              || left.localeCompare(right);
          }
          return left.localeCompare(right);
        })
        .map(([key, entries]) => ({ key, entries: entries.slice().sort(stableEntryOrder) }));
      unattested.sort(stableEntryOrder);

      const balanced = [];
      let memberRound = 0;
      if (clusterTeamRelationshipMembers) {
        orderedBuckets.forEach((bucket, bucketIndex) => {
          bucket.entries.forEach((entry, entryIndex) => {
            entry.tierMemberOrdinal = entryIndex + 1;
            entry.tierMemberRound = bucketIndex + 1;
            balanced.push(entry);
          });
        });
        memberRound = orderedBuckets.length;
      } else {
        let pending = true;
        while (pending) {
          pending = false;
          orderedBuckets.forEach((bucket) => {
            const entry = bucket.entries[memberRound];
            if (!entry) return;
            pending = true;
            entry.tierMemberOrdinal = memberRound + 1;
            entry.tierMemberRound = memberRound + 1;
            balanced.push(entry);
          });
          memberRound += 1;
        }
      }
      unattested.forEach((entry, index) => {
        entry.tierMemberOrdinal = index + 1;
        entry.tierMemberRound = memberRound + index;
        balanced.push(entry);
      });

      const originalKeys = targetIndexes
        .map((index) => String(targetEntries[index]?.key || ''))
        .sort();
      const balancedKeys = balanced
        .map((entry) => String(entry?.key || ''))
        .sort();
      if (
        balanced.length !== targetIndexes.length
        || originalKeys.some((key, index) => key !== balancedKeys[index])
      ) return false;

      targetIndexes.forEach((targetIndex, index) => {
        targetEntries[targetIndex] = balanced[index];
      });
      targetEntries.forEach((entry, index) => {
        entry.rank = index;
      });
      manifest.tierMemberBalance = manifest.tierMemberBalance || {};
      manifest.tierMemberBalance[tier.key] = {
        key: tier.key,
        order: tier.order,
        mode: clusterTeamRelationshipMembers ? 'clustered' : 'balanced',
        memberKeys: orderedBuckets.map((bucket) => bucket.key),
        memberCounts: Object.fromEntries(
          orderedBuckets.map((bucket) => [bucket.key, bucket.entries.length])
        ),
        unattestedCount: unattested.length,
        totalCount: balanced.length,
        complete: true
      };
      return true;
    }

    refreshMappedSearchTierDescriptors(manifest) {
      if (!manifest || this.engineMode !== 'v4_metafield') return [];
      const entries = Array.isArray(manifest.progressiveEntries)
        ? manifest.progressiveEntries
        : (manifest.entries || []);
      const descriptors = this.mappedSearchTierGroups(manifest.sources || []).map((group) => {
        const failedStates = group.states.filter((state) => state.failed === true);
        const complete = !failedStates.length && group.states.every((state) => {
          return state.exhausted === true && state.hasMore !== true;
        });
        const started = group.states.some((state) => Number(state.page || 0) > 0);
        const tierEntries = entries.filter((entry) => entry?.tierKey === group.key);
        const sourceIndexes = group.states.map((state) => Number(state.sourceIndex));
        const groupIds = Array.from(new Set(group.states.map((state) => {
          return this.mappedSearchSourceTierMetadata(state).groupId;
        }).filter(Boolean)));
        return {
          key: group.key,
          order: group.order,
          label: group.label,
          kind: group.kind,
          scope: group.scope,
          groupIds,
          sourceIndexes,
          sourceCount: group.states.length,
          terminalSourceCount: group.states.filter((state) => {
            return state.exhausted === true && state.hasMore !== true;
          }).length,
          failedSourceCount: failedStates.length,
          declaredCounts: group.states.map((state) => {
            return Math.max(0, Number(state.declaredCount || 0));
          }),
          rawMatchedCount: group.states.reduce((total, state) => {
            return total + Math.max(0, Number(state.rawMatchedCount || 0));
          }, 0),
          acceptedUniqueCount: tierEntries.length,
          duplicateCount: group.states.reduce((total, state) => {
            return total + Math.max(0, Number(state.duplicateCount || 0));
          }, 0),
          memberCounts: tierEntries.reduce((counts, entry) => {
            const key = String(entry?.tierMemberKey || '').trim();
            if (key) counts[key] = Math.max(0, Number(counts[key]) || 0) + 1;
            return counts;
          }, {}),
          unattestedMemberCount: tierEntries.filter((entry) => {
            return !String(entry?.tierMemberKey || '').trim();
          }).length,
          pagesFetched: group.states.reduce((total, state) => {
            return total + Math.max(0, Number(state.page || 0));
          }, 0),
          status: failedStates.length ? 'failed' : (complete ? 'complete' : (started ? 'active' : 'pending')),
          complete
        };
      });
      const descriptorByKey = new Map(descriptors.map((descriptor) => [descriptor.key, descriptor]));
      const boundaryByKey = new Map();
      entries.forEach((entry, index) => {
        const key = String(entry?.tierKey || '');
        if (!key) return;
        let boundary = boundaryByKey.get(key);
        if (!boundary) {
          const descriptor = descriptorByKey.get(key) || {};
          boundary = {
            key,
            order: Number(entry?.tierOrder ?? descriptor.order ?? 0),
            label: String(entry?.tierLabel || descriptor.label || ''),
            kind: String(entry?.tierKind || descriptor.kind || ''),
            scope: String(entry?.tierScope || descriptor.scope || ''),
            startIndex: index,
            endIndex: index,
            count: 0
          };
          boundaryByKey.set(key, boundary);
        }
        boundary.endIndex = index;
        boundary.count += 1;
      });
      manifest.tierDescriptors = descriptors;
      manifest.tierBoundaries = Array.from(boundaryByKey.values());
      return descriptors;
    }

    updateMappedSearchStatePageLimit(state, result, fallbackMax = 24) {
      const reportedPages = Math.max(0, Number(result?.totalPages || 0));
      if (
        !state
        || reportedPages < 1
        || (
          result?.totalPagesKnown !== true
          && result?.hasMore === true
        )
      ) return Number(state?.maxPages || fallbackMax);
      const sourceLimit = this.mappedSearchStateMaxPages(state, fallbackMax);
      state.maxPages = Math.max(
        1,
        Math.min(sourceLimit, reportedPages)
      );
      return state.maxPages;
    }

    mappedSearchCloseTarget(engine, query, analysis) {
      if (Array.from(new URLSearchParams(window.location.search).keys()).some((key) => key.startsWith('filter.'))) {
        return 0;
      }
      const copy = this.root.querySelector('[data-vcc-search-masthead-copy]');
      const existing = Number(copy?.dataset.closeMatchCount);
      if (Number.isFinite(existing) && existing > 0) return existing;
      const broadTotal = Number(copy?.dataset.broadCount);
      if (
        !Number.isFinite(broadTotal)
        || broadTotal <= 0
        || !analysis
        || typeof engine?.estimateCloseMatches !== 'function'
      ) return 0;

      try {
        const result = engine.estimateCloseMatches(analysis, broadTotal, null);
        return Math.max(0, Number(result?.count) || 0);
      } catch (_error) {
        return 0;
      }
    }

    createMappedSearchManifest(analysis, query, grid, closeTarget = 0, engine = null) {
      const pageSize = this.searchPageSize();
      const limits = this.mappedSearchLimits(pageSize);
      delete this.root.dataset.searchZeroFocusTerminalPromoted;
      delete this.root.dataset.searchPromotedTerminalHandles;
      const existingCards = new Map();
      Array.from(grid.querySelectorAll('.vcc-ac-card')).forEach((card) => {
        const key = this.searchProductKey({
          id: card.dataset.productId,
          handle: card.dataset.productHandle,
          url: card.dataset.productUrl
        });
        if (key) existingCards.set(key, card);
      });

      const manifest = {
        key: this.mappedSearchManifestKey(query),
        query,
        pageSize,
        limits,
         closeTarget: Math.max(0, Number(closeTarget) || 0),
         entries: [],
         baseEntries: null,
         baseCloseCount: 0,
        broadEntryCount: 0,
        broadSettledCount: 0,
        relationshipSettled: true,
         outcome: 'pending',
         exactEntries: [],
         focusedEntries: [],
         broadEntries: [],
         broadLoaded: false,
         broadExpanded: false,
         broadHasMore: false,
         broadError: false,
         zeroFocusTerminalPromoted: false,
         promotedTerminalHandles: [],
         promotedTerminalSourceKeys: [],
         promotedTerminalHasMore: false,
         viewKey: '',
        analysis,
        productKeys: new Set(),
        failedSourceKeys: new Set(),
        transportUnprovenSourceKeys: new Set(),
        existingCards,
         sources: this.mappedSearchSources(analysis, query).map((source, sourceIndex) => ({
           source,
           sourceIndex,
           scope: this.mappedSearchSourceScope(source),
           page: 0,
           acceptedCount: 0,
           rawMatchedCount: 0,
           duplicateCount: 0,
           declaredCount: Math.max(
             0,
             Number(source?.relationshipProductCount || 0)
           ),
           terminalReason: '',
           exhausted: false,
           hasMore: true,
           failed: false,
           maxPages: this.mappedSearchStateMaxPages({ source }, limits.maxSourcePages)
        })),
        sealed: false,
        capped: false,
        hasMore: false,
        cancelled: false,
        exactPageOneSettled: false,
        corePageOneSettled: false,
        safeExactFallbackBlocked: false,
        explicitSafeEmpty: false
      };

      const safeExactState = manifest.sources.find((state) => state.source.exact && state.source.safeExact);
      if (safeExactState && engine && !safeExactState.source.trustedTransport) {
        const seededProducts = [];
        existingCards.forEach((card) => {
          const product = this.productFromSearchCard(card, safeExactState.source);
          const key = this.searchProductKey(product);
          if (!key || product.available === false) return;
          if (!this.productMatchesMappedAttributes(product, analysis, safeExactState.source, engine)) return;
          seededProducts.push({
            key,
            product,
            cardHtml: card.outerHTML,
            score: engine.productScore(product, analysis, safeExactState.source),
            sourceKind: safeExactState.source.kind || 'exact'
          });
        });
        this.appendMappedSearchEntries(manifest, safeExactState, {
          products: seededProducts,
          hasMore: false
        });
        manifest.nativeExactSeedCount = seededProducts.length;
      }

      this.refreshMappedSearchTierDescriptors(manifest);

      return manifest;
    }

    appendMappedSearchEntries(manifest, sourceState, sourceResult) {
      const targetEntries = Array.isArray(manifest.progressiveEntries)
        ? manifest.progressiveEntries
        : manifest.entries;
      const previousEntryCount = targetEntries.length;
      const tierMetadata = this.engineMode === 'v4_metafield'
        ? this.mappedSearchSourceTierMetadata(sourceState)
        : null;
      const orderedProducts = sourceState?.source?.preserveTransportOrder === true
        ? sourceResult.products.slice()
        : sourceResult.products.slice().sort((left, right) => {
          const leftRank = Number(left?.product?.v4AttestationRank);
          const rightRank = Number(right?.product?.v4AttestationRank);
          const safeLeftRank = Number.isFinite(leftRank) ? leftRank : Number.MAX_SAFE_INTEGER;
          const safeRightRank = Number.isFinite(rightRank) ? rightRank : Number.MAX_SAFE_INTEGER;
          return safeLeftRank - safeRightRank
            || Number(right.score || 0) - Number(left.score || 0)
            || String(left.key || '').localeCompare(String(right.key || ''));
        });
      const sourceTeamFilter = Array.isArray(sourceState?.source?.v4Filters)
        ? sourceState.source.v4Filters.find((filter) => {
          return /^team:[a-z0-9][a-z0-9-]*$/.test(String(filter || '').trim().toLowerCase());
        })
        : '';
      const sourceEntityBlockTarget = String(
        sourceState?.source?.entityBlockTarget || ''
      ).trim().toLowerCase();
      let duplicateCount = 0;
      let cappedCount = 0;
      orderedProducts.forEach((entry) => {
          if (targetEntries.length >= manifest.limits.maxItems) {
            manifest.capped = true;
            cappedCount += 1;
            return;
          }
          if (!entry.key || manifest.productKeys.has(entry.key)) {
            if (entry.key) duplicateCount += 1;
            return;
          }
          const existingCard = manifest.existingCards.get(entry.key) || null;
          manifest.productKeys.add(entry.key);
          targetEntries.push(Object.assign({}, entry, {
            card: existingCard,
            rank: targetEntries.length,
            sourceIndex: sourceState.sourceIndex,
            ...(tierMetadata ? {
              tierKey: tierMetadata.key,
              tierOrder: tierMetadata.order,
              tierLabel: tierMetadata.label,
              tierKind: tierMetadata.kind,
              tierGroupId: tierMetadata.groupId,
              tierScope: tierMetadata.scope,
              tierMemberKey: sourceEntityBlockTarget
                || String(sourceTeamFilter || '').trim().toLowerCase()
                || this.v4MappedSearchProductTeamKey(entry.product, sourceState?.source),
              resultSectionKey: String(sourceState?.source?.resultSectionKey || ''),
              resultSectionLabel: String(sourceState?.source?.resultSectionLabel || ''),
              resultSectionKind: String(sourceState?.source?.resultSectionKind || ''),
              resultSectionOrder: Math.max(
                0,
                Number(sourceState?.source?.resultSectionOrder) || 0
              )
            } : {})
          }));
        });
      if (this.engineMode === 'v4_metafield' && sourceState) {
        sourceState.rawMatchedCount = Math.max(0, Number(sourceState.rawMatchedCount) || 0)
          + orderedProducts.length;
        sourceState.duplicateCount = Math.max(0, Number(sourceState.duplicateCount) || 0)
          + duplicateCount;
        sourceState.cappedCount = Math.max(0, Number(sourceState.cappedCount) || 0)
          + cappedCount;
      }
      if (!sourceState.source.exact) {
        manifest.broadEntryCount += Math.max(0, targetEntries.length - previousEntryCount);
      }
      return Math.max(0, targetEntries.length - previousEntryCount);
    }

    relationshipTransportUnits(states) {
      const orderedBuckets = [];
      const bucketByKey = new Map();
      (states || []).forEach((state, stateIndex) => {
        const source = state?.source;
        const groupId = String(source?.relationshipGroupId || '').trim();
        const hasTerms = Array.isArray(source?.matchTerms) && source.matchTerms.some(Boolean);
        if (!groupId || !hasTerms) {
          orderedBuckets.push({ key: `state-${stateIndex}`, states: [state] });
          return;
        }
        const key = `${groupId}::${Number(source?.tierOrder || 40)}`;
        let bucket = bucketByKey.get(key);
        if (!bucket) {
          bucket = { key, states: [] };
          bucketByKey.set(key, bucket);
          orderedBuckets.push(bucket);
        }
        bucket.states.push(state);
      });

      const units = [];
      orderedBuckets.forEach((bucket) => {
        let activeStates = [];
        let activeProductCount = 0;
        const flush = () => {
          if (activeStates.length) units.push(activeStates);
          activeStates = [];
          activeProductCount = 0;
        };
        bucket.states.forEach((state) => {
          const estimatedCount = Math.max(
            1,
            Number(state?.source?.relationshipProductCount || 0) || 18
          );
          if (
            activeStates.length
            && (activeStates.length >= 3 || activeProductCount + estimatedCount > 45)
          ) {
            flush();
          }
          activeStates.push(state);
          activeProductCount += estimatedCount;
        });
        flush();
      });
      return units;
    }

    relationshipCompositeSource(states, analysis) {
      if (!Array.isArray(states) || states.length < 2) return null;
      if (states.some((state) => {
        const source = state?.source;
        const transport = String(source?.transportQuery || '').trim();
        return Boolean(
          source?.collectionHandle
          || (
            transport
            && this.normalizeSearchPhrase(transport)
              !== this.normalizeSearchPhrase(source?.query)
          )
        );
      })) return null;
      const primaryType = analysis?.types?.[0]?.label
        || analysis?.types?.[0]?.name
        || '';
      const concepts = states.map((state) => {
        return this.relationshipCompositeConcept(state?.source, primaryType);
      }).filter(Boolean);
      if (concepts.length !== states.length || new Set(concepts).size !== states.length) return null;
      const quotedConcepts = concepts.map((concept) => {
        const escaped = String(concept).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      const sources = states.map((state) => state.source);
      const matchTerms = Array.from(new Set(sources.flatMap((source) => {
        return Array.isArray(source?.matchTerms) ? source.matchTerms : [];
      }).map((term) => String(term || '').trim()).filter(Boolean)));
      const compositeQuery = `(${quotedConcepts.join(' OR ')})${primaryType ? ` ${primaryType}` : ''}`;
      return Object.assign({}, sources[0], {
        query: compositeQuery,
        transportQuery: compositeQuery,
        matchTerms,
        weight: Math.max(...sources.map((source) => Number(source?.weight || 0))),
        relationshipComposite: true,
        relationshipMembers: sources.map((source) => Object.assign({}, source, {
          matchTerms: Array.isArray(source?.matchTerms) ? source.matchTerms.slice() : []
        }))
      });
    }

    async fetchMappedRelationshipUnit(states, page, engine, analysis, options = {}) {
      if (!Array.isArray(states) || !states.length) return [];
      if (states.length === 1) {
        const state = states[0];
        const result = await this.fetchMappedSearchPageWithRetry(state.source, page, engine, analysis, options)
          .catch((error) => {
            this.log('Mapped relationship source skipped', state.source, error);
            return {
              products: [],
              hasMore: false,
              failed: true,
              transportUnproven: state.source?.v4Metafield === true,
              failureReason: 'relationship-transport-error'
            };
          });
        return [{ state, result }];
      }

      const compositeSource = this.relationshipCompositeSource(states, analysis);
      if (!compositeSource) {
        const results = await Promise.all(states.map((state) => {
          return this.fetchMappedSearchPageWithRetry(state.source, page, engine, analysis, options)
            .catch((error) => {
              this.log('Mapped relationship source skipped', state.source, error);
              return {
                products: [],
                hasMore: false,
                failed: true,
                transportUnproven: state.source?.v4Metafield === true,
                failureReason: 'relationship-transport-error'
              };
            });
        }));
        return states.map((state, index) => ({ state, result: results[index] }));
      }

      const compositeResult = await this.fetchMappedSearchPageWithRetry(
        compositeSource,
        page,
        engine,
        analysis,
        options
      ).catch((error) => {
        this.log('Mapped relationship group skipped', compositeSource, error);
        return {
          products: [],
          hasMore: false,
          failed: true,
          transportUnproven: compositeSource?.v4Metafield === true,
          failureReason: 'relationship-composite-transport-error'
        };
      });
      if (compositeResult.failed) {
        return states.map((state) => ({
          state,
          result: {
            products: [],
            hasMore: false,
            failed: true,
            requiresIndividualFallback: true,
            transportUnproven: compositeResult.transportUnproven === true,
            failureReason: compositeResult.failureReason || 'relationship-composite-unproven'
          }
        }));
      }
      if (compositeResult.hasMore) {
        const midpoint = Math.ceil(states.length / 2);
        const [left, right] = await Promise.all([
          this.fetchMappedRelationshipUnit(states.slice(0, midpoint), page, engine, analysis, options),
          this.fetchMappedRelationshipUnit(states.slice(midpoint), page, engine, analysis, options)
        ]);
        return left.concat(right);
      }

      return states.map((state) => {
        const products = compositeResult.products
          .filter((entry) => {
            return this.productMatchesMappedAttributes(entry.product, analysis, state.source, engine);
          })
          .map((entry) => Object.assign({}, entry, {
            score: engine.productScore(entry.product, analysis, state.source),
            relationshipOrder: undefined,
            sourceKind: state.source.kind || ''
          }));
        return {
          state,
          result: {
            products,
            hasMore: false,
            failed: false
          }
        };
      });
    }

    async fetchMappedRelationshipRound(manifest, states, page, engine, analysis, onProgress, options = {}) {
      if (!states.length) {
        if (typeof onProgress === 'function') onProgress(manifest);
        return true;
      }
      const units = this.relationshipTransportUnits(states);
      const settledPairs = [];
      const work = Promise.all(units.map((unit) => {
        return this.fetchMappedRelationshipUnit(unit, page, engine, analysis, options)
          .then((pairs) => {
            settledPairs.push(...pairs);
            return pairs;
          });
      }));
      const deadlineReserveMs = Math.max(0, Number(options.deadlineReserveMs) || 0);
      let settled = true;
      let cutoffTimer = null;
      const remainingMs = this.searchCurationRemaining();
      if (deadlineReserveMs > 0 && Number.isFinite(remainingMs)) {
        const settleBudgetMs = Math.max(0, remainingMs - deadlineReserveMs);
        if (settleBudgetMs <= 0) {
          settled = false;
        } else {
          settled = await Promise.race([
            work.then(() => true),
            new Promise((resolve) => {
              cutoffTimer = window.setTimeout(() => resolve(false), settleBudgetMs);
            })
          ]);
        }
        window.clearTimeout(cutoffTimer);
      } else {
        await work;
      }
      if (!settled) {
        const settledResultByState = new Map(
          settledPairs.map((pair) => [pair.state, pair.result])
        );
        states.forEach((state) => {
          if (!settledResultByState.has(state) || settledResultByState.get(state)?.failed) {
            const result = settledResultByState.get(state) || {
              transportUnproven: state.source?.v4Metafield === true,
              failureReason: 'relationship-round-deadline'
            };
            this.recordMappedSearchSourceFailure(manifest, state, page, result);
          }
        });
        this.searchCurationAbortController?.abort();
        this.searchCurationAbortController = null;
        return false;
      }

      const resultByState = new Map(settledPairs.map((pair) => [pair.state, pair.result]));
      const retryStates = states.filter((state) => {
        return !resultByState.has(state) || resultByState.get(state)?.failed;
      });
      const optionalRetryStates = retryStates.filter((state) => {
        return resultByState.get(state)?.requiresIndividualFallback === true
          || !this.mappedSearchUsesRequiredRetry(state.source, page, options, analysis);
      });
      if (optionalRetryStates.length && this.searchCurationRemaining() > deadlineReserveMs + 450) {
        const retryResults = await Promise.all(optionalRetryStates.map((state) => {
          return this.fetchMappedSearchPage(
            state.source,
            page,
            engine,
            analysis,
            Object.assign({}, options, {
              signal: this.searchCurationAbortController?.signal
            })
          ).catch(() => ({
            products: [],
            hasMore: false,
            failed: true,
            transportUnproven: state.source?.v4Metafield === true,
            failureReason: 'relationship-retry-transport-error'
          }));
        }));
        optionalRetryStates.forEach((state, index) => {
          resultByState.set(state, retryResults[index]);
        });
      }
      if (states.some((state) => !resultByState.has(state) || resultByState.get(state)?.failed)) {
        states.forEach((state) => {
          if (!resultByState.has(state) || resultByState.get(state)?.failed) {
            const result = resultByState.get(state) || {
              transportUnproven: state.source?.v4Metafield === true,
              failureReason: 'relationship-result-missing'
            };
            this.recordMappedSearchSourceFailure(manifest, state, page, result);
            state.failed = true;
            state.exhausted = true;
          }
        });
        return false;
      }

      for (const state of states) {
        if (manifest.cancelled || manifest.capped) break;
        const sourceResult = resultByState.get(state);
        const acceptedCount = this.appendMappedSearchEntries(manifest, state, sourceResult);
        state.acceptedCount = Math.max(0, Number(state.acceptedCount) || 0)
          + acceptedCount;
        manifest.broadSettledCount += 1;
        state.page = page;
        this.updateMappedSearchStatePageLimit(
          state,
          sourceResult,
          manifest.limits.maxSourcePages
        );
        const declaredCount = Math.max(
          0,
          Number(state.source?.relationshipProductCount || 0)
        );
        const declaredCountFloor = declaredCount - Math.min(
          2,
          Math.floor(declaredCount * 0.08)
        );
        const declaredCountReached = this.engineMode !== 'v4_metafield'
          && declaredCount > 0
          && state.acceptedCount >= declaredCountFloor;
        state.hasMore = Boolean(sourceResult.hasMore) && !declaredCountReached;
        state.exhausted = !state.hasMore;
        if (typeof onProgress === 'function') onProgress(manifest);
      }
      return !manifest.cancelled && !manifest.capped;
    }

    async fetchMappedRelationshipContinuations(
      manifest,
      states,
      maxPage,
      engine,
      analysis,
      onProgress,
      options = {}
    ) {
      const pending = (states || []).filter((state) => state.hasMore && !state.exhausted);
      if (!pending.length || maxPage < 2) return true;
      const resultsByState = new Map(pending.map((state) => [state, new Map()]));
      const deadlineReserveMs = Math.max(0, Number(options.deadlineReserveMs) || 0);
      const requestOptions = Object.assign({}, options, {
        signal: options.signal || this.searchCurationAbortController?.signal
      });
      const requestPaceMs = Math.max(0, Number(options.requestPaceMs) || 0);
      const serialRequests = options.serialRequests === true;
      const requestGeneration = this.searchCurationGeneration;
      let lastRequestStartedAt = Math.max(0, Number(options.lastRequestStartedAt) || 0);
      const fetchPages = async (targetStates, pages) => {
        const jobs = targetStates.flatMap((state) => {
          const stateMaxPages = Math.max(
            1,
            Number(state?.maxPages || this.mappedSearchStateMaxPages(state, maxPage))
          );
          return pages
            .filter((page) => page <= stateMaxPages)
            .map((page) => ({ state, page }));
        });
        const runJob = async ({ state, page }) => {
          const result = await this.fetchMappedSearchPageWithRetry(
              state.source,
              page,
              engine,
              analysis,
              requestOptions
            )
            .catch(() => ({
              products: [],
              hasMore: false,
              failed: true,
              transportUnproven: state.source?.v4Metafield === true,
              failureReason: 'relationship-continuation-transport-error'
            }));
          resultsByState.get(state).set(page, result);
          return result;
        };
        if (!serialRequests) return Promise.all(jobs.map(runJob));

        const serialResults = [];
        for (const job of jobs) {
          const elapsedSinceRequest = Date.now() - lastRequestStartedAt;
          const paceWaitMs = lastRequestStartedAt
            ? Math.max(0, requestPaceMs - elapsedSinceRequest)
            : 0;
          if (
            paceWaitMs
            && !await this.waitForMappedSearchRetry(paceWaitMs, {
              signal: requestOptions.signal,
              generation: requestGeneration,
              ignoreDeadline: options.background === true
            })
          ) break;
          if (
            requestOptions.signal?.aborted
            || requestGeneration !== this.searchCurationGeneration
            || this.destroyed
            || !this.root.isConnected
          ) break;
          lastRequestStartedAt = Date.now();
          serialResults.push(await runJob(job));
        }
        return serialResults;
      };
      const settleWork = async (startWork) => {
        const remainingMs = this.searchCurationRemaining();
        if (deadlineReserveMs <= 0 || !Number.isFinite(remainingMs)) {
          await startWork();
          return true;
        }
        const settleBudgetMs = Math.max(0, remainingMs - deadlineReserveMs);
        if (settleBudgetMs <= 0) return false;
        const work = startWork();
        let cutoffTimer = null;
        const settled = await Promise.race([
          work.then(() => true),
          new Promise((resolve) => {
            cutoffTimer = window.setTimeout(() => resolve(false), settleBudgetMs);
          })
        ]);
        window.clearTimeout(cutoffTimer);
        return settled;
      };
      const applyPages = (targetStates, pages, sourceResults = resultsByState) => {
        for (const page of pages) {
          if (manifest.cancelled || manifest.capped) return false;
          const activeStates = targetStates.filter((state) => !state.exhausted);
          const pageResults = activeStates.map((state) => {
            return {
              state,
              result: sourceResults.get(state)?.get(page)
            };
          });
          const failedPairs = pageResults.filter((pair) => !pair.result || pair.result.failed);
          if (failedPairs.length) {
            failedPairs.forEach((pair) => {
              this.recordMappedSearchSourceFailure(
                manifest,
                pair.state,
                page,
                pair.result || {
                  transportUnproven: pair.state.source?.v4Metafield === true,
                  failureReason: 'relationship-continuation-missing'
                }
              );
            });
            return false;
          }
          for (const pair of pageResults) {
            const state = pair.state;
            const result = pair.result;
            const acceptedCount = this.appendMappedSearchEntries(manifest, state, result);
            state.acceptedCount = Math.max(0, Number(state.acceptedCount) || 0)
              + acceptedCount;
            if (options.countAsBroad !== false) manifest.broadSettledCount += 1;
            state.page = page;
            this.updateMappedSearchStatePageLimit(
              state,
              result,
              manifest.limits.maxSourcePages
            );
            const declaredCount = Math.max(
              0,
              Number(state.source?.relationshipProductCount || 0)
            );
            const declaredCountFloor = declaredCount - Math.min(
              2,
              Math.floor(declaredCount * 0.08)
            );
            const declaredCountReached = this.engineMode !== 'v4_metafield'
              && declaredCount > 0
              && state.acceptedCount >= declaredCountFloor;
            state.hasMore = Boolean(result.hasMore) && !declaredCountReached;
            state.exhausted = !state.hasMore;
            if (typeof onProgress === 'function') onProgress(manifest);
          }
          if (
            typeof options.stopWhen === 'function'
            && options.stopWhen(manifest, null, page)
          ) return true;
        }
        return true;
      };

      const continuationPages = [];
      for (let page = 2; page <= maxPage; page += 1) continuationPages.push(page);
      const eligibleForPage = (state, page) => {
        return page <= Math.max(
          1,
          Number(state?.maxPages || this.mappedSearchStateMaxPages(state, maxPage))
        );
      };
      const requestedWindowSize = Math.max(
        1,
        Number(options.pageWindowSize || continuationPages.length || 1)
      );
      for (
        let pageOffset = 0;
        pageOffset < continuationPages.length;
        pageOffset += requestedWindowSize
      ) {
        const windowPages = continuationPages.slice(
          pageOffset,
          pageOffset + requestedWindowSize
        );
        const windowStates = pending.filter((state) => {
          return state.hasMore
            && !state.exhausted
            && windowPages.some((page) => eligibleForPage(state, page));
        });
        if (!windowStates.length) continue;
        if (
          deadlineReserveMs > 0
          && this.searchCurationRemaining() <= deadlineReserveMs
        ) return false;
        const windowSettled = await settleWork(() => {
          return fetchPages(windowStates, windowPages);
        });
        const frozenResults = new Map(windowStates.map((state) => {
          return [state, new Map(resultsByState.get(state) || [])];
        }));
        let verifiedPrefixSettled = true;
        for (const page of windowPages) {
          const pageStates = windowStates.filter((state) => eligibleForPage(state, page));
          if (!pageStates.length) continue;
          if (!applyPages(pageStates, [page], frozenResults)) {
            verifiedPrefixSettled = false;
            break;
          }
          if (
            typeof options.stopWhen === 'function'
            && options.stopWhen(manifest)
          ) break;
        }
        if (!windowSettled) {
          this.searchCurationAbortController?.abort();
          this.searchCurationAbortController = null;
          const verifiedTargetReached = typeof options.stopWhen === 'function'
            && options.stopWhen(manifest);
          const verifiedSourcesExhausted = pending.every((state) => {
            return state.exhausted || !state.hasMore;
          });
          if (
            verifiedPrefixSettled
            && (verifiedTargetReached || verifiedSourcesExhausted)
            && !manifest.cancelled
            && !manifest.capped
          ) {
            this.searchCurationAbortController = new AbortController();
            return true;
          }
        }
        if (!windowSettled || !verifiedPrefixSettled) return false;
        if (
          typeof options.stopWhen === 'function'
          && options.stopWhen(manifest)
        ) break;
      }
      return !manifest.cancelled && !manifest.capped;
    }

    async fetchMappedSearchStrictTiers(
      manifest,
      states,
      maxPage,
      engine,
      analysis,
      onProgress,
      options = {}
    ) {
      const tiers = this.mappedSearchTierGroups(states);
      manifest.strictTierOrder = tiers.map((tier) => ({
        key: tier.key,
        order: tier.order,
        label: tier.label,
        kind: tier.kind,
        scope: tier.scope,
        sourceIndexes: tier.states.map((state) => Number(state.sourceIndex))
      }));
      manifest.completedStrictTierCount = 0;
      manifest.strictTierFailure = null;

      const failTier = (tier, reason, page = 0) => {
        const normalizedReason = String(reason || 'tier-incomplete');
        tier.states.forEach((state) => {
          if (!state.terminalReason && (state.failed || state.hasMore || !state.exhausted)) {
            state.terminalReason = normalizedReason;
          }
        });
        manifest.strictTierFailure = {
          key: tier.key,
          order: tier.order,
          label: tier.label,
          reason: normalizedReason,
          page: Math.max(0, Number(page) || 0),
          sourceIndexes: tier.states.map((state) => Number(state.sourceIndex))
        };
        manifest.activeStrictTierKey = tier.key;
        this.refreshMappedSearchTierDescriptors(manifest);
        return false;
      };

      for (let tierIndex = 0; tierIndex < tiers.length; tierIndex += 1) {
        const tier = tiers[tierIndex];
        manifest.activeStrictTierKey = tier.key;
        this.refreshMappedSearchTierDescriptors(manifest);

        for (let page = 1; page <= maxPage; page += 1) {
          if (manifest.cancelled) return failTier(tier, 'cancelled', page);
          if (manifest.capped) return failTier(tier, 'manifest-cap', page);

          const activeStates = tier.states.filter((state) => {
            const stateMaxPages = Math.max(
              1,
              Number(state?.maxPages || maxPage)
            );
            return page <= stateMaxPages
              && !state.failed
              && !state.exhausted
              && (page === 1 || state.hasMore === true);
          });
          if (!activeStates.length) break;

          // Admit each completed page round in source order, while allowing the
          // V4 transport queue to run up to its hard two-request concurrency.
          // Progressive rendering changes the abort/deadline policy only; it
          // must not serialize every relationship member or add a fixed delay.
          const progressiveRound = manifest.progressiveCommitted === true;
          const roundSettled = await this.fetchMappedSearchRound(
            manifest,
            activeStates,
            page,
            engine,
            analysis,
            onProgress,
            progressiveRound
              ? {
                deadlineReserveMs: 0,
                background: true,
                signal: this.v4ProgressiveAbortController?.signal
              }
              : {
                deadlineReserveMs: Math.max(
                  0,
                  Number(options.deadlineReserveMs) || 0
                ),
                signal: this.searchCurationAbortController?.signal
              }
          );

          if (
            tierIndex === 0
            && page === 1
            && tier.states.every((state) => {
              return Number(state.page || 0) >= 1 && state.failed !== true;
            })
          ) {
            // For strict V4 this means the highest relationship tier has a
            // verified page-one prefix. Lower tiers are still forbidden until
            // this entire tier is terminal.
            manifest.corePageOneSettled = true;
          }
          this.refreshMappedSearchTierDescriptors(manifest);

          if (!roundSettled) {
            return failTier(tier, manifest.capped ? 'manifest-cap' : 'source-failure', page);
          }

          if (typeof options.onFrontierReady === 'function') {
            await options.onFrontierReady(manifest, tier, page);
          }
        }

        const incompleteStates = tier.states.filter((state) => {
          return state.failed === true || state.hasMore === true || state.exhausted !== true;
        });
        if (incompleteStates.length) {
          const pageCapReached = incompleteStates.some((state) => {
            return state.hasMore === true
              && Number(state.page || 0) >= Math.max(1, Number(state.maxPages || maxPage));
          });
          return failTier(tier, pageCapReached ? 'source-page-cap' : 'tier-incomplete');
        }

        tier.states.forEach((state) => {
          if (!state.terminalReason) state.terminalReason = 'server-exhausted';
        });
        // The complete tier is now immutable. Balance attested team members
        // deterministically without changing its flattened handle set.
        this.rebalanceMappedSearchTierEntries(manifest, tier);
        manifest.completedStrictTierCount = tierIndex + 1;
        manifest.progressiveSettledEntries = (
          Array.isArray(manifest.progressiveEntries)
            ? manifest.progressiveEntries
            : manifest.entries
        ).slice();
        this.refreshMappedSearchTierDescriptors(manifest);
        if (typeof options.onTierSettled === 'function') {
          await options.onTierSettled(manifest, tier, tierIndex);
        }
      }

      manifest.activeStrictTierKey = '';
      this.refreshMappedSearchTierDescriptors(manifest);
      return !manifest.cancelled && !manifest.capped;
    }

    async fetchMappedSearchRound(manifest, states, page, engine, analysis, onProgress, options = {}) {
      const notify = () => {
        if (typeof onProgress === 'function') onProgress(manifest);
      };
      const deadlineReserveMs = Math.max(0, Number(options.deadlineReserveMs) || 0);
      let allSettled = true;

      for (let index = 0; index < states.length; index += manifest.limits.batchSize) {
        if (manifest.cancelled || manifest.capped) break;
        const batch = states
          .slice(index, index + manifest.limits.batchSize)
          .filter((state) => !state.exhausted);
        const settledResults = new Array(batch.length);
        const batchWork = Promise.all(batch.map((state, offset) => {
          return this.fetchMappedSearchPageWithRetry(state.source, page, engine, analysis, options)
            .catch((error) => {
              this.log('Mapped search source skipped', state.source, error);
              return {
                products: [],
                hasMore: false,
                failed: true,
                transportUnproven: state.source?.v4Metafield === true,
                failureReason: 'mapped-source-transport-error'
              };
            })
            .then((result) => {
              settledResults[offset] = result;
              return result;
            });
        }));
        let batchSettled = true;
        let cutoffTimer = null;
        const remainingMs = this.searchCurationRemaining();
        if (deadlineReserveMs > 0 && Number.isFinite(remainingMs)) {
          const settleBudgetMs = Math.max(0, remainingMs - deadlineReserveMs);
          if (settleBudgetMs <= 0) {
            batchSettled = false;
          } else {
            batchSettled = await Promise.race([
              batchWork.then(() => true),
              new Promise((resolve) => {
                cutoffTimer = window.setTimeout(() => resolve(false), settleBudgetMs);
              })
            ]);
          }
          window.clearTimeout(cutoffTimer);
        } else {
          await batchWork;
        }
        if (!batchSettled) {
          allSettled = false;
          batch.forEach((state, offset) => {
            if (!settledResults[offset] || settledResults[offset]?.failed) {
              this.recordMappedSearchSourceFailure(
                manifest,
                state,
                page,
                settledResults[offset] || {
                  transportUnproven: state.source?.v4Metafield === true,
                  failureReason: 'mapped-source-round-deadline'
                }
              );
            }
          });
          this.searchCurationAbortController?.abort();
          this.searchCurationAbortController = null;
        }

        const acceptedOffsets = batch
          .map((_state, offset) => offset)
          .filter((offset) => batchSettled || Boolean(settledResults[offset]));
        for (const offset of acceptedOffsets) {
          if (manifest.cancelled || manifest.capped) break;
          const state = batch[offset];
          const sourceResult = settledResults[offset];
          if (!state || !sourceResult) continue;
          if (sourceResult.failed) {
            if (state.source?.optionalTerminalSource === true) {
              state.optionalFailure = true;
              state.failed = false;
              state.page = Math.max(Number(state.page) || 0, Number(page) || 1);
              state.hasMore = false;
              state.exhausted = true;
              state.terminalReason = 'optional-source-unavailable';
              notify();
              continue;
            }
            allSettled = false;
            this.recordMappedSearchSourceFailure(manifest, state, page, sourceResult);
            state.failed = true;
            state.exhausted = true;
            continue;
          }
          const acceptedCount = this.appendMappedSearchEntries(manifest, state, sourceResult);
          if (this.engineMode === 'v4_metafield') {
            state.acceptedCount = Math.max(0, Number(state.acceptedCount) || 0)
              + acceptedCount;
          }
          if (!state.source.exact) manifest.broadSettledCount += 1;
          state.page = page;
          this.updateMappedSearchStatePageLimit(
            state,
            sourceResult,
            manifest.limits.maxSourcePages
          );
          const boundedLimitReached = Boolean(
            sourceResult.hasMore
            && (
              state.source?.boundedTerminalSource === true
              || state.source?.optionalTerminalSource === true
            )
            && Number(page) >= Math.max(1, Number(state.maxPages) || 1)
          );
          state.hasMore = Boolean(sourceResult.hasMore) && !boundedLimitReached;
          state.exhausted = !state.hasMore;
          if (boundedLimitReached) state.terminalReason = 'bounded-source-limit';
          notify();
        }
        if (!batchSettled || !allSettled) break;
      }
      return allSettled && !manifest.capped;
    }

    async promoteMappedTerminalFallback(manifest, engine, analysis, broadStates) {
      if (
        !manifest
        || manifest.cancelled
        || manifest.entries.length
        || manifest.relationshipSettled !== true
      ) return false;

      const terminalStates = (broadStates || [])
        .filter((state) => this.mappedSearchDomainRootFallback(state?.source))
        .sort((left, right) => {
          return Number(left?.sourceIndex || 0) - Number(right?.sourceIndex || 0);
        });
      if (!terminalStates.length) return false;

      const pageSize = Math.max(1, Number(manifest.pageSize) || this.searchPageSize());
      const promotedItemBudget = pageSize * 2;
      for (const terminalState of terminalStates) {
        const scratchState = Object.assign({}, terminalState, {
          page: 0,
          acceptedCount: 0,
          exhausted: false,
          hasMore: true,
          failed: false,
          promotedToFocused: true,
          promotedTerminalFallback: true
        });
        const scratch = Object.assign({}, manifest, {
          entries: [],
          baseEntries: [],
          productKeys: new Set(),
          failedSourceKeys: new Set(),
          transportUnprovenSourceKeys: new Set(),
          sources: [scratchState],
          limits: Object.assign({}, manifest.limits, {
            maxItems: Math.min(
              Number(manifest.limits?.maxItems || Number.POSITIVE_INFINITY),
              promotedItemBudget
            )
          }),
          capped: false,
          cancelled: false,
          broadEntryCount: 0,
          broadSettledCount: 0
        });
        const maxPages = Math.min(
          Number(scratch.limits?.maxSourcePages || 1),
          Math.max(1, Number(scratchState.maxPages || 1))
        );
        let settled = true;

        for (let page = 1; page <= maxPages; page += 1) {
          if (
            scratch.cancelled
            || this.searchCurationIsFallbackLocked()
            || this.searchCurationRemaining() <= 180
          ) {
            settled = false;
            break;
          }
          if (page > 1 && (!scratchState.hasMore || scratchState.exhausted)) break;
          const roundSettled = await this.fetchMappedSearchRound(
            scratch,
            [scratchState],
            page,
            engine,
            analysis,
            null,
            {
              deadlineReserveMs: 180,
              signal: this.searchCurationAbortController?.signal
            }
          );
          if (!roundSettled && !scratch.capped) {
            settled = false;
            break;
          }
          if (scratch.entries.length >= scratch.limits.maxItems) {
            scratch.capped = true;
            break;
          }
        }

        if (
          !settled
          || scratch.cancelled
          || this.searchCurationIsFallbackLocked()
        ) return false;
        if (!scratch.entries.length) continue;

        terminalState.page = scratchState.page;
        terminalState.acceptedCount = scratch.entries.length;
        terminalState.exhausted = scratchState.exhausted;
        terminalState.hasMore = scratchState.hasMore;
        terminalState.failed = scratchState.failed;
        terminalState.maxPages = scratchState.maxPages;
        terminalState.promotedToFocused = true;
        terminalState.promotedTerminalFallback = true;
        terminalState.promotedAcceptedCount = scratch.entries.length;

        manifest.entries = scratch.entries.map((entry, index) => Object.assign({}, entry, {
          rank: index,
          promotedTerminalFallback: true
        }));
        manifest.productKeys = new Set(manifest.entries.map((entry) => entry.key).filter(Boolean));
        manifest.zeroFocusTerminalPromoted = true;
        manifest.promotedTerminalHandles = [
          String(terminalState?.source?.collectionHandle || '').trim()
        ].filter(Boolean);
        manifest.promotedTerminalSourceKeys = [
          this.searchSourceKey(terminalState?.source)
        ].filter(Boolean);
        manifest.promotedTerminalHasMore = Boolean(
          scratch.capped
          || (scratchState.hasMore && !scratchState.exhausted)
        );
        manifest.promotedTerminalAcceptedSourceIndexes = [
          Number(terminalState.sourceIndex)
        ].filter(Number.isFinite);
        manifest.closeCount = 0;
        manifest.baseCloseCount = 0;
        manifest.broadEntryCount = 0;
        manifest.broadSettledCount = 0;
        this.root.dataset.searchZeroFocusTerminalPromoted = 'true';
        this.root.dataset.searchPromotedTerminalHandles = manifest.promotedTerminalHandles.join('|');
        return true;
      }
      return false;
    }

    authoritativeTeamStateSectionKey(state) {
      return String(
        state?.source?.resultSectionKey
        || (state?.scope === 'exact' ? 'exact-team' : '')
      ).trim();
    }

    authoritativeTeamStateOrder(left, right) {
      const roleOrder = (state) => {
        const role = String(state?.source?.clauseRole || '').trim();
        if (role === 'strict-exact') return -20;
        if (role === 'same-team-fallback') return -10;
        return 0;
      };
      return Number(left?.source?.resultSectionOrder || 0)
        - Number(right?.source?.resultSectionOrder || 0)
        || roleOrder(left) - roleOrder(right)
        || Number(left?.source?.clauseIndex || 0) - Number(right?.source?.clauseIndex || 0)
        || Number(left?.sourceIndex || 0) - Number(right?.sourceIndex || 0);
    }

    authoritativeTeamExactEntries(manifest) {
      const exactSourceIndexes = new Set((manifest?.sources || [])
        .filter((state) => state?.scope === 'exact')
        .map((state) => Number(state.sourceIndex)));
      const entries = Array.isArray(manifest?.progressiveEntries)
        ? manifest.progressiveEntries
        : (manifest?.entries || []);
      return entries.filter((entry) => exactSourceIndexes.has(Number(entry?.sourceIndex)));
    }

    applyAuthoritativeTeamStateResult(manifest, state, page, result) {
      if (!state || result?.failed) {
        if (state) {
          this.recordMappedSearchSourceFailure(
            manifest,
            state,
            page,
            result || {
              transportUnproven: state.source?.v4Metafield === true,
              failureReason: 'authoritative-team-result-missing'
            }
          );
          state.failed = true;
          state.hasMore = false;
          state.exhausted = true;
          state.terminalReason = 'request-failed';
          state.deferred = false;
        }
        manifest.authoritativeTeamHadFailure = true;
        return { accepted: 0, failed: true };
      }
      const accepted = this.appendMappedSearchEntries(manifest, state, result);
      state.acceptedCount = Math.max(0, Number(state.acceptedCount) || 0) + accepted;
      state.page = Math.max(Number(state.page) || 0, Number(page) || 1);
      this.updateMappedSearchStatePageLimit(
        state,
        result,
        manifest.limits.maxSourcePages
      );
      const serverHasMore = Boolean(result.hasMore);
      const sourceLimitReached = serverHasMore
        && state.page >= Math.max(1, Number(state.maxPages || 1));
      state.hasMore = serverHasMore && !sourceLimitReached;
      state.exhausted = !state.hasMore;
      state.failed = false;
      state.deferred = false;
      state.terminalReason = sourceLimitReached
        ? 'source-page-limit'
        : (state.hasMore ? '' : 'server-exhausted');
      if (!state.source.exact && Number(page) === 1 && state.pageOneSettled !== true) {
        state.pageOneSettled = true;
        manifest.broadSettledCount += 1;
      }
      manifest.exactEntries = this.authoritativeTeamExactEntries(manifest).slice();
      return { accepted, failed: false };
    }

    authoritativeTeamSectionHasMore(manifest, sectionKey) {
      const key = String(sectionKey || '').trim();
      const states = (manifest?.sources || []).filter((state) => {
        return this.authoritativeTeamStateSectionKey(state) === key;
      });
      const initialRequestPending = states.some((state) => {
        return state.failed !== true
          && state.initialPrincipal === true
          && state.deferred !== true
          && Number(state.page || 0) < 1;
      });
      if (initialRequestPending) return false;
      return states.some((state) => {
        if (this.authoritativeTeamStateSectionKey(state) !== key || state.failed === true) return false;
        if (state.deferred === true && Number(state.page || 0) < 1) return true;
        return state.hasMore === true
          && state.exhausted !== true
          && Number(state.page || 0) < Math.max(1, Number(state.maxPages || 1));
      });
    }

    authoritativeTeamManifestHasMore(manifest) {
      const sectionKeys = new Set((manifest?.sources || [])
        .map((state) => this.authoritativeTeamStateSectionKey(state))
        .filter(Boolean));
      return Array.from(sectionKeys).some((key) => {
        return this.authoritativeTeamSectionHasMore(manifest, key);
      });
    }

    refreshAuthoritativeTeamContinuationState(manifest) {
      if (!manifest?.authoritativeTeamRoute) return false;
      const exactStates = (manifest.sources || []).filter((state) => state.scope === 'exact');
      const coreStates = (manifest.sources || []).filter((state) => state.scope === 'core');
      manifest.exactEntries = this.authoritativeTeamExactEntries(manifest).slice();
      manifest.baseCloseCount = manifest.exactEntries.length;
      manifest.closeCount = manifest.baseCloseCount;
      const hasMore = this.authoritativeTeamManifestHasMore(manifest);
      manifest.hasMore = hasMore;
      manifest.focusedHasMore = hasMore;
      manifest.viewHasMore = hasMore;
      manifest.exactTierSettled = exactStates.length > 0 && exactStates.every((state) => {
        return state.failed !== true && state.exhausted === true && state.hasMore !== true;
      });
      manifest.relationshipSettled = manifest.authoritativeTeamHadFailure !== true
        && coreStates.every((state) => {
          return state.deferred === true
            || Number(state.page || 0) >= 1;
        });
      manifest.authoritativeAllSectionsExhausted = !hasMore;
      this.root.dataset.v4AllSectionsExhausted = String(!hasMore);
      this.root.dataset.v4DeferredSourceCount = String((manifest.sources || []).filter((state) => {
        return state.deferred === true && Number(state.page || 0) < 1;
      }).length);
      return hasMore;
    }

    async loadAuthoritativeTeamSection(sectionKey) {
      const manifest = this.searchManifest;
      const key = String(sectionKey || '').trim();
      if (
        !key
        || !manifest?.sealed
        || manifest.authoritativeTeamRoute !== true
        || !this.isCurrentMappedManifest(manifest)
        || this.destroyed
      ) return false;
      manifest.authoritativeSectionLoads = manifest.authoritativeSectionLoads || new Map();
      if (manifest.authoritativeSectionLoads.has(key)) {
        return manifest.authoritativeSectionLoads.get(key);
      }
      const generation = this.searchCurationGeneration;
      const states = (manifest.sources || [])
        .filter((state) => this.authoritativeTeamStateSectionKey(state) === key)
        .sort((left, right) => this.authoritativeTeamStateOrder(left, right));
      if (states.some((state) => {
        return state.failed !== true
          && state.initialPrincipal === true
          && state.deferred !== true
          && Number(state.page || 0) < 1;
      })) return false;
      const deferred = states.filter((state) => {
        return state.failed !== true && state.deferred === true && Number(state.page || 0) < 1;
      });
      const pageable = states.filter((state) => {
        return state.failed !== true
          && state.deferred !== true
          && state.hasMore === true
          && state.exhausted !== true
          && Number(state.page || 0) < Math.max(1, Number(state.maxPages || 1));
      }).sort((left, right) => {
        return Number(left.page || 0) - Number(right.page || 0)
          || this.authoritativeTeamStateOrder(left, right);
      });
      const jobs = (deferred.length ? deferred : pageable).slice(0, 2);
      if (!jobs.length) {
        this.refreshAuthoritativeTeamContinuationState(manifest);
        this.renderMappedSearchNavigation(manifest, 1);
        return false;
      }
      const exactPrefix = Array.isArray(manifest.authoritativeExactPrefixKeys)
        ? manifest.authoritativeExactPrefixKeys.slice()
        : [];
      const controller = new AbortController();
      jobs.forEach((state) => { state.sectionLoading = true; });
      this.root.dataset.v4SectionLoading = key;
      this.renderMappedSearchPage(manifest, 1, { animate: false });
      this.renderMappedSearchNavigation(manifest, 1);
      const work = (async () => {
        const results = await Promise.all(jobs.map((state) => {
          const page = state.deferred === true ? 1 : Number(state.page || 0) + 1;
          return this.fetchMappedSearchPage(
            state.source,
            page,
            this.searchEngine(),
            manifest.analysis,
            {
              maxPages: state.maxPages,
              signal: controller.signal,
              background: false,
              requiredRetry: false
            }
          ).then((result) => ({ state, page, result }))
            .catch(() => ({
              state,
              page,
              result: { products: [], hasMore: false, failed: true }
            }));
        }));
        if (
          generation !== this.searchCurationGeneration
          || this.destroyed
          || !this.root.isConnected
          || !this.isCurrentMappedManifest(manifest)
        ) return false;
        manifest.entries = Array.isArray(manifest.progressiveEntries)
          ? manifest.progressiveEntries.slice()
          : (manifest.baseEntries || manifest.entries).slice();
        results.forEach(({ state, page, result }) => {
          this.applyAuthoritativeTeamStateResult(manifest, state, page, result);
        });
        const canonicalEntries = manifest.entries.slice();
        manifest.entries = canonicalEntries;
        manifest.focusedEntries = canonicalEntries.slice();
        manifest.baseEntries = canonicalEntries.slice();
        this.refreshAuthoritativeTeamContinuationState(manifest);
        if (!canonicalEntries.length && manifest.hasMore === false) {
          manifest.recognizedV4FailClosed = true;
          manifest.recognizedV4FailureReason = manifest.authoritativeTeamHadFailure === true
            ? 'authoritative-team-source-incomplete'
            : '';
          manifest.outcome = 'recognized-v4-empty';
        }
        this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
        const nextPrefix = manifest.entries.slice(0, exactPrefix.length).map((entry) => entry.key);
        const prefixStable = exactPrefix.every((entryKey, index) => nextPrefix[index] === entryKey);
        manifest.progressiveExactPrefixStable = prefixStable;
        this.root.dataset.v4ExactPrefixStable = String(prefixStable);
        if (!prefixStable) throw new Error('Authoritative section continuation changed the exact prefix');
        this.refreshMappedSearchTierDescriptors(manifest);
        this.renderMappedSearchPage(manifest, 1, { animate: false });
        this.renderMappedSearchNavigation(manifest, 1);
        this.syncMappedFilterControls(new URL(window.location.href));
        this.updateMappedFacetCounts(manifest, new URL(window.location.href));
        const announcement = this.root.querySelector('[data-ac-curation-announcement]');
        if (announcement) {
          const label = String(jobs[0]?.source?.resultSectionLabel || 'Results');
          announcement.textContent = `${label} updated.`;
        }
        return true;
      })().finally(() => {
        jobs.forEach((state) => { state.sectionLoading = false; });
        if (this.root.dataset.v4SectionLoading === key) delete this.root.dataset.v4SectionLoading;
        manifest.authoritativeSectionLoads.delete(key);
        if (
          generation === this.searchCurationGeneration
          && !this.destroyed
          && this.root.isConnected
          && this.isCurrentMappedManifest(manifest)
        ) {
          this.renderMappedSearchPage(manifest, 1, { animate: false });
          this.renderMappedSearchNavigation(manifest, 1);
        }
      });
      manifest.authoritativeSectionLoads.set(key, work);
      return work;
    }

    async buildAuthoritativeTeamSearchManifest(
      manifest,
      engine,
      analysis,
      onProgress,
      options = {}
    ) {
      const exactStates = manifest.sources
        .filter((state) => state.scope === 'exact')
        .sort((left, right) => this.authoritativeTeamStateOrder(left, right));
      const strictState = exactStates.find((state) => {
        return String(state?.source?.clauseRole || '').trim() === 'strict-exact';
      }) || exactStates.find((state) => state?.source?.exactFallback !== true) || exactStates[0] || null;
      const fallbackExactStates = exactStates.filter((state) => state !== strictState);
      const coreStates = manifest.sources
        .filter((state) => state.scope === 'core')
        .sort((left, right) => this.authoritativeTeamStateOrder(left, right));
      const continuationStates = fallbackExactStates.concat(coreStates);
      const initialContinuationStates = continuationStates.slice(0, 3);
      const initialStateSet = new Set(initialContinuationStates);
      const routeStates = [strictState].concat(continuationStates).filter(Boolean);
      const generation = this.searchCurationGeneration;

      manifest.authoritativeTeamRoute = true;
      manifest.authoritativeTeamHadFailure = false;
      manifest.authoritativeSectionLoads = new Map();
      manifest.initialPrincipalRequestCount = (strictState ? 1 : 0) + initialContinuationStates.length;
      manifest.sectionVisibleMs = {};
      manifest.completedStrictTierCount = 0;
      routeStates.forEach((state) => {
        const initialPrincipal = state === strictState || initialStateSet.has(state);
        state.initialPrincipal = initialPrincipal;
        state.deferred = !initialPrincipal;
        state.initialStatus = initialPrincipal ? 'initial' : 'deferred';
      });
      this.root.dataset.v4InitialPrincipalRequests = String(manifest.initialPrincipalRequestCount);

      const activeEntries = () => {
        return Array.isArray(manifest.progressiveEntries)
          ? manifest.progressiveEntries
          : manifest.entries;
      };
      const exactEntries = () => this.authoritativeTeamExactEntries(manifest);
      const elapsedMs = () => {
        const now = window.performance?.now?.() || Date.now();
        const startedAt = Number(this.searchCuration?.startedAt || now);
        return Math.max(0, Math.round(now - startedAt));
      };
      const requestSignal = () => {
        return manifest.progressiveCommitted === true
          ? this.v4ProgressiveAbortController?.signal
          : this.searchCurationAbortController?.signal;
      };
      const invalid = () => {
        return generation !== this.searchCurationGeneration
          || manifest.cancelled
          || this.searchCurationIsFallbackLocked()
          || this.destroyed
          || !this.root.isConnected;
      };
      const fetchStatePage = (state, page) => {
        return this.fetchMappedSearchPage(
          state.source,
          page,
          engine,
          analysis,
          {
            attempts: 1,
            requiredRetry: false,
            retryDelays: [],
            deadlineReserveMs: 0,
            background: false,
            maxPages: state.maxPages,
            signal: requestSignal()
          }
        ).catch(() => ({ products: [], hasMore: false, failed: true }));
      };
      const markSectionVisible = (state) => {
        const key = String(state?.source?.resultSectionKey || '').trim();
        if (!key || manifest.sectionVisibleMs[key] !== undefined) return;
        if (!activeEntries().some((entry) => entry?.resultSectionKey === key)) return;
        manifest.sectionVisibleMs[key] = elapsedMs();
        if (key === 'franchise-history') {
          this.root.dataset.v4HistoryVisibleMs = String(manifest.sectionVisibleMs[key]);
        } else if (key === 'market-region') {
          this.root.dataset.v4MarketVisibleMs = String(manifest.sectionVisibleMs[key]);
        }
      };
      const revealFrontier = async () => {
        if (manifest.progressiveCommitted === true) return true;
        if (
          typeof options.onInitialReady !== 'function'
          || !this.stageProgressiveMappedSearchFrontier(
            manifest,
            strictState ? [strictState] : [],
            continuationStates
          )
        ) return false;
        const progressiveController = new AbortController();
        let committed = false;
        try {
          committed = options.onInitialReady(manifest) === true;
        } catch (error) {
          this.log('Authoritative team exact reveal was skipped', error);
        }
        if (!committed) {
          progressiveController.abort();
          this.unstageProgressiveMappedSearchFrontier(manifest);
          return false;
        }
        this.v4ProgressiveAbortController?.abort();
        this.v4ProgressiveAbortController = progressiveController;
        manifest.progressiveGeneration = generation;
        if (manifest.progressiveRevealPromise) await manifest.progressiveRevealPromise;
        this.armV4ProgressiveDeadline(
          manifest,
          progressiveController,
          generation,
          options.grid,
          analysis
        );
        return true;
      };
      const refreshState = async (state) => {
        if (
          manifest.progressiveCommitted !== true
          || typeof options.onTierSettled !== 'function'
        ) return false;
        try {
          const refreshed = await options.onTierSettled(
            manifest,
            this.mappedSearchSourceTierMetadata(state)
          );
          markSectionVisible(state);
          return refreshed;
        } catch (error) {
          manifest.authoritativeTeamHadFailure = true;
          this.log('Authoritative team section refresh was skipped', error);
          return false;
        }
      };

      this.setSearchCurationPhase('exact');
      this.setSearchCurationProgress(30, 'authoritative-team-exact-page-one');
      if (!strictState || invalid()) {
        manifest.authoritativeTeamHadFailure = true;
      } else {
        const exactResult = await fetchStatePage(strictState, 1);
        this.applyAuthoritativeTeamStateResult(manifest, strictState, 1, exactResult);
        manifest.exactPageOneSettled = strictState.failed !== true;
        manifest.exactEntries = exactEntries().slice();
        manifest.progressiveSettledEntries = manifest.exactEntries.slice();
        if (manifest.exactEntries.length) {
          await revealFrontier();
          manifest.authoritativeExactPrefixKeys = (
            Array.isArray(manifest.progressiveVisiblePrefixKeys)
              ? manifest.progressiveVisiblePrefixKeys
              : manifest.exactEntries.map((entry) => entry.key)
          ).filter(Boolean);
        }
      }

      if (invalid()) {
        manifest.cancelled = true;
        return manifest;
      }

      this.setSearchCurationPhase('related');
      this.setSearchCurationProgress(60, 'authoritative-team-lower-page-one');
      const initialResults = await Promise.all(initialContinuationStates.map((state) => {
        return fetchStatePage(state, 1);
      }));
      for (let index = 0; index < initialContinuationStates.length; index += 1) {
        const state = initialContinuationStates[index];
        const applied = this.applyAuthoritativeTeamStateResult(manifest, state, 1, initialResults[index]);
        if (!applied.failed) manifest.completedStrictTierCount += 1;
        manifest.progressiveSettledEntries = activeEntries().slice();
        if (manifest.progressiveCommitted !== true && activeEntries().length) {
          await revealFrontier();
        } else if (applied.accepted > 0) {
          await refreshState(state);
        }
      }
      manifest.corePageOneSettled = coreStates.every((state) => {
        return state.deferred === true
          || (Number(state.page || 0) >= 1 && state.failed !== true);
      });

      if (activeEntries().length && manifest.progressiveCommitted !== true) {
        await revealFrontier();
      }
      if (manifest.progressiveCommitted === true && manifest.progressiveRevealPromise) {
        await manifest.progressiveRevealPromise;
      }

      const finalEntries = activeEntries().slice();
      manifest.entries = finalEntries;
      manifest.exactEntries = exactEntries().slice();
      manifest.baseCloseCount = manifest.exactEntries.length;
      manifest.closeCount = manifest.baseCloseCount;
      manifest.focusedEntries = finalEntries.slice();
      manifest.baseEntries = finalEntries.slice();
      manifest.productKeys = new Set(finalEntries.map((entry) => entry.key).filter(Boolean));
      manifest.progressiveEntries = null;
      manifest.progressiveLoading = false;
      this.refreshAuthoritativeTeamContinuationState(manifest);
      manifest.sealed = !manifest.cancelled;
      const continuationOnly = finalEntries.length === 0 && manifest.hasMore === true;
      manifest.recognizedV4FailClosed = manifest.authoritativeTeamHadFailure === true
        || (finalEntries.length === 0 && !continuationOnly);
      manifest.recognizedV4FailureReason = manifest.authoritativeTeamHadFailure === true
        ? 'authoritative-team-source-incomplete'
        : '';
      manifest.outcome = finalEntries.length === 0
        ? (continuationOnly ? 'focused-curated' : 'recognized-v4-empty')
        : (manifest.authoritativeTeamHadFailure === true ? 'recognized-v4-partial' : 'focused-curated');
      const initialPrefix = Array.isArray(manifest.authoritativeExactPrefixKeys)
        ? manifest.authoritativeExactPrefixKeys
        : [];
      this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
      const finalPrefix = manifest.entries.slice(0, initialPrefix.length).map((entry) => entry.key);
      manifest.progressiveExactPrefixStable = initialPrefix.every((key, index) => {
        return finalPrefix[index] === key;
      });
      this.root.dataset.v4ExactPrefixStable = String(manifest.progressiveExactPrefixStable);
      this.setSearchCurationProgress(86, manifest.outcome);
      if (typeof onProgress === 'function') {
        onProgress(manifest, {
          stage: 'authoritative-team-initial-settled',
          complete: true,
          requestCount: this.v4RequestCount
        });
      }
      return manifest;
    }

    async buildMappedSearchManifest(manifest, engine, analysis, onProgress, options = {}) {
      // Recognized team routes use this same strict, bounded exhaustion path so
      // every attested section is complete before numbered pagination settles.
      const exactStates = manifest.sources.filter((state) => state.scope === 'exact');
      const coreStates = manifest.sources.filter((state) => state.scope === 'core');
      const broadStates = manifest.sources.filter((state) => state.scope === 'broad');
      const requestedMappedState = this.mappedSearchStateRequested();
      const reviewedSharedUnion = analysis?.ambiguous === true
        && exactStates.filter((state) => state?.source?.safeExact === true).length > 1;
      const reviewedExactInventoryStates = exactStates.filter((state) => {
        return Array.isArray(state?.source?.reviewedProductHandles)
          && state.source.reviewedProductHandles.length > 0;
      });
      const reviewedExactInventoryUnion = reviewedExactInventoryStates.length > 0;
      const reviewedTeamSearch = Boolean(
        analysis?.entity
        && String(analysis?.subject?.league || '').trim()
        && exactStates.some((state) => state?.source?.safeExact === true)
      );
      const reviewedLeagueOrSportSearch = Boolean(
        this.engineMode === 'v4_metafield'
        && this.v4MappedSearchBalancesExactTier(analysis)
        && exactStates.some((state) => state?.source?.safeExact === true)
      );
      const reviewedCollectionSearch = Boolean(
        (
          ['catalog_collection', 'collection'].includes(
            String(analysis?.entity?.type || '').trim().toLowerCase()
          )
          || /^collection-/.test(String(analysis?.entity?.id || ''))
          || (
            analysis?.titleSubject
            && /^collection-/.test(String(analysis?.titleSubject?.id || ''))
          )
        )
        && exactStates.some((state) => state?.source?.safeExact === true)
      );
      const requiresFullCurationBudget = requestedMappedState
        || reviewedTeamSearch
        || reviewedLeagueOrSportSearch
        || reviewedCollectionSearch;
      const exactDeadlineReserveMs = requiresFullCurationBudget ? 180 : 1400;
      if (requiresFullCurationBudget || reviewedSharedUnion) {
        this.grantSearchCurationRenderGrace(6200);
      }
      const closeTargetTolerance = Math.min(
        2,
        Math.floor(Math.max(0, Number(manifest.closeTarget) || 0) * 0.08)
      );
      const closeTargetFloor = Math.max(
        0,
        (Number(manifest.closeTarget) || 0) - closeTargetTolerance
      );
      const canonicalExactState = exactStates.find((state) => state?.source?.safeExact)
        || exactStates[0]
        || null;
      const strictV4TierOrder = this.engineMode === 'v4_metafield';
      const balanceTerminalExactTier = strictV4TierOrder
        && reviewedLeagueOrSportSearch;
      const canonicalFirstOnly = !strictV4TierOrder
        && !reviewedSharedUnion
        && !reviewedExactInventoryUnion
        && closeTargetFloor <= 0
        && canonicalExactState
        && exactStates.length > 1;
      const configuredExactStates = canonicalFirstOnly
        ? [canonicalExactState]
        : exactStates;
      const sequentialAuthoritativeExact = Boolean(
        strictV4TierOrder
        && analysis?.authoritativeTeamRoute === true
      );
      const primaryExactStates = sequentialAuthoritativeExact
        ? configuredExactStates.slice(0, 1)
        : configuredExactStates;
      const remainingAuthoritativeExactStates = sequentialAuthoritativeExact
        ? configuredExactStates.slice(1)
        : [];
      const requiredExactStates = strictV4TierOrder
        ? exactStates
        : reviewedSharedUnion
        ? exactStates
        : (
          canonicalExactState
            ? [canonicalExactState].concat(reviewedExactInventoryStates)
            : primaryExactStates.slice(0, 1)
        );
      const exactTargetReached = () => {
        if (strictV4TierOrder) return false;
        if (primaryExactStates.some((state) => state.source?.collectionHandle)) {
          return false;
        }
        return closeTargetFloor > 0 && manifest.entries.length >= closeTargetFloor;
      };
      const closeExactStates = () => {
        exactStates.forEach((state) => {
          state.hasMore = false;
          state.exhausted = true;
        });
      };
      const reportProgress = (stage, complete = false) => {
        let completed = 0;
        let total = 1;
        let value = 30;
        if (stage === 'exact') {
          completed = exactStates.reduce((sum, state) => sum + Math.max(0, Number(state.page) || 0), 0);
          total = Math.max(1, exactStates.length * manifest.limits.maxSourcePages);
          value = complete ? 64 : 30 + (34 * completed / total);
        } else {
          completed = coreStates.reduce((sum, state) => sum + (Number(state.page) >= 1 ? 1 : 0), 0);
          total = Math.max(1, coreStates.length);
          value = complete ? 84 : 64 + (20 * completed / total);
        }
        this.setSearchCurationProgress(value, `${stage}-sources-settled`);
        if (typeof onProgress === 'function') {
          onProgress(manifest, { stage, completed, total, complete });
        }
      };
      const restoreEntries = (entries) => {
        manifest.entries = entries.slice();
        manifest.productKeys = new Set(manifest.entries.map((entry) => entry.key).filter(Boolean));
        manifest.entries.forEach((entry, index) => {
          entry.rank = index;
        });
        manifest.capped = false;
      };
      const sealManifest = (outcome) => {
        const progressiveCommitted = manifest.progressiveCommitted === true;
        if (Array.isArray(manifest.progressiveEntries)) {
          manifest.entries = manifest.progressiveEntries.slice();
        }
        manifest.outcome = outcome;
        manifest.hasMore = manifest.promotedTerminalHasMore === true
          || manifest.capped
          || exactStates.concat(coreStates).some((state) => state.hasMore && !state.exhausted);
        manifest.focusedHasMore = manifest.hasMore;
        manifest.sealed = !manifest.cancelled && manifest.entries.length > 0;
        if (manifest.sealed) {
          if (!progressiveCommitted) this.arrangeMappedBrandQualifiedEntries(manifest);
          manifest.focusedEntries = manifest.entries.slice();
          manifest.baseEntries = manifest.entries.slice();
          manifest.baseCloseCount = Math.max(0, Number(manifest.baseCloseCount) || 0);
          this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
          this.setSearchCurationPhase('arrange');
          this.setSearchCurationProgress(86, outcome);
        }
        manifest.progressiveEntries = null;
        manifest.progressiveLoading = false;
      };
      const commitProgressiveFrontier = async () => {
        if (manifest.progressiveCommitted === true) return true;
        if (
          typeof options.onInitialReady !== 'function'
          || !this.stageProgressiveMappedSearchFrontier(manifest, exactStates, coreStates)
        ) return false;
        const progressiveController = new AbortController();
        let committed = false;
        try {
          committed = options.onInitialReady(manifest) === true;
        } catch (error) {
          this.log('Progressive mapped search reveal was skipped', error);
        }
        if (!committed) {
          progressiveController.abort();
          this.unstageProgressiveMappedSearchFrontier(manifest);
          return false;
        }
        this.v4ProgressiveAbortController?.abort();
        this.v4ProgressiveAbortController = progressiveController;
        manifest.progressiveGeneration = this.searchCurationGeneration;
        if (manifest.progressiveRevealPromise) await manifest.progressiveRevealPromise;
        this.armV4ProgressiveDeadline(
          manifest,
          progressiveController,
          this.searchCurationGeneration,
          options.grid,
          analysis
        );
        return true;
      };
      const paceProgressiveExactRequest = async () => {
        const signal = this.v4ProgressiveAbortController?.signal;
        const generation = Number(manifest.progressiveGeneration);
        const lastStartedAt = Math.max(0, Number(manifest.progressiveLastRequestStartedAt) || 0);
        const paceWaitMs = lastStartedAt
          ? Math.max(0, 1200 - (Date.now() - lastStartedAt))
          : 0;
        if (
          paceWaitMs
          && !await this.waitForMappedSearchRetry(paceWaitMs, {
            signal,
            generation,
            ignoreDeadline: true
          })
        ) return false;
        if (
          signal?.aborted
          || generation !== this.searchCurationGeneration
          || this.destroyed
          || !this.root.isConnected
        ) return false;
        manifest.progressiveLastRequestStartedAt = Date.now();
        return true;
      };
      this.setSearchCurationPhase('exact');
      this.setSearchCurationProgress(30, 'source-plan-fixed');
      let exactSettled = true;
      for (let page = 1; page <= manifest.limits.maxSourcePages; page += 1) {
        if (manifest.cancelled || manifest.capped) break;
        const pendingExact = primaryExactStates.filter((state) => {
          return page <= Number(state.maxPages || manifest.limits.maxSourcePages)
            && (page === 1 || state.hasMore);
        });
        if (!pendingExact.length) break;
        const exactEntryCountBeforeRound = manifest.entries.length;
        let roundSettled = true;
        if (
          strictV4TierOrder
          && manifest.progressiveCommitted === true
          && page > 1
        ) {
          roundSettled = await this.fetchMappedSearchRound(
            manifest,
            pendingExact,
            page,
            engine,
            analysis,
            () => reportProgress('exact'),
            {
              deadlineReserveMs: 0,
              background: true,
              signal: this.v4ProgressiveAbortController?.signal
            }
          );
        } else if (manifest.progressiveCommitted === true && page > 1) {
          for (const state of pendingExact) {
            if (!await paceProgressiveExactRequest()) {
              roundSettled = false;
              break;
            }
            roundSettled = await this.fetchMappedSearchRound(
              manifest,
              [state],
              page,
              engine,
              analysis,
              () => reportProgress('exact'),
              {
                deadlineReserveMs: 0,
                background: true,
                signal: this.v4ProgressiveAbortController?.signal
              }
            );
            if (!roundSettled) break;
          }
        } else if (
          !reviewedSharedUnion
          && !reviewedExactInventoryUnion
          && page === 1
          && manifest.closeTarget > 0
          && pendingExact.length > 1
        ) {
          roundSettled = await this.fetchMappedSearchRound(
            manifest,
            pendingExact.slice(0, 1),
            page,
            engine,
            analysis,
            () => reportProgress('exact'),
            { deadlineReserveMs: exactDeadlineReserveMs }
          );
          if (roundSettled && !exactTargetReached()) {
            roundSettled = await this.fetchMappedSearchRound(
              manifest,
              pendingExact.slice(1),
              page,
              engine,
              analysis,
              () => reportProgress('exact'),
              { deadlineReserveMs: exactDeadlineReserveMs }
            );
          }
        } else {
          roundSettled = await this.fetchMappedSearchRound(
            manifest,
            pendingExact,
            page,
            engine,
            analysis,
            () => reportProgress('exact'),
            { deadlineReserveMs: exactDeadlineReserveMs }
          );
        }
        if (!roundSettled) {
          exactSettled = false;
          break;
        }
        if (
          analysis?.authoritativeTeamRoute === true
          && page === 1
          && manifest.progressiveCommitted !== true
          && manifest.entries.length > 0
        ) {
          manifest.closeCount = manifest.entries.length;
          manifest.baseCloseCount = manifest.closeCount;
          manifest.exactEntries = manifest.entries.slice();
          await commitProgressiveFrontier();
        }
        if (
          manifest.progressiveCommitted !== true
          && !balanceTerminalExactTier
          && manifest.entries.length >= manifest.pageSize
        ) {
          manifest.closeCount = manifest.entries.length;
          manifest.baseCloseCount = manifest.closeCount;
          manifest.exactEntries = manifest.entries.slice();
          await commitProgressiveFrontier();
        }
        if (
          (reviewedSharedUnion || reviewedCollectionSearch)
          && page === 1
          && !exactTargetReached()
          && manifest.progressiveCommitted !== true
        ) {
          const prioritizedExactContinuationsSettled = await this.fetchMappedRelationshipContinuations(
            manifest,
            primaryExactStates,
            manifest.limits.maxSourcePages,
            engine,
            analysis,
            () => reportProgress('exact'),
            {
              deadlineReserveMs: 180,
              countAsBroad: false,
              pageWindowSize: 4,
              stopWhen: exactTargetReached
            }
          );
          if (!prioritizedExactContinuationsSettled) {
            exactSettled = false;
          } else if (
            exactTargetReached()
            || primaryExactStates.every((state) => state.exhausted || !state.hasMore)
          ) {
            closeExactStates();
          }
          break;
        }
        if (
          manifest.entries.length === exactEntryCountBeforeRound
          && !pendingExact.some((state) => state.hasMore && !state.exhausted)
        ) {
          pendingExact.forEach((state) => {
            state.hasMore = false;
            state.exhausted = true;
          });
          break;
        }
        if (exactTargetReached()) {
          closeExactStates();
          break;
        }
      }
      if (
        remainingAuthoritativeExactStates.length
        && exactSettled
        && !manifest.cancelled
        && !manifest.capped
      ) {
        for (const state of remainingAuthoritativeExactStates) {
          const stateMaxPages = Math.max(
            1,
            Number(state.maxPages || manifest.limits.maxSourcePages)
          );
          for (let page = 1; page <= stateMaxPages; page += 1) {
            if (manifest.cancelled || manifest.capped) break;
            if (page > 1 && (state.exhausted === true || state.hasMore !== true)) break;
            const previousEntryCount = manifest.entries.length;
            const roundSettled = await this.fetchMappedSearchRound(
              manifest,
              [state],
              page,
              engine,
              analysis,
              () => reportProgress('exact'),
              manifest.progressiveCommitted === true
                ? {
                  deadlineReserveMs: 0,
                  background: true,
                  signal: this.v4ProgressiveAbortController?.signal
                }
                : { deadlineReserveMs: exactDeadlineReserveMs }
            );
            if (!roundSettled) {
              exactSettled = false;
              break;
            }
            if (
              manifest.entries.length === previousEntryCount
              && state.hasMore !== true
            ) break;
          }
          if (!exactSettled || manifest.cancelled || manifest.capped) break;
        }
      }
      if (
        canonicalFirstOnly
        && exactSettled
        && !manifest.cancelled
        && !manifest.capped
      ) {
        const alternateExactStates = exactStates.filter((state) => state !== canonicalExactState);
        if (manifest.entries.length) {
          alternateExactStates.forEach((state) => {
            state.hasMore = false;
            state.exhausted = true;
          });
        } else {
          for (let page = 1; page <= manifest.limits.maxSourcePages; page += 1) {
            const pendingAlternates = alternateExactStates.filter((state) => {
              return page <= Number(state.maxPages || manifest.limits.maxSourcePages)
                && (page === 1 || state.hasMore);
            });
            if (!pendingAlternates.length) break;
            const previousEntryCount = manifest.entries.length;
            const roundSettled = await this.fetchMappedSearchRound(
              manifest,
              pendingAlternates,
              page,
              engine,
              analysis,
              () => reportProgress('exact'),
              { deadlineReserveMs: exactDeadlineReserveMs }
            );
            if (!roundSettled) {
              exactSettled = false;
              break;
            }
            if (
              manifest.entries.length === previousEntryCount
              && !pendingAlternates.some((state) => state.hasMore && !state.exhausted)
            ) {
              pendingAlternates.forEach((state) => {
                state.hasMore = false;
                state.exhausted = true;
              });
              break;
            }
          }
        }
      }
      if (Array.isArray(manifest.progressiveEntries)) {
        manifest.entries = manifest.progressiveEntries.slice();
      }
      manifest.closeCount = manifest.entries.length;
      manifest.baseCloseCount = manifest.closeCount;
      manifest.exactEntries = manifest.entries.slice();
      manifest.exactPageOneSettled = exactSettled
        && requiredExactStates.length > 0
        && requiredExactStates.every((state) => {
          return Number(state.page || 0) >= 1 && state.failed !== true;
        });
      if (!manifest.exactPageOneSettled) exactSettled = false;
      const exactTierStatus = this.strictV4ExactTierStatus(
        requiredExactStates,
        manifest.limits.maxSourcePages
      );
      manifest.exactTierStatus = exactTierStatus;
      manifest.exactTierSettled = exactSettled && exactTierStatus.settled;
      if (strictV4TierOrder && !manifest.exactTierSettled) {
        exactSettled = false;
        manifest.exactTierFailure = {
          reason: exactTierStatus.reason,
          sourceIndexes: exactTierStatus.sourceIndexes.slice()
        };
        requiredExactStates.forEach((state) => {
          if (
            !state.terminalReason
            && (state.failed === true || state.hasMore === true || state.exhausted !== true)
          ) {
            state.terminalReason = manifest.exactTierFailure.reason;
          }
        });
      }
      if (manifest.capped) exactSettled = false;
      if (balanceTerminalExactTier && exactSettled && manifest.exactTierSettled) {
        const terminalExactTier = this.mappedSearchTierGroups(requiredExactStates)[0];
        if (terminalExactTier) {
          this.rebalanceMappedSearchTierEntries(manifest, terminalExactTier);
          const balancedExactEntries = Array.isArray(manifest.progressiveEntries)
            ? manifest.progressiveEntries
            : manifest.entries;
          manifest.entries = balancedExactEntries.slice();
          manifest.closeCount = manifest.entries.length;
          manifest.baseCloseCount = manifest.closeCount;
          manifest.exactEntries = manifest.entries.slice();
          this.refreshMappedSearchTierDescriptors(manifest);
        }
      }
      if (exactSettled && manifest.exactTierSettled) {
        manifest.progressiveSettledEntries = manifest.exactEntries.slice();
      }
      reportProgress('exact', true);

      if (
        strictV4TierOrder
        && exactSettled
        && !manifest.cancelled
        && !manifest.capped
        && manifest.entries.length
        && coreStates.length
        && manifest.progressiveCommitted !== true
      ) {
        // Reveal a full exact page immediately, or a smaller exact tier once
        // every exact source is terminal. The persistent progressive status
        // makes that prefix explicitly nonterminal while whole relationship
        // tiers continue in the background.
        await commitProgressiveFrontier();
      }

      if (!exactSettled) {
        restoreEntries(manifest.exactEntries);
        this.settleMappedExactTierFailure(manifest, exactStates);
        reportProgress('related', true);
      } else if (!manifest.cancelled && !manifest.capped && coreStates.length) {
        this.setSearchCurationPhase('related');
        this.grantSearchCurationRenderGrace(requiresFullCurationBudget ? 6200 : 4200);
        if (strictV4TierOrder) {
          const strictTiersSettled = await this.fetchMappedSearchStrictTiers(
            manifest,
            coreStates,
            manifest.limits.maxSourcePages,
            engine,
            analysis,
            () => reportProgress('related'),
            {
              deadlineReserveMs: 180,
              onFrontierReady: commitProgressiveFrontier,
              onTierSettled: async (tierManifest, tier) => {
                if (tierManifest.progressiveCommitted !== true) {
                  await commitProgressiveFrontier();
                  return;
                }
                if (typeof options.onTierSettled === 'function') {
                  await options.onTierSettled(tierManifest, tier);
                }
              }
            }
          );
          if (Array.isArray(manifest.progressiveEntries)) {
            manifest.entries = manifest.progressiveEntries.slice();
          }
          manifest.relationshipSettled = strictTiersSettled;

          if (strictTiersSettled) {
            sealManifest('focused-curated');
          } else {
            const progressiveCommitted = manifest.progressiveCommitted === true;
            manifest.degradedReason = manifest.strictTierFailure?.reason
              || 'strict-tier-incomplete';
            manifest.continuationInterrupted = true;
            if (!progressiveCommitted) {
              // Never lock an exact-only or partial relationship prefix as a
              // successful V4 result. Keep it unsealed so the existing single
              // recovery pass retries the failed tier, then uses native search
              // if the attested route still cannot be completed.
              manifest.entries = manifest.exactEntries.slice();
              manifest.outcome = 'core-page1-incomplete';
              manifest.hasMore = false;
              manifest.focusedHasMore = false;
              manifest.sealed = false;
              manifest.focusedEntries = [];
              manifest.baseEntries = null;
              manifest.progressiveEntries = null;
              manifest.progressiveLoading = false;
              manifest.safeExactFallbackBlocked = true;
              manifest.explicitSafeEmpty = true;
            } else {
              // A failed page may contain only part of a relationship member.
              // Restore the last fully settled semantic tier before rendering.
              const settledEntries = Array.isArray(manifest.progressiveSettledEntries)
                ? manifest.progressiveSettledEntries.slice()
                : manifest.exactEntries.slice();
              manifest.entries = settledEntries;
              manifest.outcome = settledEntries.length > manifest.exactEntries.length
                ? 'focused-degraded'
                : 'exact-only';
              manifest.hasMore = true;
              manifest.focusedHasMore = true;
              manifest.sealed = !manifest.cancelled && settledEntries.length > 0;
              manifest.focusedEntries = settledEntries.slice();
              manifest.baseEntries = settledEntries.slice();
              manifest.progressiveEntries = null;
              manifest.progressiveLoading = false;
              this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
              this.setSearchCurationPhase('arrange');
              this.setSearchCurationProgress(86, manifest.outcome);
            }
          }
          this.refreshMappedSearchTierDescriptors(manifest);
          reportProgress('related', true);
        } else {
        let corePageOneSettled = true;
        if (manifest.progressiveCommitted === true) {
          for (const state of coreStates) {
            if (!await paceProgressiveExactRequest()) {
              corePageOneSettled = false;
              break;
            }
            corePageOneSettled = await this.fetchMappedSearchRound(
              manifest,
              [state],
              1,
              engine,
              analysis,
              () => reportProgress('related'),
              {
                deadlineReserveMs: 0,
                background: true,
                signal: this.v4ProgressiveAbortController?.signal
              }
            );
            if (!corePageOneSettled) break;
          }
        } else {
          corePageOneSettled = await this.fetchMappedRelationshipRound(
            manifest,
            coreStates,
            1,
            engine,
            analysis,
            () => reportProgress('related'),
            { deadlineReserveMs: 180 }
          );
        }
        manifest.corePageOneSettled = corePageOneSettled
          && !manifest.capped
          && coreStates.every((state) => {
            return Number(state.page || 0) >= 1 && state.failed !== true;
          });
        if (Array.isArray(manifest.progressiveEntries)) {
          manifest.entries = manifest.progressiveEntries.slice();
        }
        const corePageOneEntries = manifest.entries.slice();
        const corePageOneState = new Map(coreStates.map((state) => {
          return [state, {
            page: state.page,
            acceptedCount: state.acceptedCount,
            exhausted: state.exhausted,
            hasMore: state.hasMore,
            failed: state.failed
          }];
        }));
        const corePageOneBroadEntryCount = manifest.broadEntryCount;
        const corePageOneSettledCount = manifest.broadSettledCount;
        let progressiveCommitted = manifest.progressiveCommitted === true;
        if (manifest.corePageOneSettled && !progressiveCommitted) {
          progressiveCommitted = await commitProgressiveFrontier();
        }
        let coreContinuationsSettled = manifest.corePageOneSettled;
        if (manifest.corePageOneSettled) {
          coreContinuationsSettled = await this.fetchMappedRelationshipContinuations(
            manifest,
            coreStates,
            manifest.limits.maxSourcePages,
            engine,
            analysis,
            () => reportProgress('related'),
            progressiveCommitted
              ? {
                deadlineReserveMs: 0,
                pageWindowSize: 1,
                serialRequests: true,
                requestPaceMs: 1200,
                background: true,
                lastRequestStartedAt: manifest.progressiveLastRequestStartedAt,
                signal: this.v4ProgressiveAbortController?.signal
              }
              : {
                deadlineReserveMs: 180,
                pageWindowSize: 4,
                signal: this.searchCurationAbortController?.signal
              }
          );
        }
        if (Array.isArray(manifest.progressiveEntries)) {
          manifest.entries = manifest.progressiveEntries.slice();
        }
        if (manifest.capped) coreContinuationsSettled = false;
        manifest.relationshipSettled = manifest.corePageOneSettled
          && coreContinuationsSettled;

        if (!manifest.corePageOneSettled) {
          restoreEntries(manifest.exactEntries);
          manifest.broadEntryCount = 0;
          manifest.broadSettledCount = 0;
          if (progressiveCommitted && manifest.entries.length >= manifest.pageSize) {
            manifest.outcome = 'focused-degraded';
            manifest.degradedReason = 'core-page1-incomplete';
            manifest.continuationInterrupted = true;
            manifest.hasMore = true;
            manifest.focusedHasMore = true;
            manifest.sealed = true;
            manifest.focusedEntries = manifest.entries.slice();
            manifest.baseEntries = manifest.entries.slice();
            manifest.progressiveEntries = null;
            manifest.progressiveLoading = false;
            this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
          } else {
            manifest.safeExactFallbackBlocked = true;
            manifest.explicitSafeEmpty = true;
            manifest.outcome = 'core-page1-incomplete';
            manifest.hasMore = false;
            manifest.focusedHasMore = false;
            manifest.sealed = false;
          }
        } else if (!coreContinuationsSettled) {
          if (progressiveCommitted) {
            const settledEntries = Array.isArray(manifest.progressiveSettledEntries)
              ? manifest.progressiveSettledEntries.slice()
              : manifest.exactEntries.slice();
            manifest.entries = settledEntries;
            manifest.focusedEntries = settledEntries.slice();
            manifest.baseEntries = settledEntries.slice();
            manifest.productKeys = new Set(
              settledEntries.map((entry) => entry.key).filter(Boolean)
            );
          }
          if (!progressiveCommitted && (manifest.cancelled || manifest.capped)) {
            restoreEntries(corePageOneEntries);
            corePageOneState.forEach((snapshot, state) => {
              Object.assign(state, snapshot);
            });
            manifest.broadEntryCount = corePageOneBroadEntryCount;
            manifest.broadSettledCount = corePageOneSettledCount;
          }
          manifest.outcome = 'focused-degraded';
          manifest.degradedReason = 'core-continuation-incomplete';
          manifest.continuationInterrupted = true;
          manifest.hasMore = coreStates.some((state) => {
            return state.hasMore && !state.exhausted;
          });
          manifest.focusedHasMore = manifest.hasMore;
          manifest.sealed = !manifest.cancelled && manifest.entries.length > 0;
          if (manifest.sealed) {
            if (!progressiveCommitted) this.arrangeMappedBrandQualifiedEntries(manifest);
            manifest.focusedEntries = manifest.entries.slice();
            manifest.baseEntries = manifest.entries.slice();
            this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
            this.setSearchCurationPhase('arrange');
            this.setSearchCurationProgress(86, manifest.outcome);
          } else {
            manifest.safeExactFallbackBlocked = true;
            manifest.explicitSafeEmpty = true;
          }
          manifest.progressiveEntries = null;
          manifest.progressiveLoading = false;
        } else {
          sealManifest('focused-curated');
        }
        reportProgress('related', true);
        }
      } else {
        manifest.relationshipSettled = true;
        sealManifest('focused-curated');
        reportProgress('related', true);
      }

      const remainingBroadStates = broadStates.filter((state) => {
        return state.promotedToFocused !== true;
      });
      manifest.broadAvailable = Boolean(
        manifest.sealed
        && manifest.relationshipSettled
        && manifest.outcome === 'focused-curated'
        && remainingBroadStates.length > 0
      );
      manifest.broadStateCount = remainingBroadStates.length;

      if (typeof onProgress === 'function') {
        onProgress(manifest, { stage: 'sealed', completed: manifest.entries.length, total: manifest.entries.length, complete: true });
      }
      return manifest;
    }

    mappedSearchBroadStates(manifest) {
      return (manifest?.sources || []).filter((state) => {
        return state.scope === 'broad'
          && state.promotedToFocused !== true
          && state.promotedTerminalFallback !== true;
      });
    }

    mappedSearchScopeRequested(sourceUrl = window.location.href) {
      return false;
    }

    mappedSearchStateRequested(sourceUrl = window.location.href) {
      const url = sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href);
      return url.searchParams.get('vcc_mapped') === '1';
    }

    mappedSearchUniverseDestination(manifest) {
      const analysis = manifest?.analysis || {};
      const universeHandles = ['team-wear', 'vintage-classics', 'brands'];
      const source = (manifest?.sources || [])
        .map((candidate) => candidate?.source)
        .concat(analysis.searches || [])
        .find((candidate) => {
          const collectionHandle = String(
            candidate?.collectionHandle || ''
          ).trim().toLowerCase();
          return this.mappedSearchDomainRootFallback(candidate)
            && universeHandles.includes(collectionHandle);
        });
      let handle = String(source?.collectionHandle || '').trim().toLowerCase();

      if (!handle && analysis.team) handle = 'team-wear';

      if (!handle) {
        const explicitLeague = (analysis.themes || []).some((theme) => {
          const id = String(theme?.id || theme?.key || '').trim().toLowerCase();
          const label = String(theme?.label || theme?.name || '').trim().toLowerCase();
          return id.startsWith('league-')
            || ['nfl', 'nba', 'mlb', 'nhl', 'ncaa', 'cfl'].includes(label);
        });
        if (explicitLeague) handle = 'team-wear';
      }

      if (
        !handle
        && (
          analysis.entityType === 'brand'
          || (Array.isArray(analysis.brands) && analysis.brands.length > 0)
        )
      ) {
        handle = 'brands';
      }

      // Every completed mapped query belongs to one of the three customer-facing
      // archive universes. Team and brand signals win; the remaining general,
      // entertainment, animal, location, and long-tail vintage searches belong
      // to Vintage Classics.
      if (!handle) handle = 'vintage-classics';

      if (!universeHandles.includes(handle)) return '';
      let routesRoot = String(window.Shopify?.routes?.root || '/').trim() || '/';
      if (!routesRoot.startsWith('/')) routesRoot = `/${routesRoot}`;
      if (!routesRoot.endsWith('/')) routesRoot = `${routesRoot}/`;
      return `${routesRoot}collections/${handle}`;
    }

    mappedSearchCanadaVintageDestination(manifest) {
      if (manifest?.analysis?.canadaCtaEligible !== true) return '';
      let routesRoot = String(window.Shopify?.routes?.root || '/').trim() || '/';
      if (!routesRoot.startsWith('/')) routesRoot = `/${routesRoot}`;
      if (!routesRoot.endsWith('/')) routesRoot = `${routesRoot}/`;
      return `${routesRoot}collections/canada-vintage`;
    }

    mappedSearchScopeLabels(manifest) {
      const labels = [];
      const seen = new Set();
      this.mappedSearchBroadStates(manifest).forEach((state) => {
        const label = String(state?.source?.label || '')
          .replace(/\s*\+\s*type\s*$/i, '')
          .trim();
        const key = this.normalizeSearchPhrase(label);
        if (!key || seen.has(key) || /related path|broadened path/i.test(label)) return;
        seen.add(key);
        labels.push(label);
      });
      return labels.slice(0, 3);
    }

    mappedSearchScopeLabelList(labels) {
      if (!labels.length) return 'reviewed archive paths';
      if (labels.length === 1) return labels[0];
      if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
      return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
    }

    mappedSearchScopeCounts(manifest, sourceUrl = window.location.href) {
      const url = sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href);
      const activeFilters = this.activeMappedFilterGroups(url);
      const engine = this.searchEngine() || {};
      const matches = (entry) => {
        return this.productMatchesActiveMappedFilters(entry?.product, activeFilters, engine);
      };
      return {
        focused: (manifest?.focusedEntries || []).filter(matches).length,
        broad: (manifest?.broadEntries || []).filter(matches).length
      };
    }

    mappedSearchRootCtaReady(manifest, currentPage) {
      const entryCount = Math.max(0, Number(manifest?.entries?.length || 0));
      const pageSize = Math.max(1, Number(manifest?.pageSize || 1));
      const authoritativeTeamRoute = manifest?.analysis?.authoritativeTeamRoute === true;
      const sectionContinuationRoute = manifest?.authoritativeTeamRoute === true;
      const totalPages = Math.max(1, Math.ceil(entryCount / pageSize));
      const page = Math.max(1, parseInt(currentPage || '1', 10) || 1);
      const recognizedFailClosed = Boolean(
        authoritativeTeamRoute
        && manifest?.recognizedV4FailClosed === true
        && ['recognized-v4-partial', 'recognized-v4-empty'].includes(
          String(manifest?.outcome || '')
        )
      );
      const terminalOutcome = Boolean(
        (
          manifest?.outcome === 'focused-curated'
          && manifest.relationshipSettled === true
        )
        || (
          manifest?.outcome === 'exact-only'
          && manifest.exactTierSettled === true
          && manifest.safeExactFallbackBlocked !== true
        )
        || recognizedFailClosed
      );
      return Boolean(
        (entryCount > 0 || recognizedFailClosed)
        && manifest?.sealed === true
        && terminalOutcome
        && manifest.hasMore === false
        && manifest.viewHasMore === false
        && manifest.cancelled !== true
        && manifest.progressiveLoading !== true
        && manifest.broadLoading !== true
        && manifest.broadError !== true
        && (
          !sectionContinuationRoute
          || manifest.authoritativeAllSectionsExhausted === true
        )
        && (
          recognizedFailClosed
          || (
            manifest.capped !== true
            && !manifest.strictTierFailure
            && !manifest.exactTierFailure
            && manifest.continuationInterrupted !== true
            && !String(manifest.degradedReason || '').trim()
          )
        )
        && page === totalPages
      );
    }

    hideMappedSearchRootCta(mount = null) {
      const target = mount || this.root.querySelector('[data-ac-search-scope]');
      if (!target) return;
      const progressiveTail = target.querySelector('[data-ac-search-progressive-tail]');
      if (progressiveTail) progressiveTail.hidden = true;
      target.hidden = true;
      target.removeAttribute('data-state');
      target.removeAttribute('data-focused-count');
      target.removeAttribute('data-broad-count');
      target.setAttribute('aria-busy', 'false');
    }

    renderMappedSearchProgressiveTail(mount) {
      if (!mount) return false;
      let tail = mount.querySelector('[data-ac-search-progressive-tail]');
      if (!tail) {
        tail = document.createElement('span');
        tail.className = 'vcc-ac-search-progressive-tail';
        tail.setAttribute('data-ac-search-progressive-tail', '');
        tail.setAttribute('role', 'status');
        tail.setAttribute('aria-live', 'polite');
        const track = document.createElement('i');
        track.className = 'vcc-ac-search-progressive-tail__track';
        track.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.textContent = 'Organizing related results';
        tail.append(track, label);
        mount.append(tail);
      }
      const button = mount.querySelector('[data-ac-search-scope-toggle]');
      if (button) {
        button.hidden = true;
        button.disabled = true;
      }
      tail.hidden = false;
      mount.hidden = false;
      mount.dataset.state = 'loading';
      mount.removeAttribute('data-focused-count');
      mount.removeAttribute('data-broad-count');
      mount.setAttribute('aria-busy', 'true');
      return true;
    }

    renderMappedSearchScopeControl(manifest, currentPage = null) {
      const mount = this.root.querySelector('[data-ac-search-scope]');
      if (!mount) return;
      if (
        manifest?.progressiveLoading === true
        && this.root?.dataset?.searchMappedLoading === 'true'
      ) {
        this.renderMappedSearchProgressiveTail(mount);
        return;
      }
      const destination = this.mappedSearchUniverseDestination(manifest);
      const canadaDestination = this.mappedSearchCanadaVintageDestination(manifest);
      const entryCount = Math.max(0, Number(manifest?.entries?.length || 0));
      const page = currentPage || this.searchCurrentMappedPage || this.currentSearchPage();
      const finalPageReady = this.mappedSearchRootCtaReady(manifest, page);
      if (
        !finalPageReady
        || !destination
        || this.root?.dataset?.searchMappedLoading === 'true'
        || (
          this.searchCurationIsFallbackLocked()
          && manifest.outcome !== 'exact-only'
        )
      ) {
        this.hideMappedSearchRootCta(mount);
        return;
      }

      const button = mount.querySelector('[data-ac-search-scope-toggle]');
      let canadaButton = mount.querySelector('[data-ac-search-canada-vintage]');
      const progressiveTail = mount.querySelector('[data-ac-search-progressive-tail]');
      if (progressiveTail) progressiveTail.hidden = true;
      mount.hidden = false;
      mount.dataset.state = 'ready';
      mount.dataset.focusedCount = String(entryCount);
      mount.setAttribute('aria-busy', 'false');

      if (canadaDestination) {
        if (!canadaButton) {
          canadaButton = document.createElement('a');
          canadaButton.className = 'vcc-ac-search-scope__button';
          canadaButton.setAttribute('data-ac-search-canada-vintage', '');
          mount.insertBefore(canadaButton, button || mount.firstChild);
        }
        canadaButton.hidden = false;
        canadaButton.href = canadaDestination;
        canadaButton.setAttribute('aria-label', 'Shop All Canada Vintage');
        canadaButton.textContent = 'Canada Vintage';
      } else if (canadaButton) {
        canadaButton.remove();
        canadaButton = null;
      }

      if (button) {
        button.hidden = false;
        const universeHandle = destination
          .split('/')
          .filter(Boolean)
          .pop();
        const universeLabels = {
          'vintage-classics': 'Vintage Classics',
          brands: 'Brands',
          'team-wear': 'Teams'
        };
        const universeLabel = universeLabels[universeHandle] || '';
        const buttonLabel = universeHandle === 'team-wear'
          ? 'Teams & Sports'
          : (universeLabel || 'All Products');
        const shopAllLabel = `Shop All ${buttonLabel}`;
        button.disabled = false;
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', shopAllLabel);
        button.dataset.acSearchUniverseUrl = destination;
        button.dataset.acSearchUniverseHandle = universeHandle;
        button.dataset.acSearchUniverseLabel = universeLabel;
        button.textContent = buttonLabel;
      }
    }

    async loadMappedBroadScope(manifest, options = {}) {
      if (!manifest?.sealed || !this.isCurrentMappedManifest(manifest)) return false;
      if (manifest.broadLoaded) return true;
      if (this.searchScopeLoad) return this.searchScopeLoad;

      const states = this.mappedSearchBroadStates(manifest);
      if (!states.length) return false;
      const scopeGeneration = Number.isFinite(Number(options.intentGeneration))
        ? Number(options.intentGeneration)
        : ++this.searchScopeGeneration;
      const timeoutMs = clampRange(options.timeoutMs, 9000, 1200, 12000);
      const focusedEntries = (manifest.focusedEntries || manifest.baseEntries || []).slice();
      const broadPageBudget = clampRange(options.broadPageBudget, 2, 1, 3);
      const broadItemBudget = Math.max(
        Number(manifest.pageSize) || 1,
        (Number(manifest.pageSize) || 1) * broadPageBudget
      );
      const broadOrder = (state) => {
        const source = state?.source || {};
        if (this.mappedSearchDomainRootFallback(source)) {
          return 1000 + Math.max(0, Number(state?.sourceIndex || 0));
        }
        return source.relationshipKind === 'region'
          ? 35
          : Number(source.tierOrder || 40);
      };
      const scratchStates = states
        .map((state) => Object.assign({}, state, {
          page: 0,
          exhausted: false,
          hasMore: true,
          failed: false
        }))
        .sort((left, right) => {
          return broadOrder(left) - broadOrder(right)
            || Number(left.sourceIndex || 0) - Number(right.sourceIndex || 0);
        });
      const tierGroups = [];
      scratchStates.forEach((state) => {
        const order = broadOrder(state);
        let group = tierGroups[tierGroups.length - 1];
        if (!group || group.order !== order) {
          group = { order, states: [] };
          tierGroups.push(group);
        }
        group.states.push(state);
      });
      const scratch = Object.assign({}, manifest, {
        entries: focusedEntries.slice(),
        baseEntries: focusedEntries.slice(),
        productKeys: new Set(focusedEntries.map((entry) => entry.key).filter(Boolean)),
        limits: Object.assign({}, manifest.limits, {
          maxItems: Math.max(
            focusedEntries.length,
            Math.min(
              Number(manifest.limits?.maxItems || Number.POSITIVE_INFINITY),
              focusedEntries.length + broadItemBudget
            )
          )
        }),
        sources: scratchStates,
        capped: false,
        cancelled: false,
        broadEntryCount: 0,
        broadSettledCount: 0
      });
      const controller = new AbortController();
      this.searchScopeAbortController?.abort();
      this.searchScopeAbortController = controller;

      let scopeLoad;
      scopeLoad = (async () => {
        let timedOut = false;
        let failureReason = '';
        const timeout = window.setTimeout(() => {
          timedOut = true;
          failureReason = 'timeout';
          controller.abort();
        }, timeoutMs);
        let settled = true;
        try {
          const engine = this.searchEngine() || {};
          const resultsByState = new Map(
            scratchStates.map((state) => [state, new Map()])
          );
          const fetchStateRound = async (targetStates, page) => {
            const units = this.relationshipTransportUnits(targetStates);
            const pairGroups = await Promise.all(units.map((unit) => {
              return this.fetchMappedRelationshipUnit(
                unit,
                page,
                engine,
                scratch.analysis,
                { signal: controller.signal }
              );
            }));
            const pairs = pairGroups.flat();
            pairs.forEach(({ state, result }) => {
              resultsByState.get(state).set(page, result);
            });
            return pairs.map(({ state, result }) => ({ state, page, result }));
          };

          const firstPages = await fetchStateRound(scratchStates, 1);
          if (firstPages.some((entry) => entry.result.failed)) {
            settled = false;
            failureReason = 'first-page-failed';
          }

          const applyStatePage = (state, page) => {
            if (page > 1 && state.exhausted) return true;
            const result = resultsByState.get(state).get(page);
            if (!result || result.failed) return false;
            const acceptedCount = this.appendMappedSearchEntries(scratch, state, result);
            state.acceptedCount = Math.max(0, Number(state.acceptedCount) || 0)
              + acceptedCount;
            scratch.broadSettledCount += 1;
            state.page = page;
            this.updateMappedSearchStatePageLimit(
              state,
              result,
              scratch.limits.maxSourcePages
            );
            const declaredCount = Math.max(
              0,
              Number(state.source?.relationshipProductCount || 0)
            );
            const declaredCountFloor = declaredCount - Math.min(
              2,
              Math.floor(declaredCount * 0.08)
            );
            const declaredCountReached = this.engineMode !== 'v4_metafield'
              && declaredCount > 0
              && state.acceptedCount >= declaredCountFloor;
            state.hasMore = Boolean(result.hasMore) && !declaredCountReached;
            state.exhausted = !state.hasMore;
            return true;
          };
          const applyStatePages = (targetStates, pages) => {
            for (const page of pages) {
              for (const state of targetStates) {
                if (scratch.capped) return true;
                if (!applyStatePage(state, page)) return false;
              }
            }
            return true;
          };
          const fetchAndApplyStatePages = async (targetStates, pages) => {
            for (const page of pages) {
              if (scratch.capped || controller.signal.aborted) return true;
              const activeStates = targetStates.filter((state) => {
                const stateMaxPages = Math.max(1, Number(state.maxPages || 1));
                return state.hasMore && !state.exhausted && page <= stateMaxPages;
              });
              if (!activeStates.length) continue;
              await fetchStateRound(activeStates, page);
              if (controller.signal.aborted) return false;
              if (!applyStatePages(activeStates, [page])) return false;
            }
            return true;
          };

          if (settled && !controller.signal.aborted) {
            for (const group of tierGroups) {
              if (!applyStatePages(group.states, [1])) {
                settled = false;
                failureReason = 'first-page-failed';
                break;
              }
              if (scratch.capped) break;

              const familySampleOnly = group.states.every((state) => {
                return ['brand_family', 'subject_family'].includes(
                  String(state?.source?.relationshipKind || '').toLowerCase()
                );
              });
              group.familySampleOnly = familySampleOnly;
              if (familySampleOnly) {
                group.states.forEach((state) => {
                  state.sampled = true;
                  state.hasMore = false;
                  state.exhausted = true;
                });
              }
            }
          }

          if (settled && !controller.signal.aborted && !scratch.capped) {
            for (const group of tierGroups) {
              const groupMaxSourcePages = group.familySampleOnly
                ? 1
                : Math.max(
                  1,
                  ...group.states.map((state) => {
                    return Math.min(
                      scratch.limits.maxSourcePages,
                      Math.max(1, Number(state.maxPages || scratch.limits.maxSourcePages))
                    );
                  })
                );
              const continuationPages = [];
              for (let page = 2; page <= groupMaxSourcePages; page += 1) {
                continuationPages.push(page);
              }
              if (
                continuationPages.length
                && !await fetchAndApplyStatePages(group.states, continuationPages)
              ) {
                settled = false;
                failureReason = 'continuation-failed';
                break;
              }
              if (scratch.capped) break;
            }
          }
        } catch (_error) {
          settled = false;
          failureReason = failureReason || 'exception';
        } finally {
          window.clearTimeout(timeout);
        }
        if (
          timedOut
          || controller.signal.aborted
          || !settled
          || scopeGeneration !== this.searchScopeGeneration
          || !this.isCurrentMappedManifest(manifest)
        ) {
          manifest.broadLoadFailure = failureReason
            || (controller.signal.aborted ? 'aborted' : 'stale-intent');
          return false;
        }

        manifest.broadEntries = scratch.entries.slice(focusedEntries.length);
        manifest.broadLoadFailure = '';
        manifest.broadLoaded = true;
        manifest.broadSourceHasMore = scratch.capped
          || scratchStates.some((state) => state.hasMore && !state.exhausted);
        manifest.broadHasMore = manifest.broadSourceHasMore;
        manifest.broadEntryCount = manifest.broadEntries.length;
        manifest.broadSettledCount = scratch.broadSettledCount;
        return true;
      })().finally(() => {
        if (this.searchScopeAbortController === controller) this.searchScopeAbortController = null;
        if (this.searchScopeLoad === scopeLoad) this.searchScopeLoad = null;
      });

      this.searchScopeLoad = scopeLoad;
      return scopeLoad;
    }

    applyMappedSearchScope(manifest, expanded, options = {}) {
      if (!manifest?.sealed || !this.isCurrentMappedManifest(manifest)) return false;
      if (expanded && !manifest.broadLoaded) return false;
      const previous = {
        baseEntries: manifest.baseEntries,
        expanded: manifest.broadExpanded,
        hasMore: manifest.hasMore,
        viewKey: manifest.viewKey
      };
      manifest.broadExpanded = expanded;
      manifest.baseEntries = expanded
        ? (manifest.focusedEntries || []).concat(manifest.broadEntries || [])
        : (manifest.focusedEntries || []).slice();
      manifest.hasMore = expanded ? manifest.broadHasMore : manifest.focusedHasMore;

      const url = options.url instanceof URL ? new URL(options.url) : new URL(window.location.href);
      if (expanded) url.searchParams.set('vcc_scope', 'broad');
      else url.searchParams.delete('vcc_scope');
      const applied = this.applyMappedSearchUrl(url, {
        forceView: true,
        history: options.history === undefined ? true : options.history,
        scroll: options.scroll === true
      });
      if (!applied) {
        manifest.baseEntries = previous.baseEntries;
        manifest.broadExpanded = previous.expanded;
        manifest.hasMore = previous.hasMore;
        manifest.viewKey = previous.viewKey;
      }
      return applied;
    }

    async expandMappedSearchScope() {
      const destination = this.mappedSearchUniverseDestination(this.searchManifest);
      if (!destination) return false;
      window.location.assign(destination);
      return true;
    }

    collapseMappedSearchScope() {
      return false;
    }

    toggleMappedSearchScope() {
      return this.expandMappedSearchScope();
    }

    restoreRequestedMappedSearchScope() {
      return;
    }

    mappedCardKey(card) {
      return this.searchProductKey({
        id: card?.dataset?.productId,
        handle: card?.dataset?.productHandle,
        url: card?.dataset?.productUrl
      });
    }

    animateMappedGrid(grid) {
      if (this.root.dataset.reducedMotion === 'true' || mqReduced.matches) return;
      if (!(grid instanceof HTMLElement) || typeof grid.animate !== 'function') return;
      grid.getAnimations().forEach((animation) => animation.cancel());
      grid.animate([
        { opacity: 0.35 },
        { opacity: 1 }
      ], {
        duration: 160,
        easing: 'ease-out'
      });
    }

    createMappedSearchResultBoundary(section, continued = false) {
      if (!section?.key || !section?.label) return null;
      const boundary = document.createElement('div');
      boundary.className = 'vcc-ac-search-boundary';
      boundary.setAttribute('data-ac-search-tier-boundary', '');
      boundary.dataset.acSearchSectionKey = section.key;
      boundary.dataset.acSearchSectionOrder = String(section.order ?? 0);
      boundary.dataset.acSearchSectionKind = section.kind || '';
      boundary.dataset.acSearchSectionContinued = continued ? 'true' : 'false';
      const eyebrow = document.createElement('span');
      eyebrow.textContent = 'Results Section';
      const heading = document.createElement('strong');
      heading.textContent = section.label;
      boundary.append(eyebrow, heading);
      return boundary;
    }

    mappedSearchRenderSignature(children) {
      return (children || []).map((child) => {
        if (child?.matches?.('[data-ac-search-tier-boundary]')) {
          return [
            'section',
            child.dataset.acSearchSectionKey || '',
            child.dataset.acSearchSectionOrder || '',
            child.querySelector('strong')?.textContent?.trim() || ''
          ].join(':');
        }
        if (child?.classList?.contains('vcc-ac-card')) {
          return `card:${this.mappedCardKey(child)}`;
        }
        return `node:${child?.nodeName || ''}`;
      });
    }

    prepareMappedSearchPage(manifest, requestedPage) {
      const grid = this.root.querySelector('[data-ac-grid]');
      if (this.searchCurationIsFallbackLocked()) return null;
      const authoritativeTeamRoute = manifest?.authoritativeTeamRoute === true;
      if (!grid || (!manifest?.entries?.length && !authoritativeTeamRoute)) return null;
      const totalPages = authoritativeTeamRoute
        ? 1
        : Math.max(1, Math.ceil(manifest.entries.length / manifest.pageSize));
      const page = Math.max(1, Math.min(totalPages, parseInt(requestedPage || '1', 10) || 1));
      const start = authoritativeTeamRoute ? 0 : (page - 1) * manifest.pageSize;
      const focusedKeys = new Set(
        (manifest.focusedEntries || []).map((entry) => entry.key).filter(Boolean)
      );
      const broadScopeActive = manifest.broadExpanded === true;
      const visibleEntries = authoritativeTeamRoute
        ? manifest.entries
        : manifest.entries.slice(start, start + manifest.pageSize);
      const preparedEntries = visibleEntries
        .map((entry) => ({
          entry,
          card: this.materializeMappedCard(entry, { decorate: false }),
          scope: broadScopeActive && !focusedKeys.has(entry.key) ? 'broad' : 'focused'
        }))
        .filter((preparedEntry) => preparedEntry.card);
      const entries = preparedEntries.map((preparedEntry) => preparedEntry.entry);
      const cards = preparedEntries.map((preparedEntry) => preparedEntry.card);
      const scopes = preparedEntries.map((preparedEntry) => preparedEntry.scope);
      const broadBoundaryIndex = -1;
      if (!cards.length && !authoritativeTeamRoute) return null;

      const children = [];
      let previousSectionKey = '';
      preparedEntries.forEach((preparedEntry, index) => {
        const section = this.mappedSearchResultSection(preparedEntry.entry, manifest);
        if (section && section.key !== previousSectionKey) {
          const priorEntry = manifest.entries[start + index - 1];
          const priorSection = index === 0 && priorEntry
            ? this.mappedSearchResultSection(priorEntry, manifest)
            : null;
          const boundary = this.createMappedSearchResultBoundary(
            section,
            Boolean(index === 0 && priorSection?.key === section.key)
          );
          if (boundary) children.push(boundary);
        }
        children.push(preparedEntry.card);
        previousSectionKey = section?.key || '';
      });
      const currentSignature = this.mappedSearchRenderSignature(Array.from(grid.children));
      const nextSignature = this.mappedSearchRenderSignature(children);
      const samePage = currentSignature.length === nextSignature.length
        && currentSignature.every((value, index) => value === nextSignature[index]);
      const previousCards = Array.from(grid.children);
      return {
        grid,
        page,
        entries,
        cards,
        scopes,
        children,
        broadBoundaryIndex,
        samePage,
        authoritativeTeamRoute,
        rollback: {
          cards: previousCards,
           cardState: previousCards.map((card) => ({
             source: card.dataset.vccMappedSource,
             score: card.dataset.vccSearchScore,
             scope: card.dataset.vccMappedScope,
             tierKey: card.dataset.vccSearchTierKey,
             tierOrder: card.dataset.vccSearchTierOrder,
             tierLabel: card.dataset.vccSearchTierLabel,
             tierKind: card.dataset.vccSearchTierKind,
             tierScope: card.dataset.vccSearchTierScope,
             tierMemberKey: card.dataset.vccSearchTierMemberKey,
             sectionKey: card.dataset.vccSearchSectionKey,
             sectionOrder: card.dataset.vccSearchSectionOrder,
             sectionLabel: card.dataset.vccSearchSectionLabel,
             sectionKind: card.dataset.vccSearchSectionKind
           })),
          mappedEnhanced: this.root.dataset.searchMappedEnhanced,
          mappedPagePending: this.root.dataset.searchMappedPagePending,
          currentMappedPage: this.searchCurrentMappedPage,
          emptyHidden: this.root.querySelector('.vcc-ac-empty')?.hidden
        }
      };
    }

    applyPreparedMappedSearchPage(prepared, options = {}) {
      if (!prepared || this.searchCurationIsFallbackLocked()) return null;
      const {
        grid,
        page,
        entries,
        cards,
        scopes,
        children,
        broadBoundaryIndex,
        samePage,
        authoritativeTeamRoute
      } = prepared;
      if (!(grid instanceof HTMLElement) || (!cards.length && !authoritativeTeamRoute)) return null;

      cards.forEach((card, index) => {
        const entry = entries[index];
        // Mapped pages render at most one customer-visible page at a time.
        // Force those cards to participate in layout immediately: the theme's
        // 420px content-visibility placeholder is substantially taller than
        // the real archive card and otherwise leaves giant gaps until scroll.
        card.style.contentVisibility = 'visible';
        card.dataset.vccMappedSource = entry?.sourceKind || '';
        card.dataset.vccSearchScore = String(entry?.score || 0);
        card.dataset.vccMappedScope = scopes[index] || 'focused';
        if (this.engineMode === 'v4_metafield') {
          card.dataset.vccSearchTierKey = String(entry?.tierKey || '');
          card.dataset.vccSearchTierOrder = String(entry?.tierOrder ?? '');
          card.dataset.vccSearchTierLabel = String(entry?.tierLabel || '');
          card.dataset.vccSearchTierKind = String(entry?.tierKind || '');
          card.dataset.vccSearchTierScope = String(entry?.tierScope || '');
          card.dataset.vccSearchTierMemberKey = String(entry?.tierMemberKey || '');
          card.dataset.vccSearchSectionKey = String(entry?.resultSectionKey || '');
          card.dataset.vccSearchSectionOrder = String(entry?.resultSectionOrder ?? '');
          card.dataset.vccSearchSectionLabel = String(entry?.resultSectionLabel || '');
          card.dataset.vccSearchSectionKind = String(entry?.resultSectionKind || '');
        }
      });
      if (!samePage) {
        grid.replaceChildren(...children);
      }
      this.searchCurrentMappedPage = page;
      this.root.dataset.searchMappedEnhanced = 'true';
      this.root.dataset.searchMappedPagePending = 'false';
      const empty = this.root.querySelector('.vcc-ac-empty');
      if (empty) empty.hidden = true;
      return page;
    }

    finalizePreparedMappedSearchPage(prepared, options = {}) {
      if (!prepared || prepared.samePage) return;
      const { grid, cards } = prepared;
      if (!(grid instanceof HTMLElement) || !cards.length) return;
      try {
        this.gridImageQueue = this.gridImageQueue.filter((image) => image?.isConnected);
      } catch (error) {
        this.log('Mapped grid image queue cleanup was skipped', error);
      }
      try {
        this.syncWishlistStates();
      } catch (error) {
        this.log('Mapped grid wishlist sync was skipped', error);
      }
      try {
        this.configureImageLoading(grid);
      } catch (error) {
        this.log('Mapped grid image configuration was skipped', error);
      }
      try {
        if (this.root.dataset.layout === 'atlas') {
          this.pauseAtlasPreloads();
          this.preloadAtlasImages();
        }
        this.updateAtlasInspector(cards[0]);
        this.syncAtlasInspectorHeight();
      } catch (error) {
        this.log('Mapped grid Atlas sync was skipped', error);
      }
      if (options.animate !== false && !this.searchCurationIsPending()) {
        window.requestAnimationFrame(() => this.animateMappedGrid(grid));
      }
    }

    rollbackPreparedMappedSearchPage(prepared) {
      const rollback = prepared?.rollback;
      const grid = prepared?.grid;
      if (!(grid instanceof HTMLElement) || !rollback?.cards) return false;
      grid.replaceChildren(...rollback.cards);
      rollback.cards.forEach((card, index) => {
        const cardState = rollback.cardState[index] || {};
        if (cardState.source === undefined) delete card.dataset.vccMappedSource;
        else card.dataset.vccMappedSource = cardState.source;
        if (cardState.score === undefined) delete card.dataset.vccSearchScore;
        else card.dataset.vccSearchScore = cardState.score;
        if (cardState.scope === undefined) delete card.dataset.vccMappedScope;
        else card.dataset.vccMappedScope = cardState.scope;
        if (cardState.tierKey === undefined) delete card.dataset.vccSearchTierKey;
        else card.dataset.vccSearchTierKey = cardState.tierKey;
        if (cardState.tierOrder === undefined) delete card.dataset.vccSearchTierOrder;
        else card.dataset.vccSearchTierOrder = cardState.tierOrder;
        if (cardState.tierLabel === undefined) delete card.dataset.vccSearchTierLabel;
        else card.dataset.vccSearchTierLabel = cardState.tierLabel;
        if (cardState.tierKind === undefined) delete card.dataset.vccSearchTierKind;
        else card.dataset.vccSearchTierKind = cardState.tierKind;
        if (cardState.tierScope === undefined) delete card.dataset.vccSearchTierScope;
        else card.dataset.vccSearchTierScope = cardState.tierScope;
        if (cardState.tierMemberKey === undefined) delete card.dataset.vccSearchTierMemberKey;
        else card.dataset.vccSearchTierMemberKey = cardState.tierMemberKey;
        if (cardState.sectionKey === undefined) delete card.dataset.vccSearchSectionKey;
        else card.dataset.vccSearchSectionKey = cardState.sectionKey;
        if (cardState.sectionOrder === undefined) delete card.dataset.vccSearchSectionOrder;
        else card.dataset.vccSearchSectionOrder = cardState.sectionOrder;
        if (cardState.sectionLabel === undefined) delete card.dataset.vccSearchSectionLabel;
        else card.dataset.vccSearchSectionLabel = cardState.sectionLabel;
        if (cardState.sectionKind === undefined) delete card.dataset.vccSearchSectionKind;
        else card.dataset.vccSearchSectionKind = cardState.sectionKind;
      });
      if (rollback.mappedEnhanced === undefined) delete this.root.dataset.searchMappedEnhanced;
      else this.root.dataset.searchMappedEnhanced = rollback.mappedEnhanced;
      if (rollback.mappedPagePending === undefined) delete this.root.dataset.searchMappedPagePending;
      else this.root.dataset.searchMappedPagePending = rollback.mappedPagePending;
      this.searchCurrentMappedPage = rollback.currentMappedPage;
      const empty = this.root.querySelector('.vcc-ac-empty');
      if (empty && rollback.emptyHidden !== undefined) empty.hidden = rollback.emptyHidden;
      return true;
    }

    renderMappedSearchPage(manifest, requestedPage, options = {}) {
      const prepared = this.prepareMappedSearchPage(manifest, requestedPage);
      const page = this.applyPreparedMappedSearchPage(prepared, options);
      if (page) this.finalizePreparedMappedSearchPage(prepared, options);
      return page;
    }

    renderMappedSearchNavigation(manifest, page) {
      if (!manifest?.sealed) return;
      if (manifest.authoritativeTeamRoute === true) {
        this.renderMappedPagination(1, 1, 1, false);
        const statusPageSize = Math.max(1, manifest.entries.length);
        this.applyMappedResultStatus(
          manifest.entries.length,
          1,
          statusPageSize,
          manifest.viewHasMore
        );
        this.renderMappedSearchScopeControl(manifest, 1);
        this.renderMappedSearchRecoveryNotice('');
        return;
      }
      this.renderMappedPagination(manifest.entries.length, page, manifest.pageSize, manifest.viewHasMore);
      this.applyMappedResultStatus(manifest.entries.length, page, manifest.pageSize, manifest.viewHasMore);
      this.renderMappedSearchScopeControl(manifest, page);
      this.renderMappedSearchRecoveryNotice('');
    }

    mappedSearchPageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('vcc_mapped', '1');
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      this.syncUrlLayoutParam(url);
      return url;
    }

    scrollMappedResultsIntoView() {
      const behavior = this.root.dataset.reducedMotion === 'true' || mqReduced.matches ? 'auto' : 'smooth';
      const pageWrapper = document.querySelector('.page-wrapper');
      if (pageWrapper instanceof HTMLElement) {
        pageWrapper.scrollTo({ top: 0, behavior });
      }
      window.scrollTo({
        top: 0,
        behavior
      });
    }

    navigateMappedSearchPage(requestedPage, requestedUrl = null, options = {}) {
      const manifest = this.searchManifest;
      if (!manifest?.sealed || !this.isCurrentMappedManifest(manifest)) return false;
      const totalPages = Math.max(1, Math.ceil(manifest.entries.length / manifest.pageSize));
      const page = Math.max(1, Math.min(totalPages, parseInt(requestedPage || '1', 10) || 1));
      const url = requestedUrl instanceof URL ? requestedUrl : this.mappedSearchPageUrl(page);
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      this.syncUrlLayoutParam(url);
      return this.applyMappedSearchUrlWithProgress(url, {
        history: options.history !== false,
        scroll: options.scroll !== false
      });
    }

    nativeSearchCacheKey(sourceUrl = window.location.href) {
      const url = sourceUrl instanceof URL ? new URL(sourceUrl) : new URL(sourceUrl, window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.searchParams.delete('vcc_view');
      url.searchParams.delete('vcc_scope');
      url.searchParams.delete('vcc_mapped');
      const params = Array.from(url.searchParams.entries())
        .sort(([leftName, leftValue], [rightName, rightValue]) => {
          return leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue);
        })
        .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
        .join('&');
      return `${url.pathname}::${params}`;
    }

    captureNativeSearchSnapshot(sourceRoot = this.root, page = this.currentSearchPage(), options = {}) {
      const grid = sourceRoot?.querySelector?.('[data-ac-grid]');
      if (!grid) return null;
      const sourceCards = Array.from(grid.querySelectorAll(':scope > .vcc-ac-card'));
      const cards = options.importCards === true
        ? sourceCards.map((card) => document.importNode(card, true))
        : sourceCards;
      if (!cards.length) return null;
      const pagination = sourceRoot.querySelector('[data-ac-numbered-pagination]');
      const desktopPage = sourceRoot.querySelector('.vcc-ac-status--desktop [data-ac-status-page]');
      const desktopRange = sourceRoot.querySelector('.vcc-ac-status--desktop [data-ac-status-range]');
      const mobileResults = sourceRoot.querySelector('.vcc-ac-status--mobile [data-ac-status-results]');
      const mobilePage = sourceRoot.querySelector('.vcc-ac-status--mobile [data-ac-status-page]');
      return {
        page,
        cards,
        paginationHtml: this.sanitizedNativePaginationHtml(pagination),
        paginationHidden: Boolean(pagination?.hidden),
        status: {
          desktopPage: desktopPage?.textContent || '',
          desktopRange: desktopRange?.textContent || '',
          mobileResults: mobileResults?.textContent || '',
          mobilePage: mobilePage?.textContent || ''
        }
      };
    }

    nativeIdleTask(callback, timeout = 1000) {
      return new Promise((resolve) => {
        const run = () => {
          if (this.destroyed || typeof callback !== 'function') {
            resolve(null);
            return;
          }
          try {
            resolve(callback());
          } catch (error) {
            this.log('Native search background preparation was skipped', error);
            resolve(null);
          }
        };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(run, { timeout });
        } else if (window.scheduler?.postTask) {
          window.scheduler.postTask(run, { priority: 'background' });
        } else {
          window.setTimeout(run, 32);
        }
      });
    }

    customerSearchUrl(sourceUrl = window.location.href) {
      const url = sourceUrl instanceof URL ? new URL(sourceUrl) : new URL(sourceUrl, window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      return url;
    }

    sanitizedNativePaginationHtml(pagination) {
      if (!(pagination instanceof HTMLElement)) return '';
      const clone = pagination.cloneNode(true);
      clone.querySelectorAll('a[href]').forEach((link) => {
        try {
          link.href = this.customerSearchUrl(link.getAttribute('href')).toString();
        } catch (_error) {}
      });
      return clone.innerHTML;
    }

    nativeSearchTotalPages(snapshot) {
      const pageLabel = String(snapshot?.status?.desktopPage || snapshot?.status?.mobilePage || '');
      return Math.max(1, parseInt(pageLabel.match(/\bof\s+(\d+)/i)?.[1] || '1', 10) || 1);
    }

    nativeSearchSnapshotUrl(page, sourceUrl = window.location.href) {
      const url = this.customerSearchUrl(sourceUrl);
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    fetchNativeSearchSnapshot(page, sourceUrl = window.location.href, options = {}) {
      const url = this.customerSearchUrl(sourceUrl);
      const cacheKey = this.nativeSearchCacheKey(url);
      const key = `${cacheKey}::${page}`;
      if (this.searchNativePageCache.has(key)) {
        return Promise.resolve(this.searchNativePageCache.get(key));
      }
      if (this.searchNativePagePromises.has(key)) return this.searchNativePagePromises.get(key);

      const requestUrl = this.nativeSearchSnapshotUrl(page, url);
      const generation = Number(options.generation) || this.searchNativeWarmGeneration;
      const signal = options.signal || this.searchNativeAbortController?.signal;
      const stillCurrent = () => {
        return !this.destroyed
          && !signal?.aborted
          && generation === this.searchNativeWarmGeneration
          && cacheKey === this.searchNativeCacheKey;
      };
      const request = fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        signal
      })
        .then((response) => response.ok ? response.text() : '')
        .then((html) => {
          if (!html || !stillCurrent()) return null;
          return this.nativeIdleTask(() => {
            if (!stillCurrent()) return null;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const sourceRoot = doc.querySelector('[data-vcc-ac][data-kind="search"]');
            const snapshot = this.captureNativeSearchSnapshot(sourceRoot, page, { importCards: true });
            if (snapshot && stillCurrent()) {
              this.searchNativePageCache.set(key, snapshot);
              this.warmSearchPageImages(snapshot.cards);
            }
            return stillCurrent() ? snapshot : null;
          });
        })
        .then((snapshot) => {
          return snapshot;
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return null;
          this.log('Native search page preparation was skipped', error);
          return null;
        })
        .finally(() => {
          if (this.searchNativePagePromises.get(key) === request) {
            this.searchNativePagePromises.delete(key);
          }
        });
      this.searchNativePagePromises.set(key, request);
      return request;
    }

    prepareNativeSearchPages() {
      const generation = ++this.searchNativeWarmGeneration;
      if (this.kind !== 'search' || !this.searchCurationIsFallbackLocked()) return;
      this.searchNativeAbortController?.abort();
      this.searchNativeAbortController = new AbortController();
      this.searchNativePagePromises.clear();
      const currentUrl = this.customerSearchUrl(window.location.href);
      const cacheKey = this.nativeSearchCacheKey(currentUrl);
      if (this.searchNativeCacheKey && this.searchNativeCacheKey !== cacheKey) {
        this.searchNativePageCache.clear();
        this.searchNativePagePromises.clear();
      }
      this.searchNativeCacheKey = cacheKey;
      const currentPage = this.currentSearchPage();
      const currentSnapshot = this.captureNativeSearchSnapshot(this.root, currentPage);
      if (!currentSnapshot) return;
      this.searchNativePageCache.set(`${cacheKey}::${currentPage}`, currentSnapshot);
      const totalPages = this.nativeSearchTotalPages(currentSnapshot);
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const constrained = Boolean(connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || ''));
      const pages = [];
      for (let page = 1; page <= Math.min(4, totalPages); page += 1) {
        if (page !== currentPage) pages.push(page);
      }
      const warmPages = constrained
        ? pages.filter((page) => page === Math.min(totalPages, currentPage + 1)).slice(0, 1)
        : pages;
      const run = async () => {
        for (const page of warmPages) {
          if (
            generation !== this.searchNativeWarmGeneration
            || this.destroyed
            || !this.root.isConnected
            || this.searchNativeCacheKey !== cacheKey
          ) {
            return;
          }
          await this.fetchNativeSearchSnapshot(page, currentUrl, {
            generation,
            signal: this.searchNativeAbortController.signal
          });
        }
      };
      const schedule = () => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => run(), { timeout: 700 });
        } else {
          window.setTimeout(run, 180);
        }
      };
      if (document.readyState === 'complete') schedule();
      else window.addEventListener('load', schedule, { once: true });
    }

    canNavigateNativeSearchPage(sourceUrl) {
      if (this.kind !== 'search' || !this.searchCurationIsFallbackLocked()) return false;
      return Boolean(
        this.searchNativeCacheKey
        && this.nativeSearchCacheKey(sourceUrl) === this.searchNativeCacheKey
      );
    }

    nativeSearchSnapshotReady(page, sourceUrl = window.location.href) {
      const key = `${this.nativeSearchCacheKey(sourceUrl)}::${Math.max(1, Number(page) || 1)}`;
      return this.searchNativePageCache.has(key);
    }

    applyNativeSearchSnapshot(snapshot, sourceUrl, options = {}) {
      if (!snapshot?.cards?.length) return false;
      const url = this.customerSearchUrl(sourceUrl);
      const cards = snapshot.cards.filter((card) => card instanceof HTMLElement);
      if (!cards.length) return false;
      const grid = this.root.querySelector('[data-ac-grid]');
      if (!grid) return false;

      const page = Math.max(1, parseInt(snapshot.page || '1', 10) || 1);
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      this.syncUrlLayoutParam(url);
      try {
        grid.replaceChildren(...cards);
        grid.setAttribute('aria-busy', 'false');
        const pagination = this.root.querySelector('[data-ac-numbered-pagination]');
        if (pagination) {
          pagination.innerHTML = snapshot.paginationHtml;
          pagination.hidden = snapshot.paginationHidden || !snapshot.paginationHtml;
        }
        const status = Object.assign({}, snapshot.status || {});
        const totalPages = this.nativeSearchTotalPages({ status });
        const totalLabel = String(status.desktopRange || '')
          .match(/\bof\s+(.+?)\s+items\b/i)?.[1] || '';
        status.desktopPage = `Page ${page} of ${totalPages}`;
        status.mobilePage = `Page ${page} of ${totalPages}`;
        if (totalLabel) {
          const pageSize = this.searchPageSize();
          const first = ((page - 1) * pageSize) + 1;
          const last = first + cards.length - 1;
          const formatter = new Intl.NumberFormat('en-US');
          status.desktopRange = `Showing ${formatter.format(first)}-${formatter.format(last)} of ${totalLabel} items`;
        }
        Object.entries(status).forEach(([name, value]) => {
          const selectors = {
            desktopPage: '.vcc-ac-status--desktop [data-ac-status-page]',
            desktopRange: '.vcc-ac-status--desktop [data-ac-status-range]',
            mobileResults: '.vcc-ac-status--mobile [data-ac-status-results]',
            mobilePage: '.vcc-ac-status--mobile [data-ac-status-page]'
          };
          const target = this.root.querySelector(selectors[name]);
          if (target) target.textContent = value;
        });
        const empty = this.root.querySelector('.vcc-ac-empty');
        if (empty) empty.hidden = true;
        this.root.dataset.searchNativePage = String(page);
        if (options.history === 'replace') {
          window.history.replaceState({
            ...this.filterDialogHistoryStateWithoutMarker(),
            vccNativeSearch: true,
            page
          }, '', url);
        } else if (options.history !== false) {
          window.history.pushState({ vccNativeSearch: true, page }, '', url);
        }
      } catch (error) {
        this.log('Prepared native search page could not be applied', error);
        return false;
      }
      try { this.syncMobilePager(); } catch (error) { this.log('Native pager sync skipped', error); }
      try { this.syncWishlistStates(); } catch (error) { this.log('Native wishlist sync skipped', error); }
      try { this.configureImageLoading(grid); } catch (error) { this.log('Native image sync skipped', error); }
      try {
        if (this.root.dataset.layout === 'atlas') {
          this.pauseAtlasPreloads();
          this.preloadAtlasImages();
        }
        this.updateAtlasInspector(cards[0]);
        this.syncAtlasInspectorHeight();
      } catch (error) {
        this.log('Native Atlas sync skipped', error);
      }
      if (options.scroll !== false) this.scrollMappedResultsIntoView();
      this.commitFilterDialogObservedRoute(url);
      return true;
    }

    navigateNativeSearchPage(requestedPage, requestedUrl = null, options = {}) {
      const url = this.customerSearchUrl(
        requestedUrl instanceof URL ? requestedUrl : this.mappedSearchPageUrl(requestedPage)
      );
      if (!this.canNavigateNativeSearchPage(url)) return false;
      const page = Math.max(1, parseInt(requestedPage || '1', 10) || 1);
      const generation = ++this.searchViewGeneration;
      const key = `${this.nativeSearchCacheKey(url)}::${page}`;
      const snapshot = this.searchNativePageCache.get(key);
      if (!snapshot) return false;
      this.root.dataset.searchNativeUpdating = 'true';
      let applied = false;
      try {
        if (generation === this.searchViewGeneration && !this.destroyed && this.root.isConnected) {
          applied = this.applyNativeSearchSnapshot(snapshot, url, options);
        }
      } finally {
        if (generation === this.searchViewGeneration) {
          this.root.dataset.searchNativeUpdating = 'false';
        }
      }
      if (applied) return true;
      window.VCCSearchTransition?.markSecondary?.(
        'page',
        url.searchParams.get('vcc_raw') || url.searchParams.get('q') || ''
      );
      window.location.assign(url.toString());
      return false;
    }

    onSearchPopState() {
      const curationPhase = this.root.dataset.searchCuration;
      if (curationPhase === 'pending' || curationPhase === 'committing') {
        this.cancelSearchCurationWork();
        window.location.reload();
        return;
      }
      this.cancelMappedSearchScopeLoad();
      const manifest = this.searchManifest;
      if (manifest?.sealed && this.isCurrentMappedManifest(manifest)) {
        const url = new URL(window.location.href);
        const wantsBroad = this.mappedSearchScopeRequested(url);
        if (wantsBroad !== manifest.broadExpanded) {
          if (wantsBroad) {
            if (manifest.broadLoaded) {
              this.applyMappedSearchScope(manifest, true, { history: false, scroll: false, url });
            } else {
              this.expandMappedSearchScope({ history: false, url });
            }
          } else {
            this.collapseMappedSearchScope({ history: false, url });
          }
          return;
        }
        if (this.applyMappedSearchUrl(url, { history: false, scroll: false })) {
          return;
        }
        window.location.reload();
        return;
      }
      if (this.searchCurationIsFallbackLocked()) {
        const page = this.currentSearchPage();
        const url = new URL(window.location.href);
        if (this.canNavigateNativeSearchPage(url) && this.nativeSearchSnapshotReady(page, url)) {
          this.navigateNativeSearchPage(page, url, {
            history: false,
            scroll: false
          });
        } else {
          window.location.reload();
        }
        return;
      }
      window.location.reload();
    }

    renderMappedPagination(total, currentPage, pageSize, hasMore) {
      const wrapper = this.root.querySelector('[data-ac-numbered-pagination]');
      if (!wrapper) return;
      if (this.root.dataset.searchProgressiveLoading === 'true') {
        wrapper.replaceChildren();
        wrapper.hidden = true;
        this.syncMobilePager();
        return;
      }
      const pages = Math.max(1, Math.ceil(total / pageSize));
      if (pages <= 1) {
        wrapper.replaceChildren();
        wrapper.hidden = true;
        this.syncMobilePager();
        return;
      }

      const pageUrl = (page) => {
        const next = this.mappedSearchPageUrl(page);
        return `${next.pathname}${next.search}${next.hash}`;
      };
      const nav = document.createElement('nav');
      nav.className = 'vcc-ac-pagination';
      nav.setAttribute('aria-label', 'Results pages');
      const pageList = document.createElement('div');
      pageList.className = 'vcc-ac-pagination__pages';
      const appendPage = (page) => {
        if (page === currentPage) {
          const span = document.createElement('span');
          span.setAttribute('aria-current', 'page');
          span.textContent = String(page);
          pageList.append(span);
          return;
        }
        const link = document.createElement('a');
        link.href = pageUrl(page);
        link.textContent = String(page);
        pageList.append(link);
      };

      if (currentPage > 1) {
        const previous = document.createElement('a');
        previous.href = pageUrl(currentPage - 1);
        previous.rel = 'prev';
        previous.setAttribute('aria-label', 'Previous page');
        previous.textContent = '<';
        nav.append(previous);
      }

      appendPage(1);
      if (currentPage > 3) {
        const gap = document.createElement('span');
        gap.textContent = '...';
        pageList.append(gap);
      }
      for (let page = Math.max(2, currentPage - 1); page <= Math.min(pages - 1, currentPage + 1); page += 1) {
        appendPage(page);
      }
      if (currentPage < pages - 2) {
        const gap = document.createElement('span');
        gap.textContent = '...';
        pageList.append(gap);
      }
      if (pages > 1) appendPage(pages);
      nav.append(pageList);

      if (currentPage < pages) {
        const next = document.createElement('a');
        next.href = pageUrl(currentPage + 1);
        next.rel = 'next';
        next.setAttribute('aria-label', 'Next page');
        next.textContent = '>';
        nav.append(next);
      }

      wrapper.replaceChildren(nav);
      wrapper.hidden = false;
      this.syncMobilePager();
    }

    applyMappedResultStatus(total, currentPage, pageSize, hasMore) {
      const label = `${new Intl.NumberFormat('en-US').format(total)}${hasMore ? '+' : ''}`;
      const pages = Math.max(1, Math.ceil(total / pageSize));
      const first = total === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
      const last = Math.min(currentPage * pageSize, total);
      const desktopPage = this.root.querySelector('.vcc-ac-status--desktop [data-ac-status-page]');
      const desktopRange = this.root.querySelector('.vcc-ac-status--desktop [data-ac-status-range]');
      const mobileResults = this.root.querySelector('.vcc-ac-status--mobile [data-ac-status-results]');
      const mobilePage = this.root.querySelector('.vcc-ac-status--mobile [data-ac-status-page]');
      if (desktopPage) desktopPage.textContent = `Page ${currentPage} of ${pages}`;
      if (desktopRange) desktopRange.textContent = `Showing ${first}-${last} of ${label} items`;
      if (mobileResults) mobileResults.textContent = `Results - ${label} items`;
      if (mobilePage) mobilePage.textContent = `Page ${currentPage} of ${pages}`;
    }

    progressiveMappedSearchEligible(sourceUrl = window.location.href) {
      if (this.engineMode !== 'v4_metafield' || this.currentSearchPage() !== 1) return false;
      const url = this.normalizeMappedUsdPriceUrl(
        sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.href)
      );
      const band = String(
        url.searchParams.get('filter.p.m.custom.usd_price_bracket') || ''
      ).trim();
      if (USD_PRICE_BRACKETS.has(band)) return false;
      const sort = String(url.searchParams.get('sort_by') || 'relevance').trim().toLowerCase();
      return sort === '' || sort === 'relevance';
    }

    clearV4ProgressiveDeadline(manifest = null) {
      if (this.v4ProgressiveDeadlineTimer !== null) {
        window.clearTimeout(this.v4ProgressiveDeadlineTimer);
        this.v4ProgressiveDeadlineTimer = null;
      }
      if (manifest) {
        manifest.progressiveDeadlineActive = false;
        delete manifest.progressiveDeadlineAt;
      }
      this.root.dataset.v4ProgressiveDeadlineActive = 'false';
    }

    armV4ProgressiveDeadline(manifest, controller, generation, grid, analysis) {
      this.clearV4ProgressiveDeadline(manifest);
      if (
        !manifest
        || !controller
        || controller.signal.aborted
        || manifest.progressiveCommitted !== true
      ) return false;
      manifest.progressiveDeadlineActive = true;
      manifest.progressiveSlow = false;
      manifest.progressiveDeadlineAt = Date.now() + V4_PROGRESSIVE_DEADLINE_MS;
      this.root.dataset.v4ProgressiveDeadlineActive = 'true';
      this.root.dataset.v4ProgressiveDeadlineMs = String(V4_PROGRESSIVE_DEADLINE_MS);
      this.root.dataset.v4ProgressiveSlow = 'false';
      delete this.root.dataset.v4ProgressiveSlowQuery;
      this.v4ProgressiveDeadlineTimer = window.setTimeout(() => {
        this.v4ProgressiveDeadlineTimer = null;
        if (
          generation !== this.searchCurationGeneration
          || controller.signal.aborted
          || manifest.cancelled
          || this.destroyed
          || !this.root.isConnected
          || !this.isCurrentMappedManifest(manifest)
          || manifest.progressiveLoading !== true
        ) return;
        manifest.progressiveDeadlineActive = false;
        const continueVintageClassics = this.engineMode === 'v4_metafield'
          && String(
            manifest?.analysis?.rootCta?.collectionHandle
            || analysis?.rootCta?.collectionHandle
            || ''
          ).trim().toLowerCase() === 'vintage-classics';
        const continueAuthoritativeTeam = this.engineMode === 'v4_metafield'
          && (
            manifest?.analysis?.authoritativeTeamRoute === true
            || analysis?.authoritativeTeamRoute === true
            || manifest?.authoritativeTeamRoute === true
          );
        if (continueVintageClassics || continueAuthoritativeTeam) {
          // The exact/primary prefix is already visible. Vintage Classics and
          // authoritative Team routes have finite, card-attested relationship
          // tiers whose request count varies by entity, so the 12-second
          // watchdog is informational here rather than a terminal abort.
          // Per-request timeouts, retries, source page caps and route/destroy
          // aborts continue to bound the background work.
          manifest.progressiveSlow = true;
          this.root.dataset.v4ProgressiveDeadlineActive = 'false';
          this.root.dataset.v4ProgressiveSlow = 'true';
          this.root.dataset.v4ProgressiveSlowQuery = String(
            analysis?.raw || analysis?.normalized || manifest.query || ''
          );
          return;
        }
        manifest.progressiveTimedOut = true;
        manifest.continuationInterrupted = true;
        manifest.degradedReason = 'progressive-timeout';
        this.root.dataset.v4ProgressiveDeadlineActive = 'false';
        this.root.dataset.v4ProgressiveTimedOut = 'true';
        this.root.dataset.v4ProgressiveTimeoutQuery = String(
          analysis?.raw || analysis?.normalized || manifest.query || ''
        );
        controller.abort();
        if (grid) this.setMappedSearchLoading(grid, true);
      }, V4_PROGRESSIVE_DEADLINE_MS);
      return true;
    }

    stageProgressiveMappedSearchFrontier(manifest, exactStates, coreStates) {
      const exactEntries = Array.isArray(manifest?.exactEntries)
        ? manifest.exactEntries.slice()
        : [];
      const exactCount = exactEntries.length;
      const completedStrictTierCount = Math.max(
        0,
        Number(manifest?.completedStrictTierCount) || 0
      );
      const settledEntries = (
        completedStrictTierCount > 0
        && Array.isArray(manifest?.progressiveSettledEntries)
        && manifest.progressiveSettledEntries.length >= exactCount
      )
        ? manifest.progressiveSettledEntries.slice()
        : exactEntries.slice();
      const hasRelatedContribution = settledEntries.length > exactCount;
      const exactFillsVisiblePage = exactCount >= Math.max(1, Number(manifest?.pageSize) || 1);
      const completeRelatedTierAvailable = Boolean(
        hasRelatedContribution
        && completedStrictTierCount > 0
      );
      const exactTierTerminal = Boolean(
        exactCount > 0
        && Array.isArray(exactStates)
        && exactStates.length > 0
        && exactStates.every((state) => {
          return Number(state?.page || 0) >= 1
            && state.failed !== true
            && state.exhausted === true
            && state.hasMore !== true;
        })
      );
      const terminalExactBalanceRequired = this.v4MappedSearchBalancesExactTier(
        manifest?.analysis
      );
      const authoritativeExactPageOneReady = Boolean(
        manifest?.analysis?.authoritativeTeamRoute === true
        && exactCount > 0
        && Array.isArray(exactStates)
        && exactStates.length > 0
        && (() => {
          const strictState = exactStates.find((state) => {
            return String(state?.source?.clauseRole || '') === 'strict-exact';
          }) || exactStates[0];
          return Number(strictState?.page || 0) >= 1 && strictState?.failed !== true;
        })()
      );
      if (
        (terminalExactBalanceRequired && !exactTierTerminal)
        || (
          !exactFillsVisiblePage
          && !exactTierTerminal
          && !completeRelatedTierAvailable
          && !authoritativeExactPageOneReady
        )
        || manifest.cancelled
        || manifest.capped
        || !settledEntries.length
        || !this.progressiveMappedSearchEligible()
      ) return false;

      const accumulatedEntries = manifest.entries.slice();
      const canonicalEntries = settledEntries.slice();
      manifest.entries = canonicalEntries.slice();
      manifest.relationshipSettled = false;
      manifest.outcome = hasRelatedContribution ? 'focused-progressive' : 'exact-progressive';
      manifest.hasMore = hasRelatedContribution
        ? coreStates.some((state) => state.hasMore && !state.exhausted)
        : true;
      manifest.focusedHasMore = manifest.hasMore;
      manifest.sealed = true;
      manifest.progressiveLoading = true;
      manifest.progressiveInitialEntryCount = canonicalEntries.length;
      manifest.progressiveInitialExactCount = exactCount;
      manifest.progressiveInitialExactTerminal = exactTierTerminal;
      manifest.progressiveEntries = accumulatedEntries;
      manifest.progressiveSettledEntries = canonicalEntries.slice();
      manifest.focusedEntries = canonicalEntries.slice();
      manifest.baseEntries = canonicalEntries.slice();
      this.deriveMappedSearchEntries(manifest, new URL(window.location.href));
      if (!manifest.entries.length) {
        manifest.entries = accumulatedEntries;
        manifest.focusedEntries = [];
        manifest.baseEntries = null;
        manifest.progressiveEntries = null;
        manifest.progressiveLoading = false;
        manifest.sealed = false;
        manifest.outcome = 'pending';
        return false;
      }

      const exactKeys = new Set(exactEntries.map((entry) => entry.key).filter(Boolean));
      const exactPrefix = canonicalEntries.slice(0, exactEntries.length);
      if (exactPrefix.some((entry) => !exactKeys.has(entry.key))) {
        manifest.entries = accumulatedEntries;
        manifest.focusedEntries = [];
        manifest.baseEntries = null;
        manifest.progressiveEntries = null;
        manifest.progressiveLoading = false;
        manifest.sealed = false;
        manifest.outcome = 'pending';
        return false;
      }
      manifest.progressiveVisiblePrefixKeys = manifest.entries
        .slice(0, manifest.pageSize)
        .map((entry) => entry.key)
        .filter(Boolean);
      return true;
    }

    unstageProgressiveMappedSearchFrontier(manifest) {
      if (!manifest) return;
      const canonicalEntries = Array.isArray(manifest.progressiveEntries)
        ? manifest.progressiveEntries.slice()
        : manifest.entries.slice();
      manifest.entries = canonicalEntries;
      manifest.focusedEntries = [];
      manifest.baseEntries = null;
      manifest.progressiveEntries = null;
      manifest.progressiveLoading = false;
      manifest.progressiveCommitted = false;
      manifest.sealed = false;
      manifest.outcome = 'pending';
    }

    revealProgressiveMappedSearchFrontier(manifest, requestedPage, generation, grid, analysis) {
      if (
        generation !== this.searchCurationGeneration
        || manifest.cancelled
        || this.searchCurationIsFallbackLocked()
        || !manifest.sealed
      ) return false;
      const preparedPage = this.prepareMappedSearchPage(manifest, requestedPage);
      if (!preparedPage) return false;
      const renderedPage = this.applyPreparedMappedSearchPage(preparedPage, { animate: false });
      if (!renderedPage || !this.commitSearchCuration(manifest, renderedPage)) {
        this.rollbackPreparedMappedSearchPage(preparedPage);
        return false;
      }

      manifest.progressiveCommitted = true;
      manifest.requestCountAtReveal = this.v4RequestCount;
      this.root.dataset.v4RequestsAtInitialReveal = String(manifest.requestCountAtReveal);
      this.root.dataset.searchProgressiveLoading = 'true';
      this.root.dataset.searchMappedCloseCount = String(manifest.closeCount || 0);
      this.root.dataset.searchMappedResultCount = String(manifest.entries.length);
      this.markSearchCuration('search-progressive-frontier-ready', {
        query: manifest.query,
        resultCount: manifest.entries.length,
        closeCount: manifest.closeCount || 0,
        requestCount: manifest.requestCountAtReveal
      });

      let resolveInitialReveal = null;
      manifest.progressiveRevealPromise = new Promise((resolve) => {
        resolveInitialReveal = resolve;
      });

      this.revealCommittedSearchCuration(manifest, renderedPage, () => {
        const revealNow = window.performance?.now?.() || Date.now();
        const startedAt = Number(this.searchCuration?.startedAt || revealNow);
        manifest.initialRevealMs = Math.max(0, Math.round(revealNow - startedAt));
        this.root.dataset.v4InitialRevealMs = String(manifest.initialRevealMs);
        this.finalizePreparedMappedSearchPage(preparedPage, { animate: false });
        this.setProgressivePriceSortControlsPending(true);
        this.setMappedSearchLoading(grid, true);
        try {
          this.renderMappedSearchNavigation(manifest, renderedPage);
          this.syncMappedFilterControls(new URL(window.location.href));
          this.updateMappedFacetCounts(manifest, new URL(window.location.href));
        } catch (error) {
          this.log('Progressive mapped search navigation sync was skipped', error);
        }
        this.root.dispatchEvent(new CustomEvent('vcc:search-progressive-ready', {
          detail: {
            query: manifest.query,
            analysis,
            closeCount: manifest.closeCount || 0,
            resultCount: manifest.entries.length,
            requestCount: manifest.requestCountAtReveal,
            initialRevealMs: manifest.initialRevealMs
          }
        }));
        resolveInitialReveal?.(true);
      });
      return true;
    }

    refreshProgressiveMappedSearchTier(manifest, generation, grid, analysis, tier) {
      if (
        generation !== this.searchCurationGeneration
        || manifest.cancelled
        || this.destroyed
        || !this.root.isConnected
        || !this.isCurrentMappedManifest(manifest)
        || manifest.progressiveCommitted !== true
        || manifest.progressiveLoading !== true
        || !Array.isArray(manifest.progressiveEntries)
      ) return false;

      const canonicalEntries = manifest.progressiveEntries.slice();
      manifest.entries = canonicalEntries.slice();
      manifest.focusedEntries = canonicalEntries.slice();
      manifest.baseEntries = canonicalEntries.slice();
      manifest.relationshipSettled = false;
      manifest.outcome = 'focused-progressive';
      manifest.hasMore = true;
      manifest.focusedHasMore = true;
      this.deriveMappedSearchEntries(manifest, new URL(window.location.href));

      const requestedPage = this.currentSearchPage();
      const preparedPage = this.prepareMappedSearchPage(manifest, requestedPage);
      const renderedPage = preparedPage
        ? this.applyPreparedMappedSearchPage(preparedPage, { animate: false })
        : null;
      if (!preparedPage || !renderedPage) {
        if (preparedPage) this.rollbackPreparedMappedSearchPage(preparedPage);
        return false;
      }

      this.finalizePreparedMappedSearchPage(preparedPage, { animate: false });
      this.root.dataset.searchMappedCloseCount = String(manifest.closeCount || 0);
      this.root.dataset.searchMappedResultCount = String(manifest.entries.length);
      this.root.dataset.searchProgressiveLoading = 'true';
      this.setMappedSearchLoading(grid, true);
      try {
        this.renderMappedSearchNavigation(manifest, renderedPage);
        this.syncMappedFilterControls(new URL(window.location.href));
        this.updateMappedFacetCounts(manifest, new URL(window.location.href));
      } catch (error) {
        this.log('Progressive semantic tier navigation sync was skipped', error);
      }
      const announcement = this.root.querySelector('[data-ac-curation-announcement]');
      if (announcement) {
        announcement.textContent = `${manifest.entries.length} related results organized. Finding more.`;
      }
      this.root.dispatchEvent(new CustomEvent('vcc:search-progressive-tier-ready', {
        detail: {
          query: manifest.query,
          analysis,
          tierKey: tier?.key || '',
          tierOrder: Number(tier?.order || 0),
          resultCount: manifest.entries.length,
          requestCount: this.v4RequestCount
        }
      }));
      return true;
    }

    finalizeProgressiveMappedSearch(manifest, generation, grid, analysis) {
      if (
        generation !== this.searchCurationGeneration
        || manifest.cancelled
        || this.destroyed
        || !this.root.isConnected
        || !this.isCurrentMappedManifest(manifest)
      ) return false;

      this.clearV4ProgressiveDeadline(manifest);
      this.v4ProgressiveAbortController = null;
      manifest.progressiveLoading = false;
      this.setProgressivePriceSortControlsPending(false);
      this.root.dataset.searchProgressiveLoading = 'false';
      this.root.dataset.searchProgressiveComplete = 'true';
      this.root.dataset.searchManifestOutcome = manifest.outcome || 'unknown';
      this.root.dataset.searchManifestCapped = String(manifest.capped === true);
      this.root.dataset.searchMappedCloseCount = String(manifest.closeCount || 0);
      this.root.dataset.searchMappedResultCount = String(manifest.entries.length);
      this.root.dataset.searchCurationPhase = 'ready';
      this.root.dataset.searchCurationReason = manifest.outcome || 'focused-curated';
      this.root.dataset.searchCurationOutcome = manifest.outcome || 'focused-curated';

      const requestedPage = this.currentSearchPage();
      const preparedPage = this.prepareMappedSearchPage(manifest, requestedPage);
      const renderedPage = preparedPage
        ? this.applyPreparedMappedSearchPage(preparedPage, { animate: false })
        : null;
      if (preparedPage && renderedPage) {
        this.finalizePreparedMappedSearchPage(preparedPage, { animate: false });
      }
      const activePage = renderedPage || this.searchCurrentMappedPage || 1;
      this.setMappedSearchLoading(grid, false);
      try {
        if (activePage !== requestedPage) {
          const normalizedUrl = this.mappedSearchPageUrl(activePage);
          this.normalizeMappedSearchHistoryEntry(normalizedUrl, activePage);
        }
        this.renderMappedSearchNavigation(manifest, activePage);
        this.syncMappedFilterControls(new URL(window.location.href));
        this.updateMappedFacetCounts(manifest, new URL(window.location.href));
      } catch (error) {
        this.log('Progressive mapped search final navigation sync was skipped', error);
      }
      const announcement = this.root.querySelector('[data-ac-curation-announcement]');
      if (announcement) announcement.textContent = `${manifest.entries.length} results ready.`;
      this.observePathwaySearchFinal(manifest);
      this.root.dispatchEvent(new CustomEvent('vcc:search-manifest-ready', {
        detail: {
          query: manifest.query,
          analysis,
          closeCount: manifest.closeCount || 0,
          resultCount: manifest.entries.length,
          hasMore: manifest.hasMore,
          engineMode: this.engineMode,
          requestCount: this.v4RequestCount,
          exactResponseMs: this.v4ExactResponseMs,
          initialRevealMs: manifest.initialRevealMs,
          requestCountAtReveal: manifest.requestCountAtReveal
        }
      }));
      this.markSearchCuration('search-progressive-complete', {
        query: manifest.query,
        resultCount: manifest.entries.length,
        requestCount: this.v4RequestCount
      });
      this.commitFilterDialogObservedRoute();
      this.warmMappedSearchPages(manifest, activePage);
      return true;
    }

    async enhanceMappedSearchResults(analysis, engine, query, grid) {
      const cleanScopeUrl = new URL(window.location.href);
      if (cleanScopeUrl.searchParams.has('vcc_scope')) {
        cleanScopeUrl.searchParams.delete('vcc_scope');
        window.history.replaceState(window.history.state, '', cleanScopeUrl);
        if (this.searchCurationIsFallbackLocked()) {
          this.commitFilterDialogObservedRoute(cleanScopeUrl);
        }
      }
      if (this.searchCurationIsFallbackLocked()) return null;
      if (this.searchManifestBuild) return this.searchManifestBuild;
      if (this.searchManifest?.sealed && this.isCurrentMappedManifest(this.searchManifest)) {
        const cachedGeneration = this.searchCurationGeneration;
        this.setSearchCurationProgress(86, 'cached-manifest-ready');
        this.grantSearchCurationRenderGrace();
        if (this.searchCurationRemaining() <= 520) {
          this.fallbackMappedSearchCuration('deadline-before-cached-prepare', this.searchManifest);
          return this.searchManifest;
        }
        const preparedPage = this.prepareMappedSearchPage(this.searchManifest, this.currentSearchPage());
        if (!preparedPage) {
          if (this.commitMappedUsdPriceBandEmpty(this.searchManifest, 'cached-mapped-price-band-empty')) {
            return this.searchManifest;
          }
          this.fallbackMappedSearchCuration('cached-manifest-render-empty', this.searchManifest);
          return this.searchManifest;
        }
        this.setSearchCurationProgress(92, 'cached-page-materialized');
        if (this.searchCurationRemaining() <= 320) {
          this.fallbackMappedSearchCuration('deadline-before-cached-render', this.searchManifest);
          return this.searchManifest;
        }
        const page = this.applyPreparedMappedSearchPage(preparedPage, { animate: false });
        if (page) {
          this.setSearchCurationProgress(96, 'cached-order-applied');
          if (
            cachedGeneration !== this.searchCurationGeneration
            || this.searchCurationRemaining() <= 180
            || !this.commitSearchCuration(this.searchManifest, page)
          ) {
            this.rollbackPreparedMappedSearchPage(preparedPage);
            this.fallbackMappedSearchCuration('deadline-during-cached-commit', this.searchManifest);
            return this.searchManifest;
          }
          this.setSearchCurationProgress(98, 'cached-results-synchronized');
          this.revealCommittedSearchCuration(this.searchManifest, page, () => {
            this.finalizePreparedMappedSearchPage(preparedPage, { animate: false });
            try {
              this.renderMappedSearchNavigation(this.searchManifest, page);
              this.restoreRequestedMappedSearchScope(this.searchManifest);
              this.syncMappedFilterControls(new URL(window.location.href));
              this.updateMappedFacetCounts(this.searchManifest, new URL(window.location.href));
            } catch (error) {
              this.log('Cached mapped search navigation sync was skipped', error);
            }
            try {
              this.setMappedSearchLoading(grid, false);
            } catch (error) {
              this.log('Cached mapped search loading cleanup was skipped', error);
            }
          }, () => {
            this.warmMappedSearchPages(this.searchManifest, page);
          });
        } else {
          this.rollbackPreparedMappedSearchPage(preparedPage);
          this.fallbackMappedSearchCuration('cached-manifest-render-empty', this.searchManifest);
        }
        return this.searchManifest;
      }

      this.setMappedSearchLoading(grid, true);
      const requestedPage = this.currentSearchPage();
      let preserveRequestedBroadPage = this.mappedSearchScopeRequested();
      const generation = this.searchCurationGeneration;
      this.setSearchCurationPhase('exact');
      const manifest = this.createMappedSearchManifest(analysis, query, grid, 0, engine);
      this.searchManifest = manifest;
      if (this.searchCuration) {
        this.searchCuration.safeExactRequired = manifest.sources.some((state) => {
          return state?.source?.exact && state.source.safeExact;
        });
      }
      this.markSearchCuration('search-manifest-start', { query, requestedPage });
      const closeTarget = this.mappedSearchCloseTarget(engine, query, analysis);
      manifest.closeTarget = Math.max(0, Number(closeTarget) || 0);
      if (
        generation !== this.searchCurationGeneration
        || this.searchCurationIsFallbackLocked()
        || this.searchCurationRemaining() <= 0
      ) {
        this.fallbackSearchCuration('deadline-before-manifest');
        return null;
      }

      let activePreparedPage = null;
      this.searchManifestBuild = this.buildMappedSearchManifest(
        manifest,
        engine,
        analysis,
        null,
        {
          onInitialReady: (frontierManifest) => {
            return this.revealProgressiveMappedSearchFrontier(
              frontierManifest,
              requestedPage,
              generation,
              grid,
              analysis
            );
          },
          onTierSettled: (tierManifest, tier) => {
            return this.refreshProgressiveMappedSearchTier(
              tierManifest,
              generation,
              grid,
              analysis,
              tier
            );
          }
        }
      )
        .then(async (initialManifest) => {
          let completedManifest = initialManifest;
          if (
            generation === this.searchCurationGeneration
            && !completedManifest.cancelled
            && !this.searchCurationIsFallbackLocked()
          ) {
            completedManifest = await this.replayMappedSearchManifestOnce(
              completedManifest,
              engine,
              analysis,
              query,
              grid,
              generation
            );
          }
          this.root.dataset.searchManifestOutcome = completedManifest.outcome || 'unknown';
          this.root.dataset.searchManifestCapped = String(completedManifest.capped === true);
          this.root.dataset.searchManifestState = completedManifest.sources.map((state) => {
            return [
              state.scope,
              state.page,
              state.hasMore ? 'more' : 'done',
              state.failed ? 'failed' : 'ok'
            ].join(':');
          }).join('|');
          this.markSearchCuration('search-manifest-sealed', {
            query,
            resultCount: completedManifest.entries.length
          });
          if (
            generation !== this.searchCurationGeneration
            || completedManifest.cancelled
            || this.searchCurationIsFallbackLocked()
          ) {
            return completedManifest;
          }

          if (completedManifest.progressiveCommitted === true) {
            this.finalizeProgressiveMappedSearch(completedManifest, generation, grid, analysis);
            return completedManifest;
          }

          if (!completedManifest.sealed) {
            this.fallbackMappedSearchCuration('insufficient-curated-results', completedManifest);
            return completedManifest;
          }
          if (preserveRequestedBroadPage) {
            this.grantSearchCurationRenderGrace(7200);
            const directScopeIntentGeneration = ++this.searchScopeGeneration;
            const directBroadTimeoutMs = Math.max(
              1200,
              Math.min(7000, this.searchCurationRemaining() - 240)
            );
            const broadLoaded = completedManifest.broadAvailable === true
              && await this.loadMappedBroadScope(completedManifest, {
                intentGeneration: directScopeIntentGeneration,
                timeoutMs: directBroadTimeoutMs
              });
            if (
              generation !== this.searchCurationGeneration
              || directScopeIntentGeneration !== this.searchScopeGeneration
              || completedManifest.cancelled
              || this.searchCurationIsFallbackLocked()
            ) return completedManifest;
            if (broadLoaded) {
              delete this.root.dataset.searchScopeFailure;
              completedManifest.broadExpanded = true;
              completedManifest.baseEntries = (completedManifest.focusedEntries || [])
                .concat(completedManifest.broadEntries || []);
              completedManifest.hasMore = completedManifest.broadHasMore;
              this.deriveMappedSearchEntries(completedManifest, new URL(window.location.href));
            } else {
              completedManifest.broadError = true;
              this.root.dataset.searchScopeFailure = completedManifest.broadLoadFailure || 'load-failed';
              preserveRequestedBroadPage = false;
              const focusedUrl = new URL(window.location.href);
              focusedUrl.searchParams.delete('vcc_scope');
              window.history.replaceState(
                { vccMappedSearch: true, page: requestedPage },
                '',
                focusedUrl
              );
              completedManifest.broadExpanded = false;
              completedManifest.baseEntries = (completedManifest.focusedEntries || []).slice();
              completedManifest.hasMore = completedManifest.focusedHasMore;
              this.deriveMappedSearchEntries(completedManifest, focusedUrl);
            }
          }
          const continuationOnly = completedManifest.authoritativeTeamRoute === true
            && completedManifest.hasMore === true;
          if (!completedManifest.entries.length && !continuationOnly) {
            if (this.commitMappedUsdPriceBandEmpty(completedManifest, 'mapped-price-band-empty')) {
              return completedManifest;
            }
            this.fallbackMappedSearchCuration('no-curated-results', completedManifest);
            return completedManifest;
          }

          this.grantSearchCurationRenderGrace();
          this.setSearchCurationPhase('arrange');
          if (this.searchCurationRemaining() <= 520) {
            this.fallbackMappedSearchCuration('deadline-before-grid-prepare', completedManifest);
            return completedManifest;
          }
          activePreparedPage = this.prepareMappedSearchPage(completedManifest, requestedPage);
          if (!activePreparedPage) {
            this.fallbackMappedSearchCuration('curated-render-empty', completedManifest);
            return completedManifest;
          }
          this.setSearchCurationProgress(92, 'page-cards-materialized');
          if (this.searchCurationRemaining() <= 320) {
            this.fallbackMappedSearchCuration('deadline-after-manifest', completedManifest);
            return completedManifest;
          }
          const renderedPage = this.applyPreparedMappedSearchPage(activePreparedPage, { animate: false });
          if (!renderedPage) {
            this.rollbackPreparedMappedSearchPage(activePreparedPage);
            this.fallbackMappedSearchCuration('curated-render-empty', completedManifest);
            return completedManifest;
          }
          this.setSearchCurationProgress(96, 'final-order-applied');
          if (
            generation !== this.searchCurationGeneration
            || this.searchCurationRemaining() <= 180
            || !this.commitSearchCuration(completedManifest, renderedPage)
          ) {
            this.rollbackPreparedMappedSearchPage(activePreparedPage);
            this.fallbackMappedSearchCuration('deadline-during-grid-commit', completedManifest);
            return completedManifest;
          }

          this.root.dataset.searchMappedCloseCount = String(completedManifest.closeCount || 0);
          this.root.dataset.searchMappedResultCount = String(completedManifest.entries.length);
          this.setSearchCurationProgress(98, 'results-synchronized');
          this.revealCommittedSearchCuration(completedManifest, renderedPage, () => {
            this.finalizePreparedMappedSearchPage(activePreparedPage, { animate: false });
            try {
              this.observePathwaySearchFinal(completedManifest);
              this.root.dispatchEvent(new CustomEvent('vcc:search-manifest-ready', {
                detail: {
                  query: completedManifest.query,
                  analysis,
                  closeCount: completedManifest.closeCount || 0,
                  resultCount: completedManifest.entries.length,
                  hasMore: completedManifest.hasMore,
                  engineMode: this.engineMode,
                  requestCount: this.engineMode === 'v4_metafield'
                    ? this.v4RequestCount
                    : undefined,
                  exactResponseMs: this.engineMode === 'v4_metafield'
                    ? this.v4ExactResponseMs
                    : undefined
                }
              }));
            } catch (error) {
              this.log('Mapped search manifest announcement was skipped', error);
            }
            try {
              const currentUrlPage = this.currentSearchPage();
              if (renderedPage !== currentUrlPage) {
                const normalizedUrl = this.mappedSearchPageUrl(renderedPage);
                this.normalizeMappedSearchHistoryEntry(normalizedUrl, renderedPage);
              }
            } catch (error) {
              this.log('Mapped search URL normalization was skipped', error);
            }
            try {
              this.renderMappedSearchNavigation(completedManifest, renderedPage);
              this.restoreRequestedMappedSearchScope(completedManifest);
              this.syncMappedFilterControls(new URL(window.location.href));
              this.updateMappedFacetCounts(completedManifest, new URL(window.location.href));
            } catch (error) {
              this.log('Mapped search navigation sync was skipped', error);
            }
            try {
              this.setMappedSearchLoading(grid, false);
            } catch (error) {
              this.log('Mapped search loading cleanup was skipped', error);
            }
          }, () => {
            this.warmMappedSearchPages(completedManifest, renderedPage);
          });
          return completedManifest;
        })
        .catch((error) => {
          const currentManifest = this.searchManifest || manifest;
          if (
            generation !== this.searchCurationGeneration
            || this.searchCurationIsFallbackLocked()
            || this.destroyed
            || !this.root.isConnected
          ) return currentManifest;
          if (currentManifest?.progressiveCommitted === true) {
            const authoritativeTeamRoute = currentManifest?.analysis?.authoritativeTeamRoute === true;
            this.clearV4ProgressiveDeadline(currentManifest);
            this.v4ProgressiveAbortController?.abort();
            this.v4ProgressiveAbortController = null;
            const stableEntries = Array.isArray(currentManifest.progressiveSettledEntries)
              ? currentManifest.progressiveSettledEntries.slice()
              : Array.isArray(currentManifest.exactEntries)
                ? currentManifest.exactEntries.slice()
                : currentManifest.entries.slice();
            currentManifest.entries = stableEntries;
            currentManifest.outcome = authoritativeTeamRoute
              ? 'recognized-v4-partial'
              : 'focused-degraded';
            if (authoritativeTeamRoute) {
              currentManifest.recognizedV4FailClosed = true;
              currentManifest.recognizedV4FailureReason = currentManifest.progressiveTimedOut === true
                ? 'progressive-timeout'
                : 'progressive-continuation-error';
              delete currentManifest.degradedReason;
            } else {
              currentManifest.degradedReason = currentManifest.progressiveTimedOut === true
                ? 'progressive-timeout'
                : 'progressive-continuation-error';
            }
            currentManifest.relationshipSettled = false;
            currentManifest.hasMore = !authoritativeTeamRoute;
            currentManifest.focusedHasMore = !authoritativeTeamRoute;
            currentManifest.viewHasMore = !authoritativeTeamRoute;
            currentManifest.sealed = currentManifest.entries.length > 0;
            currentManifest.focusedEntries = currentManifest.entries.slice();
            currentManifest.baseEntries = currentManifest.entries.slice();
            currentManifest.progressiveEntries = null;
            currentManifest.progressiveLoading = false;
            this.deriveMappedSearchEntries(currentManifest, new URL(window.location.href));
            this.finalizeProgressiveMappedSearch(currentManifest, generation, grid, analysis);
            this.log('Progressive mapped search continuation ended safely', error);
            return currentManifest;
          }
          if (this.searchCuration?.lockMode === 'curated') {
            try {
              this.setMappedSearchLoading(grid, false);
            } catch (cleanupError) {
              this.log('Mapped search error cleanup was skipped', cleanupError);
            }
            this.revealCommittedSearchCuration(
              currentManifest,
              this.searchCurrentMappedPage || requestedPage
            );
            this.log('A non-critical mapped search finishing step was skipped', error);
            return currentManifest;
          }
          if (activePreparedPage) this.rollbackPreparedMappedSearchPage(activePreparedPage);
          this.setMappedSearchLoading(grid, false);
          this.fallbackMappedSearchCuration('curation-error', currentManifest);
          this.log('Stable mapped search manifest was skipped', error);
          return currentManifest;
        })
        .finally(() => {
          this.searchManifestBuild = null;
        });

      return this.searchManifestBuild;
    }

    searchEngine() {
      if (this.engineMode === 'native_shopify') return null;
      const engine = this.engineMode === 'v4_metafield'
        ? window.VCCSearchV4
        : (window.VCCSmartSearch || window.VCCSearchIntelligence);
      if (!engine || !engine.productScore || !(engine.analyzeSearchAsync || engine.analyzeSearch)) return null;
      return engine;
    }

    cachedSearchAnalysis(query) {
      try {
        const key = this.engineMode === 'v4_metafield'
          ? 'vcc-search-analysis-plan-v4-11-2-complete-pagination-canada-1'
          : 'vcc-search-analysis-plan-v1';
        const cached = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        const createdAt = Number(cached?.createdAt || 0);
        const strictQueryKey = (value) => {
          let normalized = String(value || '').replace(/\s+/g, ' ').trim();
          try {
            normalized = normalized.normalize('NFKC');
          } catch (_error) {}
          return normalized.toLocaleLowerCase();
        };
        const queryKey = strictQueryKey(query);
        const sameQuery = Boolean(queryKey) && strictQueryKey(cached?.query) === queryKey;
        if (
          !cached
          || !createdAt
          || Date.now() - createdAt > 60000
          || !sameQuery
          || cached.complete !== true
          || cached.analysis?.databaseReady !== true
        ) {
          window.sessionStorage.removeItem(key);
          return null;
        }
        window.sessionStorage.removeItem(key);
        return cached.analysis;
      } catch (_error) {
        try {
          window.sessionStorage.removeItem('vcc-search-analysis-plan-v1');
        } catch (_storageError) {}
        return null;
      }
    }

    waitForSearchEngine() {
      const readyEngine = this.searchEngine();
      if (readyEngine) return Promise.resolve(readyEngine);
      if (this.searchEnginePromise) return this.searchEnginePromise;

      this.searchEnginePromise = new Promise((resolve) => {
        let settled = false;
        const cleanup = () => {
          document.removeEventListener('vcc:smart-search-ready', resolveWhenReady);
          document.removeEventListener('vcc:search-intelligence-ready', resolveWhenReady);
          document.removeEventListener('vcc:search-v4-ready', resolveWhenReady);
          window.removeEventListener('load', resolveWhenReady);
        };
        const done = () => {
          const engine = this.searchEngine();
          if (!engine && !settled) return;
          if (settled) return;
          settled = true;
          cleanup();
          resolve(engine || null);
        };
        const remaining = this.searchCurationRemaining();
        const waitBudget = Number.isFinite(remaining)
          ? Math.max(80, Math.min(1800, remaining))
          : 1800;
        const timeout = window.setTimeout(() => {
          settled = true;
          cleanup();
          resolve(this.searchEngine());
        }, waitBudget);
        const resolveWhenReady = () => {
          const engine = this.searchEngine();
          if (!engine || settled) return;
          window.clearTimeout(timeout);
          done();
        };

        document.addEventListener('vcc:smart-search-ready', resolveWhenReady);
        document.addEventListener('vcc:search-intelligence-ready', resolveWhenReady);
        document.addEventListener('vcc:search-v4-ready', resolveWhenReady);
        window.addEventListener('load', resolveWhenReady);

        resolveWhenReady();
      }).finally(() => {
        this.searchEnginePromise = null;
      });

      return this.searchEnginePromise;
    }

    applySearchOrder() {
      if (this.kind !== 'search' || this.searchOrderPending) return;
      if (this.searchCurationIsFallbackLocked()) return;
      const selectedSort = new URLSearchParams(window.location.search).get('sort_by');
      if (
        selectedSort
        && selectedSort !== 'relevance'
        && !this.mappedSearchStateRequested()
      ) return;
      const params = new URLSearchParams(window.location.search);
      const query = params.get('vcc_raw') || params.get('q') || this.config.searchQuery;
      const grid = this.root.querySelector('[data-ac-grid]');
      if (!query || !grid) return;
      const engine = this.searchEngine();
      if (!engine) {
        this.setSearchCurationPhase('interpret');
        this.setMappedSearchLoading(grid, true);
        if (!this.searchEngineWaiting) {
          this.searchEngineWaiting = true;
          this.waitForSearchEngine().then((readyEngine) => {
            this.searchEngineWaiting = false;
            if (this.searchCurationIsFallbackLocked()) return;
            if (readyEngine) {
              this.markSearchCuration('search-engine-ready');
              this.applySearchOrder();
            } else {
              this.setMappedSearchLoading(grid, false);
              this.fallbackSearchCuration('search-engine-unavailable');
            }
          });
        }
        return;
      }

      this.setMappedSearchLoading(grid, true);
      this.setSearchCurationPhase('interpret');
      this.setSearchCurationProgress(8, 'search-engine-ready');

      if (this.engineMode === 'v4_metafield') {
        this.clearV4ProgressiveDeadline(this.searchManifest);
        this.v4ProgressiveAbortController?.abort();
        this.v4ProgressiveAbortController = null;
        this.v4RecognizedAnalysis = null;
        this.v4NativeFallbackAuthorized = false;
        this.setProgressivePriceSortControlsPending(false);
        this.v4RequestCount = 0;
        this.v4ExactResponseMs = null;
        this.root.dataset.v4SearchRequests = '0';
        delete this.root.dataset.v4ExactResponseMs;
        delete this.root.dataset.v4SessionCacheHit;
        delete this.root.dataset.v4InitialRevealMs;
        delete this.root.dataset.v4RequestsAtInitialReveal;
        delete this.root.dataset.searchProgressiveLoading;
        delete this.root.dataset.searchProgressiveComplete;
      }

      this.searchOrderPending = true;
      const generation = ++this.searchCurationGeneration;
      this.setSearchCurationProgress(14, 'query-analysis-started');
      this.markSearchCuration('search-analysis-start');
      const cachedAnalysis = this.cachedSearchAnalysis(query);
      const analysisPromise = cachedAnalysis
        ? Promise.resolve(cachedAnalysis)
        : this.abortableSearchCuration(engine.analyzeSearchAsync
          ? engine.analyzeSearchAsync(query)
          : Promise.resolve(engine.analyzeSearch(query)));

      analysisPromise
        .then((analysis) => {
          if (analysis?.nativeFallback === true) {
            this.v4NativeFallbackAuthorized = true;
            this.setMappedSearchLoading(grid, false);
            this.fallbackSearchCuration('v4-unrecognized-native-fallback');
            return null;
          }
          if (this.engineMode === 'v4_metafield') {
            this.v4RecognizedAnalysis = analysis || null;
            this.v4NativeFallbackAuthorized = false;
          }
          if (
            generation !== this.searchCurationGeneration
            || this.searchCurationIsFallbackLocked()
            || this.searchCurationRemaining() <= 0
          ) {
            this.fallbackSearchCuration('deadline-after-analysis');
            return null;
          }
          this.setSearchCurationProgress(26, 'query-analysis-complete');
          this.markSearchCuration('search-analysis-ready');
          return this.enhanceMappedSearchResults(analysis, engine, query, grid);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || this.searchCurationIsFallbackLocked()) return;
          this.setMappedSearchLoading(grid, false);
          this.fallbackSearchCuration('analysis-error');
          this.log('Smart search ordering was skipped', error);
        })
        .finally(() => {
          if (generation === this.searchCurationGeneration) this.searchOrderPending = false;
        });
    }

    imageWarmConcurrency(mode = 'grid') {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection?.saveData) return 2;
      const effectiveType = String(connection?.effectiveType || '').toLowerCase();
      if (effectiveType.includes('2g')) return 2;
      if (effectiveType === '3g') return mode === 'atlas' ? 4 : 3;
      return mode === 'atlas' ? 8 : 4;
    }

    startGridImage(image, priority = 'low', tracked = false) {
      if (!(image instanceof HTMLImageElement) || !image.isConnected) return false;
      if (image.dataset.acImageState === 'loading' || image.dataset.acImageState === 'ready') return false;
      if (image.complete && image.naturalWidth > 0) {
        image.dataset.acImageState = 'ready';
        this.gridImageObserver?.unobserve(image);
        return false;
      }

      image.dataset.acImageState = 'loading';
      image.loading = 'eager';
      image.fetchPriority = priority;
      this.gridImageObserver?.unobserve(image);
      if (tracked) this.gridImageActive += 1;

      let settled = false;
      let timeout = 0;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        image.dataset.acImageState = image.naturalWidth > 0 ? 'ready' : 'error';
        if (tracked) this.gridImageActive = Math.max(0, this.gridImageActive - 1);
        if (this.gridWarmStarted) this.drainGridImageQueue();
      };

      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      timeout = window.setTimeout(finish, 15000);
      if (image.complete) window.queueMicrotask(finish);
      return true;
    }

    drainGridImageQueue() {
      if (!this.gridWarmStarted) return;
      const limit = this.imageWarmConcurrency('grid');
      while (this.gridImageActive < limit && this.gridImageQueue.length) {
        const image = this.gridImageQueue.shift();
        const priority = image?.dataset.acImagePriority || 'low';
        if (image) delete image.dataset.acImagePriority;
        this.startGridImage(image, priority, true);
      }
    }

    scheduleGridImageQueue() {
      if (this.gridWarmScheduled) return;
      this.gridWarmScheduled = true;

      const begin = () => {
        const run = () => {
          this.gridWarmStarted = true;
          this.drainGridImageQueue();
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 1200 });
        } else {
          window.setTimeout(run, 180);
        }
      };

      if (document.readyState === 'complete') begin();
      else window.addEventListener('load', begin, { once: true });
    }

    configureImageLoading(scope = this.root) {
      const images = Array.from(scope.querySelectorAll('.vcc-ac-image img'));
      if (!images.length) return;

      if (!this.gridImageObserver && 'IntersectionObserver' in window) {
        this.gridImageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.startGridImage(entry.target, 'auto');
          });
        }, { rootMargin: '1200px 0px' });
      }

      images.forEach((image, index) => {
        if (image.dataset.acImageManaged === 'true') return;
        image.dataset.acImageManaged = 'true';
        const rect = image.getBoundingClientRect();
        const nearViewport = rect.top < window.innerHeight + 900 && rect.bottom > -300;
        if (nearViewport) {
          this.startGridImage(image, index < 6 ? 'high' : 'auto');
          return;
        }
        image.dataset.acImageState = 'queued';
        this.gridImageQueue.push(image);
        this.gridImageObserver?.observe(image);
      });

      this.scheduleGridImageQueue();
    }

    destroy() {
      this.destroyed = true;
      this.searchViewGeneration += 1;
      this.searchWarmGeneration += 1;
      this.searchNativeWarmGeneration += 1;
      this.searchNativeAbortController?.abort();
      this.searchNativeAbortController = null;
      this.clearV4ProgressiveDeadline(this.searchManifest);
      this.v4ProgressiveAbortController?.abort();
      this.v4ProgressiveAbortController = null;
      this.searchScopeAbortController?.abort();
      this.searchScopeAbortController = null;
      this.searchScopeLoad = null;
      this.searchNativePageCache.clear();
      this.searchNativePagePromises.clear();
      this.mappedSearchHtmlCache.clear();
      this.searchPageImageWarmers.clear();
      if (this.searchCurationFinisherFrame) {
        window.cancelAnimationFrame(this.searchCurationFinisherFrame);
        this.searchCurationFinisherFrame = 0;
      }
      window.clearTimeout(this.searchCurationFinisherTimer);
      this.searchCurationFinisherTimer = null;
      this.mappedSearchTransitionGeneration += 1;
      if (this.mappedSearchTransitionFrame) {
        window.cancelAnimationFrame(this.mappedSearchTransitionFrame);
        this.mappedSearchTransitionFrame = 0;
      }
      window.clearTimeout(this.mappedSearchTransitionTimer);
      this.mappedSearchTransitionTimer = null;
      if (this.handleWishlistSync) {
        document.removeEventListener('vcc:wishlist:ready', this.handleWishlistSync);
        document.removeEventListener('vcc:wishlist:updated', this.handleWishlistSync);
      }
      this.siteObserver?.disconnect();
      this.mobileDockObserver?.disconnect();
      if (this.mobileDockSync) {
        window.removeEventListener('scroll', this.mobileDockSync);
        this.mobileDockScrollRoot?.removeEventListener('scroll', this.mobileDockSync);
        this.mobileDockSync = null;
      }
      if (this.mobileDockFrame) {
        window.cancelAnimationFrame(this.mobileDockFrame);
        this.mobileDockFrame = null;
      }
      this.mobileDockScrollRoot = null;
      this.gridImageObserver?.disconnect();
      this.gridImageQueue = [];
      this.atlasPreloadQueue = [];
      mqMobile.removeEventListener?.('change', this.handleResponsiveChange);
      mqReduced.removeEventListener?.('change', this.handleReducedMotionChange);
      mqDark.removeEventListener?.('change', this.handleDarkModeChange);
      if (this.handleWindowResize) window.removeEventListener('resize', this.handleWindowResize);
      if (this.handlePageShow) window.removeEventListener('pageshow', this.handlePageShow);
      this.handleResponsiveChange = null;
      this.handleReducedMotionChange = null;
      this.handleDarkModeChange = null;
      this.handleWindowResize = null;
      this.handlePageShow = null;
      this.destroyFilterDialogHistory();
      if (this.handlePopState) {
        window.removeEventListener('popstate', this.handlePopState);
      }
      this.restoreNativeSearchPriceControls();
      this.cancelSearchCurationWork();
      if (this.searchCuration) {
        this.searchCuration.onDeadline = null;
        window.clearTimeout(this.searchCuration.deadlineTimer);
        window.clearTimeout(this.searchCuration.visibleTimer);
        if (typeof this.searchCuration.cancelRelease === 'function') {
          this.searchCuration.cancelRelease();
        } else {
          window.cancelAnimationFrame(this.searchCuration.releaseFrameOne);
          window.cancelAnimationFrame(this.searchCuration.releaseFrameTwo);
          window.clearTimeout(this.searchCuration.releaseTimer);
        }
      }
      window.clearTimeout(this.atlasPreloadWaitTimer);
      window.clearTimeout(this.atlasAuxTimer);
    }
  }

  function init(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    const instance = new AdvancedCollections(root);
    instances.set(root, instance);
    instance.observePathwaySearchSnapshot();
  }

  function scan(scope = document) {
    scope.querySelectorAll?.('[data-vcc-ac]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('vcc:pathway:search-ready', () => {
    if (typeof window.__vccPathwaySearch !== 'function') return;
    Array.from(document.querySelectorAll('[data-vcc-ac]')).slice(0,2).forEach(root => instances.get(root)?.observePathwaySearchSnapshot());
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target?.matches?.('[data-vcc-ac]')) roots.push(event.target);
    event.target?.querySelectorAll?.('[data-vcc-ac]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
      initialized.delete(root);
    });
  });
})();
